import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Card, Modal } from "react-bootstrap";
import Select from "react-select";
import Editor from "react-simple-code-editor";
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism.css";
import toast from "react-hot-toast";

import sendoraLogo from "../../../../images/landmark/sendora-logo-navbar-minimal-2.png";
import SalesforceLogo from "../../../../images/landmark/salesforce-logo.svg";
import BlueSquaresLoading from "../../../../images/landmark/blue-squares-loading.svg";
import BlueCasualLoader from "../../../../images/landmark/blue-casual-loader.svg";
import BlueRipple from "../../../../images/landmark/blue-ripple.svg";
import { ReactComponent as DiceOneIcon } from "../../../../images/landmark/die-one.svg";
import { ReactComponent as DiceTwoIcon } from "../../../../images/landmark/die-two.svg";
import { ReactComponent as DiceThreeIcon } from "../../../../images/landmark/die-three.svg";
import { ReactComponent as DiceFourIcon } from "../../../../images/landmark/die-four.svg";
import { ReactComponent as DiceFiveIcon } from "../../../../images/landmark/die-five.svg";
import { ReactComponent as DiceSixIcon } from "../../../../images/landmark/die-six.svg";
import EmployeeInfo from "./EmployeeInfo";
import CampaignContactUploader from "./CampaignContactUploader";
import CustomTooltip from "../../Shared/CustomTooltip";
import ApiService from "../../../../services/ApiService";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import { ThemeContext } from "../../../../context/ThemeContext";
import { epochToDateTimeString } from "../../../../utils";
import { useDocumentTitle } from "../../../utils";
import landmarkTeamEmails from "../../../../data/landmarkTeamEmails.json";
import {
  SALESFORCE_FILTER_TOGGLE_KEYS,
  DEFAULT_SALESFORCE_FILTER_TOGGLES,
  SALESFORCE_FILTER_RULE_DEFS,
} from "./salesforceLeadFilterRules";

const DEFAULT_OPTIONS = {
  senderEmails: [],
  stopSendingOnReply: false,
  stopCampaignForCompanyOnReply: false,
  stopSendingOnAutoReply: false,
  campaignPriority: "medium",
  dailyLimit: 5000,
  minTimeGapMinutes: 0.096,
  prioritizeNewLeads: false,
  aiVariables: [],
  cc: [],
  bcc: [],
  enableMillionVerifier: true,
  emailVerificationMode: "single",
  syncInvalidMailboxToSalesforce: true,
  pruneQueuedLeadsOnSalesforceStatusChange: true,
  selectedBrandVoiceId: "",
  brandVoiceCustomVoices: [],
};
const US_MAINLAND_ET_PT_BUSINESS_HOURS_TIMEZONE = "US_MAINLAND_ET_PT_BUSINESS_HOURS";
const US_MAINLAND_ET_PT_OFFSET_MINUTES = 3 * 60;
const ET_TIMEZONE = "America/New_York";

const DEFAULT_SCHEDULE = {
  scheduleId: "",
  name: "Default schedule",
  fromTime: "09:00",
  toTime: "17:00",
  timezone: US_MAINLAND_ET_PT_BUSINESS_HOURS_TIMEZONE,
  days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
  isActive: true,
  bypassSchedule: false,
  pauseSendingAfterFridayNoon: true,
};

const DAY_OPTIONS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const TIMEZONE_OPTIONS = [
  {
    value: US_MAINLAND_ET_PT_BUSINESS_HOURS_TIMEZONE,
    label: "US Mainland (ET–PT Business Hours)",
  },
  { value: "America/New_York", label: "Eastern Time (US & Canada)" },
  { value: "America/Chicago", label: "Central Time (US & Canada)" },
  { value: "America/Denver", label: "Mountain Time (US & Canada)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US & Canada)" },
  { value: "America/Phoenix", label: "Arizona Time" },
  { value: "America/Toronto", label: "Toronto" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Asia/Dubai", label: "Dubai" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "UTC", label: "UTC" },
];
const LANDMARK_EMAIL_SUFFIX = "@landmarkventures.com";
const SENDORA_SENDING_INFRASTRUCTURES = [
  { key: "amazon_ses", label: "Amazon SES" },
  { key: "mailgun", label: "Mailgun" },
  { key: "inboxroad", label: "Inboxroad" },
];
const SENDORA_DEFAULT_DOMAIN_INFRASTRUCTURE_RULES = [
  {
    domainSuffix: "landmarkventures.com",
    defaultInfrastructure: "amazon_ses",
    allowedInfrastructures: ["amazon_ses"],
  },
  {
    domainSuffix: "digital.landmarkventures.com",
    defaultInfrastructure: "mailgun",
    allowedInfrastructures: ["mailgun", "inboxroad"],
  },
  {
    domainSuffix: "industrialtech.landmarkventures.com",
    defaultInfrastructure: "mailgun",
    allowedInfrastructures: ["mailgun"],
  },
];
const SENDORA_DOMAIN_DISPLAY_ORDER = SENDORA_DEFAULT_DOMAIN_INFRASTRUCTURE_RULES.map((entry) =>
  normalizeDomainSuffix(entry?.domainSuffix || "")
).filter(Boolean);
const PREVIEW_RECIPIENT_CUSTOM_VALUE = "__custom__";
const PREVIEW_RECIPIENT_PREF_STORAGE_KEY_PREFIX = "sendora_preview_recipient_pref_v1::";
const ENABLE_MULTI_STEP_SEQUENCES = true;
const DEFAULT_SENDORA_MODE_FLAGS = {
  isProd: false,
  disableActualSend: false,
  recipientOverrideMode: false,
  recipientOverrideEmail: "",
  suppressSalesforcePostSendChanges: false,
};
const DETAIL_TABS = [
  { key: "emails", label: "Emails" },
  { key: "sequences", label: "Sequences" },
  { key: "leads", label: "Leads" },
  { key: "schedule", label: "Schedule" },
  { key: "options", label: "Options" },
  { key: "run", label: "Run" },
];
const SEQUENCE_WAIT_UNITS = ["days", "hours", "minutes"];
const CAMPAIGN_SPEED_PRESETS = {
  fast: { multiplier: 1, dailyLimit: 50000, label: "Fast" },
  medium: { multiplier: 2.5, dailyLimit: 5000, label: "Medium" },
  slow: { multiplier: 5, dailyLimit: 1000, label: "Slow" },
  custom: { multiplier: 1, dailyLimit: 12000, label: "Custom" },
};
const SENDORA_BATCH_MIN_WAIT_SECONDS = 60;
const SENDORA_BATCH_MIN_INTRA_SEND_SECONDS = 0.1;
const CUSTOM_DURATION_DAY_OPTIONS = Array.from({ length: 8 }, (_, idx) => idx);
const CUSTOM_DURATION_HOUR_OPTIONS = Array.from({ length: 25 }, (_, idx) => idx);
const CUSTOM_DURATION_MINUTE_OPTIONS = Array.from({ length: 13 }, (_, idx) => idx * 5);
const MAX_CUSTOM_DURATION_SECONDS = ((7 * 24 + 24) * 60 + 60) * 60;
const EDITOR_FONT_FAMILY_OPTIONS = [
  "Arial",
  "Helvetica",
  "Times New Roman",
  "Georgia",
  "Verdana",
  "Tahoma",
  "Trebuchet MS",
];
const EDITOR_FONT_SIZE_OPTIONS = [10, 11, 12, 13, 14, 16, 18];
const EDITOR_DISPLAY_FONT_FAMILY = "Arial";
const EDITOR_DISPLAY_FONT_SIZE_PT = 10;
const RECIPIENT_VARIABLE_TOKENS = ["firstName", "lastName", "fullName", "email", "company", "title"];
const SENDER_VARIABLE_TOKENS = [
  "senderFirstName",
  "senderLastName",
  "senderFullName",
  "senderEmail",
  "senderName",
  "senderSignature",
];
const AI_VARIABLE_PROMPT_MAX_CHARS = 4000;
const DEFAULT_SENDER_SIGNATURE_TEMPLATE = `<div style="font-size:10.0pt;font-family:&quot;Arial&quot;,sans-serif;"><span style="color:black;">_______________________________</span><br><span style="color:black;"><span style="display: inline; white-space: nowrap;">{{senderFullName}}</span><br><em>Senior Coordinator, </em></span><em>Executive <span style="color:black;">Events</span></em><br><span style="color:black;">Landmark Ventures<br>1460 Broadway&nbsp;| New York, NY&nbsp;10036 </span><br><span style="color:black;">_______________________________</span></div>`;
const SENDORA_BRAND_VOICE_TEMPLATES = [
  {
    brandVoiceId: "template_eric_sugar_landmark",
    name: "Eric Sugar",
    prompt: `Eric Sugar / Landmark - Email Voice Prompt
Role & Perspective
Write as Eric Sugar: a senior operator, investor, and advisory-firm president communicating peer-to-peer. Assume credibility and shared context. Do not pitch. Frame, align, and move things forward.

Voice Characteristics:
- Calm, confident, and pragmatic
- Clear and direct, never salesy
- Professional with restrained warmth
- Collaborative, solution-oriented
- Comfortable setting boundaries without defensiveness

Tone Rules:
- Default tone: composed, adult, respectful of time
- Sensitive topics: neutral, precise, good-faith
- Positive updates: measured optimism, no hype
- Never gushing, apologetic without cause, or performatively friendly

Writing Style:
- Plain English, short sentences
- Active voice, concrete nouns and verbs
- One idea per paragraph (1-3 lines max)
- Use bullets for clarity when needed
- White space > density

Openings:
- Start with context or purpose, not pleasantries
Examples:
"Per our conversation..."
"Here's what I put together..."
"Following up on..."
"Q1 is off to a good start."
Avoid: "Hope this finds you well," "Just circling back," excessive small talk.

Requests & CTAs:
- Be polite, optional in tone, but specific
- Always make the next step obvious
Examples:
"Happy to walk you through this if helpful."
"Let me know if you're aligned and I'll move it forward."
"Want me to plug this into DocuSign?"
Avoid vague endings like "Thoughts?" without context.

Language to Prefer:
"That said..."
"From my notes..."
"A reasonable starting point..."
"Absent written objection..."
"Happy to..."
"Eager to get started / find a path forward"

Language to Avoid:
- Buzzwords (leverage, synergy, best-in-class)
- Over-enthusiasm (!!!, emojis by default)
- Corporate filler ("kindly," "as per," "touch base")
- Defensive explanations or overselling

Boundaries & Authority:
- State positions clearly without over-justifying
- Use precise qualifiers instead of absolutes
- Signal fairness, not rigidity

Closings:
Simple and professional:
"Thanks,"
"Best,"
"Appreciate it,"

Core Principle (Do Not Break)
Write like a trusted senior peer who values clarity, progress, and long-term relationships over persuasion or theatrics.`,
  },
];
const OTT_EMAIL_VARIABLE_TOKENS = [
  "previousEmailSubject",
  "previousEmailBody",
  "previousEmailSentDate",
];
const OTT_RECIPIENT_VARIABLE_TOKENS = RECIPIENT_VARIABLE_TOKENS.map((fieldName) => {
  const trimmed = String(fieldName || "").trim();
  if (!trimmed) return "";
  return `previousRecipient${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
}).filter(Boolean);
const OTT_SENDER_VARIABLE_TOKENS = SENDER_VARIABLE_TOKENS.map((fieldName) => {
  const trimmed = String(fieldName || "").trim();
  if (!trimmed) return "";
  const suffix = trimmed.replace(/^sender/i, "");
  return `previousSender${suffix}`;
}).filter(Boolean);
const OTT_VARIABLE_TOKENS = [
  ...OTT_EMAIL_VARIABLE_TOKENS,
  ...OTT_RECIPIENT_VARIABLE_TOKENS,
  ...OTT_SENDER_VARIABLE_TOKENS,
];
const SHOW_BEHAVIOR_OPTIONS = false;
const CC_TOOLTIP_TEXT =
  "CC recipients are copied on all emails sent in this campaign. Whitespace separated emails; press space to add each address.";
const BCC_TOOLTIP_TEXT =
  "BCC recipients are hidden copies on all emails sent in this campaign. Whitespace separated emails; press space to add each address.";
const COMMENTS_FILTER_NO_COMMENT = "__NO_COMMENT__";
const LEADS_DISPLAY_LIMIT = 25;
const BULK_SALESFORCE_STATUS_REQUEST_CHUNK_SIZE = 1000;
const SENT_MONITOR_STATUSES = new Set(["sent", "simulated_sent", "bounced", "complaint"]);
const SENDORA_SELECTED_LEADS_CSV_COLUMNS = [
  { key: "_id", label: "memberId" },
  { key: "salesforceMemberId", label: "salesforceMemberId" },
  { key: "salesforceLeadId", label: "salesforceLeadId" },
  { key: "salesforceContactId", label: "salesforceContactId" },
  { key: "salesforceAccountId", label: "salesforceAccountId" },
  { key: "fullName", label: "fullName" },
  { key: "email", label: "email" },
  { key: "company", label: "company" },
  { key: "title", label: "title" },
  { key: "status", label: "status" },
  { key: "comments", label: "comments" },
  { key: "queuePreference", label: "queuePreference" },
  { key: "salesforceContactHasOptedOutOfEmail", label: "sfContactHasOptedOutOfEmail" },
  { key: "salesforceContactStatus", label: "sfContactStatus" },
  { key: "salesforceContactEmailStatus", label: "sfContactEmailStatus" },
  { key: "salesforceAccountStatus", label: "sfAccountStatus" },
  { key: "salesforceContactDncEvents", label: "sfContactDncEvents" },
  { key: "salesforceContactDncSummitEvents", label: "sfContactDncSummitEvents" },
  { key: "salesforceContactDncCurate", label: "sfContactDncCurate" },
  { key: "salesforceContactIsEmailBounced", label: "sfContactIsEmailBounced" },
  { key: "salesforceContactPendingEvents", label: "sfContactPendingEvents" },
  { key: "salesforceContactActiveOpportunities", label: "sfContactActiveOpportunities" },
  { key: "salesforceContactEventsDncsRemoves", label: "sfContactEventsDncsRemoves" },
  { key: "salesforceContactHasOpportunities", label: "sfContactHasOpportunities" },
  { key: "salesforceContactOpportunityCount", label: "sfContactOpportunityCount" },
  { key: "salesforceOutreachCount", label: "sfOutreachCount" },
  { key: "salesforceLastSentTime", label: "sfLastSentTimeEpoch" },
  { key: "lastSendSuccessTime", label: "lastSendSuccessTimeEpoch" },
];
const CAMPAIGN_STATUS_BADGE_META = {
  active: { label: "Active", className: "is-active" },
  draft: { label: "Draft", className: "is-draft" },
  paused: { label: "Paused", className: "is-paused" },
  error: { label: "Error", className: "is-error" },
  completed: { label: "Completed", className: "is-completed" },
  evergreen: { label: "Evergreen", className: "is-evergreen" },
  stopped: { label: "Stopped", className: "is-stopped" },
  deleted: { label: "Deleted", className: "is-deleted" },
};
const SENDORA_CREATE_CAMPAIGN_TIMEOUT_MS = 240000;
const SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS = 180000;
// SALESFORCE_FILTER_TOGGLE_KEYS / DEFAULT_SALESFORCE_FILTER_TOGGLES /
// SALESFORCE_FILTER_RULE_DEFS now live in ./salesforceLeadFilterRules so the
// Campaign Contact Uploader can share the same rule definitions.

function splitNameOnFirstSpace(rawName = "") {
  const fullName = String(rawName || "").trim().replace(/\s+/g, " ");
  if (!fullName) {
    return { firstName: "", lastName: "" };
  }
  const firstSpaceIndex = fullName.indexOf(" ");
  if (firstSpaceIndex < 0) {
    return { firstName: fullName, lastName: "" };
  }
  return {
    firstName: fullName.slice(0, firstSpaceIndex).trim(),
    lastName: fullName.slice(firstSpaceIndex + 1).trim(),
  };
}

const LANDMARK_EMPLOYEE_SEED_DIRECTORY = (Array.isArray(landmarkTeamEmails) ? landmarkTeamEmails : [])
  .map((entry) => {
    const emailLowercase = normalizeEmail(entry?.email || "");
    if (!emailLowercase) return null;
    const fullName = String(entry?.name || "").trim();
    const { firstName, lastName } = splitNameOnFirstSpace(fullName);
    return normalizeSenderAccount({
      email: emailLowercase,
      firstName,
      lastName,
      fullName,
    });
  })
  .filter(Boolean);

function splitCsvToArray(value = "") {
  return String(value)
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

function splitEmailsFromInput(value = "") {
  return String(value || "")
    .split(/[\s,;]+/)
    .map((v) => normalizeEmail(v))
    .filter((v) => v && v.includes("@"));
}

function toLocalIsoDate(epochSeconds) {
  if (!epochSeconds) return "Never";
  return epochToDateTimeString(epochSeconds);
}

function formatMonitorEpoch(epochSeconds) {
  if (!epochSeconds) return "-";
  return epochToDateTimeString(epochSeconds);
}

function getMemberEmailVerificationCategory(member = {}) {
  const decision = String(member?.emailVerification?.decision || "")
    .trim()
    .toLowerCase();
  if (decision === "ok_good") return "good";
  if (decision === "catch_all_risky") return "acceptable";
  if (member?.emailVerification) return "bad";
  return "not_verified";
}

function getMemberStepApprovals(member = {}) {
  return member?.stepApprovals && typeof member.stepApprovals === "object"
    ? member.stepApprovals
    : {};
}

function isMemberManualQueued(member = {}) {
  return String(member?.queuePreference || "")
    .trim()
    .toLowerCase() === "manual_queued";
}

function hasMemberEnabledManualStep(member = {}, allowedStepIds = null) {
  if (!isMemberManualQueued(member)) return false;
  const stepApprovals = getMemberStepApprovals(member);
  const approvedStepIds = Object.keys(stepApprovals).filter((stepId) => !!stepApprovals[stepId]);
  if (!approvedStepIds.length) return false;
  if (!allowedStepIds || !(allowedStepIds instanceof Set) || allowedStepIds.size === 0) {
    return true;
  }
  return approvedStepIds.some((stepId) => allowedStepIds.has(String(stepId || "").trim()));
}

function hasMemberEnabledSequence(member = {}) {
  const enabledSequenceIds = Array.isArray(member?.enabledSequenceIds) ? member.enabledSequenceIds : [];
  return enabledSequenceIds.some((sequenceId) => !!String(sequenceId || "").trim());
}

function isMemberEnabledForSendora(member = {}, allowedStepIds = null) {
  return hasMemberEnabledManualStep(member, allowedStepIds) || hasMemberEnabledSequence(member);
}

function toSafeNumber(input, fallback = 0) {
  const n = Number(input);
  return Number.isFinite(n) ? n : fallback;
}

function formatMetricNumber(value) {
  const normalized = Math.max(0, Math.trunc(toSafeNumber(value, 0)));
  return normalized >= 10000 ? normalized.toLocaleString("en-US") : String(normalized);
}

function escapeCsvValue(value) {
  const normalized = value == null ? "" : String(value);
  if (/[",\r\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
}

function downloadCsv(filename, csvContent) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.setAttribute("download", filename);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
}

function normalizeTextFilterValue(value = "") {
  return String(value || "").trim().toLowerCase();
}

function isSalesforceFilterEnabled(toggles = {}, key = "") {
  return toggles?.[key] !== false;
}

function passesSalesforceFilterRule(member = {}, filterKey = "", options = {}) {
  const contactStatus = String(member?.salesforceContactStatus || "").trim();
  const accountStatus = String(member?.salesforceAccountStatus || "").trim();
  const emailStatus = String(member?.salesforceContactEmailStatus || "").trim();
  const linkedCampaignTitle = String(options?.linkedCampaignTitle || "").trim();
  switch (filterKey) {
    case SALESFORCE_FILTER_TOGGLE_KEYS.EMAIL_OPT_OUT:
      return member?.salesforceContactHasOptedOutOfEmail !== true;
    case SALESFORCE_FILTER_TOGGLE_KEYS.STATUS: {
      const normalizedStatus = normalizeTextFilterValue(contactStatus);
      return (
        !normalizedStatus ||
        normalizedStatus === "dealmaking" ||
        normalizedStatus === "engaged"
      );
    }
    case SALESFORCE_FILTER_TOGGLE_KEYS.DNC_SUMMIT_EVENTS:
      return member?.salesforceContactDncSummitEvents !== true;
    case SALESFORCE_FILTER_TOGGLE_KEYS.EMAIL_BOUNCED:
      return member?.salesforceContactIsEmailBounced !== true;
    case SALESFORCE_FILTER_TOGGLE_KEYS.ACCOUNT_STATUS: {
      const normalizedAccountStatus = normalizeTextFilterValue(accountStatus);
      return (
        !normalizedAccountStatus ||
        normalizedAccountStatus === "active" ||
        normalizedAccountStatus === "research/vendor"
      );
    }
    case SALESFORCE_FILTER_TOGGLE_KEYS.PENDING_EVENTS: {
      const pendingEvents = normalizeTextFilterValue(
        member?.salesforceContactPendingEvents || ""
      );
      const linkedTitle = normalizeTextFilterValue(linkedCampaignTitle);
      if (!pendingEvents || !linkedTitle) return true;
      return !pendingEvents.includes(linkedTitle);
    }
    case SALESFORCE_FILTER_TOGGLE_KEYS.ACTIVE_OPPORTUNITIES: {
      const activeOpportunities = normalizeTextFilterValue(
        member?.salesforceContactActiveOpportunities || ""
      );
      const linkedTitle = normalizeTextFilterValue(linkedCampaignTitle);
      if (!activeOpportunities || !linkedTitle) return true;
      return !activeOpportunities.includes(linkedTitle);
    }
    case SALESFORCE_FILTER_TOGGLE_KEYS.DNC_EVENTS:
      return member?.salesforceContactDncEvents !== true;
    case SALESFORCE_FILTER_TOGGLE_KEYS.DNC_CURATE:
      return member?.salesforceContactDncCurate !== true;
    case SALESFORCE_FILTER_TOGGLE_KEYS.EVENTS_DNCS_REMOVES: {
      const eventsDncsRemoves = normalizeTextFilterValue(
        member?.salesforceContactEventsDncsRemoves || ""
      );
      if (!eventsDncsRemoves) return true;
      return !eventsDncsRemoves.includes("123456");
    }
    case SALESFORCE_FILTER_TOGGLE_KEYS.EMAIL_STATUS: {
      const normalizedEmailStatus = normalizeTextFilterValue(emailStatus);
      return (
        !normalizedEmailStatus ||
        normalizedEmailStatus === "accept all" ||
        normalizedEmailStatus === "verified" ||
        normalizedEmailStatus === "antispam_system" ||
        normalizedEmailStatus === "smtp_protocol"
      );
    }
    case SALESFORCE_FILTER_TOGGLE_KEYS.HAS_OPPORTUNITIES:
      return member?.salesforceContactHasOpportunities !== true;
    default:
      return true;
  }
}

function normalizeCampaignSpeed(rawValue = "") {
  const value = String(rawValue || "").trim().toLowerCase();
  if (value === "low") return "slow";
  if (value === "high") return "fast";
  if (["slow", "medium", "fast", "custom"].includes(value)) return value;
  return "medium";
}

function normalizeEmailVerificationMode(rawValue = "") {
  return String(rawValue || "").trim().toLowerCase() === "bulk" ? "bulk" : "single";
}

function isMillionVerifierPollLogMessage(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  return (
    normalized.includes("verifyemailsviamillionverifierbulk() - poll") ||
    normalized.includes("verifyemailsviamillionverifiersingle() - poll")
  );
}

function normalizeStepSpeed(rawValue = "") {
  return normalizeCampaignSpeed(rawValue);
}

function normalizeSequenceNodeSpeedMode(rawValue = "") {
  const value = String(rawValue || "").trim().toLowerCase();
  if (["slow", "medium", "fast", "custom"].includes(value)) return value;
  return "";
}

function normalizeSequenceNode(node = {}, nodeIndex = 0) {
  const speedMode = normalizeSequenceNodeSpeedMode(node?.speedMode);
  return {
    nodeId: String(node?.nodeId || "").trim() || `node_${nodeIndex + 1}`,
    stepId: String(node?.stepId || "").trim(),
    speedMode,
    targetDurationSeconds:
      speedMode === "custom" ? Math.max(0, Number(node?.targetDurationSeconds || 0) || 0) : 0,
  };
}

function getStepSpeedSettings(step = {}) {
  const speed = normalizeStepSpeed(step?.speedMode || step?.speed || "medium");
  const targetDurationSecondsRaw =
    speed === "custom"
      ? Number(step?.targetDurationSeconds || 0)
      : Number(step?.targetDurationSeconds || 0);
  const targetDurationSeconds = Math.max(
    0,
    Number.isFinite(targetDurationSecondsRaw) ? Math.round(targetDurationSecondsRaw) : 0
  );
  return { speed, targetDurationSeconds };
}

function getRunStepDurationInputKey(stepId = "", sequenceId = "") {
  const normalizedStepId = String(stepId || "").trim();
  const normalizedSequenceId = String(sequenceId || "").trim();
  if (!normalizedStepId) return "";
  return normalizedSequenceId ? `${normalizedSequenceId}::${normalizedStepId}` : normalizedStepId;
}

function normalizeCompanyKey(rawEmail = "") {
  const emailLower = normalizeEmail(rawEmail || "");
  const emailDomain = emailLower.includes("@") ? emailLower.split("@").pop() : "";
  if (emailDomain) return emailDomain;
  return "unknown_company";
}

function buildRoundBatchSizes(companyCounts = new Map()) {
  const counts = Array.from(companyCounts.values()).map((v) => Math.max(0, Number(v || 0)));
  const maxRounds = counts.length ? Math.max(...counts) : 0;
  const sizes = [];
  for (let round = 1; round <= maxRounds; round += 1) {
    sizes.push(counts.filter((count) => count >= round).length);
  }
  return sizes;
}

function buildBatchesFromMembers(members = []) {
  const buckets = new Map();
  const orderedKeys = [];
  (Array.isArray(members) ? members : []).forEach((member) => {
    const memberEmail = normalizeEmail(member?.email || "");
    if (!memberEmail) return;
    const domainKey = normalizeCompanyKey(memberEmail);
    if (!buckets.has(domainKey)) {
      buckets.set(domainKey, []);
      orderedKeys.push(domainKey);
    }
    buckets.get(domainKey).push(memberEmail);
  });
  const batches = [];
  while (true) {
    const batchEmails = [];
    orderedKeys.forEach((key) => {
      const queue = buckets.get(key) || [];
      if (!queue.length) return;
      const nextEmail = queue.shift();
      batchEmails.push(nextEmail);
    });
    if (!batchEmails.length) break;
    batches.push(batchEmails);
  }
  return batches;
}

function estimateFastDurationSecondsFromBatchSizes(batchSizes = []) {
  const sizes = Array.isArray(batchSizes) ? batchSizes : [];
  if (!sizes.length) return 0;
  let totalSeconds = 0;
  sizes.forEach((size, idx) => {
    const batchSize = Math.max(0, Number(size || 0));
    if (batchSize > 1) {
      totalSeconds += (batchSize - 1) * SENDORA_BATCH_MIN_INTRA_SEND_SECONDS;
    }
    if (idx < sizes.length - 1) {
      totalSeconds += SENDORA_BATCH_MIN_WAIT_SECONDS;
    }
  });
  return totalSeconds;
}

function resolveDurationForStep(speed = "medium", fastDurationSeconds = 0, customTargetSeconds = 0) {
  if (speed === "custom") {
    return Math.max(0, Number(customTargetSeconds || 0));
  }
  const preset = CAMPAIGN_SPEED_PRESETS[speed] || CAMPAIGN_SPEED_PRESETS.medium;
  const multiplier = Math.max(1, Number(preset?.multiplier || 1));
  return Math.max(0, Number(fastDurationSeconds || 0) * multiplier);
}

function clampInt(value, min, max) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return min;
  return Math.max(min, Math.min(max, Math.round(parsed)));
}

function durationPartsToSeconds(parts = {}) {
  const days = clampInt(parts?.days, 0, 7);
  const hours = clampInt(parts?.hours, 0, 24);
  const minutes = clampInt(parts?.minutes, 0, 60);
  return ((days * 24 + hours) * 60 + minutes) * 60;
}

function secondsToDurationParts(totalSeconds = 0) {
  const clampedSeconds = Math.max(0, Math.min(MAX_CUSTOM_DURATION_SECONDS, Number(totalSeconds || 0)));
  const totalMinutes = Math.round(clampedSeconds / 60);
  const days = Math.min(7, Math.floor(totalMinutes / (24 * 60)));
  let remainingMinutes = totalMinutes - days * 24 * 60;
  const hours = Math.min(24, Math.floor(remainingMinutes / 60));
  remainingMinutes -= hours * 60;
  const roundedMinutes = Math.min(60, Math.max(0, Math.round(remainingMinutes / 5) * 5));
  return { days, hours, minutes: roundedMinutes };
}

function parseTimeToMinutes(rawTime = "") {
  const [hRaw, mRaw] = String(rawTime || "").split(":");
  const h = Number(hRaw);
  const m = Number(mRaw);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function getScheduleEndOffsetMinutes(timezoneValue) {
  if (timezoneValue === US_MAINLAND_ET_PT_BUSINESS_HOURS_TIMEZONE) {
    return US_MAINLAND_ET_PT_OFFSET_MINUTES;
  }
  return 0;
}

function resolvePreviewTimezone(timezoneValue, fallback = "UTC") {
  if (timezoneValue === US_MAINLAND_ET_PT_BUSINESS_HOURS_TIMEZONE) {
    return ET_TIMEZONE;
  }
  return timezoneValue || fallback;
}

function getScheduleWindowMinutes(schedule) {
  const fromMinutes = parseTimeToMinutes(schedule?.fromTime || "09:00");
  const toMinutes = parseTimeToMinutes(schedule?.toTime || "17:00");
  if (!Number.isFinite(fromMinutes) || !Number.isFinite(toMinutes)) return 8 * 60;
  const adjustedToMinutes =
    toMinutes + getScheduleEndOffsetMinutes(String(schedule?.timezone || ""));
  let diff = adjustedToMinutes - fromMinutes;
  if (diff <= 0) diff += 24 * 60;
  return Math.max(1, diff);
}

function computeMinGapMinutesForSpeed(
  speedValue,
  schedule,
  dailyLimitOverride = null,
  isScheduleBypassed = false
) {
  const normalizedSpeed = normalizeCampaignSpeed(speedValue);
  const preset = CAMPAIGN_SPEED_PRESETS[normalizedSpeed] || CAMPAIGN_SPEED_PRESETS.medium;
  const effectiveDailyLimit =
    Number.isFinite(Number(dailyLimitOverride)) && Number(dailyLimitOverride) > 0
      ? Number(dailyLimitOverride)
      : Number(preset.dailyLimit || 1);
  // When schedule is bypassed, daily limit is spread across a full 24-hour day.
  const windowMinutes = isScheduleBypassed ? 24 * 60 : getScheduleWindowMinutes(schedule);
  const rawGap = windowMinutes / Math.max(1, effectiveDailyLimit);
  return Number(rawGap.toFixed(4));
}

function getTimePartsInTimezone(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "long",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const byType = {};
  parts.forEach((part) => {
    byType[part.type] = part.value;
  });
  return {
    weekday: String(byType.weekday || "").toLowerCase(),
    hour: Number(byType.hour || 0),
    minute: Number(byType.minute || 0),
    second: Number(byType.second || 0),
    dateLabel: `${byType.year || ""}-${byType.month || ""}-${byType.day || ""}`,
  };
}

function getSendWindowForDay(dayKey, schedule, fromMinutes, toMinutes) {
  const days = new Set((schedule?.days || []).map((day) => String(day || "").toLowerCase()));
  if (!days.has(dayKey)) return null;
  let start = fromMinutes;
  let end = toMinutes;
  if (end <= start) {
    // Overnight windows are treated as full-day availability for pacing preview.
    end = start + 24 * 60;
  }
  if (schedule?.pauseSendingAfterFridayNoon && dayKey === "friday") {
    end = Math.min(end, 12 * 60);
  }
  if (end <= start) return null;
  return { start, end };
}

function getMinutesUntilWindowEnd(parts, window) {
  const nowMinute = parts.hour * 60 + parts.minute + parts.second / 60;
  if (nowMinute < window.start) return 0;
  if (nowMinute >= window.end) return 0;
  return Math.max(0, window.end - nowMinute);
}

function formatDurationMinutes(totalMinutes) {
  const minutes = Math.max(0, Number(totalMinutes) || 0);
  const days = Math.floor(minutes / (24 * 60));
  const hours = Math.floor((minutes % (24 * 60)) / 60);
  const mins = Math.round(minutes % 60);
  const chunks = [];
  if (days) chunks.push(`${days}d`);
  if (hours) chunks.push(`${hours}h`);
  if (mins || !chunks.length) chunks.push(`${mins}m`);
  return chunks.join(" ");
}

function getTimezoneShortLabel(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "short",
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find((part) => part.type === "timeZoneName");
  return tzPart?.value || "UTC";
}

function formatCompletionTimeLabel(date, timeZone) {
  const timeLabel = date.toLocaleTimeString(undefined, {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const dateLabel = date.toLocaleDateString(undefined, {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const tzShort = getTimezoneShortLabel(date, timeZone);
  return `${dateLabel} at ${timeLabel} ${tzShort}`.trim();
}

function formatGapBetweenEmailsLabel(gapMinutes) {
  const safeGapMinutes = Math.max(0, Number(gapMinutes) || 0);
  const gapSeconds = safeGapMinutes * 60;
  if (gapSeconds < 60) {
    if (gapSeconds < 0.9) {
      return "(< 1 second between emails)";
    }
    const seconds = gapSeconds < 1 ? 1 : Math.round(gapSeconds);
    const unit = seconds === 1 ? "second" : "seconds";
    return `(${seconds} ${unit} between emails)`;
  }
  const totalSeconds = Math.round(gapSeconds);
  const minutesPart = Math.floor(totalSeconds / 60);
  const secondsPart = totalSeconds % 60;
  return `(${minutesPart}m ${secondsPart}s between emails)`;
}

function getSendIntervalsCount(leadsRemaining = 0) {
  return Math.max(0, Math.max(0, Number(leadsRemaining || 0)) - 1);
}

function logSendSpeedApiDebug(eventName = "", payload = {}) {
  try {
    const timestamp = new Date().toISOString();
    console.log(`[SendoraSpeedDebug][${timestamp}] ${String(eventName || "")}`, payload || {});
  } catch (error) {
    // no-op
  }
}

function formatDurationMinutesWithSubMinuteLabel(totalMinutes) {
  const safeMinutes = Math.max(0, Number(totalMinutes) || 0);
  if (safeMinutes > 0 && safeMinutes < 1) return "less than 1m";
  return formatDurationMinutes(safeMinutes);
}

function formatDurationSecondsCompact(totalSeconds = 0) {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  if (safeSeconds > 0 && safeSeconds < 1) {
    const roundedTenths = Math.max(0.1, Math.round(safeSeconds * 10) / 10);
    return `${roundedTenths.toFixed(1).replace(/\.0$/, "")}s`;
  }
  if (safeSeconds < 60) return `${Math.max(1, Math.round(safeSeconds))}s`;
  return formatDurationMinutesWithSubMinuteLabel(safeSeconds / 60);
}

function getBackendMinimumCustomTargetSeconds(summary = null) {
  const requiredCustomTargetDurationSeconds = Math.max(
    0,
    Number(summary?.pacing?.requiredCustomTargetDurationSeconds || 0)
  );
  if (requiredCustomTargetDurationSeconds > 0) {
    return requiredCustomTargetDurationSeconds;
  }
  const fastDurationSeconds = Math.max(0, Number(summary?.pacing?.fastDurationSeconds || 0));
  const scheduleAvailabilityMultiplier = Math.max(
    0,
    Number(summary?.pacing?.scheduleAvailabilityMultiplier || 0)
  );
  if (fastDurationSeconds <= 0 || scheduleAvailabilityMultiplier <= 0) return 0;
  return fastDurationSeconds / scheduleAvailabilityMultiplier;
}

function getBackendMinimumCustomTargetSecondsFromError(errorMessage = "") {
  const message = String(errorMessage || "");
  const match = message.match(/minimum possible is\s+(\d+(?:\.\d+)?)\s+seconds/i);
  if (!match) return 0;
  const seconds = Number(match[1]);
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return seconds;
}

function alignCursorToNextPreviewWindow(cursor, previewSchedule, previewTimezone, fromMinutes, adjustedToMinutes) {
  let next = new Date(cursor);
  const maxIterations = 10000;
  let iterations = 0;
  while (iterations < maxIterations) {
    iterations += 1;
    const parts = getTimePartsInTimezone(next, previewTimezone);
    const nowMinute = parts.hour * 60 + parts.minute + parts.second / 60;
    const window = getSendWindowForDay(parts.weekday, previewSchedule, fromMinutes, adjustedToMinutes);
    if (window) {
      if (nowMinute < window.start) {
        const jumpToWindowStart = Math.max(1, window.start - nowMinute);
        next = new Date(next.getTime() + jumpToWindowStart * 60 * 1000);
        continue;
      }
      if (nowMinute < window.end) {
        return next;
      }
    }
    const jumpToNextDay = Math.max(1, 24 * 60 - nowMinute);
    next = new Date(next.getTime() + jumpToNextDay * 60 * 1000);
  }
  return next;
}

function buildSendTimelineForInputs({
  selectedSchedule,
  endFridayAtNoon,
  scheduleBypassEnabled,
  gapMinutesInput,
  leadsRemainingInput,
}) {
  const previewSchedule = {
    ...(selectedSchedule || {}),
    pauseSendingAfterFridayNoon: !!endFridayAtNoon,
  };
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const scheduleTimezone = previewSchedule?.timezone || userTimezone;
  const previewTimezone = scheduleBypassEnabled
    ? userTimezone
    : resolvePreviewTimezone(scheduleTimezone, userTimezone);
  const now = new Date();
  const leadsRemaining = Math.max(0, Number(leadsRemainingInput || 0));
  const gapMinutes = Math.max(0.0001, Number(gapMinutesInput || 0));
  if (leadsRemaining <= 0) {
    return {
      timezone: previewTimezone,
      rows: [],
      firstSendAt: null,
      completionAt: null,
      hasScheduleWindow: true,
    };
  }
  const fromMinutes = parseTimeToMinutes(previewSchedule?.fromTime || "09:00");
  const toMinutes = parseTimeToMinutes(previewSchedule?.toTime || "17:00");
  if (!scheduleBypassEnabled && (!Number.isFinite(fromMinutes) || !Number.isFinite(toMinutes))) {
    return {
      timezone: previewTimezone,
      rows: [],
      firstSendAt: null,
      completionAt: null,
      hasScheduleWindow: false,
    };
  }
  const adjustedToMinutes = Number.isFinite(toMinutes)
    ? toMinutes + getScheduleEndOffsetMinutes(String(previewSchedule?.timezone || ""))
    : 0;
  let cursor = new Date(now);
  if (!scheduleBypassEnabled) {
    cursor = alignCursorToNextPreviewWindow(
      cursor,
      previewSchedule,
      previewTimezone,
      fromMinutes,
      adjustedToMinutes
    );
  }
  const rowsByDate = new Map();
  let firstSendAt = null;
  let completionAt = null;
  for (let idx = 0; idx < leadsRemaining; idx += 1) {
    const sendAt = new Date(cursor);
    if (!firstSendAt) firstSendAt = sendAt;
    completionAt = sendAt;
    const parts = getTimePartsInTimezone(sendAt, previewTimezone);
    const dateKey = String(parts.dateLabel || "");
    const existing = rowsByDate.get(dateKey) || {
      dateKey,
      dayLabel: String(parts.weekday || "").trim(),
      count: 0,
      firstSendAt: sendAt,
      lastSendAt: sendAt,
    };
    existing.count += 1;
    if (sendAt < existing.firstSendAt) existing.firstSendAt = sendAt;
    if (sendAt > existing.lastSendAt) existing.lastSendAt = sendAt;
    rowsByDate.set(dateKey, existing);
    let nextCursor = new Date(sendAt.getTime() + gapMinutes * 60 * 1000);
    if (!scheduleBypassEnabled) {
      nextCursor = alignCursorToNextPreviewWindow(
        nextCursor,
        previewSchedule,
        previewTimezone,
        fromMinutes,
        adjustedToMinutes
      );
    }
    cursor = nextCursor;
  }
  const rows = Array.from(rowsByDate.values())
    .sort((a, b) => a.firstSendAt.getTime() - b.firstSendAt.getTime())
    .map((entry) => ({
      dateLabel: entry.firstSendAt.toLocaleDateString(undefined, {
        timeZone: previewTimezone,
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      windowLabel: scheduleBypassEnabled
        ? "24/7"
        : `${String(selectedSchedule?.fromTime || "09:00")} - ${String(selectedSchedule?.toTime || "17:00")}`,
      count: Number(entry.count || 0),
      firstSendLabel: formatCompletionTimeLabel(entry.firstSendAt, previewTimezone),
      lastSendLabel: formatCompletionTimeLabel(entry.lastSendAt, previewTimezone),
    }));
  return {
    timezone: previewTimezone,
    rows,
    firstSendAt,
    completionAt,
    hasScheduleWindow: true,
  };
}

function buildSpeedPreviewForInputs({
  selectedSchedule,
  endFridayAtNoon,
  scheduleBypassEnabled,
  gapMinutesInput,
  leadsRemainingInput,
}) {
  const previewSchedule = {
    ...(selectedSchedule || {}),
    pauseSendingAfterFridayNoon: !!endFridayAtNoon,
  };
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const scheduleTimezone = previewSchedule?.timezone || userTimezone;
  const previewTimezone = scheduleBypassEnabled
    ? userTimezone
    : resolvePreviewTimezone(scheduleTimezone, userTimezone);
  const now = new Date();
  const timezoneShort = getTimezoneShortLabel(now, previewTimezone);
  const leadsRemaining = Math.max(0, Number(leadsRemainingInput || 0));
  const gapMinutes = Math.max(0.0001, Number(gapMinutesInput || 0));
  const sendDurationMinutes = getSendIntervalsCount(leadsRemaining) * gapMinutes;
  if (leadsRemaining <= 0) {
    return {
      timezone: previewTimezone,
      timezoneShort,
      leadsRemaining: 0,
      gapMinutes,
      durationLabel: "0m",
      startTimeLabel: null,
      completionTimeLabel: `Completed (${timezoneShort})`,
      hasScheduleWindow: true,
    };
  }
  if (scheduleBypassEnabled) {
    const completionDate = new Date(now.getTime() + sendDurationMinutes * 60 * 1000);
    return {
      timezone: previewTimezone,
      timezoneShort,
      leadsRemaining,
      gapMinutes,
      durationLabel: formatDurationMinutes(sendDurationMinutes),
      startTimeLabel: null,
      completionTimeLabel: formatCompletionTimeLabel(completionDate, previewTimezone),
      hasScheduleWindow: true,
    };
  }
  const fromMinutes = parseTimeToMinutes(previewSchedule?.fromTime || "09:00");
  const toMinutes = parseTimeToMinutes(previewSchedule?.toTime || "17:00");
  if (!Number.isFinite(fromMinutes) || !Number.isFinite(toMinutes)) {
    return {
      timezone: previewTimezone,
      timezoneShort,
      leadsRemaining,
      gapMinutes,
      durationLabel: "Unavailable",
      startTimeLabel: null,
      completionTimeLabel: "Set a valid schedule window",
      hasScheduleWindow: false,
    };
  }
  let remainingSendMinutes = sendDurationMinutes;
  let cursor = new Date(now);
  let firstSendAt = null;
  const adjustedToMinutes = toMinutes + getScheduleEndOffsetMinutes(String(previewSchedule?.timezone || ""));
  const maxIterations = 10000;
  let iterations = 0;
  while (remainingSendMinutes > 0 && iterations < maxIterations) {
    iterations += 1;
    const parts = getTimePartsInTimezone(cursor, previewTimezone);
    const nowMinute = parts.hour * 60 + parts.minute + parts.second / 60;
    const window = getSendWindowForDay(parts.weekday, previewSchedule, fromMinutes, adjustedToMinutes);
    if (window) {
      if (nowMinute < window.start) {
        const jumpToWindowStart = Math.max(1, window.start - nowMinute);
        cursor = new Date(cursor.getTime() + jumpToWindowStart * 60 * 1000);
        if (!firstSendAt) firstSendAt = new Date(cursor);
        continue;
      }
      const minutesToEnd = getMinutesUntilWindowEnd(parts, window);
      if (minutesToEnd > 0) {
        if (!firstSendAt) firstSendAt = new Date(cursor);
        const chunk = Math.min(remainingSendMinutes, minutesToEnd);
        remainingSendMinutes -= chunk;
        cursor = new Date(cursor.getTime() + chunk * 60 * 1000);
        continue;
      }
    }
    const jumpToNextDay = Math.max(1, 24 * 60 - nowMinute);
    cursor = new Date(cursor.getTime() + jumpToNextDay * 60 * 1000);
  }
  if (iterations >= maxIterations) {
    return {
      timezone: previewTimezone,
      timezoneShort,
      leadsRemaining,
      gapMinutes,
      durationLabel: "Unavailable",
      startTimeLabel: null,
      completionTimeLabel: "No active schedule window available",
      hasScheduleWindow: false,
    };
  }
  const sendStart = firstSendAt || now;
  const totalDurationMinutes = Math.max(0, (cursor.getTime() - sendStart.getTime()) / 60000);
  return {
    timezone: previewTimezone,
    timezoneShort,
    leadsRemaining,
    gapMinutes,
    durationLabel: formatDurationMinutes(totalDurationMinutes),
    startTimeLabel: formatCompletionTimeLabel(sendStart, previewTimezone),
    completionTimeLabel: formatCompletionTimeLabel(cursor, previewTimezone),
    hasScheduleWindow: true,
  };
}

function formatHtmlForCodeView(input = "") {
  const normalized = String(input || "").trim();
  if (!normalized) return "";

  const withLineBreaks = normalized.replace(/>\s*</g, ">\n<");
  const lines = withLineBreaks.split("\n");
  let indentLevel = 0;
  const voidTagPattern = /^<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)\b/i;

  return lines
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return "";

      const isClosingTag = /^<\//.test(trimmed);
      const isSelfClosingTag = /\/>$/.test(trimmed) || voidTagPattern.test(trimmed);
      const isOpeningTag =
        /^<[^!/][^>]*>$/.test(trimmed) && !isClosingTag && !isSelfClosingTag && !trimmed.includes("</");

      if (isClosingTag) {
        indentLevel = Math.max(indentLevel - 1, 0);
      }

      const indented = `${"  ".repeat(indentLevel)}${trimmed}`;

      if (isOpeningTag) {
        indentLevel += 1;
      }

      return indented;
    })
    .filter(Boolean)
    .join("\n");
}

function buildScheduleAutoSaveSignature(scheduleDrafts, campaignDateWindow, endFridayAtNoon) {
  const normalizedSchedules = (scheduleDrafts || []).map((schedule) => ({
    scheduleId: schedule?.scheduleId || "",
    fromTime: schedule?.fromTime || "",
    toTime: schedule?.toTime || "",
    timezone: schedule?.timezone || "",
    days: Array.isArray(schedule?.days) ? [...schedule.days] : [],
    isActive: schedule?.isActive !== false,
    bypassSchedule: !!schedule?.bypassSchedule,
    pauseSendingAfterFridayNoon: !!endFridayAtNoon,
  }));
  return JSON.stringify({
    schedules: normalizedSchedules,
    dateWindow: {
      startDate: campaignDateWindow?.startDate || "",
      endDate: campaignDateWindow?.endDate || "",
      noEndDate: !!campaignDateWindow?.noEndDate,
    },
    endFridayAtNoon: !!endFridayAtNoon,
  });
}

function buildSequenceSaveSignature(sequenceDraft) {
  // Only steps (and variant content) count for dirty - sender accounts auto-save separately
  const normalizedSteps = (sequenceDraft?.steps || [])
    .map((step, stepIndex) => ({
      stepId: String(step?.stepId || ""),
      orderIndex: Number(step?.orderIndex || stepIndex + 1),
      name: String(step?.name || ""),
      mode: String(step?.mode || "").trim().toLowerCase() === "ott" ? "ott" : "standard",
      ottSourceStepId: String(step?.ottSourceStepId || ""),
      ottUsePreviousSender: step?.ottUsePreviousSender !== false,
      speed: normalizeStepSpeed(step?.speedMode || step?.speed || "medium"),
      speedMode: normalizeStepSpeed(step?.speedMode || step?.speed || "medium"),
      targetDurationSeconds: Math.max(0, Number(step?.targetDurationSeconds || 0) || 0),
      durationPresetMultiplier: Math.max(0.0001, Number(step?.durationPresetMultiplier || 1) || 1),
      variants: (step?.variants || []).map((variant) => ({
        variantId: String(variant?.variantId || ""),
        isActive: variant?.isActive !== false,
        senderAccountEmail: normalizeEmail(
          variant?.senderAccount?.emailLowercase ||
            variant?.senderAccount?.email ||
            variant?.senderAccountEmail ||
            ""
        ),
        subject: String(variant?.subject || ""),
        body: String(variant?.body || ""),
      })),
    }))
    .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
  return JSON.stringify({ steps: normalizedSteps });
}

function buildSequenceDefinitionsSaveSignature(sequencesDraft = []) {
  const normalized = (Array.isArray(sequencesDraft) ? sequencesDraft : []).map((entry) => ({
    sequenceId: String(entry?.sequenceId || "").trim(),
    name: String(entry?.name || "").trim(),
    nodes: (entry?.nodes || []).map((node, nodeIndex) => ({
      nodeId: String(node?.nodeId || "").trim() || `node_${nodeIndex + 1}`,
      stepId: String(node?.stepId || "").trim(),
      speedMode: normalizeSequenceNodeSpeedMode(node?.speedMode || ""),
      targetDurationSeconds: Math.max(0, Number(node?.targetDurationSeconds || 0) || 0),
    })),
    waits: (entry?.waits || []).map((wait) => ({
      amount: Math.max(1, Number(wait?.amount || 1) || 1),
      unit: SEQUENCE_WAIT_UNITS.includes(String(wait?.unit || "")) ? String(wait.unit) : "days",
    })),
  }));
  return JSON.stringify(normalized);
}

function getSequenceDirtyReason(currentSignature = "", savedSignature = "") {
  const current = String(currentSignature || "");
  const saved = String(savedSignature || "");
  if (current === saved) return "no_diff";
  let firstDiffAt = -1;
  for (let i = 0; i < Math.max(current.length, saved.length); i++) {
    if ((current[i] || "") !== (saved[i] || "")) {
      firstDiffAt = i;
      break;
    }
  }
  const previewStart = Math.max(0, firstDiffAt - 60);
  const previewEnd = firstDiffAt + 60;
  let structuredPath = "";
  try {
    const curObj = JSON.parse(current);
    const savObj = JSON.parse(saved);
    const curSteps = Array.isArray(curObj?.steps) ? curObj.steps : [];
    const savSteps = Array.isArray(savObj?.steps) ? savObj.steps : [];
    if (curSteps.length !== savSteps.length) {
      structuredPath = `steps.length (${curSteps.length} vs ${savSteps.length})`;
    } else {
      outer: for (let s = 0; s < curSteps.length; s++) {
        const curStep = curSteps[s] || {};
        const savStep = savSteps[s] || {};
        const stepKeys = [
          "stepId",
          "orderIndex",
          "name",
          "mode",
          "ottSourceStepId",
          "ottUsePreviousSender",
          "speed",
          "dailyLimit",
        ];
        for (const key of stepKeys) {
          if (JSON.stringify(curStep[key]) !== JSON.stringify(savStep[key])) {
            structuredPath = `steps[${s}].${key}`;
            break outer;
          }
        }
        const curVariants = Array.isArray(curStep?.variants) ? curStep.variants : [];
        const savVariants = Array.isArray(savStep?.variants) ? savStep.variants : [];
        if (curVariants.length !== savVariants.length) {
          structuredPath = `steps[${s}].variants.length (${curVariants.length} vs ${savVariants.length})`;
          break;
        }
        for (let v = 0; v < curVariants.length; v++) {
          const cv = curVariants[v] || {};
          const sv = savVariants[v] || {};
          const variantKeys = ["variantId", "isActive", "senderAccountEmail", "subject", "body"];
          for (const key of variantKeys) {
            if (JSON.stringify(cv[key]) !== JSON.stringify(sv[key])) {
              structuredPath = `steps[${s}].variants[${v}].${key}`;
              break outer;
            }
          }
        }
      }
    }
  } catch {
    structuredPath = "signature_json_parse_failed";
  }
  return {
    structuredPath: structuredPath || "unknown_field_mismatch",
    currentLen: current.length,
    savedLen: saved.length,
    firstDiffAt,
    currentPreview: current.slice(previewStart, previewEnd),
    savedPreview: saved.slice(previewStart, previewEnd),
  };
}

function buildSenderAccountsSignature(sequenceDraft) {
  const normalized = normalizeSenderAccounts(
    sequenceDraft?.senderAccounts || [],
    sequenceDraft?.senderAccountEmails || []
  )
    .map((entry) => ({
      emailLowercase: String(entry?.emailLowercase || ""),
      firstName: String(entry?.firstName || ""),
      lastName: String(entry?.lastName || ""),
      fullName: String(entry?.fullName || ""),
    }))
    .sort((a, b) => (a.emailLowercase || "").localeCompare(b.emailLowercase || ""));
  return JSON.stringify(normalized);
}

function getCampaignStatusBadgeMeta(statusInput = "") {
  const statusKey = String(statusInput || "draft").toLowerCase();
  return (
    CAMPAIGN_STATUS_BADGE_META[statusKey] || {
      label: statusKey ? `${statusKey[0].toUpperCase()}${statusKey.slice(1)}` : "Draft",
      className: "is-draft",
    }
  );
}

function getCampaignProgressSnapshot(campaign = {}, stats = {}) {
  const hasCampaignStarted =
    Number(campaign?.lifecycle?.startedTime || 0) > 0 ||
    !!String(campaign?.lifecycle?.memberSnapshotSyncId || "").trim();
  if (!hasCampaignStarted) {
    return { scheduledLeads: 0, sentLeads: 0, progressPercent: 0 };
  }
  const scheduledLeads = toSafeNumber(
    stats.scheduledLeads,
    toSafeNumber(stats.includedLeads)
  );
  const sentLeads = Math.min(scheduledLeads, toSafeNumber(stats.sentLeads));
  if (scheduledLeads <= 0) {
    return { scheduledLeads: 0, sentLeads: 0, progressPercent: 0 };
  }
  const ratio = (sentLeads / scheduledLeads) * 100;
  return {
    scheduledLeads,
    sentLeads,
    progressPercent: Math.max(0, Math.min(100, Math.round(ratio))),
  };
}

function buildCampaignProgressFromMonitorSnapshot(campaign = {}, snapshot = {}) {
  const metrics = snapshot?.metrics && typeof snapshot.metrics === "object" ? snapshot.metrics : {};
  const stepMetrics =
    snapshot?.stepMetrics && typeof snapshot.stepMetrics === "object" ? snapshot.stepMetrics : {};
  const runSequences = Array.isArray(snapshot?.runSequences) ? snapshot.runSequences : [];
  const lifecycleStatus = String(snapshot?.lifecycleStatus || "").trim().toLowerCase();
  const orderedSteps = [...(Array.isArray(campaign?.sequences?.steps) ? campaign.sequences.steps : [])]
    .sort((a, b) => Number(a?.orderIndex || 0) - Number(b?.orderIndex || 0))
    .map((step, index) => ({
      stepId: String(step?.stepId || "").trim() || `step-${index + 1}`,
      orderIndex: Number(step?.orderIndex || index + 1),
    }));
  const stepProgress = orderedSteps.map((step, index) => {
    const stepId = String(step?.stepId || "").trim();
    const counts =
      stepMetrics?.[stepId] && typeof stepMetrics[stepId] === "object" ? stepMetrics[stepId] : {};
    const enabledCount = Math.max(0, Number(counts?.enabledCount || 0));
    const blockedBadEmailCount = Math.max(0, Number(counts?.blockedBadEmailCount || 0));
    const actionableCount = Math.max(0, Number(counts?.actionableCount || 0));
    const queuedMinusBlockedBadEmailCount = Math.max(0, enabledCount - blockedBadEmailCount);
    return {
      stepId,
      orderIndex: Number(step?.orderIndex || index + 1),
      label: `Step ${Number(step?.orderIndex || index + 1) || index + 1}`,
      queuedCount: Math.max(0, Number(counts?.queuedCount || 0)),
      sentCount: Math.max(0, Number(counts?.sentCount || 0)),
      failedCount: Math.max(0, Number(counts?.failedCount || 0)),
      canceledCount: Math.max(0, Number(counts?.canceledCount || 0)),
      enabledCount,
      eligibleCount: actionableCount,
      blockedBadEmailCount,
      queuedMinusBlockedBadEmailCount,
    };
  });
  return {
    lifecycle:
      lifecycleStatus && lifecycleStatus !== String(campaign?.lifecycle?.status || "").trim().toLowerCase()
        ? {
            ...(campaign?.lifecycle || {}),
            status: lifecycleStatus,
          }
        : campaign?.lifecycle || {},
    stats: {
      ...(campaign?.stats || {}),
      scheduledLeads: Math.max(0, Number(metrics?.scheduledLeads || 0)),
      sentLeads: Math.max(0, Number(metrics?.sentCount || 0)),
    },
    stepProgress,
    runSequences,
    sequenceProgress: runSequences,
    sequencePauseMap:
      snapshot?.sequencePauseMap && typeof snapshot.sequencePauseMap === "object"
        ? snapshot.sequencePauseMap
        : campaign?.sequencePauseMap || {},
  };
}

function getCampaignPersonHandle(person = {}) {
  const email = normalizeEmail(person?.email || person?.emailLowercase || "");
  if (email.includes("@")) {
    return email.split("@")[0];
  }
  const fullName = String(person?.fullName || "").trim();
  if (!fullName) return "";
  return fullName.replace(/\s+/g, " ").toLowerCase();
}

function getCampaignLifecycleToggleAction(statusInput = "") {
  const statusKey = String(statusInput || "draft").toLowerCase();
  if (statusKey === "active") return "pause";
  if (statusKey === "paused") return "resume";
  return "";
}

function getUniqueCampaignNameFromBase(baseName = "", existingCampaigns = []) {
  const base = String(baseName || "").trim();
  if (!base) return "";
  const existingNameSet = new Set(
    (Array.isArray(existingCampaigns) ? existingCampaigns : [])
      .map((campaign) => String(campaign?.name || "").trim().toLowerCase())
      .filter(Boolean)
  );
  const baseLower = base.toLowerCase();
  if (!existingNameSet.has(baseLower)) {
    return base;
  }
  let version = 2;
  while (existingNameSet.has(`${base} v${version}`.toLowerCase())) {
    version += 1;
  }
  return `${base} V${version}`;
}

function epochToDateInput(epochSeconds) {
  if (!epochSeconds) return "";
  const date = new Date(epochSeconds * 1000);
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function dateInputToEpoch(dateValue = "") {
  if (!dateValue) return 0;
  const ts = Date.parse(`${dateValue}T00:00:00`);
  if (!Number.isFinite(ts)) return 0;
  return Math.floor(ts / 1000);
}

function isEpochAtLocalMidnight(epochSeconds = 0) {
  const numeric = Number(epochSeconds || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return false;
  const date = new Date(numeric * 1000);
  return (
    date.getHours() === 0 &&
    date.getMinutes() === 0 &&
    date.getSeconds() === 0 &&
    date.getMilliseconds() === 0
  );
}

function formatTimeLabel(hour24, minute) {
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;
  const minuteLabel = `${minute}`.padStart(2, "0");
  return `${hour12}:${minuteLabel} ${period}`;
}

const TIME_INTERVAL_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 === 0 ? 0 : 30;
  const value = `${`${hour}`.padStart(2, "0")}:${`${minute}`.padStart(2, "0")}`;
  return { value, label: formatTimeLabel(hour, minute) };
});

function formatDateText(dateValue = "") {
  if (!dateValue) return "";
  const parsed = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return dateValue;
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function hasHtmlMarkup(input = "") {
  return /<\/?[a-z][\s\S]*>/i.test(String(input || ""));
}

function escapeHtml(input = "") {
  return String(input || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function normalizeBodyToHtml(input = "") {
  const raw = String(input || "");
  if (!raw.trim()) return "";
  if (hasHtmlMarkup(raw)) return raw;
  return raw
    .split(/\r?\n/)
    .map((line) => escapeHtml(line))
    .join("<br/>");
}

function normalizeRichEditorHtml(input = "") {
  const raw = String(input || "").trim();
  if (!raw) return "";
  if (!hasHtmlMarkup(raw)) {
    return raw
      .split(/\r?\n/)
      .map((line) => `<div>${escapeHtml(line)}</div>`)
      .join("");
  }
  if (typeof document === "undefined") {
    return raw
      .replace(/<o:p>\s*<\/o:p>/gi, "")
      .replace(/\sclass="([^"]*\s)?xelementtoproof(\s[^"]*)?"/gi, "");
  }
  const root = document.createElement("div");
  root.innerHTML = raw;

  root.querySelectorAll("o\\:p").forEach((node) => {
    const text = String(node.textContent || "").replace(/\u00a0/g, " ").trim();
    if (text) {
      node.replaceWith(document.createTextNode(text));
      return;
    }
    node.remove();
  });

  root.querySelectorAll(".xelementtoproof").forEach((node) => {
    node.classList.remove("xelementtoproof");
    if (!String(node.getAttribute("class") || "").trim()) {
      node.removeAttribute("class");
    }
  });

  root.querySelectorAll("p").forEach((node) => {
    node.style.removeProperty("margin");
    node.style.removeProperty("margin-top");
    node.style.removeProperty("margin-bottom");
    node.style.removeProperty("margin-left");
    node.style.removeProperty("margin-right");
    if (!String(node.getAttribute("style") || "").trim()) {
      node.removeAttribute("style");
    }
  });

  // Browsers often implement contentEditable indent by wrapping selection in
  // <blockquote>. Convert those wrappers into margin-based blocks so preview
  // and sent content keep visual indenting without blockquote styling.
  root.querySelectorAll("blockquote").forEach((node) => {
    const replacement = document.createElement("div");
    Array.from(node.attributes || []).forEach((attribute) => {
      const attrName = String(attribute?.name || "").toLowerCase();
      if (!attrName || attrName === "style") return;
      replacement.setAttribute(attribute.name, attribute.value);
    });
    const existingStyle = String(node.getAttribute("style") || "").trim();
    if (existingStyle) {
      replacement.setAttribute("style", existingStyle);
    }
    const parsedMargin = Number.parseFloat(String(node.style.marginLeft || "").replace("px", ""));
    const currentMarginLeft = Number.isFinite(parsedMargin) ? parsedMargin : 0;
    replacement.style.marginLeft = `${Math.max(0, currentMarginLeft + 40)}px`;
    replacement.style.marginTop = "0px";
    replacement.style.marginBottom = "0px";
    while (node.firstChild) {
      replacement.appendChild(node.firstChild);
    }
    node.replaceWith(replacement);
  });

  const paragraphNodes = Array.from(root.querySelectorAll("p"));
  paragraphNodes.forEach((node) => {
    const parent = node.parentNode;
    if (!parent) return;
    const paragraphHtml = String(node.innerHTML || "");
    const hasMeaningfulContent = !isRichEditorHtmlEffectivelyEmpty(paragraphHtml);
    const nextSibling = node.nextSibling;
    const fragment = document.createDocumentFragment();
    while (node.firstChild) {
      fragment.appendChild(node.firstChild);
    }
    const lastChild = fragment.lastChild;
    const endsWithBr =
      !!lastChild &&
      lastChild.nodeType === 1 &&
      String(lastChild.nodeName || "").toLowerCase() === "br";
    const shouldAddBreak = hasMeaningfulContent && !!nextSibling && !endsWithBr;
    if (shouldAddBreak) {
      fragment.appendChild(document.createElement("br"));
    }
    parent.replaceChild(fragment, node);
  });

  const hasMeaningfulContent = !isRichEditorHtmlEffectivelyEmpty(root.innerHTML || "");
  if (hasMeaningfulContent) {
    const isIgnorableTrailingNode = (node) => {
      if (!node) return false;
      if (node.nodeType === 3) {
        return !String(node.textContent || "").replace(/\u00a0/g, " ").trim();
      }
      if (node.nodeType !== 1) return false;
      const tag = String(node.nodeName || "").toLowerCase();
      if (tag === "br") return true;
      const html = String(node.innerHTML || "");
      if (!html.trim()) return true;
      const plain = html
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/<[^>]*>/g, "")
        .replace(/\u200B/g, "")
        .trim();
      if (plain) return false;
      const hasOnlyBreakMarkup = !String(node.outerHTML || "")
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, "")
        .replace(/<[^>]*>/g, "")
        .replace(/\u200B/g, "")
        .trim();
      return hasOnlyBreakMarkup;
    };

    while (root.lastChild && isIgnorableTrailingNode(root.lastChild)) {
      root.removeChild(root.lastChild);
    }
  }

  return root.innerHTML;
}

function isRichEditorHtmlEffectivelyEmpty(input = "") {
  const plain = String(input || "")
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/\u200B/g, "")
    .trim();
  return !plain;
}

function buildDefaultBodyTypographyHtml(
  fontFamily = EDITOR_DISPLAY_FONT_FAMILY,
  fontSize = String(EDITOR_DISPLAY_FONT_SIZE_PT)
) {
  const family = String(fontFamily || EDITOR_DISPLAY_FONT_FAMILY)
    .replace(/"/g, "")
    .trim() || EDITOR_DISPLAY_FONT_FAMILY;
  const sizePt = Math.max(
    8,
    Math.min(
      48,
      Number.parseInt(String(fontSize || EDITOR_DISPLAY_FONT_SIZE_PT), 10) ||
        EDITOR_DISPLAY_FONT_SIZE_PT
    )
  );
  return `<div style="font-family: ${family}; font-size: ${sizePt}pt;"><br></div>`;
}

function stripZeroWidthSpaces(input = "") {
  return String(input || "").replace(/\u200B/g, "");
}

function stripVariableHighlightMarkup(input = "") {
  if (typeof document === "undefined") {
    return String(input || "").replace(
      /<span[^>]*class="[^"]*\bsendora-token\b[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
      "$1"
    );
  }
  const root = document.createElement("div");
  root.innerHTML = String(input || "");
  const tokenNodes = Array.from(root.querySelectorAll("span.sendora-token"));
  tokenNodes.forEach((node) => {
    const text = String(node.textContent || "");
    if (!text) {
      node.remove();
      return;
    }
    if (node.classList.contains("is-spintax-delimiter")) {
      node.replaceWith(document.createTextNode(text));
      return;
    }
    const inlineStyle = String(node.getAttribute("style") || "").trim();
    if (inlineStyle) {
      const wrapper = document.createElement("span");
      wrapper.setAttribute("style", inlineStyle);
      wrapper.textContent = text;
      node.replaceWith(wrapper);
      return;
    }
    node.replaceWith(document.createTextNode(text));
  });
  return root.innerHTML;
}

function highlightVariablesInEditorHtml(input = "") {
  const withoutHighlights = stripVariableHighlightMarkup(input);
  const knownRecipientTokens = new Set(
    RECIPIENT_VARIABLE_TOKENS.map((token) =>
      String(token || "").toLowerCase()
    )
  );
  const knownSenderTokens = new Set(
    SENDER_VARIABLE_TOKENS.map((token) =>
      String(token || "").toLowerCase()
    )
  );
  const knownOttTokens = new Set(
    OTT_VARIABLE_TOKENS.map((token) =>
      String(token || "").toLowerCase()
    )
  );
  const knownTokenKeys = new Set([...knownRecipientTokens, ...knownSenderTokens, ...knownOttTokens]);
  const isKnownAiTokenKey = (value = "") => /^aivariable\d+$/i.test(String(value || "").trim());
  const withSpintaxHighlights = String(withoutHighlights || "").replace(
    /(\{[^{}]*\})/g,
    (tokenText) => {
      const token = String(tokenText || "");
      const inner = token.slice(1, -1);
      const innerKey = String(inner || "").trim().toLowerCase();
      const hasPipe = inner.includes("|");
      if (!hasPipe || knownTokenKeys.has(innerKey)) return token;
      const left =
        '<span contenteditable="false" class="sendora-token is-spintax-delimiter">{</span>';
      const right =
        '<span contenteditable="false" class="sendora-token is-spintax-delimiter">}</span>';
      const body = inner
        .split("|")
        .map((part, index) =>
          index === 0
            ? part
            : `<span contenteditable="false" class="sendora-token is-spintax-delimiter">|</span>${part}`
        )
        .join("");
      return `${left}${body}${right}`;
    }
  );
  const safeHtml = String(withSpintaxHighlights || "");
  if (typeof document === "undefined") {
    return safeHtml.replace(/(\{\{\s*([a-zA-Z][\w]*)\s*\}\})/g, (fullToken, tokenText, tokenKey) => {
      const key = String(tokenKey || "").toLowerCase();
      if (!knownTokenKeys.has(key) && !isKnownAiTokenKey(key)) return fullToken;
      const tokenClass = getTokenHighlightClass(tokenKey);
      return `<span contenteditable="false" class="sendora-token ${tokenClass}">${tokenText}</span>`;
    });
  }
  const root = document.createElement("div");
  root.innerHTML = safeHtml;
  const textNodes = [];
  const walker = document.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walker.nextNode();
  }
  textNodes.forEach((node) => {
    if (!node || !node.nodeValue) return;
    const parentElement = node.parentElement;
    if (parentElement?.closest?.(".sendora-token")) return;
    const rawText = String(node.nodeValue || "");
    const tokenRegex = /\{\{\s*([a-zA-Z][\w]*)\s*\}\}/g;
    let match = tokenRegex.exec(rawText);
    if (!match) return;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    while (match) {
      const fullToken = String(match?.[0] || "");
      const tokenKey = String(match?.[1] || "");
      const keyLower = tokenKey.toLowerCase();
      const start = Number(match.index || 0);
      if (start > lastIndex) {
        fragment.appendChild(document.createTextNode(rawText.slice(lastIndex, start)));
      }
      if (knownTokenKeys.has(keyLower) || isKnownAiTokenKey(keyLower)) {
        const tokenSpan = document.createElement("span");
        tokenSpan.setAttribute("contenteditable", "false");
        tokenSpan.className = `sendora-token ${getTokenHighlightClass(tokenKey)}`;
        tokenSpan.textContent = fullToken;
        fragment.appendChild(tokenSpan);
      } else {
        fragment.appendChild(document.createTextNode(fullToken));
      }
      lastIndex = start + fullToken.length;
      match = tokenRegex.exec(rawText);
    }
    if (lastIndex < rawText.length) {
      fragment.appendChild(document.createTextNode(rawText.slice(lastIndex)));
    }
    node.replaceWith(fragment);
  });
  return root.innerHTML;
}

function getTokenHighlightClass(fieldName = "") {
  const key = String(fieldName || "").toLowerCase();
  if (/^aivariable\d+$/.test(key)) return "is-ai-variable";
  const isOttToken =
    key.startsWith("previousemail") ||
    key.startsWith("previousrecipient") ||
    key.startsWith("previoussender");
  if (isOttToken) return "is-ott";
  const isSenderToken = [
    "senderfirstname",
    "senderlastname",
    "senderfullname",
    "senderemail",
    "sendername",
    "sendersignature",
  ].includes(key);
  return isSenderToken ? "is-sender" : "is-recipient";
}

function normalizeEditorFontFamilyForOption(fontFamily = "") {
  const value = String(fontFamily || "").toLowerCase();
  if (!value) return "";
  return (
    EDITOR_FONT_FAMILY_OPTIONS.find((option) =>
      value.includes(String(option || "").toLowerCase())
    ) || ""
  );
}

function normalizeEditorFontSizeForOption(fontSize = "") {
  const raw = String(fontSize || "").trim().toLowerCase();
  if (!raw) return "";
  const parsed = Number.parseFloat(raw.replace("px", "").replace("pt", "").trim());
  if (!Number.isFinite(parsed)) return "";
  const asPt = raw.endsWith("px") ? (parsed * 72) / 96 : parsed;
  const rounded = Math.round(asPt);
  return EDITOR_FONT_SIZE_OPTIONS.includes(rounded) ? String(rounded) : "";
}

function stripHighlightedSubjectMarkup(input = "") {
  return String(input || "").replace(
    /<span[^>]*class="[^"]*\bsendora-token\b[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
    "$1"
  );
}

function highlightVariablesInSubjectText(input = "") {
  const plain = stripHighlightedSubjectMarkup(input);
  const escaped = escapeHtml(String(plain || ""));
  const knownRecipientTokens = new Set(
    RECIPIENT_VARIABLE_TOKENS.map((token) =>
      String(token || "").toLowerCase()
    )
  );
  const knownSenderTokens = new Set(
    SENDER_VARIABLE_TOKENS.map((token) =>
      String(token || "").toLowerCase()
    )
  );
  const knownOttTokens = new Set(
    OTT_VARIABLE_TOKENS.map((token) =>
      String(token || "").toLowerCase()
    )
  );
  const knownTokenKeys = new Set([...knownRecipientTokens, ...knownSenderTokens, ...knownOttTokens]);
  const isKnownAiTokenKey = (value = "") => /^aivariable\d+$/i.test(String(value || "").trim());
  const withSpintax = escaped.replace(/\{([^{}]*\|[^{}]*)\}/g, (_, innerRaw) => {
    const inner = String(innerRaw || "");
    const left =
      '<span contenteditable="false" class="sendora-token is-spintax-delimiter">{</span>';
    const right =
      '<span contenteditable="false" class="sendora-token is-spintax-delimiter">}</span>';
    const body = inner
      .split("|")
      .map((part, index) =>
        index === 0
          ? part
          : `<span contenteditable="false" class="sendora-token is-spintax-delimiter">|</span>${part}`
      )
      .join("");
    return `${left}${body}${right}`;
  });
  return withSpintax.replace(/(\{\{\s*([a-zA-Z][\w]*)\s*\}\})/g, (fullToken, tokenText, tokenKey) => {
    const key = String(tokenKey || "").toLowerCase();
    if (!knownTokenKeys.has(key) && !isKnownAiTokenKey(key)) return fullToken;
    const tokenClass = getTokenHighlightClass(tokenKey);
    return `<span contenteditable="false" class="sendora-token ${tokenClass}">${tokenText}</span>`;
  });
}

function applyMergeTokens(templateHtml = "", lead = {}, sender = {}) {
  const rawLeadName = String(lead?.fullName || "").trim();
  const [leadFirst = "", ...leadRest] = rawLeadName ? rawLeadName.split(/\s+/) : [];
  const leadLast = leadRest.join(" ");
  const senderTokenMap = {
    senderfirstname: sender?.firstName || "",
    senderlastname: sender?.lastName || "",
    senderfullname: sender?.fullName || "",
    senderemail: sender?.email || sender?.emailLowercase || "",
    sendername: sender?.fullName || "",
  };
  const senderSignatureTemplate = String(
    sender?.senderSignatureHtml || DEFAULT_SENDER_SIGNATURE_TEMPLATE
  );
  const senderSignatureHtml = senderSignatureTemplate.replace(
    /\{\{\s*([\w]+)\s*\}\}|\{([\w]+)\}/g,
    (_, a, b) => {
      const token = String(a || b || "").toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(senderTokenMap, token)) return _;
      return escapeHtml(String(senderTokenMap[token] || ""));
    }
  );
  const tokenMap = {
    firstname: lead?.firstName || leadFirst,
    lastname: lead?.lastName || leadLast,
    fullname: lead?.fullName || "",
    email: lead?.email || "",
    company: lead?.company || "",
    title: lead?.title || "",
    ...senderTokenMap,
    sendersignature: senderSignatureHtml,
  };
  return String(templateHtml || "").replace(/\{\{\s*([\w]+)\s*\}\}|\{([\w]+)\}/g, (_, a, b) => {
    const token = String(a || b || "").toLowerCase();
    if (!Object.prototype.hasOwnProperty.call(tokenMap, token)) return _;
    if (token === "sendersignature") return String(tokenMap[token] || "");
    return escapeHtml(String(tokenMap[token] || ""));
  });
}

function createSeededRandom(seed = 1) {
  let state = (Number(seed) >>> 0) || 1;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function applySpintaxPreviewVariants(input = "", seed = 1, options = {}) {
  if (!input) return "";
  const random = createSeededRandom(seed);
  const shouldFlashSelection = options?.flashSelection === true;
  const flashKey = String(options?.flashKey || "").replace(/[^a-zA-Z0-9_-]/g, "");
  return String(input).replace(/\{([^{}]*\|[^{}]*)\}/g, (_, optionsRaw) => {
    const options = String(optionsRaw || "").split("|");
    if (!options.length) return "";
    const index = Math.min(options.length - 1, Math.floor(random() * options.length));
    const selected = options[index] ?? "";
    if (!shouldFlashSelection) return selected;
    const flashClass = flashKey
      ? `sendora-preview-spintax-flash sendora-preview-spintax-flash-${flashKey}`
      : "sendora-preview-spintax-flash";
    return `<span class="${flashClass}">${selected}</span>`;
  });
}

function buildEmailPreviewDocument(bodyHtml = "") {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html {
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;
        scrollbar-color: rgba(15, 23, 42, 0.45) rgba(15, 23, 42, 0.12);
      }
      body {
        margin: 0;
        min-height: 100%;
        color: #0f172a;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 10pt;
        line-height: 1.55;
        background: #ffffff;
        zoom: 1.1;
        overflow-y: auto;
        overflow-x: hidden;
      }
      body,
      p,
      div,
      span,
      td,
      th,
      li,
      blockquote,
      a {
        white-space: normal !important;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      table {
        max-width: 100% !important;
      }
      td,
      th {
        max-width: 100% !important;
      }
      img {
        max-width: 100% !important;
        height: auto !important;
      }
      html::-webkit-scrollbar,
      body::-webkit-scrollbar {
        width: 11px;
      }
      html::-webkit-scrollbar-track,
      body::-webkit-scrollbar-track {
        background: rgba(15, 23, 42, 0.12);
      }
      html::-webkit-scrollbar-thumb,
      body::-webkit-scrollbar-thumb {
        background: rgba(15, 23, 42, 0.45);
        border-radius: 8px;
      }
      html::-webkit-scrollbar-thumb:hover,
      body::-webkit-scrollbar-thumb:hover {
        background: rgba(15, 23, 42, 0.58);
      }
      p { margin: 0 0 12px; }
      h1, h2, h3, h4, h5, h6 { margin: 0 0 12px; }
      ul, ol { margin: 0 0 12px 20px; }
      a { color: #006cff; }
      blockquote {
        margin: 0 0 12px;
        padding-left: 12px;
        border-left: 3px solid rgba(15, 23, 42, 0.2);
        color: rgba(15, 23, 42, 0.75);
      }
      /* Keep OTT quoted previous-email spacing aligned with received emails. */
      .sendora-ott-quote-header + .sendora-ott-quote-body {
        margin-top: 12px;
      }
      .sendora-preview-spintax-flash {
        color: #f97316;
        animation: sendoraSpintaxFadeToBase 1.2s ease forwards;
      }
      @keyframes sendoraSpintaxFadeToBase {
        0% { color: #f97316; }
        100% { color: inherit; }
      }
    </style>
  </head>
  <body>${bodyHtml || ""}</body>
</html>`;
}

function getOrdinalSuffix(day = 1) {
  const normalizedDay = Math.max(1, Math.min(31, Number(day || 1)));
  const remainder100 = normalizedDay % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return "th";
  const remainder10 = normalizedDay % 10;
  if (remainder10 === 1) return "st";
  if (remainder10 === 2) return "nd";
  if (remainder10 === 3) return "rd";
  return "th";
}

function formatMonitorPopupEpoch(epochSeconds = 0) {
  const epoch = Number(epochSeconds || 0);
  if (!epoch) return "-";
  const date = new Date(epoch * 1000);
  if (Number.isNaN(date.getTime())) return "-";
  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day}${getOrdinalSuffix(day)} ${month} ${year} ${time}`;
}

function makeId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function normalizeLandmarkEmail(rawInput = "") {
  const clean = String(rawInput || "").trim().toLowerCase();
  if (!clean) return "";
  if (clean.includes("@")) return clean;
  return `${clean}${LANDMARK_EMAIL_SUFFIX}`;
}

function normalizeEmail(rawInput = "") {
  return String(rawInput || "").trim().toLowerCase();
}

function normalizeDomainSuffix(rawInput = "") {
  return String(rawInput || "")
    .trim()
    .toLowerCase()
    .replace(/^@+/, "");
}

function getDomainSuffixFromEmail(rawInput = "") {
  const email = normalizeEmail(rawInput);
  if (!email || !email.includes("@")) return "";
  return String(email.split("@")[1] || "").trim();
}

function normalizeInfrastructureKey(rawInput = "") {
  const key = String(rawInput || "")
    .trim()
    .toLowerCase();
  const allowed = new Set(SENDORA_SENDING_INFRASTRUCTURES.map((entry) => entry.key));
  return allowed.has(key) ? key : "";
}

function normalizeDomainInfrastructureRule(rawRule = {}) {
  const domainSuffix = normalizeDomainSuffix(rawRule?.domainSuffix || rawRule?.domain);
  if (!domainSuffix) return null;
  const allowedInfrastructures = Array.from(
    new Set(
      (Array.isArray(rawRule?.allowedInfrastructures) ? rawRule.allowedInfrastructures : [])
        .map((entry) => normalizeInfrastructureKey(entry))
        .filter(Boolean)
    )
  );
  const fallbackDefault =
    normalizeInfrastructureKey(rawRule?.defaultInfrastructure || "") ||
    allowedInfrastructures[0] ||
    "amazon_ses";
  const mergedAllowed = Array.from(new Set([...allowedInfrastructures, fallbackDefault]));
  return {
    domainSuffix,
    defaultInfrastructure: mergedAllowed.includes(fallbackDefault)
      ? fallbackDefault
      : mergedAllowed[0] || "amazon_ses",
    allowedInfrastructures: mergedAllowed.length ? mergedAllowed : [fallbackDefault],
  };
}

function normalizeDomainInfrastructureRules(rawRules = []) {
  const defaults = new Map(
    SENDORA_DEFAULT_DOMAIN_INFRASTRUCTURE_RULES.map((entry) => {
      const normalized = normalizeDomainInfrastructureRule(entry);
      return [normalized?.domainSuffix || "", normalized];
    }).filter(([domainSuffix, normalized]) => !!domainSuffix && !!normalized)
  );
  (Array.isArray(rawRules) ? rawRules : [])
    .map((entry) => normalizeDomainInfrastructureRule(entry))
    .filter(Boolean)
    .forEach((entry) => {
      defaults.set(entry.domainSuffix, entry);
    });
  return Array.from(defaults.values()).sort((a, b) =>
    String(a?.domainSuffix || "").localeCompare(String(b?.domainSuffix || ""))
  );
}

function buildDomainInfrastructureSignature(rules = []) {
  const normalized = normalizeDomainInfrastructureRules(rules).map((rule) => ({
    domainSuffix: normalizeDomainSuffix(rule?.domainSuffix || ""),
    defaultInfrastructure: normalizeInfrastructureKey(rule?.defaultInfrastructure || ""),
    allowedInfrastructures: Array.from(
      new Set(
        (Array.isArray(rule?.allowedInfrastructures) ? rule.allowedInfrastructures : [])
          .map((entry) => normalizeInfrastructureKey(entry))
          .filter(Boolean)
      )
    ).sort(),
  }));
  return JSON.stringify(normalized);
}

function isValidEmailAddress(rawInput = "") {
  const email = String(rawInput || "").trim();
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function formatSenderAccountLabel(account = {}) {
  const firstName = String(account.firstName || "").trim();
  const lastName = String(account.lastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim() || String(account.fullName || "").trim();
  const email = String(account.emailLowercase || account.email || "").trim();
  if (!email) return fullName || "Sender account";
  return fullName ? `${fullName} (${email})` : email;
}

function normalizeSenderAccount(rawAccount = {}) {
  const senderAccountId = String(
    rawAccount?.senderAccountId || rawAccount?.accountId || rawAccount?.id || ""
  ).trim();
  const emailLowercase = normalizeEmail(rawAccount?.emailLowercase || rawAccount?.email || "");
  const firstName = String(rawAccount?.firstName || "").trim();
  const lastName = String(rawAccount?.lastName || "").trim();
  const fullName =
    `${firstName} ${lastName}`.trim() || String(rawAccount?.fullName || "").trim();
  const senderSignatureHtml = String(rawAccount?.senderSignatureHtml || "").trim();
  if (!emailLowercase) return null;
  return {
    senderAccountId,
    email: emailLowercase,
    emailLowercase,
    firstName,
    lastName,
    fullName,
    senderSignatureHtml,
  };
}

function buildSenderAccountFromEmail(rawEmail = "") {
  const emailLowercase = normalizeEmail(rawEmail);
  if (!emailLowercase) return null;
  return normalizeSenderAccount({ email: emailLowercase });
}

function normalizeSenderAccounts(rawAccounts = [], fallbackEmails = []) {
  const normalizedFromObjects = (Array.isArray(rawAccounts) ? rawAccounts : [])
    .map((entry) => normalizeSenderAccount(entry))
    .filter(Boolean);
  const normalizedFromEmails = (Array.isArray(fallbackEmails) ? fallbackEmails : [])
    .map((email) => buildSenderAccountFromEmail(email))
    .filter(Boolean);
  const merged = [...normalizedFromObjects, ...normalizedFromEmails];
  const map = new Map();
  merged.forEach((entry) => {
    const existing = map.get(entry.emailLowercase);
    if (!existing) {
      map.set(entry.emailLowercase, entry);
      return;
    }
    // Keep name metadata when a fallback email-only duplicate is present.
    map.set(entry.emailLowercase, {
      ...existing,
      ...entry,
      firstName: String(entry?.firstName || "").trim() || String(existing?.firstName || "").trim(),
      lastName: String(entry?.lastName || "").trim() || String(existing?.lastName || "").trim(),
      fullName: String(entry?.fullName || "").trim() || String(existing?.fullName || "").trim(),
    });
  });
  return Array.from(map.values());
}

function normalizeSenderSignatureHtml(rawHtml = "") {
  const html = String(rawHtml || "").trim();
  return html || DEFAULT_SENDER_SIGNATURE_TEMPLATE;
}

function normalizeCustomSenderSignature(rawSignature = {}) {
  const signatureId = String(rawSignature?.signatureId || "").trim();
  const html = normalizeSenderSignatureHtml(rawSignature?.html || "");
  const now = Math.floor(Date.now() / 1000);
  const createdTime = Math.max(0, Number(rawSignature?.createdTime || 0)) || now;
  return {
    signatureId: signatureId || `sig_${makeId("sender")}`,
    html,
    createdTime,
    updatedTime: Math.max(createdTime, Number(rawSignature?.updatedTime || 0), now),
  };
}

function normalizeSenderSignaturePreference(rawPreference = {}) {
  const senderEmailLowercase = normalizeEmail(
    rawPreference?.senderEmailLowercase || rawPreference?.senderEmail || ""
  );
  if (!senderEmailLowercase) return null;
  const customSignatures = (Array.isArray(rawPreference?.customSignatures)
    ? rawPreference.customSignatures
    : []
  )
    .map((entry) => normalizeCustomSenderSignature(entry))
    .filter((entry) => !!entry?.signatureId);
  const deduped = new Map();
  customSignatures.forEach((entry) => deduped.set(entry.signatureId, entry));
  const dedupedCustomSignatures = Array.from(deduped.values());
  const selectedSignatureIdRaw = String(rawPreference?.selectedSignatureId || "default").trim();
  const selectedSignatureId =
    selectedSignatureIdRaw === "default" ||
    dedupedCustomSignatures.some((entry) => entry.signatureId === selectedSignatureIdRaw)
      ? selectedSignatureIdRaw
      : "default";
  return {
    senderEmailLowercase,
    selectedSignatureId: selectedSignatureId || "default",
    customSignatures: dedupedCustomSignatures,
    updatedTime: Math.max(0, Number(rawPreference?.updatedTime || 0)),
  };
}

function createDefaultSenderSignaturePreference(senderEmail = "") {
  const senderEmailLowercase = normalizeEmail(senderEmail);
  if (!senderEmailLowercase) return null;
  return {
    senderEmailLowercase,
    selectedSignatureId: "default",
    customSignatures: [],
    updatedTime: 0,
  };
}

function serializeSenderSignaturePreference(preference = {}) {
  const normalized = normalizeSenderSignaturePreference(preference);
  if (!normalized) return "";
  return JSON.stringify({
    senderEmailLowercase: normalized.senderEmailLowercase,
    selectedSignatureId: normalized.selectedSignatureId || "default",
    customSignatures: (normalized.customSignatures || []).map((entry) => ({
      signatureId: String(entry?.signatureId || "").trim(),
      html: normalizeSenderSignatureHtml(entry?.html || ""),
    })),
  });
}

function buildSenderSignaturePreviewHtml(rawHtml = "") {
  const normalizedHtml = normalizeSenderSignatureHtml(rawHtml);
  return highlightVariablesInEditorHtml(normalizedHtml);
}

function buildSenderSignatureCodeViewHtml(rawHtml = "") {
  const escaped = escapeHtml(stripVariableHighlightMarkup(normalizeSenderSignatureHtml(rawHtml)));
  return escaped.replace(
    /(\{\{\s*([a-zA-Z][\w]*)\s*\}\})/g,
    '<span class="sendora-token is-sender">$1</span>'
  );
}

function resolveSenderSignatureHtmlFromPreference(preference = null) {
  if (!preference) return DEFAULT_SENDER_SIGNATURE_TEMPLATE;
  const selectedSignatureId = String(preference?.selectedSignatureId || "default").trim() || "default";
  if (selectedSignatureId === "default") return DEFAULT_SENDER_SIGNATURE_TEMPLATE;
  const selectedCustom = (Array.isArray(preference?.customSignatures)
    ? preference.customSignatures
    : []
  ).find((entry) => String(entry?.signatureId || "").trim() === selectedSignatureId);
  return normalizeSenderSignatureHtml(selectedCustom?.html || "");
}

function normalizeBrandVoice(rawVoice = {}, fallback = {}) {
  const brandVoiceId = String(
    rawVoice?.brandVoiceId || rawVoice?.voiceId || rawVoice?.id || fallback?.brandVoiceId || ""
  ).trim();
  const name = String(rawVoice?.name || rawVoice?.label || fallback?.name || "").trim();
  const prompt = String(rawVoice?.prompt || fallback?.prompt || "").replace(/\u0000/g, "");
  const sourceType =
    String(rawVoice?.sourceType || fallback?.sourceType || "custom").trim().toLowerCase() === "template"
      ? "template"
      : "custom";
  if (!brandVoiceId || !name) return null;
  const now = Math.floor(Date.now() / 1000);
  const createdTime = Math.max(0, Number(rawVoice?.createdTime || fallback?.createdTime || 0)) || now;
  return {
    brandVoiceId,
    name,
    prompt,
    sourceType,
    createdTime,
    updatedTime: Math.max(
      createdTime,
      Number(rawVoice?.updatedTime || fallback?.updatedTime || 0),
      now
    ),
  };
}

const NORMALIZED_SENDORA_BRAND_VOICE_TEMPLATES = SENDORA_BRAND_VOICE_TEMPLATES.map((entry) =>
  normalizeBrandVoice(entry, { sourceType: "template" })
).filter(Boolean);

function normalizeCustomBrandVoices(rawVoices = []) {
  const deduped = new Map();
  (Array.isArray(rawVoices) ? rawVoices : [])
    .map((entry) => normalizeBrandVoice(entry, { sourceType: "custom" }))
    .filter((entry) => entry && entry.sourceType !== "template")
    .forEach((entry) => {
      deduped.set(entry.brandVoiceId, {
        ...entry,
        sourceType: "custom",
      });
    });
  return Array.from(deduped.values());
}

function buildBrandVoiceLibrary(customVoices = []) {
  return [...NORMALIZED_SENDORA_BRAND_VOICE_TEMPLATES, ...normalizeCustomBrandVoices(customVoices)];
}

function resolveSelectedBrandVoiceId(rawSelectedId = "", voices = []) {
  const selectedId = String(rawSelectedId || "").trim();
  const voiceList = Array.isArray(voices) ? voices : [];
  if (selectedId && voiceList.some((entry) => String(entry?.brandVoiceId || "").trim() === selectedId)) {
    return selectedId;
  }
  return String(voiceList?.[0]?.brandVoiceId || "").trim();
}

function createDefaultCustomBrandVoice(index = 1) {
  return normalizeBrandVoice({
    brandVoiceId: `voice_${makeId("brand")}`,
    name: `Custom Brand Voice ${Math.max(1, Number(index || 1) || 1)}`,
    prompt: "",
    sourceType: "custom",
  });
}

function serializeCustomBrandVoices(rawVoices = []) {
  return JSON.stringify(
    normalizeCustomBrandVoices(rawVoices).map((entry) => ({
      brandVoiceId: String(entry?.brandVoiceId || "").trim(),
      name: String(entry?.name || "").trim(),
      prompt: String(entry?.prompt || ""),
    }))
  );
}

function buildAiVariableTokenKey(index = 1) {
  return `aiVariable${Math.max(1, Number(index || 1) || 1)}`;
}

function parseAiVariableTokenIndex(tokenKey = "", fallbackIndex = 1) {
  const match = String(tokenKey || "").trim().match(/^aivariable(\d+)$/i);
  const parsed = Number(match?.[1] || fallbackIndex || 1);
  return Math.max(1, Number.isFinite(parsed) ? parsed : 1);
}

function normalizeCampaignAiVariable(rawVariable = {}, index = 0) {
  const fallbackIndex = index + 1;
  const tokenKey = buildAiVariableTokenKey(
    parseAiVariableTokenIndex(
      rawVariable?.tokenKey || rawVariable?.token || rawVariable?.variableId,
      fallbackIndex
    )
  );
  const now = Math.floor(Date.now() / 1000);
  const createdTime = Math.max(0, Number(rawVariable?.createdTime || 0)) || now;
  return {
    variableId: String(rawVariable?.variableId || "").trim() || makeId("aivar"),
    tokenKey,
    label:
      String(rawVariable?.label || "").trim() ||
      `AI Variable ${parseAiVariableTokenIndex(tokenKey, fallbackIndex)}`,
    prompt: String(rawVariable?.prompt || "")
      .replace(/\u0000/g, "")
      .slice(0, AI_VARIABLE_PROMPT_MAX_CHARS),
    isActive: rawVariable?.isActive !== false,
    createdTime,
    updatedTime: Math.max(createdTime, Number(rawVariable?.updatedTime || 0), now),
  };
}

function normalizeCampaignAiVariables(rawVariables = []) {
  const list = Array.isArray(rawVariables) ? rawVariables : [];
  const dedupedByToken = new Map();
  list
    .map((entry, index) => normalizeCampaignAiVariable(entry, index))
    .forEach((entry) => {
      const tokenKeyLower = String(entry?.tokenKey || "").toLowerCase();
      if (!tokenKeyLower) return;
      dedupedByToken.set(tokenKeyLower, entry);
    });
  return Array.from(dedupedByToken.values()).sort(
    (a, b) =>
      parseAiVariableTokenIndex(a?.tokenKey || "", 1) -
      parseAiVariableTokenIndex(b?.tokenKey || "", 1)
  );
}

function createDefaultCampaignAiVariable(index = 1) {
  return normalizeCampaignAiVariable(
    {
      tokenKey: buildAiVariableTokenKey(index),
      label: `AI Variable ${Math.max(1, Number(index || 1) || 1)}`,
      prompt: "",
      isActive: true,
    },
    index - 1
  );
}

function serializeCampaignAiVariables(rawVariables = []) {
  return JSON.stringify(
    normalizeCampaignAiVariables(rawVariables).map((entry) => ({
      tokenKey: String(entry?.tokenKey || "").trim(),
      prompt: String(entry?.prompt || ""),
      isActive: entry?.isActive !== false,
    }))
  );
}

function Button({ variant, className = "", type = "button", children, ...rest }) {
  return (
    <button type={type} className={`sendora-native-btn ${className}`.trim()} {...rest}>
      {children}
    </button>
  );
}

function createDefaultVariant(now) {
  return {
    variantId: makeId("vrn"),
    label: "Variant A",
    isActive: true,
    senderAccount: null,
    senderAccountEmail: "",
    subject: "",
    body: "",
    createdTime: now,
    updatedTime: now,
  };
}

function createDefaultStep(orderIndex = 1) {
  const now = Math.floor(Date.now() / 1000);
  return {
    stepId: makeId("stp"),
    orderIndex,
    name: `Step ${orderIndex}`,
    mode: "standard",
    ottSourceStepId: "",
    ottUsePreviousSender: true,
    speed: "medium",
    speedMode: "medium",
    dailyLimit: 5000,
    targetDurationSeconds: 0,
    durationPresetMultiplier: 1,
    pacingSnapshot: {},
    sendNextMessageIn: { days: 0, hours: 0, minutes: 0 },
    variants: [createDefaultVariant(now)],
    createdTime: now,
    updatedTime: now,
  };
}

function normalizeSequence(
  sequenceObj,
  fallbackSenderAccounts = [],
  fallbackSenderAccountEmails = []
) {
  const stepsInput = Array.isArray(sequenceObj?.steps) ? sequenceObj.steps : [];
  const sequenceHasSenderAccounts = Array.isArray(sequenceObj?.senderAccounts);
  const sequenceHasSenderAccountEmails = Array.isArray(sequenceObj?.senderAccountEmails);
  const senderAccountsInput =
    sequenceHasSenderAccounts && sequenceObj.senderAccounts.length > 0
      ? sequenceObj.senderAccounts
      : fallbackSenderAccounts || [];
  const senderAccountEmailsInput =
    sequenceHasSenderAccountEmails && sequenceObj.senderAccountEmails.length > 0
      ? sequenceObj.senderAccountEmails
      : fallbackSenderAccountEmails || [];
  const senderAccounts = normalizeSenderAccounts(
    senderAccountsInput,
    senderAccountEmailsInput
  );
  const senderAccountEmails = senderAccounts.map((entry) => entry.emailLowercase);
  const senderAccountByEmail = new Map(senderAccounts.map((entry) => [entry.emailLowercase, entry]));
  if (!stepsInput.length) return { steps: [createDefaultStep(1)], senderAccounts, senderAccountEmails };
  const steps = [...stepsInput]
    .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0))
    .map((step, index) => ({
      ...step,
      stepId: String(step?.stepId || "").trim() || `legacy_step_${index + 1}`,
      orderIndex: index + 1,
      name: step?.name || `Step ${index + 1}`,
      mode: String(step?.mode || "").trim().toLowerCase() === "ott" ? "ott" : "standard",
      ottSourceStepId: String(step?.ottSourceStepId || "").trim(),
      ottUsePreviousSender: step?.ottUsePreviousSender !== false,
      speed: normalizeStepSpeed(step?.speedMode || step?.speed || "medium"),
      speedMode: normalizeStepSpeed(step?.speedMode || step?.speed || "medium"),
      dailyLimit: Math.max(1, Number(step?.dailyLimit || 5000) || 5000),
      targetDurationSeconds: Math.max(0, Number(step?.targetDurationSeconds || 0) || 0),
      durationPresetMultiplier: Math.max(0.0001, Number(step?.durationPresetMultiplier || 1) || 1),
      pacingSnapshot:
        step?.pacingSnapshot && typeof step?.pacingSnapshot === "object" ? step.pacingSnapshot : {},
      sendNextMessageIn: {
        days: Number(step?.sendNextMessageIn?.days || 0),
        hours: Number(step?.sendNextMessageIn?.hours || 0),
        minutes: Number(step?.sendNextMessageIn?.minutes || 0),
      },
      variants: (() => {
        const rawVariants = Array.isArray(step?.variants) && step.variants.length
          ? step.variants
          : [createDefaultVariant(Math.floor(Date.now() / 1000))];
        const normalizedVariants = rawVariants.map((variant, vIndex) => ({
          ...variant,
          label: variant?.label || `Variant ${String.fromCharCode(65 + vIndex)}`,
          isActive:
            typeof variant?.isActive === "boolean" ? variant.isActive : vIndex === 0,
          senderAccount: (() => {
            const fromObject = normalizeSenderAccount(variant?.senderAccount || {});
            if (fromObject?.emailLowercase) return fromObject;
            const legacyEmail = normalizeEmail(variant?.senderAccountEmail || "");
            if (!legacyEmail) return null;
            return senderAccountByEmail.get(legacyEmail) || buildSenderAccountFromEmail(legacyEmail);
          })(),
          senderAccountEmail: normalizeEmail(
            variant?.senderAccount?.emailLowercase ||
              variant?.senderAccount?.email ||
              variant?.senderAccountEmail ||
              ""
          ),
          subject: variant?.subject || "",
          body: variant?.body || "",
        }));
        const hasActive = normalizedVariants.some((variant) => variant.isActive);
        if (!hasActive && normalizedVariants.length) {
          normalizedVariants[0] = { ...normalizedVariants[0], isActive: true };
        }
        return normalizedVariants;
      })(),
    }));
  const seenStepIds = new Set();
  const validatedSteps = steps.map((step, index) => {
    const mode =
      index === 0
        ? "standard"
        : String(step?.mode || "standard").trim().toLowerCase() === "ott"
        ? "ott"
        : "standard";
    const sourceStepId = String(step?.ottSourceStepId || "").trim();
    const hasValidSource = !!sourceStepId && seenStepIds.has(sourceStepId);
    seenStepIds.add(String(step?.stepId || ""));
    return {
      ...step,
      mode,
      ottSourceStepId: mode === "ott" && hasValidSource ? sourceStepId : "",
      ottUsePreviousSender: step?.ottUsePreviousSender !== false,
    };
  });
  return { steps: validatedSteps, senderAccounts, senderAccountEmails };
}

function getSequenceLabelByIndex(index = 0) {
  let remaining = Math.max(0, Number(index) || 0);
  let label = "";
  do {
    const charCode = 65 + (remaining % 26);
    label = String.fromCharCode(charCode) + label;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return `Sequence ${label}`;
}

function htmlToPreviewText(value = "") {
  return String(value || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatSequenceWaitLabel(wait = {}) {
  const amount = Math.max(1, Math.trunc(Number(wait?.amount || 1) || 1));
  const unitRaw = String(wait?.unit || "days").toLowerCase();
  const unit = SEQUENCE_WAIT_UNITS.includes(unitRaw) ? unitRaw : "days";
  const singular = unit.endsWith("s") ? unit.slice(0, -1) : unit;
  const displayUnit = amount === 1 ? singular : unit;
  return `${amount} ${displayUnit}`;
}

function convertSequenceWaitToMinutes(wait = {}) {
  const amount = Math.max(1, Number(wait?.amount || 1) || 1);
  const unitRaw = String(wait?.unit || "days").toLowerCase();
  const unit = SEQUENCE_WAIT_UNITS.includes(unitRaw) ? unitRaw : "days";
  if (unit === "minutes") return amount;
  if (unit === "hours") return amount * 60;
  return amount * 24 * 60;
}

function buildSequenceRuntimePreviewForInputs({
  selectedSchedule,
  endFridayAtNoon,
  scheduleBypassEnabled,
  stepDurationsMinutesInput = [],
  waitDurationsMinutesInput = [],
}) {
  const previewSchedule = {
    ...(selectedSchedule || {}),
    pauseSendingAfterFridayNoon: !!endFridayAtNoon,
  };
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const scheduleTimezone = previewSchedule?.timezone || userTimezone;
  const previewTimezone = scheduleBypassEnabled
    ? userTimezone
    : resolvePreviewTimezone(scheduleTimezone, userTimezone);
  const now = new Date();
  const timezoneShort = getTimezoneShortLabel(now, previewTimezone);
  const stepDurationsMinutes = (stepDurationsMinutesInput || []).map((value) =>
    Math.max(0, Number(value || 0))
  );
  const waitDurationsMinutes = (waitDurationsMinutesInput || []).map((value) =>
    Math.max(0, Number(value || 0))
  );
  const totalStepMinutes = stepDurationsMinutes.reduce((sum, value) => sum + value, 0);
  const totalWaitMinutes = waitDurationsMinutes.reduce((sum, value) => sum + value, 0);
  const totalMinutes = totalStepMinutes + totalWaitMinutes;
  if (totalMinutes <= 0) {
    return {
      timezone: previewTimezone,
      timezoneShort,
      durationLabel: "0m",
      completionTimeLabel: `Completed (${timezoneShort})`,
      hasScheduleWindow: true,
      totalStepMinutes,
      totalWaitMinutes,
      totalMinutes,
    };
  }
  if (scheduleBypassEnabled) {
    const completionDate = new Date(now.getTime() + totalMinutes * 60 * 1000);
    return {
      timezone: previewTimezone,
      timezoneShort,
      durationLabel: formatDurationMinutes(totalMinutes),
      completionTimeLabel: formatCompletionTimeLabel(completionDate, previewTimezone),
      hasScheduleWindow: true,
      totalStepMinutes,
      totalWaitMinutes,
      totalMinutes,
    };
  }
  const fromMinutes = parseTimeToMinutes(previewSchedule?.fromTime || "09:00");
  const toMinutes = parseTimeToMinutes(previewSchedule?.toTime || "17:00");
  if (!Number.isFinite(fromMinutes) || !Number.isFinite(toMinutes)) {
    return {
      timezone: previewTimezone,
      timezoneShort,
      durationLabel: "Unavailable",
      completionTimeLabel: "Set a valid schedule window",
      hasScheduleWindow: false,
      totalStepMinutes,
      totalWaitMinutes,
      totalMinutes,
    };
  }
  const adjustedToMinutes = toMinutes + getScheduleEndOffsetMinutes(String(previewSchedule?.timezone || ""));
  let cursor = new Date(now);
  let firstSendAt = null;
  const maxIterations = 10000;
  let iterations = 0;
  const consumeScheduledMinutes = (minutesToConsume = 0) => {
    let remainingMinutes = Math.max(0, Number(minutesToConsume || 0));
    while (remainingMinutes > 0 && iterations < maxIterations) {
      iterations += 1;
      const parts = getTimePartsInTimezone(cursor, previewTimezone);
      const nowMinute = parts.hour * 60 + parts.minute + parts.second / 60;
      const window = getSendWindowForDay(parts.weekday, previewSchedule, fromMinutes, adjustedToMinutes);
      if (window) {
        if (nowMinute < window.start) {
          const jumpToWindowStart = Math.max(1, window.start - nowMinute);
          cursor = new Date(cursor.getTime() + jumpToWindowStart * 60 * 1000);
          if (!firstSendAt) firstSendAt = new Date(cursor);
          continue;
        }
        const minutesToEnd = getMinutesUntilWindowEnd(parts, window);
        if (minutesToEnd > 0) {
          if (!firstSendAt) firstSendAt = new Date(cursor);
          const chunk = Math.min(remainingMinutes, minutesToEnd);
          remainingMinutes -= chunk;
          cursor = new Date(cursor.getTime() + chunk * 60 * 1000);
          continue;
        }
      }
      const jumpToNextDay = Math.max(1, 24 * 60 - nowMinute);
      cursor = new Date(cursor.getTime() + jumpToNextDay * 60 * 1000);
    }
    return remainingMinutes <= 0;
  };
  for (let stepIndex = 0; stepIndex < stepDurationsMinutes.length; stepIndex += 1) {
    if (!consumeScheduledMinutes(stepDurationsMinutes[stepIndex])) break;
    if (stepIndex < waitDurationsMinutes.length) {
      cursor = new Date(
        cursor.getTime() + Math.max(0, Number(waitDurationsMinutes[stepIndex] || 0)) * 60 * 1000
      );
    }
  }
  if (iterations >= maxIterations) {
    return {
      timezone: previewTimezone,
      timezoneShort,
      durationLabel: "Unavailable",
      completionTimeLabel: "No active schedule window available",
      hasScheduleWindow: false,
      totalStepMinutes,
      totalWaitMinutes,
      totalMinutes,
    };
  }
  const durationMinutes = firstSendAt
    ? Math.max(0, (cursor.getTime() - firstSendAt.getTime()) / 60000)
    : totalMinutes;
  return {
    timezone: previewTimezone,
    timezoneShort,
    durationLabel: formatDurationMinutes(durationMinutes),
    completionTimeLabel: formatCompletionTimeLabel(cursor, previewTimezone),
    hasScheduleWindow: true,
    totalStepMinutes,
    totalWaitMinutes,
    totalMinutes,
  };
}

function createDefaultSequenceChain(emailSteps = [], labelIndex = 0) {
  const nodes = (emailSteps || [])
    .map((step, index) =>
      normalizeSequenceNode(
        {
          nodeId: makeId("sqn_node"),
          stepId: String(step?.stepId || "").trim(),
          speedMode: "",
          targetDurationSeconds: 0,
        },
        index
      )
    )
    .filter((entry) => !!entry.stepId);
  return {
    sequenceId: makeId("sqn"),
    name: labelIndex === 0 ? "Main" : "Alternate",
    nodes,
    waits: Array.from({ length: Math.max(0, nodes.length - 1) }, () => ({
      amount: 1,
      unit: "days",
    })),
  };
}

const SendoraDashboard = () => {
  useDocumentTitle("Sendora - Landmark Ventures");

  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();
  const employeeId = employee?.employeeId || "";

  const [campaigns, setCampaigns] = useState([]);
  const [sendoraModeFlags, setSendoraModeFlags] = useState(DEFAULT_SENDORA_MODE_FLAGS);
  const [salesforceCampaigns, setSalesforceCampaigns] = useState([]);
  const [employeeEmails, setEmployeeEmails] = useState([]);
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [senderDirectory] = useState(LANDMARK_EMPLOYEE_SEED_DIRECTORY);
  const [defaultSenderAccountEmail, setDefaultSenderAccountEmail] = useState("");
  const [selectedSenderAccountEmail, setSelectedSenderAccountEmail] = useState("");
  const [isSenderAccountModalOpen, setIsSenderAccountModalOpen] = useState(false);
  const [isSenderSignatureModalOpen, setIsSenderSignatureModalOpen] = useState(false);
  const [showEmailAdvancedOptions, setShowEmailAdvancedOptions] = useState(false);
  const [selectedSignatureSenderEmail, setSelectedSignatureSenderEmail] = useState("");
  const [senderSignaturePreferences, setSenderSignaturePreferences] = useState([]);
  const [senderSignatureDraftByEmail, setSenderSignatureDraftByEmail] = useState({});
  const [senderSignatureInitialByEmail, setSenderSignatureInitialByEmail] = useState({});
  const [senderSignatureCodeViewById, setSenderSignatureCodeViewById] = useState({});
  const [savingSenderSignatures, setSavingSenderSignatures] = useState(false);
  const [isBrandVoiceModalOpen, setIsBrandVoiceModalOpen] = useState(false);
  const [brandVoiceCustomDraft, setBrandVoiceCustomDraft] = useState([]);
  const [brandVoiceCustomInitial, setBrandVoiceCustomInitial] = useState([]);
  const [brandVoiceSelectedIdDraft, setBrandVoiceSelectedIdDraft] = useState("");
  const [brandVoiceSelectedIdInitial, setBrandVoiceSelectedIdInitial] = useState("");
  const [savingBrandVoices, setSavingBrandVoices] = useState(false);
  const [sendingInfrastructureOptions, setSendingInfrastructureOptions] = useState(
    SENDORA_SENDING_INFRASTRUCTURES
  );
  const [domainInfrastructureRules, setDomainInfrastructureRules] = useState(
    normalizeDomainInfrastructureRules([])
  );
  const [savingDomainInfrastructure, setSavingDomainInfrastructure] = useState(false);
  const [isAiVariablesModalOpen, setIsAiVariablesModalOpen] = useState(false);
  const [selectedAiVariableTokenKey, setSelectedAiVariableTokenKey] = useState("");
  const [campaignAiVariablesDraft, setCampaignAiVariablesDraft] = useState([]);
  const [campaignAiVariablesInitial, setCampaignAiVariablesInitial] = useState([]);
  const [savingCampaignAiVariables, setSavingCampaignAiVariables] = useState(false);
  const [senderAccountForm, setSenderAccountForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });
  const [variantSenderModal, setVariantSenderModal] = useState({
    isOpen: false,
    stepIndex: -1,
    variantIndex: -1,
  });
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState("leads");
  const [maxReachedTabIndex, setMaxReachedTabIndex] = useState(0);
  const [members, setMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [campaignSentEmails, setCampaignSentEmails] = useState([]);
  const [memberSelection, setMemberSelection] = useState({});
  const [sequenceDraft, setSequenceDraft] = useState({
    steps: [createDefaultStep(1)],
    senderAccounts: [],
    senderAccountEmails: [],
  });
  const [sequencesDraft, setSequencesDraft] = useState([]);
  const [activeSequenceId, setActiveSequenceId] = useState("");
  const [isSequenceAddStepMenuOpen, setIsSequenceAddStepMenuOpen] = useState(false);

  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingSalesforceCampaigns, setLoadingSalesforceCampaigns] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingAllMembers, setLoadingAllMembers] = useState(false);
  const [allMembersLoadedCampaignId, setAllMembersLoadedCampaignId] = useState("");
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [savingSequence, setSavingSequence] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [runningMemberAction, setRunningMemberAction] = useState(false);
  const [campaignMonitor, setCampaignMonitor] = useState(null);
  const [campaignMonitorConnected, setCampaignMonitorConnected] = useState(false);
  const [campaignMonitorExpanded, setCampaignMonitorExpanded] = useState(false);
  const [selectedMonitorEmailPreview, setSelectedMonitorEmailPreview] = useState(null);
  const [monitorPreviewLoadingQueueId, setMonitorPreviewLoadingQueueId] = useState("");
  const [monitorPreviewPopupPosition, setMonitorPreviewPopupPosition] = useState({
    top: 140,
    left: 140,
  });
  const [showVerboseRunFeed, setShowVerboseRunFeed] = useState(false);
  const [splitMonitorByStep, setSplitMonitorByStep] = useState(true);
  const [campaignRowActionId, setCampaignRowActionId] = useState("");
  const [openCampaignMenuId, setOpenCampaignMenuId] = useState("");
  const [openCampaignMenuPosition, setOpenCampaignMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const monitorPreviewPopupRef = useRef(null);
  const monitorPreviewRequestIdRef = useRef(0);
  const pendingCampaignTabOverrideRef = useRef("");
  const [isRenameCampaignModalOpen, setIsRenameCampaignModalOpen] = useState(false);
  const [renameCampaignId, setRenameCampaignId] = useState("");
  const [renameCampaignName, setRenameCampaignName] = useState("");

  const [campaignSearch, setCampaignSearch] = useState("");
  const [memberNameFilter, setMemberNameFilter] = useState("");
  const [memberEmailFilter, setMemberEmailFilter] = useState("");
  const [memberCompanyFilter, setMemberCompanyFilter] = useState("");
  const [memberTitleFilter, setMemberTitleFilter] = useState("");
  const [memberCommentsFilter, setMemberCommentsFilter] = useState("");
  const [memberCommentsValueFilter, setMemberCommentsValueFilter] = useState("all");
  const [memberStatusFilter, setMemberStatusFilter] = useState("unsent");
  const [memberVerificationFilter, setMemberVerificationFilter] = useState("all");
  const [filterQueueStatus, setFilterQueueStatus] = useState("all");
  const [memberSendingFilter, setMemberSendingFilter] = useState("all");
  const [enabledStepsFilterEnabled, setEnabledStepsFilterEnabled] = useState(false);
  const [enabledStepsFilterStepIds, setEnabledStepsFilterStepIds] = useState([]);
  const [enabledSequencesFilterEnabled, setEnabledSequencesFilterEnabled] = useState(false);
  const [enabledSequencesFilterSequenceIds, setEnabledSequencesFilterSequenceIds] = useState([]);
  const [sentStepsFilterEnabled, setSentStepsFilterEnabled] = useState(false);
  const [sentStepsFilterStepIds, setSentStepsFilterStepIds] = useState([]);
  const [sentStepsFilterIncludeNothingSent, setSentStepsFilterIncludeNothingSent] =
    useState(false);
  const [bulkSalesforceStatusTarget, setBulkSalesforceStatusTarget] = useState("");
  const [isBulkSalesforceStatusConfirmModalOpen, setIsBulkSalesforceStatusConfirmModalOpen] =
    useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [hoveredMemberSalesforceLinkId, setHoveredMemberSalesforceLinkId] = useState("");
  const [isSalesforceFiltersModalOpen, setIsSalesforceFiltersModalOpen] = useState(false);
  const [isCampaignContactUploadModalOpen, setIsCampaignContactUploadModalOpen] = useState(false);
  const [isCampaignContactUploadModalMinimized, setIsCampaignContactUploadModalMinimized] =
    useState(false);
  const [isCampaignContactUploadProcessing, setIsCampaignContactUploadProcessing] = useState(false);
  const [campaignContactUploadSelectedFile, setCampaignContactUploadSelectedFile] = useState(null);
  const campaignContactUploadFileInputRef = useRef(null);
  const [runStepDailyLimitInputs, setRunStepDailyLimitInputs] = useState({});
  const [estimatedCompletionTimelineModal, setEstimatedCompletionTimelineModal] = useState({
    isOpen: false,
    loading: false,
    title: "",
    timezone: "UTC",
    hasScheduleWindow: true,
    rows: [],
    leadsRemaining: 0,
    completionTimeLabel: "",
  });
  const [backendStepSchedulePreviewByStepId, setBackendStepSchedulePreviewByStepId] = useState({});
  const [backendStepSchedulePreviewLoadingByStepId, setBackendStepSchedulePreviewLoadingByStepId] =
    useState({});
  const [backendStepScheduleMinimumCustomSecondsByStepId, setBackendStepScheduleMinimumCustomSecondsByStepId] =
    useState({});
  const [runStepPreviewBackendSyncStateByStepId, setRunStepPreviewBackendSyncStateByStepId] =
    useState({});
  const backendStepSchedulePreviewRequestIdRef = useRef(0);
  const [expandedRunStepBreakdownIds, setExpandedRunStepBreakdownIds] = useState({});
  const [expandedRunStepBatchRows, setExpandedRunStepBatchRows] = useState({});
  const [expandedRunStepAllBatchRows, setExpandedRunStepAllBatchRows] = useState({});
  const [salesforceFilterToggles, setSalesforceFilterToggles] = useState(
    DEFAULT_SALESFORCE_FILTER_TOGGLES
  );
  const [leadsFiltersEnabled, setLeadsFiltersEnabled] = useState(true);
  const [leadsDisplayExpanded, setLeadsDisplayExpanded] = useState(false);
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);
  const [openVariableMenu, setOpenVariableMenu] = useState(false);
  const [selectedApprovalStepIds, setSelectedApprovalStepIds] = useState([]);
  const [leadSendMode, setLeadSendMode] = useState(null);
  const [selectedLeadSequenceId, setSelectedLeadSequenceId] = useState(null);
  const [leadSequenceWaitEditor, setLeadSequenceWaitEditor] = useState({
    isOpen: false,
    sequenceId: "",
    fromStepId: "",
    toStepId: "",
    edgeIndex: -1,
    amount: "1",
    unit: "days",
  });
  const [editorInsertTarget, setEditorInsertTarget] = useState("body");
  const [isCodeView, setIsCodeView] = useState(false);
  const [editorFontFamilySelectionValue, setEditorFontFamilySelectionValue] = useState(
    EDITOR_DISPLAY_FONT_FAMILY
  );
  const [editorFontSizeSelectionValue, setEditorFontSizeSelectionValue] = useState(
    String(EDITOR_DISPLAY_FONT_SIZE_PT)
  );
  const [editorInlineStyleState, setEditorInlineStyleState] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewForm, setPreviewForm] = useState({
    sendFrom: "",
    sendTo: "",
    customSendTo: "",
    leadId: "",
  });
  const [previewRenderState, setPreviewRenderState] = useState({
    loading: false,
    subject: "",
    html: "",
    text: "",
    error: "",
  });
  const [spintaxSuggestionPopup, setSpintaxSuggestionPopup] = useState({
    visible: false,
    top: 0,
    left: 0,
    selectionText: "",
    fullContextText: "",
    selectionStart: 0,
    selectionEnd: 0,
  });
  const [generatingSpintaxSuggestions, setGeneratingSpintaxSuggestions] = useState(false);
  const [autoGeneratingSpintax, setAutoGeneratingSpintax] = useState(false);
  const [cleaningHtml, setCleaningHtml] = useState(false);
  const [fixingFormatting, setFixingFormatting] = useState(false);
  const [customPromptOpen, setCustomPromptOpen] = useState(false);
  const [customPromptText, setCustomPromptText] = useState("");
  const [applyingCustomPrompt, setApplyingCustomPrompt] = useState(false);
  const [scanningDeliverability, setScanningDeliverability] = useState(false);
  const [deliverabilitySuggestions, setDeliverabilitySuggestions] = useState([]);
  const [deliverabilitySuggestionSelections, setDeliverabilitySuggestionSelections] = useState({});
  const [applyingDeliverabilitySuggestions, setApplyingDeliverabilitySuggestions] = useState(false);
  const [deliverabilityAppliedSuggestionKeys, setDeliverabilityAppliedSuggestionKeys] = useState({});
  const [deliverabilitySuggestionsCollapsed, setDeliverabilitySuggestionsCollapsed] = useState(false);
  const [spintaxVariantRemoveControls, setSpintaxVariantRemoveControls] = useState([]);
  const [previewSpintaxSeed, setPreviewSpintaxSeed] = useState(() => Date.now());
  const [previewSpintaxDisplayOverride, setPreviewSpintaxDisplayOverride] = useState(null);
  const [previewDiceFaceIndex, setPreviewDiceFaceIndex] = useState(4);
  const [previewDiceRolling, setPreviewDiceRolling] = useState(false);
  const editorRef = useRef(null);
  const sequenceAddStepMenuRef = useRef(null);
  const codeEditorRef = useRef(null);
  const subjectEditorRef = useRef(null);
  const subjectSelectionRangeRef = useRef(null);
  const bodySelectionRangeRef = useRef(null);
  const activeVariableTokenRef = useRef(null);
  const bodyHistoryRef = useRef({ key: "", stack: [], index: -1 });
  const applyingBodyHistoryRef = useRef(false);
  const previewDiceRollTimerRef = useRef(null);
  const previewDiceSwapTimerRef = useRef(null);
  const activeSpintaxContextRef = useRef(null);
  const lastSyncedVariantKeyRef = useRef("");
  const lastSyncedSubjectVariantKeyRef = useRef("");
  const hasInitializedEnabledStepsFilterRef = useRef(false);
  const enabledStepsFilterKnownIdsRef = useRef(new Set());
  const enabledSequencesFilterKnownIdsRef = useRef(new Set());
  const hasInitializedSentStepsFilterRef = useRef(false);
  const sentStepsFilterKnownIdsRef = useRef(new Set());

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createModalForm, setCreateModalForm] = useState({
    name: "",
    notes: "",
    salesforceCampaignId: "",
  });

  const [editForm, setEditForm] = useState({
    name: "",
    notes: "",
    salesforceCampaignId: "",
    collaboratorEmails: [],
    selectedCollaboratorEmail: "",
    ccEmails: [],
    bccEmails: [],
    ccInput: "",
    bccInput: "",
    campaignPriority: "medium",
    dailyLimit: 5000,
    minTimeGapMinutes: 0.096,
    stopSendingOnReply: false,
    stopCampaignForCompanyOnReply: false,
    stopSendingOnAutoReply: false,
    prioritizeNewLeads: false,
  });
  const [runSettingsForm, setRunSettingsForm] = useState({
    enableMillionVerifier: true,
    emailVerificationMode: "single",
    syncInvalidMailboxToSalesforce: true,
    syncSalesforceStatusAfterSend: true,
    postSendSalesforceStatus: "",
    updateSalesforceOutreachCounters: true,
    pruneQueuedLeadsOnSalesforceStatusChange: true,
  });
  const [showAllRunSalesforcePrunedLogs, setShowAllRunSalesforcePrunedLogs] = useState(false);
  const [showAllRunVerificationBlockedLogs, setShowAllRunVerificationBlockedLogs] = useState(false);
  const [showAllRunBounceLogs, setShowAllRunBounceLogs] = useState(false);
  const [showAllRunComplaintLogs, setShowAllRunComplaintLogs] = useState(false);
  const [showAllRunLastSesEventLogs, setShowAllRunLastSesEventLogs] = useState(false);
  const [campaignMemberStatusOptions, setCampaignMemberStatusOptions] = useState([]);
  const [scheduleDrafts, setScheduleDrafts] = useState([]);
  const [endFridayAtNoon, setEndFridayAtNoon] = useState(true);
  const [campaignDateWindow, setCampaignDateWindow] = useState({
    startDate: "",
    endDate: "",
    noEndDate: true,
  });
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  const scheduleAutosaveTimerRef = useRef(null);
  const lastSavedScheduleSignatureRef = useRef("");
  const [lastSavedSequenceSignature, setLastSavedSequenceSignature] = useState("");
  const [lastSavedSequenceDefinitionsSignature, setLastSavedSequenceDefinitionsSignature] = useState("");
  const [runStepPreviewUpdatingByKey, setRunStepPreviewUpdatingByKey] = useState({});
  const sequenceSenderAccountsAutosaveTimerRef = useRef(null);
  const sequenceRunStepAutosaveTimerRef = useRef(null);
  const speedChangeRequeueTimersRef = useRef({});
  const speedChangeSequenceIdsRef = useRef({});
  const lastSavedSenderAccountsSignatureRef = useRef("");
  const campaignSettingsAutosaveTimerRef = useRef(null);
  const lastSavedCampaignSettingsSignatureRef = useRef("");
  const campaignSettingsSeededCampaignIdRef = useRef("");
  const domainInfrastructureAutosaveTimerRef = useRef(null);
  const lastSavedDomainInfrastructureSignatureRef = useRef("");
  const leadSendModeTouchedRef = useRef(false);
  const campaignMonitorStreamRef = useRef(null);
  const latestCampaignLoadRequestIdRef = useRef(0);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const salesforceCampaignsInFlightRef = useRef(false);
  const salesforceCampaignsFetchedForEmployeeIdRef = useRef("");

  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !employeeId) return;
    const api = new ApiService({ employee: { employeeId } });
    api.get("landmark/employees/login");
  }, [isAuthenticated, employeeId]);

  useEffect(
    () => () => {
      closeCampaignMonitorStream();
      if (scheduleAutosaveTimerRef.current) {
        clearTimeout(scheduleAutosaveTimerRef.current);
      }
      if (sequenceSenderAccountsAutosaveTimerRef.current) {
        clearTimeout(sequenceSenderAccountsAutosaveTimerRef.current);
      }
      if (sequenceRunStepAutosaveTimerRef.current) {
        clearTimeout(sequenceRunStepAutosaveTimerRef.current);
      }
      Object.values(speedChangeRequeueTimersRef.current || {}).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
      if (campaignSettingsAutosaveTimerRef.current) {
        clearTimeout(campaignSettingsAutosaveTimerRef.current);
      }
      if (domainInfrastructureAutosaveTimerRef.current) {
        clearTimeout(domainInfrastructureAutosaveTimerRef.current);
      }
    },
    []
  );

  const loadCampaigns = async () => {
    if (!employee) return;
    setLoadingCampaigns(true);
    try {
      const api = new ApiService({ employee });
      const data = await api.get("landmark/sendora/getCampaigns");
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("Could not load Sendora campaigns");
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadSalesforceCampaigns = async ({ force = false } = {}) => {
    if (!employee) return;
    const currentEmployeeId = employee.employeeId || "";
    if (
      !force &&
      salesforceCampaignsFetchedForEmployeeIdRef.current === currentEmployeeId &&
      salesforceCampaigns.length
    ) {
      return;
    }
    if (salesforceCampaignsInFlightRef.current) return;

    salesforceCampaignsInFlightRef.current = true;
    setLoadingSalesforceCampaigns(true);
    try {
      const api = new ApiService({ employee });
      const data = await api.get(
        "landmark/sendora/getSalesforceCampaigns",
        null,
        null,
        { timeout: 120000 }
      );
      if (data?.error) {
        throw new Error(String(data.error || "Could not load Salesforce campaigns"));
      }
      setSalesforceCampaigns(Array.isArray(data) ? data : []);
      salesforceCampaignsFetchedForEmployeeIdRef.current = currentEmployeeId;
    } catch (err) {
      console.error(err);
      toast.error("Could not load Salesforce campaigns");
    } finally {
      salesforceCampaignsInFlightRef.current = false;
      setLoadingSalesforceCampaigns(false);
    }
  };

  const loadEmployeeEmails = async () => {
    if (!employee) return;
    try {
      const api = new ApiService({ employee });
      const data = await api.get("landmark/sendora/getEmployeeEmails");
      setEmployeeEmails(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("Could not load employee emails");
    }
  };

  const loadSendoraModeFlags = async () => {
    if (!employee) return;
    try {
      const api = new ApiService({ employee });
      const data = await api.get("landmark/sendora/getModeFlags");
      setSendoraModeFlags({
        isProd: !!data?.isProd,
        disableActualSend: !!data?.disableActualSend,
        recipientOverrideMode: !!data?.recipientOverrideMode,
        recipientOverrideEmail: String(data?.recipientOverrideEmail || ""),
        suppressSalesforcePostSendChanges: !!data?.suppressSalesforcePostSendChanges,
      });
    } catch (err) {
      console.error(err);
      setSendoraModeFlags(DEFAULT_SENDORA_MODE_FLAGS);
    }
  };

  const loadSenderAccounts = async () => {
    if (!employee) return;
    try {
      const api = new ApiService({ employee });
      const data = await api.get("landmark/sendora/getSenderAccounts");
      const normalizedPreferences = (Array.isArray(data?.senderSignaturePreferences)
        ? data.senderSignaturePreferences
        : []
      )
        .map((entry) => normalizeSenderSignaturePreference(entry))
        .filter(Boolean);
      const preferenceByEmail = new Map(
        normalizedPreferences.map((entry) => [entry.senderEmailLowercase, entry])
      );
      const accounts = (Array.isArray(data?.accounts) ? data.accounts : [])
        .map((entry) => normalizeSenderAccount(entry))
        .filter(Boolean)
        .map((entry) => {
          const pref = preferenceByEmail.get(entry.emailLowercase) || null;
          return {
            ...entry,
            senderSignatureHtml: normalizeSenderSignatureHtml(
              entry?.senderSignatureHtml || resolveSenderSignatureHtmlFromPreference(pref)
            ),
          };
        });
      setSenderAccounts(accounts);
      setSenderSignaturePreferences(normalizedPreferences);
      setDefaultSenderAccountEmail(normalizeEmail(data?.defaultAccountEmail || ""));
      const normalizedInfrastructureOptions = (
        Array.isArray(data?.sendingInfrastructureOptions)
          ? data.sendingInfrastructureOptions
          : SENDORA_SENDING_INFRASTRUCTURES
      )
        .map((entry) => ({
          key: normalizeInfrastructureKey(entry?.key || ""),
          label: String(entry?.label || "").trim(),
        }))
        .filter((entry) => !!entry.key)
        .map((entry) => ({
          ...entry,
          label:
            entry.label ||
            SENDORA_SENDING_INFRASTRUCTURES.find((candidate) => candidate.key === entry.key)?.label ||
            entry.key,
        }));
      const nextInfrastructureOptions = normalizedInfrastructureOptions.length
        ? normalizedInfrastructureOptions
        : SENDORA_SENDING_INFRASTRUCTURES;
      const domainRules = normalizeDomainInfrastructureRules(data?.senderDomainInfrastructurePreferences || []);
      setSendingInfrastructureOptions(nextInfrastructureOptions);
      setDomainInfrastructureRules(domainRules);
      lastSavedDomainInfrastructureSignatureRef.current = buildDomainInfrastructureSignature(domainRules);
    } catch (err) {
      console.error(err);
      toast.error("Could not load sender accounts");
    }
  };

  const syncEditFormFromCampaign = (campaign) => {
    const options = { ...DEFAULT_OPTIONS, ...(campaign?.options || {}) };
    const normalizedAiVariables = normalizeCampaignAiVariables(options?.aiVariables || []);
    const collaborators = (campaign?.collaborators || [])
      .map((c) => normalizeLandmarkEmail(c.email || ""))
      .filter(Boolean);
    setEditForm({
      name: campaign?.name || "",
      notes: campaign?.notes || campaign?.description || "",
      salesforceCampaignId: campaign?.salesforceCampaignId || "",
      collaboratorEmails: Array.from(new Set(collaborators)),
      selectedCollaboratorEmail: "",
      ccEmails: splitEmailsFromInput((options.cc || []).join(" ")),
      bccEmails: splitEmailsFromInput((options.bcc || []).join(" ")),
      ccInput: "",
      bccInput: "",
      campaignPriority: normalizeCampaignSpeed(options.campaignPriority || "medium"),
      dailyLimit: options.dailyLimit,
      minTimeGapMinutes: options.minTimeGapMinutes,
      stopSendingOnReply: !!options.stopSendingOnReply,
      stopCampaignForCompanyOnReply: !!options.stopCampaignForCompanyOnReply,
      stopSendingOnAutoReply: !!options.stopSendingOnAutoReply,
      prioritizeNewLeads: !!options.prioritizeNewLeads,
    });
    setRunSettingsForm((prev) => ({
      ...prev,
      enableMillionVerifier:
        typeof options?.enableMillionVerifier === "boolean" ? options.enableMillionVerifier : true,
      emailVerificationMode: normalizeEmailVerificationMode(options?.emailVerificationMode),
      syncInvalidMailboxToSalesforce:
        typeof options?.syncInvalidMailboxToSalesforce === "boolean"
          ? options.syncInvalidMailboxToSalesforce
          : true,
      syncSalesforceStatusAfterSend:
        typeof options?.syncSalesforceStatusAfterSend === "boolean"
          ? options.syncSalesforceStatusAfterSend
          : true,
      postSendSalesforceStatus: String(options?.postSendSalesforceStatus || "").trim(),
      updateSalesforceOutreachCounters:
        typeof options?.updateSalesforceOutreachCounters === "boolean"
          ? options.updateSalesforceOutreachCounters
          : true,
      pruneQueuedLeadsOnSalesforceStatusChange:
        typeof options?.pruneQueuedLeadsOnSalesforceStatusChange === "boolean"
          ? options.pruneQueuedLeadsOnSalesforceStatusChange
          : true,
    }));
    setCampaignAiVariablesDraft(normalizedAiVariables);
    setCampaignAiVariablesInitial(normalizedAiVariables);
    setSelectedAiVariableTokenKey(String(normalizedAiVariables?.[0]?.tokenKey || "aiVariable1"));
    const now = Math.floor(Date.now() / 1000);
    const schedules = Array.isArray(campaign?.schedules) && campaign.schedules.length
      ? campaign.schedules
      : [
          {
            ...DEFAULT_SCHEDULE,
            scheduleId: makeId("sch"),
            createdTime: now,
            updatedTime: now,
          },
        ];
    const normalizedSchedules = schedules.map((schedule, index) => ({
      ...DEFAULT_SCHEDULE,
      ...schedule,
      scheduleId: schedule.scheduleId || makeId(`sch_${index + 1}`),
      days: Array.isArray(schedule?.days) ? schedule.days : [...DEFAULT_SCHEDULE.days],
      bypassSchedule:
        typeof schedule?.bypassSchedule === "boolean"
          ? schedule.bypassSchedule
          : schedule?.isActive === false,
      pauseSendingAfterFridayNoon:
        typeof schedule?.pauseSendingAfterFridayNoon === "boolean"
          ? schedule.pauseSendingAfterFridayNoon
          : true,
      createdTime: schedule.createdTime || now,
      updatedTime: schedule.updatedTime || now,
    }));
    setScheduleDrafts(normalizedSchedules);
    const nextEndFridayAtNoon =
      typeof normalizedSchedules[0]?.pauseSendingAfterFridayNoon === "boolean"
        ? normalizedSchedules[0].pauseSendingAfterFridayNoon
        : true;
    setEndFridayAtNoon(nextEndFridayAtNoon);

    const rawStartDate = Number(campaign?.dateWindow?.startDate || 0) || 0;
    // Legacy saves used "now" epoch for default start; treat that as implicit "Now".
    const startDate = isEpochAtLocalMidnight(rawStartDate) ? rawStartDate : 0;
    const endDate = Number(campaign?.dateWindow?.endDate || 0) || 0;
    const nextCampaignDateWindow = {
      startDate: startDate > 0 ? epochToDateInput(startDate) : "",
      endDate: endDate > 0 ? epochToDateInput(endDate) : "",
      noEndDate: endDate <= 0,
    };
    setCampaignDateWindow(nextCampaignDateWindow);
    lastSavedScheduleSignatureRef.current = buildScheduleAutoSaveSignature(
      normalizedSchedules,
      nextCampaignDateWindow,
      nextEndFridayAtNoon
    );
  };

  const loadCampaignById = async (
    campaignId,
    {
      preserveActiveTab = false,
      preserveSequenceSelection = false,
      preserveSequenceDraft = false,
    } = {}
  ) => {
    if (!employee || !campaignId) return;
    const requestId = latestCampaignLoadRequestIdRef.current + 1;
    latestCampaignLoadRequestIdRef.current = requestId;
    try {
      const api = new ApiService({ employee });
      const campaign = await api.get(
        `landmark/sendora/getCampaignByCampaignId/${campaignId}`
      );
      if (requestId !== latestCampaignLoadRequestIdRef.current) {
        return;
      }
      if (!campaign || campaign.error) {
        throw new Error(campaign?.error || "Failed to load campaign");
      }
      setSelectedCampaign(campaign);
      if (!preserveActiveTab) {
        setActiveTab("leads");
      }
      syncEditFormFromCampaign(campaign);
      if (!preserveSequenceDraft) {
        const normalizedSequence = normalizeSequence(
          campaign.sequences,
          campaign?.options?.senderAccounts || [],
          campaign?.options?.senderAccountEmails || []
        );
        setSequenceDraft(normalizedSequence);
        const rawSequenceDefinitions = Array.isArray(campaign?.sequences?.sequenceDefinitions)
          ? campaign.sequences.sequenceDefinitions
          : [];
        const normalizedSequenceDefinitions = rawSequenceDefinitions
          .map((entry, index) => {
            const nodes = Array.isArray(entry?.nodes)
              ? entry.nodes
                  .map((node, nodeIndex) => ({
                    ...normalizeSequenceNode(
                      {
                        ...node,
                        nodeId: String(node?.nodeId || "").trim() || makeId("sqn_node"),
                      },
                      nodeIndex
                    ),
                    orderHint: nodeIndex,
                  }))
                  .filter((node) => !!node.stepId)
              : [];
            if (!nodes.length) return null;
            const waits = Array.from({ length: Math.max(0, nodes.length - 1) }, (_, waitIndex) => {
              const source = (entry?.waits || [])[waitIndex] || {};
              const amount = Math.max(1, Number(source?.amount || 1) || 1);
              const unit = SEQUENCE_WAIT_UNITS.includes(String(source?.unit || ""))
                ? source.unit
                : "days";
              return { amount, unit };
            });
            return {
              sequenceId: String(entry?.sequenceId || "").trim() || makeId("sqn"),
              name: String(entry?.name || "").trim() || `Sequence ${String.fromCharCode(65 + index)}`,
              nodes: nodes.map((node, nodeIndex) => normalizeSequenceNode(node, nodeIndex)),
              waits,
            };
          })
          .filter(Boolean);
        setSequencesDraft(normalizedSequenceDefinitions);
        setActiveSequenceId(String(normalizedSequenceDefinitions?.[0]?.sequenceId || ""));
        setLastSavedSequenceDefinitionsSignature(
          buildSequenceDefinitionsSaveSignature(normalizedSequenceDefinitions)
        );
        setLastSavedSequenceSignature(buildSequenceSaveSignature(normalizedSequence));
        lastSavedSenderAccountsSignatureRef.current = buildSenderAccountsSignature(normalizedSequence);
      }
      if (!preserveSequenceSelection) {
        setSelectedStepIndex(0);
        setSelectedVariantIndex(0);
      }
    } catch (err) {
      if (requestId !== latestCampaignLoadRequestIdRef.current) {
        return;
      }
      console.error(err);
      toast.error("Could not load selected campaign");
    }
  };

  const loadAllMembersForMetrics = async (campaignId) => {
    if (!employee || !campaignId) return;
    setAllMembersLoadedCampaignId("");
    setLoadingAllMembers(true);
    try {
      const api = new ApiService({ employee });
      const data = await api.get(
        `landmark/sendora/getCampaignMembers/${campaignId}`,
        { includeInactive: true }
      );
      const nextAllMembers = Array.isArray(data) ? data : [];
      const nextActiveMembers = nextAllMembers.filter((member) => member && member.isActive !== false);
      console.log("[Sendora] Setting leads data", {
        campaignId: String(campaignId || ""),
        includeInactive: true,
        allMembersCount: nextAllMembers.length,
        activeMembersCount: nextActiveMembers.length,
        data: nextAllMembers,
      });
      setAllMembers(nextAllMembers);
      setMembers(nextActiveMembers);
      setMemberSelection({});
      setAllMembersLoadedCampaignId(String(campaignId || ""));
    } catch (err) {
      console.error(err);
      setAllMembersLoadedCampaignId("");
    } finally {
      setLoadingAllMembers(false);
    }
  };
  const loadCampaignSentEmailsForLeads = async (campaignId) => {
    if (!employee || !campaignId) return;
    try {
      const api = new ApiService({ employee });
      const data = await api.get(`landmark/sendora/getCampaignSentEmails/${campaignId}`);
      setCampaignSentEmails(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setCampaignSentEmails([]);
    }
  };

  const refreshSelectedCampaignData = async (
    campaignId = selectedCampaignId,
    { preserveActiveTab = true } = {}
  ) => {
    if (!campaignId) return;
    await Promise.all([
      loadCampaignById(campaignId, { preserveActiveTab }),
      loadAllMembersForMetrics(campaignId),
      loadCampaignSentEmailsForLeads(campaignId),
      loadCampaigns(),
    ]);
  };

  const closeCampaignMonitorStream = () => {
    if (campaignMonitorStreamRef.current?.close) {
      try {
        campaignMonitorStreamRef.current.close();
      } catch {}
    }
    campaignMonitorStreamRef.current = null;
    setCampaignMonitorConnected(false);
  };

  const loadCampaignMonitorSnapshot = async (campaignId) => {
    if (!employee || !campaignId) return null;
    const api = new ApiService({ employee });
    const response = await api.get(`landmark/sendora/getCampaignMonitor/${campaignId}`);
    if (response?.error) {
      throw new Error(response.error || "Could not load campaign monitor");
    }
    const snapshot = response?.snapshot || null;
    setCampaignMonitor(snapshot);
    if (snapshot) {
      setCampaigns((prev) =>
        (Array.isArray(prev) ? prev : []).map((campaign) =>
          String(campaign?.campaignId || "").trim() === String(campaignId || "").trim()
            ? {
                ...campaign,
                ...buildCampaignProgressFromMonitorSnapshot(campaign, snapshot),
              }
            : campaign
        )
      );
      setSelectedCampaign((prev) =>
        prev && String(prev?.campaignId || "").trim() === String(campaignId || "").trim()
          ? {
              ...prev,
              ...buildCampaignProgressFromMonitorSnapshot(prev, snapshot),
            }
          : prev
      );
    }
    if (snapshot?.hasProgress) {
      setCampaignMonitorExpanded(true);
    }
    return snapshot;
  };

  const openCampaignMonitorStream = (campaignId) => {
    if (!employee || !campaignId) return;
    closeCampaignMonitorStream();
    const api = new ApiService({ employee });
    const encodedEmployeeId = encodeURIComponent(String(employee?.employeeId || ""));
    const path = `landmark/sendora/streamCampaignMonitor/${campaignId}?employeeId=${encodedEmployeeId}`;
    const stream = api.openEventStream({
      path,
      onOpen: () => {
        setCampaignMonitorConnected(true);
      },
      onError: () => {
        setCampaignMonitorConnected(false);
      },
      events: {
        snapshot: (payload) => {
          const snapshot = payload || null;
          setCampaignMonitor(snapshot);
          if (snapshot) {
            setCampaigns((prev) =>
              (Array.isArray(prev) ? prev : []).map((campaign) =>
                String(campaign?.campaignId || "").trim() === String(campaignId || "").trim()
                  ? {
                      ...campaign,
                      ...buildCampaignProgressFromMonitorSnapshot(campaign, snapshot),
                    }
                  : campaign
              )
            );
            setSelectedCampaign((prev) =>
              prev && String(prev?.campaignId || "").trim() === String(campaignId || "").trim()
                ? {
                    ...prev,
                    ...buildCampaignProgressFromMonitorSnapshot(prev, snapshot),
                  }
                : prev
            );
          }
          if (snapshot?.hasProgress) {
            setCampaignMonitorExpanded(true);
          }
        },
      },
    });
    campaignMonitorStreamRef.current = stream;
  };

  const refreshAllData = async () => {
    await Promise.all([
      loadCampaigns(),
      loadSalesforceCampaigns({ force: true }),
      loadEmployeeEmails(),
      loadSenderAccounts(),
      loadSendoraModeFlags(),
    ]);
  };

  const sendoraNavbarModePills = useMemo(() => {
    if (sendoraModeFlags?.isProd) return [];
    const pills = [];
    if (sendoraModeFlags?.disableActualSend) {
      pills.push({ key: "simulated", label: "Simulated Sends" });
    }
    if (sendoraModeFlags?.recipientOverrideMode) {
      const email = String(sendoraModeFlags?.recipientOverrideEmail || "");
      pills.push({
        key: "override",
        label: email ? `Recipient Override: ${email}` : "Recipient Override Enabled",
      });
    }
    if (sendoraModeFlags?.suppressSalesforcePostSendChanges) {
      pills.push({ key: "sf-write-disabled", label: "Salesforce writing disabled" });
    }
    return pills;
  }, [sendoraModeFlags]);

  useEffect(() => {
    if (!isAuthenticated || !employeeId) return;
    refreshAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, employeeId]);

  useEffect(() => {
    if (!selectedCampaignId || !employee?.employeeId) return;
    openCampaignMonitorStream(selectedCampaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee?.employeeId]);

  useEffect(() => {
    if (!selectedCampaignId) {
      closeCampaignMonitorStream();
      setCampaignMonitor(null);
      setCampaignMonitorExpanded(false);
      setSelectedMonitorEmailPreview(null);
      setSelectedCampaign(null);
      setActiveTab("leads");
      setMaxReachedTabIndex(0);
      setMembers([]);
      setAllMembers([]);
      setCampaignSentEmails([]);
      setAllMembersLoadedCampaignId("");
      setMemberSelection({});
      setMemberStatusFilter("unsent");
      setScheduleDrafts([]);
      setSelectedStepIndex(0);
      setSelectedVariantIndex(0);
      return;
    }
    setSelectedMonitorEmailPreview(null);
    setMaxReachedTabIndex(0);
    setMemberStatusFilter("unsent");
    const pendingTabOverride = String(pendingCampaignTabOverrideRef.current || "").trim();
    if (pendingTabOverride) {
      setActiveTab(pendingTabOverride);
    }
    loadCampaignById(selectedCampaignId, {
      preserveActiveTab: !!pendingTabOverride,
    });
    pendingCampaignTabOverrideRef.current = "";
    loadAllMembersForMetrics(selectedCampaignId);
    loadCampaignSentEmailsForLeads(selectedCampaignId);
    loadCampaignMonitorSnapshot(selectedCampaignId).catch((error) => {
      console.error(error);
    });
    openCampaignMonitorStream(selectedCampaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignId]);

  useEffect(() => {
    if (!selectedCampaign) return;
    const hasProgress =
      Number(selectedCampaign?.lifecycle?.startedTime || 0) > 0 ||
      !!String(selectedCampaign?.runtime?.currentRunId || "").trim() ||
      campaignMonitor?.hasProgress === true;
    if (hasProgress) {
      setCampaignMonitorExpanded(true);
    }
  }, [selectedCampaign, campaignMonitor?.hasProgress]);
  useEffect(() => {
    const addedSenderEmailSet = new Set(
      normalizeSenderAccounts(
        sequenceDraft?.senderAccounts || [],
        sequenceDraft?.senderAccountEmails || []
      )
        .map((entry) => normalizeEmail(entry?.emailLowercase || entry?.email || ""))
        .filter(Boolean)
    );
    const availableSenderEmails = (senderAccounts || [])
      .map((account) => normalizeEmail(account?.emailLowercase || account?.email || ""))
      .filter((email) => email && !addedSenderEmailSet.has(email));
    if (!availableSenderEmails.length) {
      if (selectedSenderAccountEmail) {
        setSelectedSenderAccountEmail("");
      }
      return;
    }
    if (!availableSenderEmails.includes(normalizeEmail(selectedSenderAccountEmail))) {
      setSelectedSenderAccountEmail(availableSenderEmails[0]);
    }
  }, [sequenceDraft, senderAccounts, selectedSenderAccountEmail]);

  useEffect(() => {
    const steps = sequenceDraft.steps || [];
    if (!steps.length) {
      if (selectedStepIndex !== 0) setSelectedStepIndex(0);
      if (selectedVariantIndex !== 0) setSelectedVariantIndex(0);
      return;
    }
    const clampedStepIndex = Math.max(0, Math.min(selectedStepIndex, steps.length - 1));
    if (clampedStepIndex !== selectedStepIndex) {
      setSelectedStepIndex(clampedStepIndex);
    }
    const variants = steps[clampedStepIndex]?.variants || [];
    const clampedVariantIndex = Math.max(
      0,
      Math.min(selectedVariantIndex, Math.max(variants.length - 1, 0))
    );
    if (clampedVariantIndex !== selectedVariantIndex) {
      setSelectedVariantIndex(clampedVariantIndex);
    }
  }, [sequenceDraft, selectedStepIndex, selectedVariantIndex]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".variable-dropdown")) {
        setOpenVariableMenu(false);
      }
      if (
        !event.target.closest(".campaign-row-menu") &&
        !event.target.closest(".campaign-row-menu-dropdown")
      ) {
        setOpenCampaignMenuId("");
      }
      if (!event.target.closest(".sendora-spintax-suggestion-popover")) {
        setSpintaxSuggestionPopup((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      }
      if (
        !event.target.closest(".sendora-spintax-variant-remove-btn") &&
        !event.target.closest(".sequence-rich-editor")
      ) {
        hideSpintaxVariantRemoveControls();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!openCampaignMenuId) return undefined;
    const closeMenu = () => setOpenCampaignMenuId("");
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [openCampaignMenuId]);

  useEffect(() => {
    if (isCodeView || activeTab !== "emails") {
      setSpintaxSuggestionPopup((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      hideSpintaxVariantRemoveControls();
      setEditorInlineStyleState({ bold: false, italic: false, underline: false });
    }
  }, [isCodeView, activeTab]);

  useEffect(() => {
    if (isCodeView || activeTab !== "emails") {
      setEditorFontFamilySelectionValue(EDITOR_DISPLAY_FONT_FAMILY);
      setEditorFontSizeSelectionValue(String(EDITOR_DISPLAY_FONT_SIZE_PT));
      return;
    }
    setEditorFontFamilySelectionValue(EDITOR_DISPLAY_FONT_FAMILY);
    setEditorFontSizeSelectionValue(String(EDITOR_DISPLAY_FONT_SIZE_PT));
  }, [
    isCodeView,
    activeTab,
    selectedCampaignId,
    selectedStepIndex,
    selectedVariantIndex,
  ]);

  useEffect(() => {
    if (!spintaxVariantRemoveControls.length) return undefined;
    const syncControls = () => updateSpintaxVariantRemoveControls();
    window.addEventListener("resize", syncControls);
    window.addEventListener("scroll", syncControls, true);
    return () => {
      window.removeEventListener("resize", syncControls);
      window.removeEventListener("scroll", syncControls, true);
    };
  }, [spintaxVariantRemoveControls.length]);

  useEffect(() => {
    if (isCodeView || !editorRef.current) return;
    if (activeTab !== "emails") return;
    const activeVariant =
      sequenceDraft.steps?.[selectedStepIndex]?.variants?.[selectedVariantIndex] || null;
    const variantKey = `${selectedCampaignId}:${selectedStepIndex}:${selectedVariantIndex}:${
      activeVariant?.variantId || ""
    }`;
    const nextHtml = highlightVariablesInEditorHtml(
      normalizeBodyToHtml(activeVariant?.body || "")
    );
    const isEditorFocused =
      typeof document !== "undefined" && document.activeElement === editorRef.current;
    const variantChanged = lastSyncedVariantKeyRef.current !== variantKey;
    if ((variantChanged || !isEditorFocused) && editorRef.current.innerHTML !== nextHtml) {
      editorRef.current.innerHTML = nextHtml;
    }
    lastSyncedVariantKeyRef.current = variantKey;
  }, [sequenceDraft, selectedStepIndex, selectedVariantIndex, isCodeView, activeTab]);

  useEffect(() => {
    if (!subjectEditorRef.current) return;
    if (activeTab !== "emails") return;
    const activeVariant =
      sequenceDraft.steps?.[selectedStepIndex]?.variants?.[selectedVariantIndex] || null;
    const variantKey = `${selectedCampaignId}:${selectedStepIndex}:${selectedVariantIndex}:${
      activeVariant?.variantId || ""
    }`;
    const nextHtml = highlightVariablesInSubjectText(activeVariant?.subject || "");
    const isFocused =
      typeof document !== "undefined" && document.activeElement === subjectEditorRef.current;
    const variantChanged = lastSyncedSubjectVariantKeyRef.current !== variantKey;
    if ((variantChanged || !isFocused) && subjectEditorRef.current.innerHTML !== nextHtml) {
      subjectEditorRef.current.innerHTML = nextHtml;
    }
    lastSyncedSubjectVariantKeyRef.current = variantKey;
  }, [sequenceDraft, selectedStepIndex, selectedVariantIndex, activeTab, selectedCampaignId]);

  const handleSubjectEditorInput = () => {
    const plain = stripHighlightedSubjectMarkup(subjectEditorRef.current?.innerText || "");
    const normalized = String(plain || "").replace(/[\r\n]+/g, " ");
    handleUpdateVariantField(selectedStepIndex, selectedVariantIndex, "subject", normalized);
  };

  const handleSubjectEditorKeyDown = (event) => {
    if (event.key === "|") {
      const insertedPipe = insertSpintaxPipeAtSelection("subject");
      if (insertedPipe) {
        event.preventDefault();
        return;
      }
    }
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleSubjectEditorPaste = (event) => {
    event.preventDefault();
    const clipboardText =
      event.clipboardData?.getData("text/plain") ||
      window.clipboardData?.getData("Text") ||
      "";
    const sanitized = String(clipboardText || "").replace(/[\r\n]+/g, " ");
    document.execCommand("insertText", false, sanitized);
  };

  const handleSubjectEditorBlur = () => {
    if (!subjectEditorRef.current) return;
    const highlighted = highlightVariablesInSubjectText(
      String(subjectEditorRef.current.innerText || "").replace(/[\r\n]+/g, " ")
    );
    if (subjectEditorRef.current.innerHTML !== highlighted) {
      subjectEditorRef.current.innerHTML = highlighted;
    }
  };

  const captureEditorSelection = (target = "body") => {
    if (typeof window === "undefined") return;
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount <= 0) return;
    const range = selection.getRangeAt(0);
    const container = target === "subject" ? subjectEditorRef.current : editorRef.current;
    if (!container) return;
    const isWithinEditor =
      container.contains(range.startContainer) && container.contains(range.endContainer);
    if (!isWithinEditor) return;
    if (target === "subject") {
      subjectSelectionRangeRef.current = range.cloneRange();
    } else {
      bodySelectionRangeRef.current = range.cloneRange();
    }
  };

  const applySelectionStyleToVariableTokens = (applyStyle) => {
    if (!editorRef.current || typeof applyStyle !== "function") return 0;
    const selection = window.getSelection?.();
    const liveRange =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const isLiveRangeWithinEditor =
      liveRange &&
      editorRef.current.contains(liveRange.startContainer) &&
      editorRef.current.contains(liveRange.endContainer);
    const savedRange = bodySelectionRangeRef.current;
    const isSavedRangeWithinEditor =
      savedRange &&
      editorRef.current.contains(savedRange.startContainer) &&
      editorRef.current.contains(savedRange.endContainer);
    const range = isLiveRangeWithinEditor
      ? liveRange
      : isSavedRangeWithinEditor
      ? savedRange
      : null;
    if (!range) return 0;
    const tokenNodes = Array.from(
      editorRef.current.querySelectorAll(".sendora-token.is-recipient, .sendora-token.is-sender")
    ).filter((node) => {
      try {
        return range.intersectsNode(node);
      } catch (error) {
        return false;
      }
    });
    if (tokenNodes.length <= 0) {
      const activeToken = activeVariableTokenRef.current;
      if (
        activeToken &&
        editorRef.current.contains(activeToken) &&
        activeToken.matches?.(".sendora-token.is-recipient, .sendora-token.is-sender")
      ) {
        applyStyle(activeToken.style);
        return 1;
      }
    }
    tokenNodes.forEach((node) => applyStyle(node.style));
    return tokenNodes.length;
  };

  const handleTokenMouseDown = (event, target = "body") => {
    const tokenNode = event.target?.closest?.(".sendora-token.is-recipient, .sendora-token.is-sender");
    if (!tokenNode) return;
    const container = target === "subject" ? subjectEditorRef.current : editorRef.current;
    if (!container || !container.contains(tokenNode)) return;
    event.preventDefault();
    event.stopPropagation();
    activeVariableTokenRef.current = target === "body" ? tokenNode : null;
    const selection = window.getSelection?.();
    if (!selection) return;
    const range = document.createRange();
    const ensureCaretAnchor = (node, side = "after") => {
      const sibling = side === "before" ? node.previousSibling : node.nextSibling;
      if (sibling?.nodeType === Node.TEXT_NODE) {
        return {
          node: sibling,
          offset: side === "before" ? String(sibling.textContent || "").length : 0,
        };
      }
      const anchorNode = document.createTextNode("\u200B");
      if (side === "before") {
        node.parentNode?.insertBefore(anchorNode, node);
        return { node: anchorNode, offset: 0 };
      }
      node.parentNode?.insertBefore(anchorNode, node.nextSibling);
      return { node: anchorNode, offset: 1 };
    };
    const tokenRect = tokenNode.getBoundingClientRect?.();
    const clickX = Number(event.clientX);
    const hasPointerPosition =
      tokenRect &&
      Number.isFinite(clickX) &&
      Number.isFinite(tokenRect.left) &&
      Number.isFinite(tokenRect.right) &&
      tokenRect.width > 0;
    if (hasPointerPosition) {
      const edgeZonePx = Math.min(8, Math.max(4, tokenRect.width * 0.22));
      const isLeftEdgeClick = clickX <= tokenRect.left + edgeZonePx;
      const isRightEdgeClick = clickX >= tokenRect.right - edgeZonePx;
      if (isLeftEdgeClick || isRightEdgeClick) {
        const anchor = ensureCaretAnchor(tokenNode, isLeftEdgeClick ? "before" : "after");
        range.setStart(anchor.node, anchor.offset);
        range.collapse(true);
        activeVariableTokenRef.current = null;
      } else {
        range.selectNode(tokenNode);
      }
    } else {
      range.selectNode(tokenNode);
    }
    selection.removeAllRanges();
    selection.addRange(range);
    if (target === "subject") {
      captureEditorSelection("subject");
    } else {
      captureEditorSelection("body");
      syncEditorFontControlsFromSelection();
      updateSpintaxSuggestionPopupFromSelection();
    }
  };

  const handleRichEditorMouseDown = (event) => {
    const tokenNode = event.target?.closest?.(".sendora-token.is-recipient, .sendora-token.is-sender");
    if (tokenNode) {
      handleTokenMouseDown(event, "body");
      return;
    }
    const container = editorRef.current;
    if (!container || !container.contains(event.target)) return;
    const clickX = Number(event.clientX);
    const clickY = Number(event.clientY);
    if (!Number.isFinite(clickX) || !Number.isFinite(clickY)) return;
    // If click resolves to an in-text caret location, let native browser caret handling win.
    let nativePointRange = null;
    if (typeof document.caretRangeFromPoint === "function") {
      nativePointRange = document.caretRangeFromPoint(clickX, clickY);
    } else if (typeof document.caretPositionFromPoint === "function") {
      const pointPos = document.caretPositionFromPoint(clickX, clickY);
      if (pointPos?.offsetNode) {
        nativePointRange = document.createRange();
        nativePointRange.setStart(pointPos.offsetNode, Number(pointPos.offset || 0));
        nativePointRange.collapse(true);
      }
    }
    if (nativePointRange && container.contains(nativePointRange.startContainer)) {
      const nativeRects = Array.from(nativePointRange.getClientRects?.() || []).filter(
        (rect) => rect && rect.height > 0
      );
      const nativeRect = nativeRects[0] || null;
      if (nativeRect && clickX <= Number(nativeRect.right || 0) + 4) {
        return;
      }
    }
    const tokenNodes = Array.from(
      container.querySelectorAll(".sendora-token.is-recipient, .sendora-token.is-sender")
    );
    if (!tokenNodes.length) return;
    const lineCandidates = tokenNodes
      .map((node) => ({ node, rect: node.getBoundingClientRect?.() || null }))
      .filter(
        ({ rect }) =>
          rect &&
          rect.width > 0 &&
          rect.height > 0 &&
          clickY >= rect.top - 8 &&
          clickY <= rect.bottom + 8
      );
    if (!lineCandidates.length) {
      if (event.target !== container) return;
      const nearestByY = tokenNodes
        .map((node) => ({ node, rect: node.getBoundingClientRect?.() || null }))
        .filter(({ rect }) => rect && rect.width > 0 && rect.height > 0)
        .map(({ node, rect }) => ({
          node,
          rect,
          distance: Math.abs(clickY - (Number(rect.top || 0) + Number(rect.height || 0) / 2)),
        }))
        .sort((a, b) => a.distance - b.distance)[0];
      if (!nearestByY) return;
      lineCandidates.push({ node: nearestByY.node, rect: nearestByY.rect });
    }
    let rightMost = lineCandidates[0];
    lineCandidates.forEach((candidate) => {
      if ((candidate.rect?.right || 0) > (rightMost.rect?.right || 0)) {
        rightMost = candidate;
      }
    });
    if (!rightMost?.rect) return;
    const activeLineRect = rightMost.rect;
    const isSameVisualLine = (rect) => {
      if (!rect || !activeLineRect) return false;
      const overlapTop = Math.max(Number(rect.top || 0), Number(activeLineRect.top || 0));
      const overlapBottom = Math.min(Number(rect.bottom || 0), Number(activeLineRect.bottom || 0));
      const overlap = Math.max(0, overlapBottom - overlapTop);
      const minHeight = Math.max(1, Math.min(Number(rect.height || 0), Number(activeLineRect.height || 0)));
      return overlap / minHeight >= 0.45;
    };
    const getInlineNodeLineRect = (node) => {
      if (!node) return null;
      if (node.nodeType === Node.TEXT_NODE) {
        const textValue = String(node.textContent || "");
        if (!textValue) return null;
        const textRange = document.createRange();
        textRange.selectNodeContents(node);
        const rects = Array.from(textRange.getClientRects?.() || []).filter(
          (rect) =>
            rect &&
            rect.width > 0 &&
            rect.height > 0 &&
            isSameVisualLine(rect)
        );
        if (!rects.length) return null;
        return rects.reduce((maxRect, rect) => (rect.right > maxRect.right ? rect : maxRect), rects[0]);
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const rect = node.getBoundingClientRect?.() || null;
        if (
          !rect ||
          !(rect.width > 0) ||
          !(rect.height > 0) ||
          !isSameVisualLine(rect)
        ) {
          return null;
        }
        return rect;
      }
      return null;
    };
    let lineRightEdge = rightMost.rect.right;
    let tailNode = rightMost.node;
    let sibling = rightMost.node.nextSibling;
    while (sibling) {
      if (sibling.nodeType === Node.ELEMENT_NODE && sibling.tagName === "BR") break;
      const rect = getInlineNodeLineRect(sibling);
      if (rect && rect.right >= lineRightEdge) {
        lineRightEdge = rect.right;
        tailNode = sibling;
      }
      sibling = sibling.nextSibling;
    }
    const textWalker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    while (textWalker.nextNode()) {
      const textNode = textWalker.currentNode;
      const rect = getInlineNodeLineRect(textNode);
      if (rect && rect.right >= lineRightEdge) {
        lineRightEdge = rect.right;
        tailNode = textNode;
      }
    }
    const tailIsVariableTokenPrecheck =
      tailNode?.nodeType === Node.ELEMENT_NODE &&
      tailNode?.matches?.(".sendora-token.is-recipient, .sendora-token.is-sender");
    const clickedToRightOfVariableTail =
      tailIsVariableTokenPrecheck && clickX > Number(rightMost?.rect?.right || 0) + 2;
    if (clickX <= lineRightEdge + 2 && !clickedToRightOfVariableTail) return;
    event.preventDefault();
    const selection = window.getSelection?.();
    if (!selection) return;
    container.focus?.();
    const range = document.createRange();
    const ensureCaretAnchorAfterNode = (node) => {
      if (!node?.parentNode) return null;
      const nextSibling = node.nextSibling;
      if (nextSibling?.nodeType === Node.TEXT_NODE) {
        const textValue = String(nextSibling.textContent || "");
        if (!textValue) {
          nextSibling.textContent = "\u200B";
          return { node: nextSibling, offset: 1 };
        }
        if (textValue.startsWith("\u200B")) {
          return { node: nextSibling, offset: 1 };
        }
        return { node: nextSibling, offset: 0 };
      }
      const anchorNode = document.createTextNode("\u200B");
      node.parentNode.insertBefore(anchorNode, nextSibling || null);
      return { node: anchorNode, offset: 1 };
    };
    const getLineRectForCollapsedRange = (collapsedRange) => {
      if (!collapsedRange) return null;
      const rects = Array.from(collapsedRange.getClientRects?.() || []).filter(
        (rect) =>
          rect &&
          rect.width >= 0 &&
          rect.height > 0 &&
          isSameVisualLine(rect)
      );
      if (!rects.length) return null;
      return rects.reduce((maxRect, rect) => (rect.right > maxRect.right ? rect : maxRect), rects[0]);
    };
    let bestCaret = null;
    let bestCaretRect = null;
    const maybeUseCaret = (node, offset) => {
      if (!node) return;
      const candidateRange = document.createRange();
      candidateRange.setStart(node, Math.max(0, Number(offset || 0)));
      candidateRange.collapse(true);
      const rect = getLineRectForCollapsedRange(candidateRange);
      if (!rect) return;
      if (!bestCaretRect || rect.right > bestCaretRect.right) {
        bestCaret = { node, offset: Math.max(0, Number(offset || 0)) };
        bestCaretRect = rect;
      }
    };
    lineCandidates.forEach(({ node }) => {
      if (!node?.parentNode) return;
      try {
        const candidateRange = document.createRange();
        candidateRange.setStartAfter(node);
        candidateRange.collapse(true);
        const rect = getLineRectForCollapsedRange(candidateRange);
        if (rect && (!bestCaretRect || rect.right > bestCaretRect.right)) {
          bestCaret = {
            node: candidateRange.startContainer,
            offset: Number(candidateRange.startOffset || 0),
          };
          bestCaretRect = rect;
        }
      } catch (error) {
        // Ignore invalid range candidates for detached nodes.
      }
    });
    const endTextWalker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    while (endTextWalker.nextNode()) {
      const textNode = endTextWalker.currentNode;
      const textValue = String(textNode?.textContent || "");
      if (!textValue.length) continue;
      maybeUseCaret(textNode, textValue.length);
    }
    const tailIsVariableToken = tailIsVariableTokenPrecheck;
    if (tailIsVariableToken) {
      const anchor = ensureCaretAnchorAfterNode(tailNode);
      if (anchor) range.setStart(anchor.node, anchor.offset);
      else range.setStartAfter(tailNode);
    } else if (bestCaret && bestCaretRect && clickX > bestCaretRect.right + 2) {
      range.setStart(bestCaret.node, bestCaret.offset);
    } else if (tailNode?.nodeType === Node.TEXT_NODE) {
      range.setStart(tailNode, String(tailNode.textContent || "").length);
    } else if (tailNode?.parentNode) {
      const anchor = ensureCaretAnchorAfterNode(tailNode);
      if (anchor) range.setStart(anchor.node, anchor.offset);
      else range.setStartAfter(tailNode);
    } else {
      const anchorNode = document.createTextNode("\u200B");
      rightMost.node.parentNode?.insertBefore(anchorNode, rightMost.node.nextSibling);
      range.setStart(anchorNode, 1);
    }
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    activeVariableTokenRef.current = null;
    captureEditorSelection("body");
    syncEditorFontControlsFromSelection();
    syncEditorInlineStyleControlsFromSelection();
    updateSpintaxSuggestionPopupFromSelection();
  };

  const restoreEditorSelection = (target = "body") => {
    if (typeof window === "undefined") return;
    const container = target === "subject" ? subjectEditorRef.current : editorRef.current;
    if (!container) return;
    const savedRange = target === "subject"
      ? subjectSelectionRangeRef.current
      : bodySelectionRangeRef.current;
    const selection = window.getSelection?.();
    if (!selection) return;
    selection.removeAllRanges();
    const isSavedRangeValid =
      savedRange &&
      container.contains(savedRange.startContainer) &&
      container.contains(savedRange.endContainer);
    if (isSavedRangeValid) {
      selection.addRange(savedRange);
      return;
    }
    const fallbackRange = document.createRange();
    fallbackRange.selectNodeContents(container);
    fallbackRange.collapse(false);
    selection.addRange(fallbackRange);
  };

  const getEditorFontSelectionState = () => {
    if (!editorRef.current || typeof window === "undefined") {
      return {
        family: EDITOR_DISPLAY_FONT_FAMILY,
        size: String(EDITOR_DISPLAY_FONT_SIZE_PT),
      };
    }
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount <= 0) {
      return {
        family: EDITOR_DISPLAY_FONT_FAMILY,
        size: String(EDITOR_DISPLAY_FONT_SIZE_PT),
      };
    }
    const range = selection.getRangeAt(0);
    const isWithinEditor =
      editorRef.current.contains(range.startContainer) &&
      editorRef.current.contains(range.endContainer);
    if (!isWithinEditor) {
      return {
        family: EDITOR_DISPLAY_FONT_FAMILY,
        size: String(EDITOR_DISPLAY_FONT_SIZE_PT),
      };
    }

    const resolveNodeStyle = (node) => {
      const element =
        node?.nodeType === Node.TEXT_NODE ? node.parentElement : node instanceof Element ? node : null;
      if (!element || !editorRef.current.contains(element)) return null;
      if (element.closest?.(".sendora-token.is-spintax-delimiter")) {
        return null;
      }
      const computed = window.getComputedStyle(element);
      const family = normalizeEditorFontFamilyForOption(computed?.fontFamily || "");
      const size = normalizeEditorFontSizeForOption(computed?.fontSize || "");
      if (!family && !size) return null;
      return { family, size };
    };

    if (range.collapsed) {
      const caretCandidates = [];
      const pushCandidate = (node) => {
        if (!node) return;
        if (!caretCandidates.includes(node)) {
          caretCandidates.push(node);
        }
      };
      const startNode = range.startContainer;
      const startOffset = Number(range.startOffset || 0);
      pushCandidate(startNode);

      if (startNode?.nodeType === Node.TEXT_NODE) {
        if (startOffset > 0) {
          pushCandidate(startNode);
        } else {
          pushCandidate(startNode.previousSibling);
        }
        if (startOffset >= String(startNode.textContent || "").length) {
          pushCandidate(startNode.nextSibling);
        }
        pushCandidate(startNode.parentElement);
      } else if (startNode instanceof Element) {
        const childNodes = startNode.childNodes || [];
        pushCandidate(childNodes[startOffset - 1]);
        pushCandidate(childNodes[startOffset]);
        pushCandidate(startNode);
      }

      const caretStyle =
        caretCandidates
          .map((node) => resolveNodeStyle(node))
          .find((style) => style && (style.family || style.size)) || null;
      return {
        family: caretStyle?.family || EDITOR_DISPLAY_FONT_FAMILY,
        size: caretStyle?.size || String(EDITOR_DISPLAY_FONT_SIZE_PT),
      };
    }

    const samples = [];
    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const textNode = walker.currentNode;
      const textValue = String(textNode?.textContent || "").replace(/\u200B/g, "");
      if (!textValue.trim()) continue;
      let intersects = false;
      try {
        intersects = range.intersectsNode(textNode);
      } catch (error) {
        intersects = false;
      }
      if (!intersects) continue;
      const styleSample = resolveNodeStyle(textNode);
      if (styleSample) samples.push(styleSample);
    }
    if (!samples.length) {
      return {
        family: EDITOR_DISPLAY_FONT_FAMILY,
        size: String(EDITOR_DISPLAY_FONT_SIZE_PT),
      };
    }
    const firstFamily = String(samples[0]?.family || "");
    const firstSize = String(samples[0]?.size || "");
    const uniformFamily = firstFamily && samples.every((sample) => String(sample.family || "") === firstFamily);
    const uniformSize = firstSize && samples.every((sample) => String(sample.size || "") === firstSize);
    return {
      family: uniformFamily ? firstFamily : "",
      size: uniformSize ? firstSize : "",
    };
  };

  const getEditorInlineStyleSelectionState = () => {
    if (!editorRef.current || typeof window === "undefined") {
      return { bold: false, italic: false, underline: false };
    }
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount <= 0) {
      return { bold: false, italic: false, underline: false };
    }
    const range = selection.getRangeAt(0);
    const isWithinEditor =
      editorRef.current.contains(range.startContainer) &&
      editorRef.current.contains(range.endContainer);
    if (!isWithinEditor) {
      return { bold: false, italic: false, underline: false };
    }

    const resolveNodeInlineStyle = (node) => {
      const element =
        node?.nodeType === Node.TEXT_NODE ? node.parentElement : node instanceof Element ? node : null;
      if (!element || !editorRef.current.contains(element)) return null;
      if (element.closest?.(".sendora-token.is-spintax-delimiter")) {
        return null;
      }
      const computed = window.getComputedStyle(element);
      const weight = String(computed?.fontWeight || "").trim();
      const numericWeight = Number.parseInt(weight, 10);
      const isBold =
        Number.isFinite(numericWeight) ? numericWeight >= 600 : /bold/i.test(weight);
      const isItalic = /(italic|oblique)/i.test(String(computed?.fontStyle || ""));
      const isUnderline = /underline/i.test(String(computed?.textDecorationLine || ""));
      return { bold: isBold, italic: isItalic, underline: isUnderline };
    };

    if (range.collapsed) {
      const candidates = [];
      const pushCandidate = (node) => {
        if (!node) return;
        if (!candidates.includes(node)) candidates.push(node);
      };
      const startNode = range.startContainer;
      const startOffset = Number(range.startOffset || 0);
      pushCandidate(startNode);
      if (startNode?.nodeType === Node.TEXT_NODE) {
        if (startOffset === 0) {
          pushCandidate(startNode.previousSibling);
        }
        if (startOffset >= String(startNode.textContent || "").length) {
          pushCandidate(startNode.nextSibling);
        }
        pushCandidate(startNode.parentElement);
      } else if (startNode instanceof Element) {
        const childNodes = startNode.childNodes || [];
        pushCandidate(childNodes[startOffset - 1]);
        pushCandidate(childNodes[startOffset]);
        pushCandidate(startNode);
      }
      return (
        candidates
          .map((node) => resolveNodeInlineStyle(node))
          .find((style) => style) || { bold: false, italic: false, underline: false }
      );
    }

    const samples = [];
    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const textNode = walker.currentNode;
      const textValue = String(textNode?.textContent || "").replace(/\u200B/g, "");
      if (!textValue.trim()) continue;
      let intersects = false;
      try {
        intersects = range.intersectsNode(textNode);
      } catch (error) {
        intersects = false;
      }
      if (!intersects) continue;
      const styleSample = resolveNodeInlineStyle(textNode);
      if (styleSample) samples.push(styleSample);
    }
    if (!samples.length) return { bold: false, italic: false, underline: false };
    return {
      bold: samples.every((sample) => sample.bold === true),
      italic: samples.every((sample) => sample.italic === true),
      underline: samples.every((sample) => sample.underline === true),
    };
  };

  const syncEditorFontControlsFromSelection = () => {
    const selectionState = getEditorFontSelectionState();
    setEditorFontFamilySelectionValue(selectionState.family || "");
    setEditorFontSizeSelectionValue(selectionState.size || "");
  };

  const syncEditorInlineStyleControlsFromSelection = () => {
    setEditorInlineStyleState(getEditorInlineStyleSelectionState());
  };


  const hideSpintaxSuggestionPopup = () => {
    setSpintaxSuggestionPopup((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  };

  const hideSpintaxVariantRemoveControls = () => {
    activeSpintaxContextRef.current = null;
    setSpintaxVariantRemoveControls((prev) => (prev.length ? [] : prev));
  };

  const updateSpintaxSuggestionPopupFromSelection = () => {
    if (isCodeView || activeTab !== "emails" || !editorRef.current) {
      hideSpintaxSuggestionPopup();
      return;
    }
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount <= 0) {
      hideSpintaxSuggestionPopup();
      return;
    }
    const range = selection.getRangeAt(0);
    const isWithinEditor =
      editorRef.current.contains(range.startContainer) &&
      editorRef.current.contains(range.endContainer);
    if (!isWithinEditor || range.collapsed) {
      hideSpintaxSuggestionPopup();
      return;
    }
    const rawSelectedText = String(selection.toString() || "");
    const selectedText = rawSelectedText.trim();
    if (!selectedText || rawSelectedText.includes("\n")) {
      hideSpintaxSuggestionPopup();
      return;
    }
    if (!/[A-Za-z0-9]/.test(selectedText)) {
      hideSpintaxSuggestionPopup();
      return;
    }

    const selectionRect = range.getBoundingClientRect();
    const hasRect = selectionRect && (selectionRect.width > 0 || selectionRect.height > 0);
    if (!hasRect) {
      hideSpintaxSuggestionPopup();
      return;
    }

    const preRange = document.createRange();
    preRange.selectNodeContents(editorRef.current);
    preRange.setEnd(range.startContainer, range.startOffset);
    const selectionStart = preRange.toString().length;
    const selectionEnd = selectionStart + rawSelectedText.length;
    const fullContextText = stripZeroWidthSpaces(
      stripVariableHighlightMarkup(editorRef.current.innerText || "")
    );
    const selectedContainsDelimiters = /[{}|]/.test(rawSelectedText);
    const insideSpintax =
      getActiveSpintaxContext(editorRef.current, range) !== null ||
      isSelectionInsideSpintax(editorRef.current, range);
    const variableTokenNodes = Array.from(
      editorRef.current.querySelectorAll(".sendora-token.is-recipient, .sendora-token.is-sender")
    );
    const intersectsVariableToken = variableTokenNodes.some((node) => {
      try {
        return range.intersectsNode(node);
      } catch (error) {
        return false;
      }
    });
    if (selectedContainsDelimiters || insideSpintax || intersectsVariableToken) {
      hideSpintaxSuggestionPopup();
      return;
    }

    setSpintaxSuggestionPopup({
      visible: true,
      top: Math.max(8, selectionRect.top - 8),
      left: selectionRect.left + selectionRect.width / 2,
      selectionText: selectedText,
      fullContextText,
      selectionStart,
      selectionEnd,
    });
  };

  const insertInlineTokenAtSelection = (container, token, tokenClass) => {
    if (typeof window === "undefined" || !container) return false;
    const selection = window.getSelection?.();
    if (!selection) return false;
    if (!selection.rangeCount) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(container);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
    }
    let range = selection.getRangeAt(0);
    const isWithinEditor =
      container.contains(range.startContainer) && container.contains(range.endContainer);
    if (!isWithinEditor) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(container);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
      range = selection.getRangeAt(0);
    }
    // Always insert at caret end so we don't replace adjacent nodes (e.g. trailing <br>).
    range.collapse(false);
    const tokenNode = document.createElement("span");
    tokenNode.setAttribute("contenteditable", "false");
    tokenNode.className = `sendora-token ${tokenClass}`;
    tokenNode.style.display = "inline";
    tokenNode.style.whiteSpace = "nowrap";
    tokenNode.textContent = token;
    range.insertNode(tokenNode);

    const nextRange = document.createRange();
    nextRange.setStartAfter(tokenNode);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    return true;
  };

  const insertTextAtSelection = (container, textToInsert = "") => {
    if (typeof window === "undefined" || !container || !textToInsert) return false;
    const selection = window.getSelection?.();
    if (!selection) return false;
    if (!selection.rangeCount) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(container);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
    }
    let range = selection.getRangeAt(0);
    const isWithinEditor =
      container.contains(range.startContainer) && container.contains(range.endContainer);
    if (!isWithinEditor) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(container);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
      range = selection.getRangeAt(0);
    }
    range.deleteContents();
    const textNode = document.createTextNode(String(textToInsert));
    range.insertNode(textNode);
    const nextRange = document.createRange();
    nextRange.setStartAfter(textNode);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    return true;
  };

  const insertSpintaxTokenAtSelection = (container, options = []) => {
    if (typeof window === "undefined" || !container || !Array.isArray(options) || !options.length) {
      return false;
    }
    const selection = window.getSelection?.();
    if (!selection) return false;
    if (!selection.rangeCount) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(container);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
    }
    let range = selection.getRangeAt(0);
    const isWithinEditor =
      container.contains(range.startContainer) && container.contains(range.endContainer);
    if (!isWithinEditor) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(container);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
      range = selection.getRangeAt(0);
    }

    range.deleteContents();
    const fragment = document.createDocumentFragment();
    const makeDelimiter = (symbol) => {
      const node = document.createElement("span");
      node.className = "sendora-token is-spintax-delimiter";
      node.setAttribute("contenteditable", "false");
      node.textContent = symbol;
      return node;
    };

    fragment.appendChild(makeDelimiter("{"));
    options.forEach((option, index) => {
      if (index > 0) {
        fragment.appendChild(makeDelimiter("|"));
      }
      fragment.appendChild(document.createTextNode(String(option || "")));
    });
    const rightDelimiter = makeDelimiter("}");
    fragment.appendChild(rightDelimiter);
    range.insertNode(fragment);

    const nextRange = document.createRange();
    nextRange.setStartAfter(rightDelimiter);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    return true;
  };

  const getSpintaxDelimiterPairs = (container) => {
    if (!container) return [];
    const delimiterNodes = Array.from(
      container.querySelectorAll(".sendora-token.is-spintax-delimiter")
    );
    const pairs = [];
    let openNode = null;
    delimiterNodes.forEach((node) => {
      const token = String(node.textContent || "").trim();
      if (token === "{") {
        openNode = node;
        return;
      }
      if (token === "}" && openNode) {
        pairs.push({ left: openNode, right: node });
        openNode = null;
      }
    });
    return pairs;
  };

  const cleanupEmptySpintaxPairs = (container, options = {}) => {
    if (!container) return false;
    const onlyWhenSelectionOutside = options?.onlyWhenSelectionOutside !== false;
    const selection = window.getSelection?.();
    const selectionRange =
      selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const pairs = getSpintaxDelimiterPairs(container);
    if (!pairs.length) return false;
    let removedAny = false;
    pairs.forEach((pair) => {
      if (!pair?.left || !pair?.right) return;
      const betweenNodes = [];
      let cursor = pair.left.nextSibling;
      while (cursor && cursor !== pair.right) {
        betweenNodes.push(cursor);
        cursor = cursor.nextSibling;
      }
      if (!betweenNodes.length) return;
      const hasPipeDelimiter = betweenNodes.some(
        (node) =>
          node?.nodeType === Node.ELEMENT_NODE &&
          node.classList?.contains?.("sendora-token") &&
          node.classList?.contains?.("is-spintax-delimiter") &&
          String(node.textContent || "").trim() === "|"
      );
      const hasVisibleContent = betweenNodes.some((node) => {
        if (!node) return false;
        const text = String(node.textContent || "").replace(/\u200B/g, "").trim();
        return Boolean(text);
      });
      if (hasPipeDelimiter || hasVisibleContent) return;
      if (onlyWhenSelectionOutside && selectionRange) {
        const afterLeft = document.createRange();
        afterLeft.setStartAfter(pair.left);
        afterLeft.collapse(true);
        const beforeRight = document.createRange();
        beforeRight.setStartBefore(pair.right);
        beforeRight.collapse(true);
        const startsAfterLeft =
          selectionRange.compareBoundaryPoints(Range.START_TO_START, afterLeft) >= 0;
        const startsBeforeRight =
          selectionRange.compareBoundaryPoints(Range.START_TO_START, beforeRight) <= 0;
        if (startsAfterLeft && startsBeforeRight) return;
      }
      betweenNodes.forEach((node) => node.remove());
      pair.left.remove();
      pair.right.remove();
      removedAny = true;
    });
    return removedAny;
  };

  const isSelectionInsideSpintax = (container, selectionRange) => {
    if (!container || !selectionRange) return false;
    const pairs = getSpintaxDelimiterPairs(container);
    if (!pairs.length) return false;
    return pairs.some(({ left, right }) => {
      const afterLeft = document.createRange();
      afterLeft.setStartAfter(left);
      afterLeft.collapse(true);
      const beforeRight = document.createRange();
      beforeRight.setStartBefore(right);
      beforeRight.collapse(true);
      const startsAfterLeft =
        selectionRange.compareBoundaryPoints(Range.START_TO_START, afterLeft) >= 0;
      const startsBeforeRight =
        selectionRange.compareBoundaryPoints(Range.START_TO_START, beforeRight) <= 0;
      return startsAfterLeft && startsBeforeRight;
    });
  };

  const getActiveSpintaxContext = (container, selectionRange) => {
    if (!container || !selectionRange) return null;
    const pairs = getSpintaxDelimiterPairs(container);
    if (!pairs.length) return null;
    for (let index = 0; index < pairs.length; index += 1) {
      const pair = pairs[index];
      const afterLeft = document.createRange();
      afterLeft.setStartAfter(pair.left);
      afterLeft.collapse(true);
      const beforeRight = document.createRange();
      beforeRight.setStartBefore(pair.right);
      beforeRight.collapse(true);
      const startsAfterLeft =
        selectionRange.compareBoundaryPoints(Range.START_TO_START, afterLeft) >= 0;
      const startsBeforeRight =
        selectionRange.compareBoundaryPoints(Range.START_TO_START, beforeRight) <= 0;
      if (!startsAfterLeft || !startsBeforeRight) continue;

      const allDelimiters = Array.from(
        container.querySelectorAll(".sendora-token.is-spintax-delimiter")
      );
      const leftIndex = allDelimiters.indexOf(pair.left);
      const rightIndex = allDelimiters.indexOf(pair.right);
      if (leftIndex < 0 || rightIndex < 0 || rightIndex <= leftIndex) continue;
      const pipes = allDelimiters
        .slice(leftIndex + 1, rightIndex)
        .filter((node) => String(node.textContent || "").trim() === "|");
      return { left: pair.left, right: pair.right, pipes };
    }
    return null;
  };

  const updateSpintaxVariantRemoveControls = () => {
    if (isCodeView || !editorRef.current || activeTab !== "emails") {
      hideSpintaxVariantRemoveControls();
      return;
    }
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount) {
      hideSpintaxVariantRemoveControls();
      return;
    }
    const range = selection.getRangeAt(0);
    const isWithinEditor =
      editorRef.current.contains(range.startContainer) &&
      editorRef.current.contains(range.endContainer);
    if (!isWithinEditor) {
      hideSpintaxVariantRemoveControls();
      return;
    }
    const context = getActiveSpintaxContext(editorRef.current, range);
    if (!context || !Array.isArray(context.pipes) || context.pipes.length < 1) {
      hideSpintaxVariantRemoveControls();
      return;
    }

    const separators = [context.left, ...context.pipes, context.right];
    const controls = [];
    for (let index = 0; index < separators.length - 1; index += 1) {
      const variantRange = document.createRange();
      variantRange.setStartAfter(separators[index]);
      variantRange.setEndBefore(separators[index + 1]);
      const rect = variantRange.getBoundingClientRect();
      if ((!rect || rect.width === 0) && !context.pipes.length) continue;
      const fallbackLeftRect = separators[index].getBoundingClientRect?.() || null;
      const fallbackRightRect = separators[index + 1].getBoundingClientRect?.() || null;
      const centerX =
        rect && rect.width > 0
          ? rect.left + rect.width / 2
          : fallbackLeftRect && fallbackRightRect
            ? (fallbackLeftRect.right + fallbackRightRect.left) / 2
            : 0;
      const topY =
        rect && rect.height > 0
          ? rect.top
          : fallbackLeftRect
            ? fallbackLeftRect.top
            : 0;
      if (!Number.isFinite(centerX) || !Number.isFinite(topY)) continue;
      controls.push({ index, left: centerX, top: Math.max(6, topY) });
    }

    if (!controls.length) {
      hideSpintaxVariantRemoveControls();
      return;
    }
    activeSpintaxContextRef.current = context;
    setSpintaxVariantRemoveControls(controls);
  };

  const handleRemoveSpintaxVariant = (variantIndex) => {
    const container = editorRef.current;
    const context = activeSpintaxContextRef.current;
    if (!container || !context) return;
    const separators = [context.left, ...(context.pipes || []), context.right];
    const variantCount = separators.length - 1;
    if (variantCount <= 1) return;
    const index = Math.max(0, Math.min(Number(variantIndex) || 0, variantCount - 1));

    const removeRange = document.createRange();
    removeRange.setStartAfter(separators[index]);
    removeRange.setEndBefore(separators[index + 1]);
    removeRange.deleteContents();

    let pipeToRemove = null;
    if (index < variantCount - 1) {
      pipeToRemove = separators[index + 1];
    } else if (index > 0) {
      pipeToRemove = separators[index];
    }
    if (pipeToRemove && String(pipeToRemove.textContent || "").trim() === "|") {
      pipeToRemove.remove();
    }

    const remainingVariantCount = variantCount - 1;
    const shouldUnwrapSpintax = remainingVariantCount <= 1;
    let caretAnchorNode = null;
    let caretAnchorParent = null;
    if (shouldUnwrapSpintax) {
      const unwrapRange = document.createRange();
      unwrapRange.setStartAfter(context.left);
      unwrapRange.setEndBefore(context.right);
      const remainingContent = unwrapRange.extractContents();
      const parentNode = context.left.parentNode;
      if (parentNode) {
        parentNode.insertBefore(remainingContent, context.left);
        caretAnchorNode = context.left.previousSibling;
        caretAnchorParent = parentNode;
      }
      context.left.remove();
      context.right.remove();
      activeSpintaxContextRef.current = null;
      hideSpintaxVariantRemoveControls();
    }

    const selection = window.getSelection?.();
    if (selection) {
      const nextRange = document.createRange();
      if (shouldUnwrapSpintax) {
        if (caretAnchorNode?.nodeType === Node.TEXT_NODE) {
          nextRange.setStart(caretAnchorNode, String(caretAnchorNode.textContent || "").length);
          nextRange.collapse(true);
        } else if (caretAnchorNode instanceof Element) {
          nextRange.selectNodeContents(caretAnchorNode);
          nextRange.collapse(false);
        } else if (caretAnchorParent) {
          nextRange.selectNodeContents(caretAnchorParent);
          nextRange.collapse(false);
        } else {
          nextRange.selectNodeContents(container);
          nextRange.collapse(false);
        }
      } else {
        nextRange.setStartAfter(context.left);
        nextRange.collapse(true);
      }
      selection.removeAllRanges();
      selection.addRange(nextRange);
    }
    captureEditorSelection("body");
    handleRichEditorInput();
    setTimeout(() => {
      updateSpintaxVariantRemoveControls();
      captureEditorSelection("body");
    }, 0);
  };

  const insertSpintaxPipeAtSelection = (target = "body") => {
    if (isCodeView) return false;
    const container = target === "subject" ? subjectEditorRef.current : editorRef.current;
    if (!container) return false;
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount) return false;
    let range = selection.getRangeAt(0);
    const isWithinEditor = container.contains(range.startContainer) && container.contains(range.endContainer);
    if (!isWithinEditor) return false;
    if (!isSelectionInsideSpintax(container, range)) return false;
    range.deleteContents();
    const pipeNode = document.createElement("span");
    pipeNode.className = "sendora-token is-spintax-delimiter";
    pipeNode.setAttribute("contenteditable", "false");
    pipeNode.textContent = "|";
    range.insertNode(pipeNode);
    const nextRange = document.createRange();
    nextRange.setStartAfter(pipeNode);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    captureEditorSelection(target);
    if (target === "subject") {
      handleSubjectEditorInput();
    } else {
      handleRichEditorInput();
    }
    return true;
  };


  const campaignRows = useMemo(() => {
    const query = campaignSearch.trim().toLowerCase();
    if (!query) return campaigns;
    return campaigns.filter((c) => {
      const combined = [
        c.name,
        c.notes || c.description,
        c.salesforceCampaignName,
        c.salesforceCampaignId,
        c.lifecycle?.status,
      ]
        .join(" ")
        .toLowerCase();
      return combined.includes(query);
    });
  }, [campaigns, campaignSearch]);
  const openCampaignMenuCampaign = useMemo(
    () => campaignRows.find((c) => c.campaignId === openCampaignMenuId) || null,
    [campaignRows, openCampaignMenuId]
  );
  const createModalSalesforceCampaigns = useMemo(
    () =>
      (Array.isArray(salesforceCampaigns) ? salesforceCampaigns : [])
        .map((campaign) => ({
          id: String(campaign?.Id || campaign?.id || "").trim(),
          name: String(campaign?.Name || campaign?.name || "").trim(),
        }))
        .filter((campaign) => campaign.id && campaign.name)
        .reverse(),
    [salesforceCampaigns]
  );
  const createModalSalesforceSelectOptions = useMemo(
    () =>
      createModalSalesforceCampaigns.map((campaign) => ({
        value: campaign.id,
        label: `${campaign.name} (${campaign.id})`,
      })),
    [createModalSalesforceCampaigns]
  );
  const createModalSelectedSalesforceOption = useMemo(() => {
    const selectedId = String(createModalForm?.salesforceCampaignId || "").trim();
    if (!selectedId) return null;
    return (
      createModalSalesforceSelectOptions.find((option) => option.value === selectedId) || null
    );
  }, [createModalForm?.salesforceCampaignId, createModalSalesforceSelectOptions]);
  const selectedCampaignSalesforceId = useMemo(
    () => String(selectedCampaign?.salesforceCampaignId || "").trim(),
    [selectedCampaign?.salesforceCampaignId]
  );
  const selectedCampaignForContactUpload = useMemo(() => {
    if (!selectedCampaign) return null;
    if (!selectedCampaignSalesforceId) return selectedCampaign;
    const matchedSalesforceCampaign = (salesforceCampaigns || []).find((campaign) => {
      const campaignId = String(campaign?.Id || campaign?.id || "").trim();
      return campaignId && campaignId === selectedCampaignSalesforceId;
    });
    return matchedSalesforceCampaign || selectedCampaign;
  }, [selectedCampaign, selectedCampaignSalesforceId, salesforceCampaigns]);
  const isCreateCampaignModalWaitingForSalesforce =
    loadingSalesforceCampaigns && !createModalSalesforceCampaigns.length;
  const linkedSalesforceCampaignTitle = useMemo(
    () => String(selectedCampaign?.salesforceCampaignName || "").trim(),
    [selectedCampaign?.salesforceCampaignName]
  );
  const activeSalesforceMembers = useMemo(
    () => (allMembers || []).filter((member) => member && member.isActive !== false),
    [allMembers]
  );
  const allSalesforceFiltersBypassed = useMemo(
    () =>
      SALESFORCE_FILTER_RULE_DEFS.every(
        (rule) => !isSalesforceFilterEnabled(salesforceFilterToggles, rule.key)
      ),
    [salesforceFilterToggles]
  );
  const salesforceFilteredMembers = useMemo(() => {
    if (allSalesforceFiltersBypassed) return members || [];
    return (members || []).filter((member) =>
      SALESFORCE_FILTER_RULE_DEFS.every((rule) => {
        if (!isSalesforceFilterEnabled(salesforceFilterToggles, rule.key)) return true;
        return passesSalesforceFilterRule(member, rule.key, {
          linkedCampaignTitle: linkedSalesforceCampaignTitle,
        });
      })
    );
  }, [
    allSalesforceFiltersBypassed,
    linkedSalesforceCampaignTitle,
    members,
    salesforceFilterToggles,
  ]);
  const salesforceFilteredAllMembers = useMemo(() => {
    if (allSalesforceFiltersBypassed) return activeSalesforceMembers;
    return activeSalesforceMembers.filter((member) =>
      SALESFORCE_FILTER_RULE_DEFS.every((rule) => {
        if (!isSalesforceFilterEnabled(salesforceFilterToggles, rule.key)) return true;
        return passesSalesforceFilterRule(member, rule.key, {
          linkedCampaignTitle: linkedSalesforceCampaignTitle,
        });
      })
    );
  }, [
    activeSalesforceMembers,
    allSalesforceFiltersBypassed,
    linkedSalesforceCampaignTitle,
    salesforceFilterToggles,
  ]);
  // Per-campaign status profile derived from the Salesforce CampaignMemberStatus
  // list. The valid statuses are campaign-specific (not record-type driven), so
  // this lets the UI adapt to both Events team (Unsent/Invited) and BD team
  // (To be sent/Sent) campaigns instead of assuming the Events labels.
  const campaignSalesforceStatusProfile = useMemo(() => {
    const options = campaignMemberStatusOptions || [];
    const findByLower = (target) =>
      options.find((o) => String(o?.value || "").trim().toLowerCase() === target);
    const defaultOption = options.find((o) => !!o?.isDefault);
    const defaultStatus = String(defaultOption?.value || "").trim();
    const configuredPostSend = String(runSettingsForm?.postSendSalesforceStatus || "").trim();
    const sentStatus =
      configuredPostSend ||
      String(findByLower("invited")?.value || "").trim() ||
      String(findByLower("sent")?.value || "").trim();
    return {
      defaultStatus,
      defaultStatusLower: defaultStatus.toLowerCase(),
      sentStatus,
      sentStatusLower: String(sentStatus || "").toLowerCase(),
    };
  }, [campaignMemberStatusOptions, runSettingsForm?.postSendSalesforceStatus]);

  // Statuses that represent "still in our outreach flow" for the campaign (its
  // default "not yet sent" status and the status we apply after sending). Used
  // by the composite filter and totals. Legacy Events labels are kept so older
  // campaigns keep working.
  const flowStatusesLower = useMemo(() => {
    const set = new Set(["unsent", "not sent", "invited"]);
    if (campaignSalesforceStatusProfile.defaultStatusLower) {
      set.add(campaignSalesforceStatusProfile.defaultStatusLower);
    }
    if (campaignSalesforceStatusProfile.sentStatusLower) {
      set.add(campaignSalesforceStatusProfile.sentStatusLower);
    }
    return set;
  }, [campaignSalesforceStatusProfile]);

  const memberStatusFilterOptions = useMemo(() => {
    const profileDefault = campaignSalesforceStatusProfile.defaultStatusLower;
    const profileSent = campaignSalesforceStatusProfile.sentStatusLower;
    // Order the campaign's own flow statuses first, then the common response
    // statuses. Falls back to the Events ordering when no profile is loaded.
    const statusOrder = [
      profileDefault || "unsent",
      profileSent || "invited",
      "hold",
      "declined",
      "attending",
    ].filter(Boolean);
    const statusOrderIndex = new Map(statusOrder.map((status, index) => [status, index]));
    const byStatus = new Map();
    (salesforceFilteredAllMembers || []).forEach((member) => {
      if (member?.isActive === false) return;
      const rawStatus = String(member?.status || "").trim();
      if (!rawStatus) return;
      const normalizedStatus = rawStatus.toLowerCase();
      if (byStatus.has(normalizedStatus)) return;
      byStatus.set(normalizedStatus, {
        value: normalizedStatus,
        label: rawStatus,
      });
    });
    const options = Array.from(byStatus.values()).sort((a, b) => {
      const aValue = a.value === "not sent" ? profileDefault || "unsent" : a.value;
      const bValue = b.value === "not sent" ? profileDefault || "unsent" : b.value;
      const aOrder = statusOrderIndex.has(aValue) ? statusOrderIndex.get(aValue) : Number.POSITIVE_INFINITY;
      const bOrder = statusOrderIndex.has(bValue) ? statusOrderIndex.get(bValue) : Number.POSITIVE_INFINITY;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.label || "").localeCompare(String(b.label || ""));
    });
    // Composite "in our flow" filter: default + sent statuses. Label adapts to
    // the campaign (Unsent/Invited for Events, To be sent/Sent for BD).
    const compositeDefaultLabel = campaignSalesforceStatusProfile.defaultStatus || "Unsent";
    const compositeSentLabel = campaignSalesforceStatusProfile.sentStatus || "Invited";
    const flowOption = {
      value: "unsent_invited",
      label: `${compositeDefaultLabel}/${compositeSentLabel}`,
    };
    const sentIndex = options.findIndex((o) => o.value === (profileSent || "invited"));
    const insertAt = sentIndex >= 0 ? sentIndex + 1 : options.length;
    options.splice(insertAt, 0, flowOption);
    return [{ value: "all", label: "All" }, ...options];
  }, [salesforceFilteredAllMembers, campaignSalesforceStatusProfile]);
  const runSettingsSalesforceStatusOptions = useMemo(() => {
    if (campaignMemberStatusOptions.length) {
      return campaignMemberStatusOptions;
    }
    const byStatus = new Map();
    (salesforceFilteredAllMembers || []).forEach((member) => {
      const rawStatus = String(member?.status || "").trim();
      if (!rawStatus) return;
      const normalizedStatus = rawStatus.toLowerCase();
      if (byStatus.has(normalizedStatus)) return;
      byStatus.set(normalizedStatus, { value: rawStatus, label: rawStatus });
    });
    const options = Array.from(byStatus.values()).sort((a, b) =>
      String(a.label || "").localeCompare(String(b.label || ""))
    );
    if (!options.length) {
      return [{ value: "Sent", label: "Sent" }];
    }
    return options;
  }, [salesforceFilteredAllMembers, campaignMemberStatusOptions]);

  useEffect(() => {
    if (!employee || !selectedCampaign?.salesforceCampaignId) {
      setCampaignMemberStatusOptions([]);
      return;
    }
    let cancelled = false;
    const loadCampaignMemberStatuses = async () => {
      try {
        const api = new ApiService({ employee });
        const response = await api.get(
          `landmark/salesforce/getCampaignMemberStatuses/${selectedCampaign.salesforceCampaignId}`
        );
        if (cancelled) return;
        const options = Array.isArray(response)
          ? response
              .map((status) => {
                const label = String(status?.label || status?.value || "").trim();
                if (!label) return null;
                return {
                  value: label,
                  label,
                  isDefault: !!status?.isDefault,
                  sortOrder: Number(status?.sortOrder || 0),
                };
              })
              .filter(Boolean)
          : [];
        options.sort((a, b) => {
          const aIsDefault = !!a.isDefault;
          const bIsDefault = !!b.isDefault;
          if (aIsDefault !== bIsDefault) return aIsDefault ? -1 : 1;
          const sortOrderDiff = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
          if (sortOrderDiff !== 0) return sortOrderDiff;
          return String(a.label || "").localeCompare(String(b.label || ""));
        });
        setCampaignMemberStatusOptions(options);
      } catch (_error) {
        if (cancelled) return;
        setCampaignMemberStatusOptions([]);
      }
    };
    loadCampaignMemberStatuses();
    return () => {
      cancelled = true;
    };
  }, [employee, selectedCampaign?.salesforceCampaignId]);

  useEffect(() => {
    const optionValues = new Set(memberStatusFilterOptions.map((option) => option.value));
    if (memberStatusFilter && optionValues.has(memberStatusFilter)) return;
    const profileDefault = campaignSalesforceStatusProfile.defaultStatusLower;
    const preferredOption =
      (profileDefault &&
        memberStatusFilterOptions.find((option) => option.value === profileDefault)) ||
      memberStatusFilterOptions.find((option) => option.value === "unsent") ||
      memberStatusFilterOptions.find((option) => option.value !== "all") ||
      memberStatusFilterOptions[0] ||
      { value: "all" };
    setMemberStatusFilter(preferredOption.value);
  }, [memberStatusFilterOptions, memberStatusFilter, campaignSalesforceStatusProfile]);
  useEffect(() => {
    const options = runSettingsSalesforceStatusOptions || [];
    if (!options.length) {
      setBulkSalesforceStatusTarget("");
      return;
    }
    const optionValueSet = new Set(options.map((option) => String(option?.value || "").trim()).filter(Boolean));
    if (optionValueSet.has(String(bulkSalesforceStatusTarget || "").trim())) return;
    const defaultStatus = String(options?.[0]?.value || "").trim();
    setBulkSalesforceStatusTarget(defaultStatus);
  }, [runSettingsSalesforceStatusOptions, bulkSalesforceStatusTarget]);
  useEffect(() => {
    const invitedStatusOption = runSettingsSalesforceStatusOptions.find(
      (option) => String(option.value || "").toLowerCase() === "invited"
    );
    const sentStatusOption = runSettingsSalesforceStatusOptions.find(
      (option) => String(option.value || "").toLowerCase() === "sent"
    );
    const preferredStatusOption = invitedStatusOption || sentStatusOption;
    if (!preferredStatusOption) {
      setRunSettingsForm((prev) => {
        if (!prev.syncSalesforceStatusAfterSend && !prev.postSendSalesforceStatus) {
          return prev;
        }
        return {
          ...prev,
          syncSalesforceStatusAfterSend: false,
          postSendSalesforceStatus: "",
        };
      });
      return;
    }
    const hasSelectedStatusOption = runSettingsSalesforceStatusOptions.some(
      (option) => option.value === runSettingsForm.postSendSalesforceStatus
    );
    if (hasSelectedStatusOption) return;
    setRunSettingsForm((prev) => ({
      ...prev,
      postSendSalesforceStatus: preferredStatusOption.value,
    }));
  }, [runSettingsSalesforceStatusOptions, runSettingsForm.postSendSalesforceStatus]);

  const memberCommentsValueFilterOptions = useMemo(() => {
    const sourceMembers =
      (Array.isArray(salesforceFilteredAllMembers) && salesforceFilteredAllMembers.length
        ? salesforceFilteredAllMembers
        : salesforceFilteredMembers) || [];
    const uniqueCommentsMap = new Map();
    sourceMembers.forEach((member) => {
      const trimmedComment = String(member?.comments || "").trim();
      if (!trimmedComment) return;
      const key = trimmedComment.toLowerCase();
      if (!uniqueCommentsMap.has(key)) {
        uniqueCommentsMap.set(key, trimmedComment);
      }
    });
    const uniqueCommentValues = Array.from(uniqueCommentsMap.values()).sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    return [
      { value: "all", label: "Comments: All" },
      ...uniqueCommentValues.map((commentValue) => ({
        value: commentValue,
        label: commentValue,
      })),
      { value: COMMENTS_FILTER_NO_COMMENT, label: "No Comment" },
    ];
  }, [salesforceFilteredAllMembers, salesforceFilteredMembers]);
  const memberSentStepIdsByKey = useMemo(() => {
    const byMemberKey = new Map();
    (campaignSentEmails || []).forEach((item) => {
      const status = String(item?.status || "")
        .trim()
        .toLowerCase();
      if (!SENT_MONITOR_STATUSES.has(status)) return;
      const stepId = String(item?.stepId || "").trim();
      if (!stepId) return;
      const memberId = String(item?.memberId || "").trim();
      const memberEmail = normalizeEmail(item?.memberEmail || "");
      const memberKeys = [];
      if (memberId) memberKeys.push(`id:${memberId}`);
      if (memberEmail) memberKeys.push(`email:${memberEmail}`);
      memberKeys.forEach((key) => {
        const currentSet = byMemberKey.get(key) || new Set();
        currentSet.add(stepId);
        byMemberKey.set(key, currentSet);
      });
    });
    return byMemberKey;
  }, [campaignSentEmails]);

  const visibleMembers = useMemo(() => {
    const nameNeedle = leadsFiltersEnabled ? memberNameFilter.trim().toLowerCase() : "";
    const emailNeedle = leadsFiltersEnabled ? memberEmailFilter.trim().toLowerCase() : "";
    const companyNeedle = leadsFiltersEnabled ? memberCompanyFilter.trim().toLowerCase() : "";
    const titleNeedle = leadsFiltersEnabled ? memberTitleFilter.trim().toLowerCase() : "";
    const commentsNeedle = leadsFiltersEnabled ? memberCommentsFilter.trim().toLowerCase() : "";
    const commentsValueNeedle = leadsFiltersEnabled
      ? String(memberCommentsValueFilter || "").trim().toLowerCase()
      : "";
    const statusNeedle = leadsFiltersEnabled
      ? String(memberStatusFilter || "").trim().toLowerCase()
      : "";
    const verificationNeedle = leadsFiltersEnabled
      ? String(memberVerificationFilter || "").trim().toLowerCase()
      : "";
    const sendingNeedle = leadsFiltersEnabled
      ? String(memberSendingFilter || "").trim().toLowerCase()
      : "";
    const queueStatusNeedle = leadsFiltersEnabled
      ? String(filterQueueStatus || "").trim().toLowerCase()
      : "";
    const availableStepIds = (sequenceDraft?.steps || [])
      .map((step) => String(step?.stepId || "").trim())
      .filter(Boolean);
    const availableStepIdSet = new Set(availableStepIds);
    const sequenceById = new Map(
      (sequencesDraft || [])
        .map((sequence) => [
          String(sequence?.sequenceId || "").trim(),
          sequence || null,
        ])
        .filter(([sequenceId]) => !!sequenceId)
    );
    const enabledSequencesFilterSet = new Set(
      (enabledSequencesFilterSequenceIds || [])
        .map((sequenceId) => String(sequenceId || "").trim())
        .filter((sequenceId) => sequenceById.has(sequenceId))
    );
    const enabledStepsFilterSet = new Set(
      (enabledStepsFilterStepIds || [])
        .map((stepId) => String(stepId || "").trim())
        .filter((stepId) => availableStepIdSet.has(stepId))
    );
    const sentStepsFilterSet = new Set(
      (sentStepsFilterStepIds || [])
        .map((stepId) => String(stepId || "").trim())
        .filter((stepId) => availableStepIdSet.has(stepId))
    );
    const isEnabledStepsFilterActive =
      leadsFiltersEnabled && enabledStepsFilterEnabled && availableStepIds.length > 0;
    const isEnabledSequencesFilterActive =
      leadsFiltersEnabled && enabledSequencesFilterEnabled && sequenceById.size > 0;
    const isSentStepsFilterActive =
      leadsFiltersEnabled &&
      sentStepsFilterEnabled &&
      (availableStepIds.length > 0 || sentStepsFilterIncludeNothingSent);
    if (
      !nameNeedle &&
      !emailNeedle &&
      !companyNeedle &&
      !titleNeedle &&
      !commentsNeedle &&
      (!commentsValueNeedle || commentsValueNeedle === "all") &&
      !statusNeedle &&
      !verificationNeedle &&
      !sendingNeedle &&
      !queueStatusNeedle &&
      !isEnabledSequencesFilterActive &&
      !isEnabledStepsFilterActive &&
      !isSentStepsFilterActive
    ) {
      return salesforceFilteredMembers;
    }
    return salesforceFilteredMembers.filter((member) => {
      const fullName = String(member?.fullName || "").toLowerCase();
      const email = String(member?.email || "").toLowerCase();
      const company = String(member?.company || "").toLowerCase();
      const title = String(member?.title || "").toLowerCase();
      const comments = String(member?.comments || "").toLowerCase();
      const commentsTrimmed = String(member?.comments || "").trim();
      const commentsTrimmedLower = commentsTrimmed.toLowerCase();
      const statusValue = String(member?.status || "").trim().toLowerCase();
      if (nameNeedle && !fullName.includes(nameNeedle)) return false;
      if (emailNeedle && !email.includes(emailNeedle)) return false;
      if (companyNeedle && !company.includes(companyNeedle)) return false;
      if (titleNeedle && !title.includes(titleNeedle)) return false;
      if (commentsNeedle && !comments.includes(commentsNeedle)) return false;
      if (commentsValueNeedle && commentsValueNeedle !== "all") {
        if (commentsValueNeedle === COMMENTS_FILTER_NO_COMMENT.toLowerCase()) {
          if (commentsTrimmed) return false;
        } else if (commentsTrimmedLower !== commentsValueNeedle) {
          return false;
        }
      }
      if (statusNeedle && statusNeedle !== "all") {
        let statusMatches = statusValue === statusNeedle;
        if (statusNeedle === "unsent_invited") {
          const normalizedForCompare = statusValue === "not sent" ? "unsent" : statusValue;
          statusMatches =
            flowStatusesLower.has(statusValue) || flowStatusesLower.has(normalizedForCompare);
        }
        if (!statusMatches) return false;
      }
      if (queueStatusNeedle && queueStatusNeedle !== "all") {
        const isQueued = isMemberEnabledForSendora(member, availableStepIdSet);
        if (queueStatusNeedle === "queued" && !isQueued) return false;
        if (queueStatusNeedle === "not_queued" && isQueued) return false;
      }
      if (sendingNeedle && sendingNeedle !== "all") {
        const hasBeenSent = Number(member?.lastSendSuccessTime || 0) > 0;
        if (sendingNeedle === "sent" && !hasBeenSent) return false;
        if (sendingNeedle === "not_sent" && hasBeenSent) return false;
      }
      if (verificationNeedle && verificationNeedle !== "all") {
        const verificationCategory = getMemberEmailVerificationCategory(member);
        if (verificationNeedle === "good_acceptable") {
          const isGoodOrAcceptable =
            verificationCategory === "good" || verificationCategory === "acceptable";
          if (!isGoodOrAcceptable) return false;
        } else if (verificationCategory !== verificationNeedle) {
          return false;
        }
      }
      if (isEnabledStepsFilterActive) {
        if (!enabledStepsFilterSet.size) return false;
        const stepApprovals =
          member?.stepApprovals && typeof member.stepApprovals === "object"
            ? member.stepApprovals
            : {};
        const hasEnabledStepMatch = Array.from(enabledStepsFilterSet).every(
          (stepId) => !!stepApprovals[stepId]
        );
        if (!hasEnabledStepMatch) return false;
      }
      if (isEnabledSequencesFilterActive) {
        if (!enabledSequencesFilterSet.size) return false;
        const memberEnabledSequenceIdSet = new Set(
          (Array.isArray(member?.enabledSequenceIds) ? member.enabledSequenceIds : [])
            .map((sequenceId) => String(sequenceId || "").trim())
            .filter(Boolean)
        );
        const hasEnabledSequenceMatch = Array.from(enabledSequencesFilterSet).every((sequenceId) => {
          return memberEnabledSequenceIdSet.has(sequenceId);
        });
        if (!hasEnabledSequenceMatch) return false;
      }
      if (isSentStepsFilterActive) {
        const memberId = String(member?._id || "").trim();
        const memberEmail = normalizeEmail(member?.email || "");
        const sentStepSet =
          memberSentStepIdsByKey.get(`id:${memberId}`) ||
          memberSentStepIdsByKey.get(`email:${memberEmail}`) ||
          null;
        const hasNothingSent = !sentStepSet || sentStepSet.size === 0;
        const hasSentStepMatch =
          sentStepsFilterSet.size > 0 &&
          !!sentStepSet &&
          Array.from(sentStepsFilterSet).every((stepId) => sentStepSet.has(stepId));
        if (!hasSentStepMatch && !(sentStepsFilterIncludeNothingSent && hasNothingSent)) {
          return false;
        }
      }
      return true;
    });
  }, [
    salesforceFilteredMembers,
    memberNameFilter,
    memberEmailFilter,
    memberCompanyFilter,
    memberTitleFilter,
    memberCommentsFilter,
    memberCommentsValueFilter,
    memberStatusFilter,
    flowStatusesLower,
    memberVerificationFilter,
    memberSendingFilter,
    filterQueueStatus,
    enabledSequencesFilterEnabled,
    enabledSequencesFilterSequenceIds,
    enabledStepsFilterEnabled,
    enabledStepsFilterStepIds,
    sentStepsFilterEnabled,
    sentStepsFilterStepIds,
    sentStepsFilterIncludeNothingSent,
    sequencesDraft,
    sequenceDraft?.steps,
    leadsFiltersEnabled,
    memberSentStepIdsByKey,
  ]);

  const displayedMembersForTable = useMemo(() => {
    if (leadsDisplayExpanded || visibleMembers.length <= LEADS_DISPLAY_LIMIT) {
      return visibleMembers;
    }
    return visibleMembers.slice(0, LEADS_DISPLAY_LIMIT);
  }, [visibleMembers, leadsDisplayExpanded]);

  const selectedMemberIds = useMemo(
    () =>
      Object.keys(memberSelection).filter((key) => memberSelection[key] === true),
    [memberSelection]
  );
  const selectedMembersForExport = useMemo(() => {
    if (!selectedMemberIds.length) return [];
    const selectedIdSet = new Set(selectedMemberIds.map((id) => String(id || "").trim()));
    return (salesforceFilteredMembers || []).filter((member) =>
      selectedIdSet.has(String(member?._id || "").trim())
    );
  }, [salesforceFilteredMembers, selectedMemberIds]);

  const selectedCount = selectedMemberIds.length;
  const selectedLeadNoun = selectedCount === 1 ? "lead" : "leads";
  const selectedBulkSalesforceStatusLabel = useMemo(() => {
    const selectedValue = String(bulkSalesforceStatusTarget || "").trim();
    if (!selectedValue) return "";
    const matchedOption = (runSettingsSalesforceStatusOptions || []).find(
      (option) => String(option?.value || "").trim() === selectedValue
    );
    return String(matchedOption?.label || selectedValue).trim();
  }, [bulkSalesforceStatusTarget, runSettingsSalesforceStatusOptions]);
  const hasActiveLeadFilters = useMemo(() => {
    if (!leadsFiltersEnabled) return false;
    return (
      !!memberNameFilter.trim() ||
      !!memberEmailFilter.trim() ||
      !!memberCompanyFilter.trim() ||
      !!memberTitleFilter.trim() ||
      !!memberCommentsFilter.trim() ||
      String(memberCommentsValueFilter || "all").trim().toLowerCase() !== "all" ||
      String(memberStatusFilter || "all").trim().toLowerCase() !== "all" ||
      String(memberVerificationFilter || "all").trim().toLowerCase() !== "all" ||
      String(memberSendingFilter || "all").trim().toLowerCase() !== "all" ||
      String(filterQueueStatus || "all").trim().toLowerCase() !== "all" ||
      (() => {
        if (!enabledSequencesFilterEnabled) return false;
        return (sequencesDraft || []).length > 0;
      })() ||
      (() => {
        if (!enabledStepsFilterEnabled) return false;
        const availableStepIds = (sequenceDraft?.steps || [])
          .map((step) => String(step?.stepId || "").trim())
          .filter(Boolean);
        if (!availableStepIds.length) return false;
        return true;
      })() ||
      (() => {
        if (!sentStepsFilterEnabled) return false;
        if (sentStepsFilterIncludeNothingSent) return true;
        const availableStepIds = (sequenceDraft?.steps || [])
          .map((step) => String(step?.stepId || "").trim())
          .filter(Boolean);
        if (!availableStepIds.length) return false;
        return true;
      })()
    );
  }, [
    leadsFiltersEnabled,
    memberNameFilter,
    memberEmailFilter,
    memberCompanyFilter,
    memberTitleFilter,
    memberCommentsFilter,
    memberCommentsValueFilter,
    memberStatusFilter,
    memberVerificationFilter,
    memberSendingFilter,
    filterQueueStatus,
    enabledSequencesFilterEnabled,
    enabledSequencesFilterSequenceIds,
    sequencesDraft,
    enabledStepsFilterEnabled,
    enabledStepsFilterStepIds,
    sentStepsFilterEnabled,
    sentStepsFilterStepIds,
    sentStepsFilterIncludeNothingSent,
    sequenceDraft?.steps,
  ]);
  const handleClearAllLeadFilters = () => {
    setLeadsFiltersEnabled(true);
    setMemberNameFilter("");
    setMemberEmailFilter("");
    setMemberCompanyFilter("");
    setMemberTitleFilter("");
    setMemberCommentsFilter("");
    setMemberCommentsValueFilter("all");
    setMemberStatusFilter("unsent");
    setMemberVerificationFilter("all");
    setFilterQueueStatus("all");
    setMemberSendingFilter("all");
    setEnabledSequencesFilterEnabled(false);
    setEnabledSequencesFilterSequenceIds([]);
    setEnabledStepsFilterEnabled(false);
    setEnabledStepsFilterStepIds([]);
    setSentStepsFilterEnabled(false);
    setSentStepsFilterStepIds([]);
    setSentStepsFilterIncludeNothingSent(false);
  };
  const handleExportSelectedLeadsAsCsv = () => {
    if (!selectedMembersForExport.length) {
      toast.error("Select at least one lead to export");
      return;
    }
    const headerRow = SENDORA_SELECTED_LEADS_CSV_COLUMNS.map((column) =>
      escapeCsvValue(column.label)
    ).join(",");
    const dataRows = selectedMembersForExport.map((member) =>
      SENDORA_SELECTED_LEADS_CSV_COLUMNS.map((column) =>
        escapeCsvValue(member?.[column.key])
      ).join(",")
    );
    const csvContent = [headerRow, ...dataRows].join("\n");
    const baseCampaignName =
      String(selectedCampaign?.name || "sendora-campaign")
        .trim()
        .replace(/[^a-z0-9-_]+/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "sendora-campaign";
    const fileName = `${baseCampaignName}-selected-leads-${Date.now()}.csv`;
    downloadCsv(fileName, csvContent);
    toast.success(`Exported ${selectedMembersForExport.length} selected ${selectedLeadNoun}`);
  };
  const selectedCampaignStats = selectedCampaign?.stats || {};
  const leadStatusSummary = useMemo(() => {
    const countsByStatus = new Map();
    let unsentCount = 0;
    const profileDefault = campaignSalesforceStatusProfile.defaultStatusLower;
    const profileSent = campaignSalesforceStatusProfile.sentStatusLower;
    // "Not yet sent" statuses: the campaign default (e.g. BD "To be sent") plus
    // the legacy Events labels, so the leads-remaining estimate is accurate.
    const unsentStatusKeys = new Set(["unsent", "not sent"]);
    if (profileDefault) unsentStatusKeys.add(profileDefault);
    const priorityOrder = [
      profileDefault || "unsent",
      profileSent || "invited",
      "hold",
      "declined",
      "attending",
    ].filter(Boolean);
    const statusColorClassByKey = {
      unsent: "is-unsent",
      "not sent": "is-unsent",
      hold: "is-hold",
      invited: "is-invited",
      attending: "is-attending",
    };
    if (profileDefault) statusColorClassByKey[profileDefault] = "is-unsent";
    if (profileSent) statusColorClassByKey[profileSent] = "is-invited";
    (salesforceFilteredAllMembers || []).forEach((member) => {
      const rawStatus = String(member?.status || "").trim();
      const normalizedStatus = rawStatus.toLowerCase();
      const statusKey = normalizedStatus || "__no_status__";
      const statusLabel = rawStatus || "No status";
      const existing = countsByStatus.get(statusKey) || { key: statusKey, label: statusLabel, value: 0 };
      existing.value += 1;
      countsByStatus.set(statusKey, existing);
      if (!normalizedStatus || unsentStatusKeys.has(normalizedStatus)) {
        unsentCount += 1;
      }
    });
    const rows = Array.from(countsByStatus.values())
      .sort((a, b) => {
        const aPriority = priorityOrder.indexOf(String(a.key || ""));
        const bPriority = priorityOrder.indexOf(String(b.key || ""));
        const aHasPriority = aPriority !== -1;
        const bHasPriority = bPriority !== -1;
        if (aHasPriority && bHasPriority) return aPriority - bPriority;
        if (aHasPriority) return -1;
        if (bHasPriority) return 1;
        return String(a.label || "").localeCompare(String(b.label || ""));
      })
      .map((entry) => ({
        ...entry,
        rowClass: statusColorClassByKey[String(entry.key || "")] || "is-default",
      }));
    return { rows, unsentCount };
  }, [salesforceFilteredAllMembers, campaignSalesforceStatusProfile]);
  const leadStatusTotalsRows = leadStatusSummary.rows;
  const totalLeadsBeforeSalesforceFilters = useMemo(
    () => activeSalesforceMembers.length,
    [activeSalesforceMembers]
  );
  const salesforceFilterCatchRows = useMemo(
    () =>
      SALESFORCE_FILTER_RULE_DEFS.map((rule) => {
        const dynamicLabel =
          rule.key === SALESFORCE_FILTER_TOGGLE_KEYS.PENDING_EVENTS
            ? `Pending Events does not contain "${linkedSalesforceCampaignTitle}"`
            : rule.key === SALESFORCE_FILTER_TOGGLE_KEYS.ACTIVE_OPPORTUNITIES
              ? `Active Opportunities does not contain "${linkedSalesforceCampaignTitle}"`
              : rule.label;
        const caughtCount = activeSalesforceMembers.reduce((count, member) => {
          if (
            passesSalesforceFilterRule(member, rule.key, {
              linkedCampaignTitle: linkedSalesforceCampaignTitle,
            })
          )
            return count;
          return count + 1;
        }, 0);
        return {
          key: rule.key,
          label: dynamicLabel,
          enabled: isSalesforceFilterEnabled(salesforceFilterToggles, rule.key),
          caughtCount,
        };
      }),
    [activeSalesforceMembers, linkedSalesforceCampaignTitle, salesforceFilterToggles]
  );
  const totalLeadsAfterSalesforceFilters = useMemo(() => {
    if (allSalesforceFiltersBypassed) return activeSalesforceMembers.length;
    return activeSalesforceMembers.filter((member) =>
      SALESFORCE_FILTER_RULE_DEFS.every((rule) => {
        if (!isSalesforceFilterEnabled(salesforceFilterToggles, rule.key)) return true;
        return passesSalesforceFilterRule(member, rule.key, {
          linkedCampaignTitle: linkedSalesforceCampaignTitle,
        });
      })
    ).length;
  }, [
    activeSalesforceMembers,
    allSalesforceFiltersBypassed,
    linkedSalesforceCampaignTitle,
    salesforceFilterToggles,
  ]);
  const setAllSalesforceFiltersEnabled = (enabled) => {
    setSalesforceFilterToggles((prev) => {
      const next = { ...prev };
      SALESFORCE_FILTER_RULE_DEFS.forEach((rule) => {
        next[rule.key] = !!enabled;
      });
      return next;
    });
  };
  const toggleSalesforceFilter = (filterKey, checked) => {
    setSalesforceFilterToggles((prev) => ({
      ...prev,
      [filterKey]: !!checked,
    }));
  };
  const unsentLeadCount = leadStatusSummary.unsentCount;
  const queuedLeadCount = useMemo(
    () =>
      (salesforceFilteredAllMembers || []).filter(
        (member) =>
          member &&
          isMemberEnabledForSendora(member) &&
          Number(member?.lastSendSuccessTime || 0) <= 0
      ).length,
    [salesforceFilteredAllMembers]
  );
  const enabledLeadCount = useMemo(() => {
    const validStepIds = new Set(
      (sequenceDraft?.steps || [])
        .map((step) => String(step?.stepId || "").trim())
        .filter(Boolean)
    );
    return (salesforceFilteredAllMembers || []).filter((member) => {
      if (!member) return false;
      if (!hasMemberEnabledManualStep(member, validStepIds)) return false;
      const stepApprovals = getMemberStepApprovals(member);
      return Array.from(validStepIds).some((stepId) => !!stepApprovals[stepId]);
    }).length;
  }, [salesforceFilteredAllMembers, sequenceDraft?.steps]);
  const enabledLeadBreakdownByStep = useMemo(() => {
    const stepOptions = (sequenceDraft?.steps || [])
      .map((step, index) => ({
        stepId: String(step?.stepId || "").trim(),
        orderIndex: Number(step?.orderIndex || index + 1),
      }))
      .filter((option) => !!option.stepId)
      .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
    const countsByStepId = new Map(stepOptions.map((option) => [option.stepId, 0]));
    let totalEnabledAcrossSteps = 0;
    (salesforceFilteredAllMembers || []).forEach((member) => {
      if (!member) return;
      if (!hasMemberEnabledManualStep(member)) return;
      const stepApprovals = getMemberStepApprovals(member);
      let hasAnyEnabledStep = false;
      stepOptions.forEach((option) => {
        if (!stepApprovals[option.stepId]) return;
        hasAnyEnabledStep = true;
        countsByStepId.set(
          option.stepId,
          Number(countsByStepId.get(option.stepId) || 0) + 1
        );
      });
      if (hasAnyEnabledStep) {
        totalEnabledAcrossSteps += 1;
      }
    });
    const rows = stepOptions.map((option) => ({
      label: `Step ${Number(option.orderIndex || 0) || 1}`,
      value: Number(countsByStepId.get(option.stepId) || 0),
    }));
    return {
      totalEnabledAcrossSteps,
      rows,
    };
  }, [salesforceFilteredAllMembers, sequenceDraft?.steps]);
  const enabledLeadBreakdownBySequence = useMemo(() => {
    const rows = (sequencesDraft || []).map((sequence, index) => {
      const sequenceId = String(sequence?.sequenceId || "").trim();
      const sequenceOrdinalLabel = getSequenceLabelByIndex(index);
      const letter =
        String(sequenceOrdinalLabel || "Sequence A").replace(/^Sequence\s+/, "") || "A";
      const value = (salesforceFilteredAllMembers || []).reduce((count, member) => {
        if (!member || !sequenceId) return count;
        const enabledSequenceIds = Array.isArray(member?.enabledSequenceIds)
          ? member.enabledSequenceIds
          : [];
        const hasSequenceEnabled = enabledSequenceIds.some(
          (memberSequenceId) => String(memberSequenceId || "").trim() === sequenceId
        );
        return hasSequenceEnabled ? count + 1 : count;
      }, 0);
      return {
        label: `Sequence ${letter}`,
        value,
      };
    });
    return { rows };
  }, [salesforceFilteredAllMembers, sequencesDraft]);
  const sentLeadCount = useMemo(
    () => Math.max(0, toSafeNumber(selectedCampaignStats.sentLeads, 0)),
    [selectedCampaignStats.sentLeads]
  );
  const includedLeadCountFromStats = Math.max(0, toSafeNumber(selectedCampaignStats.includedLeads, 0));
  const queuedLeadCountFromStats = Math.max(0, includedLeadCountFromStats - sentLeadCount);
  const monitorMetrics = campaignMonitor?.metrics || {};
  const monitorStepMetrics =
    campaignMonitor?.stepMetrics && typeof campaignMonitor.stepMetrics === "object"
      ? campaignMonitor.stepMetrics
      : {};
  const monitorStepMetricRows = Object.values(monitorStepMetrics || {});
  const hasMonitorStepMetrics = monitorStepMetricRows.length > 0;
  const monitorStepQueuedCount = monitorStepMetricRows.reduce(
    (sum, metric) => sum + Math.max(0, Number(metric?.queuedCount || 0)),
    0
  );
  const monitorStepSentCount = monitorStepMetricRows.reduce(
    (sum, metric) => sum + Math.max(0, Number(metric?.sentCount || 0)),
    0
  );
  const headerMetricsReady =
    !!selectedCampaignId &&
    !loadingAllMembers &&
    String(allMembersLoadedCampaignId || "") === String(selectedCampaignId || "");
  const isLeadsTabLoading =
    !!selectedCampaignId &&
    (loadingMembers ||
      loadingAllMembers ||
      String(allMembersLoadedCampaignId || "") !== String(selectedCampaignId || ""));
  const lifecycleStatusForQueuedMetric = String(
    campaignMonitor?.lifecycleStatus || selectedCampaign?.lifecycle?.status || ""
  )
    .trim()
    .toLowerCase();
  const runtimeStageForQueuedMetric = String(
    campaignMonitor?.latestRun?.stage || campaignMonitor?.runtime?.stage || ""
  )
    .trim()
    .toLowerCase();
  const isPreSchedulingRunStage = [
    "queued",
    "syncing_members",
    "verifying_emails",
    "applying_queue_policy",
  ].includes(runtimeStageForQueuedMetric);
  const pendingQueueCountMetric = Number.isFinite(Number(monitorMetrics?.pendingQueueCount))
    ? Math.max(0, Number(monitorMetrics.pendingQueueCount))
    : null;
  const queuedLeadCountEffective = Math.max(
    0,
    queuedLeadCount,
    lifecycleStatusForQueuedMetric === "active" ? queuedLeadCountFromStats : 0
  );
  const shouldFallbackToMemberQueuedCount =
    pendingQueueCountMetric === 0 &&
    queuedLeadCountEffective > 0 &&
    lifecycleStatusForQueuedMetric === "active";
  const liveQueuedCount = hasMonitorStepMetrics
    ? monitorStepQueuedCount
    : pendingQueueCountMetric === null
    ? queuedLeadCountEffective
    : isPreSchedulingRunStage
    ? queuedLeadCountEffective
    : shouldFallbackToMemberQueuedCount
    ? queuedLeadCountEffective
    : lifecycleStatusForQueuedMetric && lifecycleStatusForQueuedMetric !== "active"
    ? queuedLeadCountEffective
    : pendingQueueCountMetric;
  const liveSentCount = hasMonitorStepMetrics
    ? monitorStepSentCount
    : Number.isFinite(Number(monitorMetrics?.sentCount))
    ? Number(monitorMetrics.sentCount)
    : sentLeadCount;
  const baseLiveProgressPercent = Number.isFinite(Number(monitorMetrics?.progressPercent))
    ? Math.max(0, Math.min(100, Number(monitorMetrics.progressPercent)))
    : 0;
  const latestRunVerificationPercent = Number.isFinite(
    Number(campaignMonitor?.latestRun?.verification?.percent)
  )
    ? Math.max(0, Math.min(100, Number(campaignMonitor?.latestRun?.verification?.percent)))
    : 0;
  const runtimeVerificationPercent = Number.isFinite(
    Number(campaignMonitor?.runtime?.verification?.percent)
  )
    ? Math.max(0, Math.min(100, Number(campaignMonitor?.runtime?.verification?.percent)))
    : 0;
  const verificationProgressPercent = Math.max(
    latestRunVerificationPercent,
    runtimeVerificationPercent
  );
  const liveProgressPercentBase =
    isPreSchedulingRunStage && verificationProgressPercent > baseLiveProgressPercent
      ? verificationProgressPercent
      : baseLiveProgressPercent;
  const monitorUpcomingQueue = Array.isArray(campaignMonitor?.upcomingQueue)
    ? campaignMonitor.upcomingQueue
    : [];
  const monitorRecentActivity = Array.isArray(campaignMonitor?.recentActivity)
    ? campaignMonitor.recentActivity
    : [];
  const memberStepDeliveryStatusByKey = useMemo(() => {
    const byKey = new Map();
    const statusPriority = {
      approved: 0,
      queued: 1,
      sent: 2,
      bounced: 3,
      complaint: 4,
    };
    const setStatus = (memberIdValue, memberEmailValue, stepIdValue, nextStatus) => {
      const stepId = String(stepIdValue || "").trim();
      if (!stepId) return;
      const memberId = String(memberIdValue || "").trim();
      const memberEmail = normalizeEmail(memberEmailValue || "");
      const keys = [];
      if (memberId) keys.push(`id:${memberId}::${stepId}`);
      if (memberEmail) keys.push(`email:${memberEmail}::${stepId}`);
      if (!keys.length) return;
      keys.forEach((key) => {
        const existing = byKey.get(key);
        const existingPriority = Number(statusPriority[existing] ?? -1);
        const nextPriority = Number(statusPriority[nextStatus] ?? -1);
        if (nextPriority >= existingPriority) {
          byKey.set(key, nextStatus);
        }
      });
    };
    (monitorUpcomingQueue || []).forEach((item) => {
      setStatus(item?.memberId, item?.memberEmail, item?.stepId, "queued");
    });
    (monitorRecentActivity || []).forEach((item) => {
      const status = String(item?.status || "").trim().toLowerCase();
      if (status === "sent" || status === "simulated_sent") {
        setStatus(item?.memberId, item?.memberEmail, item?.stepId, "sent");
      } else if (status === "bounced") {
        setStatus(item?.memberId, item?.memberEmail, item?.stepId, "bounced");
      } else if (status === "complaint") {
        setStatus(item?.memberId, item?.memberEmail, item?.stepId, "complaint");
      }
    });
    return byKey;
  }, [monitorUpcomingQueue, monitorRecentActivity]);
  useEffect(() => {
    if (!selectedMonitorEmailPreview) return;
    if (selectedMonitorEmailPreview.source !== "upcoming") return;
    const previewQueueId = String(selectedMonitorEmailPreview.queueId || "");
    const stillExistsInUpcoming = monitorUpcomingQueue.some(
      (item) => String(item?.queueId || "") === previewQueueId
    );
    if (!stillExistsInUpcoming) {
      monitorPreviewRequestIdRef.current += 1;
      setMonitorPreviewLoadingQueueId("");
      setSelectedMonitorEmailPreview(null);
    }
  }, [selectedMonitorEmailPreview, monitorUpcomingQueue]);
  useEffect(() => {
    if (!selectedMonitorEmailPreview) return undefined;
    const handleMouseDown = (event) => {
      if (monitorPreviewPopupRef.current?.contains(event.target)) return;
      monitorPreviewRequestIdRef.current += 1;
      setMonitorPreviewLoadingQueueId("");
      setSelectedMonitorEmailPreview(null);
    };
    const handleEsc = (event) => {
      if (event.key !== "Escape") return;
      monitorPreviewRequestIdRef.current += 1;
      setMonitorPreviewLoadingQueueId("");
      setSelectedMonitorEmailPreview(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [selectedMonitorEmailPreview]);
  useEffect(() => {
    if (selectedMonitorEmailPreview) return;
    setMonitorPreviewLoadingQueueId("");
  }, [selectedMonitorEmailPreview]);
  const selectedMonitorEmailPreviewIsLoading =
    !!selectedMonitorEmailPreview &&
    String(selectedMonitorEmailPreview?.queueId || "").trim() &&
    String(monitorPreviewLoadingQueueId || "").trim() ===
      String(selectedMonitorEmailPreview?.queueId || "").trim();
  const selectedMonitorEmailPreviewHasHtml = String(selectedMonitorEmailPreview?.html || "").trim().length > 0;
  const selectedMonitorPreviewDocumentHtml = useMemo(
    () => buildEmailPreviewDocument(String(selectedMonitorEmailPreview?.html || "")),
    [selectedMonitorEmailPreview?.html]
  );
  const selectedMonitorPreviewTimestampLabel = useMemo(() => {
    const status = String(selectedMonitorEmailPreview?.status || "")
      .trim()
      .toLowerCase();
    if (selectedMonitorEmailPreview?.source !== "recent") return "Scheduled";
    if (status === "sent" || status === "simulated_sent") return "Sent";
    if (status === "bounced") return "Bounced";
    if (status === "complaint") return "Complaint";
    if (status === "canceled") return "Canceled";
    if (status === "failed") return "Failed";
    return "Updated";
  }, [selectedMonitorEmailPreview?.source, selectedMonitorEmailPreview?.status]);
  const selectedMonitorPreviewTimestampEpoch = useMemo(
    () =>
      Number(
        selectedMonitorEmailPreview?.source === "recent"
          ? selectedMonitorEmailPreview?.sentTime || selectedMonitorEmailPreview?.updatedTime || 0
          : selectedMonitorEmailPreview?.scheduledTime || 0
      ) || 0,
    [
      selectedMonitorEmailPreview?.source,
      selectedMonitorEmailPreview?.sentTime,
      selectedMonitorEmailPreview?.updatedTime,
      selectedMonitorEmailPreview?.scheduledTime,
    ]
  );
  const selectedMonitorPreviewReason = useMemo(() => {
    if (selectedMonitorEmailPreview?.source !== "recent") return "";
    const status = String(selectedMonitorEmailPreview?.status || "")
      .trim()
      .toLowerCase();
    if (!["canceled", "failed", "bounced", "complaint"].includes(status)) return "";
    return String(selectedMonitorEmailPreview?.error || "").trim();
  }, [
    selectedMonitorEmailPreview?.source,
    selectedMonitorEmailPreview?.status,
    selectedMonitorEmailPreview?.error,
  ]);
  const openMonitorEmailPreview = (item, source = "upcoming", clickEvent = null) => {
    if (!item) return;
    const nextQueueId = String(item?.queueId || "");
    if (
      selectedMonitorEmailPreview?.source === source &&
      selectedMonitorEmailPreview?.queueId === nextQueueId
    ) {
      monitorPreviewRequestIdRef.current += 1;
      setMonitorPreviewLoadingQueueId("");
      setSelectedMonitorEmailPreview(null);
      return;
    }
    const clickX = Number(clickEvent?.clientX || 0);
    const clickY = Number(clickEvent?.clientY || 0);
    const fallbackRect =
      clickEvent?.currentTarget && typeof clickEvent.currentTarget.getBoundingClientRect === "function"
        ? clickEvent.currentTarget.getBoundingClientRect()
        : null;
    const fallbackX = fallbackRect
      ? Number(fallbackRect.left || 0) + Number(fallbackRect.width || 0)
      : 0;
    const fallbackY = fallbackRect
      ? Number(fallbackRect.top || 0) + Number(fallbackRect.height || 0) / 2
      : 0;
    const anchorX = clickX || fallbackX;
    const anchorY = clickY || fallbackY;
    const popupWidth = 660;
    const popupHeight = 560;
    const viewportWidth = Math.max(0, Number(window?.innerWidth || 0));
    const viewportHeight = Math.max(0, Number(window?.innerHeight || 0));
    const nextLeft = anchorX
      ? Math.max(10, Math.min(viewportWidth - popupWidth - 10, anchorX + 12))
      : 120;
    const nextTop = anchorY
      ? Math.max(10, Math.min(viewportHeight - popupHeight - 10, anchorY - 16))
      : 120;
    setMonitorPreviewPopupPosition({
      top: nextTop,
      left: nextLeft,
    });
    setSelectedMonitorEmailPreview({
      source,
      queueId: nextQueueId,
      memberEmail: String(item?.memberEmail || ""),
      fromEmail: String(item?.fromEmail || ""),
      fromName: String(item?.fromName || ""),
      stepId: String(item?.stepId || ""),
      stepOrderIndex: Number(item?.stepOrderIndex || 0),
      variantId: String(item?.variantId || ""),
      senderAccountId: String(item?.senderAccountId || ""),
      subject: String(item?.subject || ""),
      html: String(item?.html || ""),
      text: String(item?.text || ""),
      status: String(item?.status || ""),
      error: String(item?.error || ""),
      scheduledTime: Number(item?.scheduledTime || 0),
      sentTime: Number(item?.sentTime || 0),
      updatedTime: Number(item?.updatedTime || 0),
    });
    const hasInlinePreviewContent =
      String(item?.subject || "").trim().length > 0 ||
      String(item?.html || "").trim().length > 0 ||
      String(item?.text || "").trim().length > 0;
    if (hasInlinePreviewContent || !nextQueueId || !employee || !selectedCampaignId) {
      setMonitorPreviewLoadingQueueId("");
      return;
    }
    const requestId = monitorPreviewRequestIdRef.current + 1;
    monitorPreviewRequestIdRef.current = requestId;
    setMonitorPreviewLoadingQueueId(nextQueueId);
    const api = new ApiService({ employee });
    api
      .get(`landmark/sendora/getCampaignMonitorEmailPreview/${selectedCampaignId}/${nextQueueId}`)
      .then((response) => {
        if (monitorPreviewRequestIdRef.current !== requestId) return;
        if (response?.error) return;
        const preview = response?.preview && typeof response.preview === "object" ? response.preview : {};
        setSelectedMonitorEmailPreview((prev) => {
          if (!prev || String(prev?.queueId || "") !== nextQueueId) return prev;
          return {
            ...prev,
            subject: String(preview?.subject || prev?.subject || ""),
            html: String(preview?.html || prev?.html || ""),
            text: String(preview?.text || prev?.text || ""),
            fromEmail: String(preview?.fromEmail || prev?.fromEmail || ""),
            fromName: String(preview?.fromName || prev?.fromName || ""),
            memberEmail: String(preview?.memberEmail || prev?.memberEmail || ""),
            status: String(preview?.status || prev?.status || ""),
            error: String(preview?.error || prev?.error || ""),
            scheduledTime: Number(preview?.scheduledTime || prev?.scheduledTime || 0),
            sentTime: Number(preview?.sentTime || prev?.sentTime || 0),
            updatedTime: Number(preview?.updatedTime || prev?.updatedTime || 0),
          };
        });
      })
      .catch((error) => {
        if (monitorPreviewRequestIdRef.current !== requestId) return;
        console.error(error);
      })
      .finally(() => {
        if (monitorPreviewRequestIdRef.current !== requestId) return;
        setMonitorPreviewLoadingQueueId((current) =>
          String(current || "") === nextQueueId ? "" : current
        );
      });
  };
  const isQueueItemPausedBySchedule = (item = {}) =>
    String(item?.error || "")
      .trim()
      .toLowerCase()
      .includes("schedule");
  const isQueueItemPausedByStepOrSequence = (item = {}) =>
    String(item?.error || "")
      .trim()
      .toLowerCase()
      .includes("paused");
  const getMonitorSourcePillClassName = (item = {}) => {
    const queueMode = String(item?.queueMode || "").trim().toLowerCase();
    const sourceLabel = String(item?.sourceLabel || "").trim().toLowerCase();
    if (queueMode === "sequence" || sourceLabel.includes("sequence")) {
      return "sendora-live-monitor-source-pill is-sequence";
    }
    return "sendora-live-monitor-source-pill is-manual";
  };
  const monitorRunLogs = useMemo(() => {
    const runtimeLogs = Array.isArray(campaignMonitor?.runtime?.recentLogs)
      ? campaignMonitor.runtime.recentLogs
      : [];
    const runLogs = Array.isArray(campaignMonitor?.latestRun?.logs)
      ? campaignMonitor.latestRun.logs
      : [];
    const merged = [...runtimeLogs, ...runLogs]
      .filter((entry) => entry && (entry.message || entry.msg))
      .map((entry, index) => ({
        key: `${Number(entry?.time || 0)}-${index}-${String(entry?.message || entry?.msg || "")}`,
        time: Number(entry?.time || 0),
        level: String(entry?.level || "info"),
        message: String(entry?.message || entry?.msg || ""),
        meta: entry?.meta && typeof entry.meta === "object" ? entry.meta : {},
      }))
      .sort((a, b) => a.time - b.time);
    const compacted = [];
    let latestPollLogIndex = -1;
    merged.forEach((entry) => {
      const normalizedMessage = String(entry?.message || "").toLowerCase();
      const isPollLog = isMillionVerifierPollLogMessage(normalizedMessage);
      if (isPollLog) {
        if (latestPollLogIndex >= 0) {
          compacted[latestPollLogIndex] = entry;
          return;
        }
        latestPollLogIndex = compacted.length;
      }
      compacted.push(entry);
    });
    return compacted.slice(-120);
  }, [campaignMonitor]);
  const summarizeRunLogMeta = (meta = {}) => {
    if (!meta || typeof meta !== "object") return "";
    const chunks = [];
    const toSafeInt = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? Math.max(0, Math.round(n)) : null;
    };
    const uniqueQueued = toSafeInt(meta.uniqueQueuedEmails);
    const submitted = toSafeInt(meta.submittedToMillionVerifier);
    const reused = toSafeInt(meta.reusedFromCache);
    const blocked = toSafeInt(meta.blockedCount);
    const removed = toSafeInt(meta.removedFromQueueCount);
    const queuedCount = toSafeInt(meta.queuedCount);
    const eligibleStates = toSafeInt(meta.eligibleStates);
    const dueStateCount = toSafeInt(meta.dueStateCount);
    const dueMemberCount = toSafeInt(meta.dueMemberCount);
    if (uniqueQueued !== null) chunks.push(`Leads: ${uniqueQueued}`);
    if (submitted !== null || reused !== null) {
      const submittedPart = submitted !== null ? submitted : 0;
      const reusedPart = reused !== null ? reused : 0;
      chunks.push(`Verification input: ${submittedPart} submitted, ${reusedPart} cached`);
    }
    if (blocked !== null || removed !== null) {
      chunks.push(
        `Removed: ${blocked !== null ? blocked : 0} blocked, ${
          removed !== null ? removed : 0
        } dequeued`
      );
    }
    if (dueStateCount !== null || dueMemberCount !== null) {
      chunks.push(
        `Due members: ${dueStateCount !== null ? dueStateCount : 0} states, ${
          dueMemberCount !== null ? dueMemberCount : 0
        } members`
      );
    }
    if (queuedCount !== null || eligibleStates !== null) {
      chunks.push(
        `Queueing: ${queuedCount !== null ? queuedCount : 0} queued, ${
          eligibleStates !== null ? eligibleStates : 0
        } eligible states`
      );
    }
    if (meta?.skipped === true) {
      chunks.push(`Skipped${meta?.skipReason ? ` (${String(meta.skipReason)})` : ""}`);
    }
    const breakdown = meta.breakdown && typeof meta.breakdown === "object" ? meta.breakdown : null;
    if (breakdown) {
      const breakdownParts = Object.entries(breakdown)
        .map(([key, value]) => [String(key || ""), Number(value)])
        .filter(([key, value]) => key && Number.isFinite(value) && value > 0)
        .map(([key, value]) => `${key}:${value}`);
      if (breakdownParts.length) {
        chunks.push(`Breakdown: ${breakdownParts.join(", ")}`);
      }
    }
    return chunks.join(" | ");
  };
  const basicRunFeedState = useMemo(() => {
    const latestRun = campaignMonitor?.latestRun || {};
    const runtime = campaignMonitor?.runtime || {};
    const runStage = String(runtime?.stage || latestRun?.stage || "").trim().toLowerCase();
    const runStatus = String(runtime?.status || latestRun?.status || "").trim().toLowerCase();
    const runtimeVerificationInProgress =
      runtime?.verification &&
      ["pending", "uploaded", "in_progress"].includes(
        String(runtime?.verification?.status || "").trim().toLowerCase()
      );
    const runtimeVerification = runtime?.verification || {};
    const latestRunVerification = latestRun?.verification || {};
    const verification =
      runtimeVerificationInProgress || !latestRunVerification?.status
        ? runtimeVerification || latestRunVerification || {}
        : latestRunVerification || runtimeVerification || {};
    const verificationStatus = String(verification?.status || "").trim().toLowerCase();
    const runtimeVerificationPercent = Number.isFinite(Number(runtimeVerification?.percent))
      ? Math.max(0, Math.min(100, Number(runtimeVerification.percent)))
      : 0;
    const latestRunVerificationPercent = Number.isFinite(Number(latestRunVerification?.percent))
      ? Math.max(0, Math.min(100, Number(latestRunVerification.percent)))
      : 0;
    const verificationPercentFromState = Math.max(
      runtimeVerificationPercent,
      latestRunVerificationPercent
    );
    const latestVerificationPollPercent = [...monitorRunLogs]
      .reverse()
      .find((entry) => isMillionVerifierPollLogMessage(entry?.message || ""));
    const verificationPercentFromPollLog = Number.isFinite(
      Number(latestVerificationPollPercent?.meta?.percent)
    )
      ? Math.max(0, Math.min(100, Number(latestVerificationPollPercent?.meta?.percent)))
      : 0;
    const verificationPercent = Math.max(
      verificationPercentFromState,
      verificationPercentFromPollLog
    );
    const normalizeMessage = (value = "") => String(value || "").trim().toLowerCase();
    const workflowRelevantLog = (entry = {}) => {
      const message = normalizeMessage(entry?.message || "");
      const trigger = String(entry?.meta?.trigger || "").trim().toLowerCase();
      if (trigger === "sequence_start" || trigger === "manual_step_start") return true;
      return (
        message.includes("sequence start") ||
        message.includes("step start requested") ||
        message.includes("queued step start request while another step start is in progress") ||
        message.includes("running salesforce status checks for sequence leads") ||
        message.includes("running salesforce status checks for step leads") ||
        message.includes("salesforce status checks in progress") ||
        message.includes("salesforce status checks completed for sequence leads") ||
        message.includes("salesforce status checks completed for step leads") ||
        message.includes("skipped salesforce sync before sequence queueing") ||
        message.includes("skipped salesforce sync before step queueing") ||
        message.includes("collected due sequence members for verification") ||
        message.includes("collected enabled manual step leads for verification") ||
        message.includes("running email verification for sequence leads") ||
        message.includes("running email verification for enabled manual step leads") ||
        message.includes("verifyqueuedleademailsforcampaign() - candidates") ||
        message.includes("verifyqueuedleademailsforcampaign() - cache plan") ||
        message.includes("email verification complete for sequence leads") ||
        message.includes("email verification completed for enabled manual step leads") ||
        message.includes("queueing sequence emails for dispatch") ||
        message.includes("queueing step emails for dispatch") ||
        message.includes("sequence queueing") ||
        message.includes("queue rebuilt")
      );
    };
    const workflowLogs = monitorRunLogs.filter((entry) => workflowRelevantLog(entry));
    const latestRequestLog = [...workflowLogs]
      .reverse()
      .find((entry) => {
        const message = normalizeMessage(entry?.message || "");
        return message.includes("sequence start requested") || message.includes("step start requested");
      });
    const latestTriggerType =
      String(latestRequestLog?.meta?.trigger || "").trim().toLowerCase() === "manual_step_start"
        ? "step"
        : "sequence";
    const subjectNoun = latestTriggerType === "step" ? "step" : "sequence";
    const feedStartTime = Number(latestRequestLog?.time || 0);
    const feedLogs =
      feedStartTime > 0
        ? workflowLogs.filter((entry) => Number(entry?.time || 0) >= feedStartTime)
        : workflowLogs;
    const findLastLog = (matcher = () => false) =>
      [...feedLogs].reverse().find((entry) => matcher(normalizeMessage(entry?.message || ""), entry));
    const hasActiveWorkflowStage = ["syncing_members", "verifying_emails", "scheduling"].includes(runStage);
    const hasActiveSequenceStart = feedLogs.length > 0 || hasActiveWorkflowStage;
    const formatCount = (value) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric < 0) return null;
      return Math.round(numeric);
    };
    const safePlural = (count, singular, plural = `${singular}s`) =>
      `${count} ${count === 1 ? singular : plural}`;

    const salesforceStartLog = findLastLog(
      (message) =>
        message.includes("running salesforce status checks for sequence leads") ||
        message.includes("running salesforce status checks for step leads")
    );
    const salesforceDoneLog = findLastLog(
      (message) =>
        message.includes("salesforce status checks completed for sequence leads") ||
        message.includes("salesforce status checks completed for step leads") ||
        message.includes("skipped salesforce sync before sequence queueing") ||
        message.includes("skipped salesforce sync before step queueing")
    );
    const salesforceProgressLog = findLastLog((message) =>
      message.includes("salesforce status checks in progress")
    );
    const salesforceDoneByStage =
      !!salesforceStartLog &&
      ["scheduling", "idle", "completed", "paused", "stopped", "failed", "canceled"].includes(runStage);
    const salesforceDone = !!salesforceDoneLog || salesforceDoneByStage;
    const salesforceLoading =
      !salesforceDone &&
      (runStage === "syncing_members" ||
        (!!salesforceStartLog &&
          !["scheduling", "idle", "completed", "paused", "stopped", "failed", "canceled"].includes(
            runStage
          )));
    const salesforceReason = String(salesforceDoneLog?.meta?.reason || "").trim();
    const salesforceStartTotalMembers = formatCount(salesforceStartLog?.meta?.salesforceTotalMembers);
    const salesforceProgressCheckedCount = formatCount(salesforceProgressLog?.meta?.salesforceCheckedCount);
    const salesforceProgressTotalMembers = formatCount(salesforceProgressLog?.meta?.salesforceTotalMembers);
    const salesforceDoneCheckedCount = formatCount(salesforceDoneLog?.meta?.salesforceCheckedCount);
    const salesforceDoneTotalMembers = formatCount(salesforceDoneLog?.meta?.salesforceTotalMembers);
    const loadingSalesforceChecked = salesforceProgressCheckedCount !== null ? salesforceProgressCheckedCount : 0;
    const loadingSalesforceTotal =
      salesforceProgressTotalMembers !== null
        ? salesforceProgressTotalMembers
        : salesforceStartTotalMembers !== null
        ? salesforceStartTotalMembers
        : null;
    const salesforceProgressDetail = salesforceDone
      ? salesforceDoneTotalMembers !== null
        ? `Checking all campaign members (${
            salesforceDoneCheckedCount !== null
              ? salesforceDoneCheckedCount
              : salesforceDoneTotalMembers
          } / ${salesforceDoneTotalMembers})`
        : ""
      : salesforceLoading && loadingSalesforceTotal !== null
      ? `Checking all campaign members (${loadingSalesforceChecked} / ${loadingSalesforceTotal})`
      : "";

    const dueScanLog = findLastLog(
      (message) =>
        message.includes("collected due sequence members for verification") ||
        message.includes("collected enabled manual step leads for verification")
    );
    const dueScanStarted = !!dueScanLog || !!findLastLog((message) => message.includes("running email verification"));
    const dueScanDone = !!dueScanLog;
    const dueStateCount = formatCount(dueScanLog?.meta?.dueStateCount);
    const dueMemberCount = formatCount(dueScanLog?.meta?.dueMemberCount);

    const verificationStartLog = findLastLog(
      (message) =>
        message.includes("running email verification for sequence leads") ||
        message.includes("running email verification for enabled manual step leads")
    );
    const verificationCandidatesLog = findLastLog((message) =>
      message.includes("verifyqueuedleademailsforcampaign() - candidates")
    );
    const verificationCachePlanLog = findLastLog((message) =>
      message.includes("verifyqueuedleademailsforcampaign() - cache plan")
    );
    const verificationDoneLog = findLastLog(
      (message) =>
        message.includes("email verification complete for sequence leads") ||
        message.includes("email verification completed for enabled manual step leads")
    );
    const verificationStatusIsCached = verificationStatus === "cached";
    const verificationTotalCheckedCount = formatCount(
      verificationCachePlanLog?.meta?.uniqueQueuedEmails ??
        verificationCandidatesLog?.meta?.uniqueQueuedEmails ??
        runtime?.verificationSummary?.uniqueQueuedEmails
    );
    const verificationAlreadyVerifiedCount = formatCount(
      verificationCachePlanLog?.meta?.reusedFromCache ??
        verificationDoneLog?.meta?.reusedFromCache ??
        runtime?.verificationSummary?.reusedFromCache
    );
    const verificationSubmittedCount = formatCount(
      verificationCachePlanLog?.meta?.submittedToMillionVerifier ??
        verificationDoneLog?.meta?.submittedToMillionVerifier ??
        runtime?.verificationSummary?.submittedToMillionVerifier
    );
    const verificationPercentFromPlan =
      verificationSubmittedCount !== null && verificationSubmittedCount === 0 ? 100 : null;
    const effectiveVerificationPercent = Math.max(
      0,
      Math.min(
        100,
        Math.max(verificationPercent, verificationPercentFromPlan !== null ? verificationPercentFromPlan : 0)
      )
    );
    const verificationDoneByStage =
      !!verificationStartLog &&
      ["syncing_members", "scheduling", "idle", "completed", "paused", "stopped"].includes(runStage);
    const verificationDone =
      !!verificationDoneLog ||
      verificationStatus === "finished" ||
      verificationStatusIsCached ||
      verificationDoneByStage;
    const verificationLoading =
      !verificationDone &&
      (!!verificationStartLog || runStage === "verifying_emails" || runtimeVerificationInProgress);
    const effectiveDueScanDone = dueScanDone || !!verificationStartLog || verificationDone;
    const verificationBlockedCount = formatCount(
      verificationDoneLog?.meta?.blockedCount ?? runtime?.verificationSummary?.blockedCount
    );
    const queueStartLog = findLastLog(
      (message) =>
        message.includes("queueing sequence emails for dispatch") ||
        message.includes("queueing step emails for dispatch")
    );
    const queueResultLog = findLastLog(
      (message) => message.includes("sequence queueing") || message.includes("queue rebuilt")
    );
    const queueingDone = !!queueResultLog;
    const queueingLoading = !queueingDone && (!!queueStartLog || runStage === "scheduling");
    const queuedCount = formatCount(queueResultLog?.meta?.queuedCount);
    const queueEligibleMemberCount = formatCount(queueResultLog?.meta?.eligibleMemberCount);
    const queueAlreadySentCount = formatCount(queueResultLog?.meta?.alreadySentCount);
    const queueBlockedMemberCount = formatCount(queueResultLog?.meta?.blockedMemberCount);
    const queueSkippedNoEmailCount = formatCount(queueResultLog?.meta?.skippedNoEmailCount);
    const queueSkippedOutsideDateWindowCount = formatCount(
      queueResultLog?.meta?.skippedOutsideDateWindowCount
    );
    const queueSkippedMissingOttSourceCount = formatCount(
      queueResultLog?.meta?.skippedMissingOttSourceCount
    );
    const queueSkippedNotApprovedCount = formatCount(queueResultLog?.meta?.skippedNotApprovedCount);
    const queueSkipSummaryParts = [
      queueAlreadySentCount > 0
        ? `${safePlural(queueAlreadySentCount, "lead")} skipped due to step already sent`
        : "",
      queueBlockedMemberCount > 0 ? `${safePlural(queueBlockedMemberCount, "lead")} blocked` : "",
      queueSkippedNoEmailCount > 0
        ? `${safePlural(queueSkippedNoEmailCount, "lead")} skipped (missing email)`
        : "",
      queueSkippedOutsideDateWindowCount > 0
        ? `${safePlural(queueSkippedOutsideDateWindowCount, "lead")} skipped (outside date window)`
        : "",
      queueSkippedMissingOttSourceCount > 0
        ? `${safePlural(queueSkippedMissingOttSourceCount, "lead")} skipped (missing OTT source)`
        : "",
      queueSkippedNotApprovedCount > 0
        ? `${safePlural(queueSkippedNotApprovedCount, "lead")} skipped (not approved for step)`
        : "",
    ].filter(Boolean);
    const queueZeroReasonSummary = queueSkipSummaryParts.join(" | ");
    const queueSkipped = !!queueResultLog?.meta?.skipped;
    const queueSkipReason = String(queueResultLog?.meta?.skipReason || "").trim();
    const queueWarning = queueingDone && (queueSkipped || Number(queuedCount || 0) === 0);

    const lines = [];
    lines.push({
      key: "request",
      text: `${subjectNoun === "step" ? "Step" : "Sequence"} start request accepted`,
      status: "done",
      detail:
        subjectNoun === "step"
          ? Number(latestRequestLog?.meta?.stepOrderIndex || 0) > 0
            ? `Step ${Number(latestRequestLog.meta.stepOrderIndex)}`
            : ""
          : latestRequestLog?.meta?.sequenceName
          ? `Sequence: ${String(latestRequestLog.meta.sequenceName)}`
          : "",
    });
    lines.push({
      key: "salesforce",
      text: `Salesforce status checks for ${subjectNoun} leads`,
      status: salesforceDone ? "done" : salesforceLoading ? "loading" : "pending",
      detail:
        salesforceDone &&
        ["minimum_interval_not_reached", "min_interval_not_elapsed"].includes(salesforceReason)
          ? `Skipped (minimum sync interval not reached)${
              salesforceProgressDetail ? ` | ${salesforceProgressDetail}` : ""
            }`
          : salesforceProgressDetail,
    });
    lines.push({
      key: "due-state-scan",
      text:
        subjectNoun === "step"
          ? "Collecting enabled step members"
          : "Collecting due sequence members",
      status: effectiveDueScanDone ? "done" : dueScanStarted ? "loading" : "pending",
      detail:
        effectiveDueScanDone && dueMemberCount !== null
          ? subjectNoun === "step"
            ? `${safePlural(dueMemberCount, "member")}`
            : dueStateCount !== null
            ? `${safePlural(dueStateCount, "due state")} across ${safePlural(
                dueMemberCount,
                "member"
              )}`
            : `${safePlural(dueMemberCount, "member")}`
          : "",
    });
    lines.push({
      key: "verification",
      text: `Email verification for ${subjectNoun === "step" ? "enabled step" : "due sequence"} leads`,
      status: verificationDone ? "done" : verificationLoading ? "loading" : "pending",
      detail: verificationDone
        ? [
            verificationTotalCheckedCount !== null
              ? `${safePlural(verificationTotalCheckedCount, "lead")} checked`
              : "",
            verificationAlreadyVerifiedCount !== null
              ? `${safePlural(verificationAlreadyVerifiedCount, "lead")} already verified`
              : "",
            verificationSubmittedCount !== null
              ? `${safePlural(verificationSubmittedCount, "lead")} sent to MillionVerifier`
              : "",
            verificationBlockedCount !== null
              ? `${safePlural(verificationBlockedCount, "blocked email")} removed`
              : "",
          ]
            .filter(Boolean)
            .join(" | ")
        : verificationLoading
        ? [
            verificationTotalCheckedCount !== null
              ? `${safePlural(verificationTotalCheckedCount, "lead")} to check`
              : "",
            verificationAlreadyVerifiedCount !== null
              ? `${safePlural(verificationAlreadyVerifiedCount, "lead")} already verified`
              : "",
            verificationSubmittedCount !== null
              ? `${safePlural(verificationSubmittedCount, "lead")} via MillionVerifier`
              : "",
            `${effectiveVerificationPercent}%`,
          ]
            .filter(Boolean)
            .join(" | ")
        : "",
    });
    lines.push({
      key: "queueing",
      text: `Queueing ${subjectNoun} emails for dispatch`,
      status: queueWarning ? "warning" : queueingDone ? "done" : queueingLoading ? "loading" : "pending",
      detail: queueingDone
        ? [
            queueEligibleMemberCount !== null
              ? `${safePlural(queueEligibleMemberCount, "valid lead")}`
              : "",
            ...(Number(queuedCount || 0) > 0 ? queueSkipSummaryParts : []),
            queuedCount !== null ? `${safePlural(queuedCount, "email")} queued` : "",
            Number(queuedCount || 0) === 0 && queueZeroReasonSummary
              ? `No emails queued: ${queueZeroReasonSummary}`
              : "",
            queueSkipReason ? `Reason: ${queueSkipReason}` : "",
          ]
            .filter(Boolean)
            .join(" | ")
        : "",
    });
    return {
      hasActiveSequenceStart,
      lines: lines.filter(Boolean),
      verificationLoading,
      verificationDone,
      verificationPercent: effectiveVerificationPercent,
    };
  }, [campaignMonitor, monitorRunLogs]);
  const liveProgressPercent =
    basicRunFeedState.verificationLoading && !basicRunFeedState.verificationDone
      ? Math.max(
          0,
          Math.min(
            100,
            Math.max(
              Number(liveProgressPercentBase || 0),
              Number(basicRunFeedState?.verificationPercent || 0)
            )
          )
        )
      : liveProgressPercentBase;
  const isLiveMonitorProgressComplete =
    liveProgressPercent >= 100 ||
    String(campaignMonitor?.lifecycleStatus || "")
      .trim()
      .toLowerCase() === "completed";
  const getSalesforceStatusCellClassName = (statusValue = "") => {
    const normalized = String(statusValue || "").trim().toLowerCase();
    if (normalized === "unsent" || normalized === "not sent") {
      return "is-unsent";
    }
    // Campaign default ("not yet sent") status, e.g. BD "To be sent".
    if (
      campaignSalesforceStatusProfile.defaultStatusLower &&
      normalized === campaignSalesforceStatusProfile.defaultStatusLower
    ) {
      return "is-unsent";
    }
    if (normalized === "hold") {
      return "is-hold";
    }
    if (normalized === "invited") {
      return "is-invited";
    }
    // Status Sendora applies after sending, e.g. BD "Sent".
    if (
      campaignSalesforceStatusProfile.sentStatusLower &&
      normalized === campaignSalesforceStatusProfile.sentStatusLower
    ) {
      return "is-invited";
    }
    if (normalized === "attending") {
      return "is-attending";
    }
    return "is-default";
  };
  const getMemberSalesforceContactOrLeadUrl = (member = {}) => {
    const salesforceContactId = String(member?.salesforceContactId || "").trim();
    if (salesforceContactId) {
      return `https://landmarkventures.lightning.force.com/lightning/r/Contact/${salesforceContactId}/view`;
    }
    const salesforceLeadId = String(member?.salesforceLeadId || "").trim();
    if (salesforceLeadId) {
      return `https://landmarkventures.lightning.force.com/lightning/r/Lead/${salesforceLeadId}/view`;
    }
    return "";
  };
  const getMemberSalesforceCampaignMemberUrl = (member = {}) => {
    const salesforceMemberId = String(member?.salesforceMemberId || "").trim();
    if (!salesforceMemberId) return "";
    return `https://landmarkventures.lightning.force.com/lightning/r/CampaignMember/${salesforceMemberId}/view`;
  };
  const getMemberEmailVerificationIndicator = (member = {}) => {
    if (!member?.emailVerification) {
      return null;
    }
    const decision = String(member?.emailVerification?.decision || "")
      .trim()
      .toLowerCase();
    if (decision === "ok_good") {
      return {
        iconClass: "fa fa-check",
        className: "is-ok",
        title: "Email verification: OK | good",
      };
    }
    if (decision === "catch_all_risky") {
      return {
        iconClass: "fa fa-check",
        className: "is-catch-all",
        title: "Email verification: catch_all | risky",
      };
    }
    if (member?.emailVerification) {
      const result = String(member?.emailVerification?.result || "unknown");
      const quality = String(member?.emailVerification?.quality || "unknown");
      return {
        iconClass: "fa fa-times",
        className: "is-blocked",
        title: `Email verification: blocked (${result} | ${quality})`,
      };
    }
    return null;
  };
  const campaignLifecycle = selectedCampaign?.lifecycle || {};
  const selectedCampaignStatus = campaignLifecycle.status || "draft";
  const effectiveCampaignStatus = useMemo(() => {
    const monitorStatus = String(campaignMonitor?.lifecycleStatus || "")
      .trim()
      .toLowerCase();
    if (monitorStatus) return monitorStatus;
    return String(selectedCampaignStatus || "draft").trim().toLowerCase() || "draft";
  }, [campaignMonitor?.lifecycleStatus, selectedCampaignStatus]);
  const campaignHasEverRun = useMemo(
    () =>
      Number(selectedCampaign?.lifecycle?.startedTime || 0) > 0 ||
      !!String(selectedCampaign?.lifecycle?.memberSnapshotSyncId || "").trim(),
    [selectedCampaign]
  );
  const showQueuePopulateHint =
    !monitorUpcomingQueue.length &&
    queuedLeadCount > 0 &&
    (effectiveCampaignStatus === "paused" || !campaignHasEverRun);
  const queuePopulateActionText = campaignHasEverRun ? "resume" : "start";

  const selectedStep = useMemo(
    () => sequenceDraft.steps?.[selectedStepIndex] || null,
    [sequenceDraft.steps, selectedStepIndex]
  );
  const isSelectedStepOttMode = useMemo(() => {
    if (!selectedStep) return false;
    if (Number(selectedStepIndex) === 0) return false;
    return String(selectedStep?.mode || "").trim().toLowerCase() === "ott";
  }, [selectedStep, selectedStepIndex]);
  const sequenceStepsForUi = useMemo(() => {
    const allSteps = sequenceDraft.steps || [];
    if (ENABLE_MULTI_STEP_SEQUENCES) return allSteps;
    return allSteps.slice(0, 1);
  }, [sequenceDraft.steps]);
  const emailStepsById = useMemo(
    () =>
      new Map(
        (sequenceStepsForUi || []).map((step, index) => [
          String(step?.stepId || ""),
          { ...step, orderIndex: Number(step?.orderIndex || index + 1) || index + 1 },
        ])
      ),
    [sequenceStepsForUi]
  );
  const activeSequence = useMemo(
    () => (sequencesDraft || []).find((entry) => entry.sequenceId === activeSequenceId) || null,
    [sequencesDraft, activeSequenceId]
  );
  const activeSequenceUnusedEmailSteps = useMemo(() => {
    const used = new Set((activeSequence?.nodes || []).map((node) => String(node?.stepId || "")));
    return (sequenceStepsForUi || []).filter((step) => !used.has(String(step?.stepId || "")));
  }, [activeSequence, sequenceStepsForUi]);
  const sequenceChainValidation = useMemo(() => {
    if (!activeSequence) {
      return { isValid: false, reason: "Add a sequence to configure wait times between steps." };
    }
    const nodes = activeSequence?.nodes || [];
    if (!nodes.length) {
      return { isValid: false, reason: "Add at least one step to this sequence." };
    }
    const seen = new Set();
    for (let index = 0; index < nodes.length; index += 1) {
      const stepId = String(nodes[index]?.stepId || "");
      const step = emailStepsById.get(stepId);
      if (!step) {
        return { isValid: false, reason: "This sequence includes a step that no longer exists." };
      }
      if (seen.has(stepId)) {
        return { isValid: false, reason: "A step can only appear once in a sequence chain." };
      }
      seen.add(stepId);
      const isOtt = String(step?.mode || "").trim().toLowerCase() === "ott";
      if (isOtt) {
        const sourceStepId = String(step?.ottSourceStepId || "").trim();
        if (!sourceStepId || !seen.has(sourceStepId)) {
          return {
            isValid: false,
            reason: "OTT steps require their referenced source step earlier in the chain.",
          };
        }
      }
    }
    return { isValid: true, reason: "" };
  }, [activeSequence, emailStepsById]);
  useEffect(() => {
    if (!activeSequence || !activeSequenceUnusedEmailSteps.length) {
      setIsSequenceAddStepMenuOpen(false);
    }
  }, [activeSequence, activeSequenceUnusedEmailSteps]);
  useEffect(() => {
    if (!isSequenceAddStepMenuOpen) return undefined;
    const handleOutsideClick = (event) => {
      const target = event?.target;
      if (!target) return;
      if (sequenceAddStepMenuRef.current?.contains?.(target)) return;
      setIsSequenceAddStepMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isSequenceAddStepMenuOpen]);
  const sequenceStepApprovalOptions = useMemo(
    () =>
      (sequenceDraft.steps || []).map((step, index) => {
        const activeVariant =
          (step?.variants || []).find((variant) => variant?.isActive !== false) ||
          (step?.variants || [])[0] ||
          null;
        const subject = String(activeVariant?.subject || "").trim();
        return {
          stepId: String(step?.stepId || ""),
          orderIndex: Number(step?.orderIndex || index + 1),
          mode:
            index === 0
              ? "standard"
              : String(step?.mode || "standard").trim().toLowerCase() === "ott"
              ? "ott"
              : "standard",
          ottSourceStepId: String(step?.ottSourceStepId || "").trim(),
          subject: subject || "No subject",
        };
      }),
    [sequenceDraft.steps]
  );
  const leadSequencePills = useMemo(
    () =>
      (sequencesDraft || []).map((sequence, index) => {
        const sequenceOrdinalLabel = getSequenceLabelByIndex(index);
        const letter = String(sequenceOrdinalLabel || "Sequence A").replace(/^Sequence\s+/, "") || "A";
        const title = String(sequence?.name || "").trim() || sequenceOrdinalLabel;
        return {
          sequenceId: String(sequence?.sequenceId || ""),
          letter,
          title,
          sequence,
        };
      }),
    [sequencesDraft]
  );
  const selectedLeadSequence = useMemo(
    () =>
      (sequencesDraft || []).find(
        (entry) => String(entry?.sequenceId || "") === String(selectedLeadSequenceId || "")
      ) || null,
    [sequencesDraft, selectedLeadSequenceId]
  );
  const selectedLeadSequenceWaitByFromStepId = useMemo(() => {
    const map = new Map();
    const nodes = selectedLeadSequence?.nodes || [];
    const waits = selectedLeadSequence?.waits || [];
    for (let idx = 0; idx < nodes.length - 1; idx += 1) {
      const fromStepId = String(nodes[idx]?.stepId || "").trim();
      const toStepId = String(nodes[idx + 1]?.stepId || "").trim();
      if (!fromStepId || !toStepId) continue;
      map.set(fromStepId, {
        edgeIndex: idx,
        fromStepId,
        toStepId,
        wait: waits[idx] || { amount: 1, unit: "days" },
      });
    }
    return map;
  }, [selectedLeadSequence]);
  const selectedLeadSequenceHasEnabledLeads = useMemo(() => {
    if (!selectedLeadSequence) return false;
    const selectedStepIds = new Set(
      (selectedLeadSequence?.nodes || []).map((node) => String(node?.stepId || "").trim()).filter(Boolean)
    );
    if (!selectedStepIds.size) return false;
    return (salesforceFilteredAllMembers || []).some((member) => {
      if (!member) return false;
      if (!hasMemberEnabledManualStep(member, selectedStepIds)) return false;
      const stepApprovals = getMemberStepApprovals(member);
      return Array.from(selectedStepIds).some((stepId) => !!stepApprovals?.[stepId]);
    });
  }, [selectedLeadSequence, salesforceFilteredAllMembers]);
  useEffect(() => {
    if (!leadSequenceWaitEditor?.isOpen) return;
    const activeSequenceId = String(selectedLeadSequenceId || "");
    const editorSequenceId = String(leadSequenceWaitEditor?.sequenceId || "");
    if (!activeSequenceId || !editorSequenceId || activeSequenceId !== editorSequenceId) {
      setLeadSequenceWaitEditor((prev) => ({ ...prev, isOpen: false }));
    }
  }, [selectedLeadSequenceId, leadSequenceWaitEditor?.isOpen, leadSequenceWaitEditor?.sequenceId]);
  const selectedVariant = useMemo(
    () => selectedStep?.variants?.[selectedVariantIndex] || null,
    [selectedStep, selectedVariantIndex]
  );
  const currentSequenceSignature = useMemo(
    () => buildSequenceSaveSignature(sequenceDraft),
    [sequenceDraft]
  );
  const currentSequenceDefinitionsSignature = useMemo(
    () => buildSequenceDefinitionsSaveSignature(sequencesDraft),
    [sequencesDraft]
  );
  const isSequenceDirty = useMemo(
    () =>
      !!selectedCampaignId &&
      currentSequenceSignature !== String(lastSavedSequenceSignature || ""),
    [selectedCampaignId, currentSequenceSignature, lastSavedSequenceSignature]
  );
  const isSequenceDefinitionsDirty = useMemo(
    () =>
      !!selectedCampaignId &&
      currentSequenceDefinitionsSignature !== String(lastSavedSequenceDefinitionsSignature || ""),
    [selectedCampaignId, currentSequenceDefinitionsSignature, lastSavedSequenceDefinitionsSignature]
  );

  useEffect(() => {
    const handler = (event) => {
      if (
        !(
          (activeTab === "emails" && isSequenceDirty) ||
          (activeTab === "sequences" && isSequenceDefinitionsDirty)
        )
      ) {
        return undefined;
      }
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [activeTab, isSequenceDirty, isSequenceDefinitionsDirty]);

  useEffect(() => {
    setSequencesDraft((prev) => (prev || []).map((entry) => sanitizeSequenceChain(entry)));
  }, [sequenceStepsForUi]);

  // Auto-save sequence sender accounts when they change
  useEffect(() => {
    if (!selectedCampaignId || !employee) return;
    const currentSig = buildSenderAccountsSignature(sequenceDraft);
    if (currentSig === String(lastSavedSenderAccountsSignatureRef.current || "")) return;
    if (sequenceSenderAccountsAutosaveTimerRef.current) {
      clearTimeout(sequenceSenderAccountsAutosaveTimerRef.current);
    }
    sequenceSenderAccountsAutosaveTimerRef.current = setTimeout(async () => {
      sequenceSenderAccountsAutosaveTimerRef.current = null;
      try {
        const normalized = normalizeSenderAccounts(
          sequenceDraft?.senderAccounts || [],
          sequenceDraft?.senderAccountEmails || []
        );
        const api = new ApiService({ employee });
        const response = await api.post("landmark/sendora/updateCampaign", {
          campaignId: selectedCampaignId,
          options: {
            senderAccounts: normalized,
            senderAccountEmails: normalized.map((e) => e.emailLowercase),
          },
        });
        if (response?.error) {
          toast.error(response.error);
          return;
        }
        lastSavedSenderAccountsSignatureRef.current = buildSenderAccountsSignature(sequenceDraft);
      } catch (err) {
        console.error(err);
        toast.error("Could not save sender accounts");
      }
    }, 700);
    return () => {
      if (sequenceSenderAccountsAutosaveTimerRef.current) {
        clearTimeout(sequenceSenderAccountsAutosaveTimerRef.current);
        sequenceSenderAccountsAutosaveTimerRef.current = null;
      }
    };
  }, [
    selectedCampaignId,
    employee,
    sequenceDraft?.senderAccounts,
    sequenceDraft?.senderAccountEmails,
  ]);

  useEffect(() => {
    const validStepIds = new Set(
      sequenceStepApprovalOptions.map((option) => String(option?.stepId || "")).filter(Boolean)
    );
    setSelectedApprovalStepIds((prev) => {
      const prevSet = new Set(
        (Array.isArray(prev) ? prev : [])
          .map((stepId) => String(stepId || ""))
          .filter((stepId) => validStepIds.has(stepId))
      );
      if (!prevSet.size && sequenceStepApprovalOptions[0]?.stepId) {
        prevSet.add(String(sequenceStepApprovalOptions[0].stepId));
      }
      sequenceStepApprovalOptions.forEach((option) => {
        if (option.mode !== "ott") return;
        if (!prevSet.has(option.stepId)) return;
        if (!prevSet.has(option.ottSourceStepId)) {
          prevSet.delete(option.stepId);
        }
      });
      const next = Array.from(prevSet);
      const current = Array.isArray(prev) ? prev : [];
      if (next.length === current.length && next.every((value, idx) => value === current[idx])) {
        return current;
      }
      return next;
    });
  }, [sequenceStepApprovalOptions]);
  useEffect(() => {
    leadSendModeTouchedRef.current = false;
    setLeadSendMode(null);
    setSelectedLeadSequenceId(null);
  }, [selectedCampaign?.campaignId]);
  useEffect(() => {
    if (!leadSequencePills.length) {
      if (!leadSendMode || leadSendMode !== "manual") {
        setLeadSendMode("manual");
      }
      return;
    }
    if (!leadSendMode || (!leadSendModeTouchedRef.current && leadSendMode === "manual")) {
      setLeadSendMode("sequence");
    }
  }, [leadSequencePills, leadSendMode]);
  useEffect(() => {
    if (!leadSequencePills.length) {
      if (selectedLeadSequenceId !== "") setSelectedLeadSequenceId("");
      return;
    }
    if (leadSendMode === "manual") {
      if (selectedLeadSequenceId !== "") setSelectedLeadSequenceId("");
      return;
    }
    if (selectedLeadSequenceId === null || selectedLeadSequenceId === "") {
      setSelectedLeadSequenceId(String(leadSequencePills[0]?.sequenceId || ""));
      return;
    }
    const exists = leadSequencePills.some((entry) => entry.sequenceId === selectedLeadSequenceId);
    if (!exists) {
      setSelectedLeadSequenceId(String(leadSequencePills[0]?.sequenceId || ""));
    }
  }, [leadSequencePills, leadSendMode, selectedLeadSequenceId]);
  useEffect(() => {
    if (!selectedLeadSequenceId) return;
    const selectedSequence = (sequencesDraft || []).find(
      (entry) => String(entry?.sequenceId || "") === String(selectedLeadSequenceId || "")
    );
    if (!selectedSequence) return;
    const validStepIds = new Set(
      sequenceStepApprovalOptions.map((option) => String(option?.stepId || "")).filter(Boolean)
    );
    const nextSet = new Set(
      (selectedSequence?.nodes || [])
        .map((node) => String(node?.stepId || "").trim())
        .filter((stepId) => validStepIds.has(stepId))
    );
    sequenceStepApprovalOptions.forEach((option) => {
      if (option.mode !== "ott") return;
      if (!nextSet.has(option.stepId)) return;
      if (!nextSet.has(option.ottSourceStepId)) {
        nextSet.delete(option.stepId);
      }
    });
    const next = Array.from(nextSet);
    setSelectedApprovalStepIds((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (next.length === current.length && next.every((value, idx) => value === current[idx])) {
        return current;
      }
      return next;
    });
  }, [selectedLeadSequenceId, sequencesDraft, sequenceStepApprovalOptions]);
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Shift") {
        setIsShiftPressed(true);
      }
    };
    const handleKeyUp = (event) => {
      if (event.key === "Shift") {
        setIsShiftPressed(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);
  useEffect(() => {
    hasInitializedEnabledStepsFilterRef.current = false;
    enabledStepsFilterKnownIdsRef.current = new Set();
    enabledSequencesFilterKnownIdsRef.current = new Set();
    hasInitializedSentStepsFilterRef.current = false;
    sentStepsFilterKnownIdsRef.current = new Set();
    setEnabledSequencesFilterEnabled(false);
    setEnabledSequencesFilterSequenceIds([]);
    setEnabledStepsFilterEnabled(false);
    setEnabledStepsFilterStepIds([]);
    setSentStepsFilterEnabled(false);
    setSentStepsFilterStepIds([]);
    setSentStepsFilterIncludeNothingSent(false);
    setBulkSalesforceStatusTarget("");
    setIsBulkSalesforceStatusConfirmModalOpen(false);
    setHoveredMemberSalesforceLinkId("");
    setLeadsDisplayExpanded(false);
    setLeadSequenceWaitEditor((prev) => ({ ...prev, isOpen: false }));
  }, [selectedCampaignId]);
  useEffect(() => {
    const validSequenceIds = leadSequencePills
      .map((entry) => String(entry?.sequenceId || "").trim())
      .filter(Boolean);
    const validSequenceIdSet = new Set(validSequenceIds);
    setEnabledSequencesFilterSequenceIds((prev) => {
      const previousSelected = (Array.isArray(prev) ? prev : [])
        .map((sequenceId) => String(sequenceId || "").trim())
        .filter((sequenceId) => validSequenceIdSet.has(sequenceId));
      return [...previousSelected];
    });
    enabledSequencesFilterKnownIdsRef.current = new Set(validSequenceIds);
  }, [leadSequencePills]);
  useEffect(() => {
    const validStepIds = sequenceStepApprovalOptions
      .map((option) => String(option?.stepId || "").trim())
      .filter(Boolean);
    const validStepIdSet = new Set(validStepIds);
    const knownStepIdSet = enabledStepsFilterKnownIdsRef.current || new Set();
    setEnabledStepsFilterStepIds((prev) => {
      const previousSelected = (Array.isArray(prev) ? prev : [])
        .map((stepId) => String(stepId || "").trim())
        .filter((stepId) => validStepIdSet.has(stepId));
      if (!hasInitializedEnabledStepsFilterRef.current) {
        hasInitializedEnabledStepsFilterRef.current = true;
        return [];
      }
      const nextSelected = [...previousSelected];
      if (knownStepIdSet.size > 0) {
        validStepIds.forEach((stepId) => {
          if (!knownStepIdSet.has(stepId) && !nextSelected.includes(stepId)) {
            nextSelected.push(stepId);
          }
        });
      }
      return nextSelected;
    });
    enabledStepsFilterKnownIdsRef.current = new Set(validStepIds);
  }, [sequenceStepApprovalOptions]);
  useEffect(() => {
    const validStepIds = sequenceStepApprovalOptions
      .map((option) => String(option?.stepId || "").trim())
      .filter(Boolean);
    const validStepIdSet = new Set(validStepIds);
    const knownStepIdSet = sentStepsFilterKnownIdsRef.current || new Set();
    setSentStepsFilterStepIds((prev) => {
      const previousSelected = (Array.isArray(prev) ? prev : [])
        .map((stepId) => String(stepId || "").trim())
        .filter((stepId) => validStepIdSet.has(stepId));
      if (!hasInitializedSentStepsFilterRef.current) {
        hasInitializedSentStepsFilterRef.current = true;
        return [];
      }
      const nextSelected = [...previousSelected];
      if (knownStepIdSet.size > 0) {
        validStepIds.forEach((stepId) => {
          if (!knownStepIdSet.has(stepId) && !nextSelected.includes(stepId)) {
            nextSelected.push(stepId);
          }
        });
      }
      return nextSelected;
    });
    sentStepsFilterKnownIdsRef.current = new Set(validStepIds);
  }, [sequenceStepApprovalOptions]);
  const selectedSchedule = useMemo(() => {
    if (!scheduleDrafts.length) return null;
    return scheduleDrafts[0];
  }, [scheduleDrafts]);
  const scheduleBypassEnabled =
    selectedSchedule?.bypassSchedule === true || selectedSchedule?.isActive === false;
  const runStepMetrics = useMemo(() => {
    const stepMonitorMetrics =
      campaignMonitor?.stepMetrics && typeof campaignMonitor.stepMetrics === "object"
        ? campaignMonitor.stepMetrics
        : {};
    const runtime =
      campaignMonitor?.runtime && typeof campaignMonitor.runtime === "object"
        ? campaignMonitor.runtime
        : {};
    const pendingManualStepIdSet = new Set(
      (Array.isArray(runtime?.manualStepTriggerStepIds) ? runtime.manualStepTriggerStepIds : [])
        .map((stepId) => String(stepId || "").trim())
        .filter(Boolean)
    );
    const stepPauseMap =
      campaignMonitor?.stepPauseMap && typeof campaignMonitor.stepPauseMap === "object"
        ? campaignMonitor.stepPauseMap
        : {};
    const savedSteps = Array.isArray(selectedCampaign?.sequences?.steps)
      ? selectedCampaign.sequences.steps
      : [];
    const savedStepsByOrderIndex = new Map(
      savedSteps.map((step, index) => [
        Number(step?.orderIndex || index + 1),
        String(step?.stepId || "").trim(),
      ])
    );
    const sourceOptions = savedSteps.length
      ? savedSteps.map((step, index) => {
          const activeVariant =
            (step?.variants || []).find((variant) => variant?.isActive !== false) ||
            (step?.variants || [])[0] ||
            null;
          const subject = String(activeVariant?.subject || "").trim();
          return {
            stepId: String(step?.stepId || ""),
            orderIndex: Number(step?.orderIndex || index + 1),
            mode:
              index === 0
                ? "standard"
                : String(step?.mode || "standard").trim().toLowerCase() === "ott"
                ? "ott"
                : "standard",
            ottSourceStepId: String(step?.ottSourceStepId || "").trim(),
            subject: subject || "No subject",
          };
        })
      : sequenceStepApprovalOptions || [];
    return sourceOptions.map((option) => {
      const persistedStepId =
        savedStepsByOrderIndex.get(Number(option?.orderIndex || 0)) || "";
      const stepId = persistedStepId || String(option?.stepId || "");
      const approvedCount = (allMembers || []).reduce((count, member) => {
        if (!member || member.isActive === false) return count;
        if (!hasMemberEnabledManualStep(member)) return count;
        const stepApprovals = getMemberStepApprovals(member);
        return stepApprovals[stepId] ? count + 1 : count;
      }, 0);
      const approvedMembersForStep = (allMembers || []).filter((member) => {
        if (!member || member.isActive === false) return false;
        if (!hasMemberEnabledManualStep(member)) return false;
        const stepApprovals = getMemberStepApprovals(member);
        return !!stepApprovals[stepId];
      });
      const clientBlockedBadEmailCount = (allMembers || []).reduce((count, member) => {
        if (!member || member.isActive === false) return count;
        if (!hasMemberEnabledManualStep(member)) return count;
        const stepApprovals = getMemberStepApprovals(member);
        if (!stepApprovals[stepId]) return count;
        return getMemberEmailVerificationCategory(member) === "bad" ? count + 1 : count;
      }, 0);
      const monitorForStep = stepMonitorMetrics?.[stepId] || {};
      const sentCount = Math.max(0, Number(monitorForStep?.sentCount || 0));
      const queuedCount = Math.max(0, Number(monitorForStep?.queuedCount || 0));
      const failedCount = Math.max(0, Number(monitorForStep?.failedCount || 0));
      const monitorEnabledCount = Math.max(0, Number(monitorForStep?.enabledCount || 0));
      const blockedBadEmailCount =
        Number.isFinite(Number(monitorForStep?.blockedBadEmailCount))
          ? Math.max(0, Number(monitorForStep.blockedBadEmailCount))
          : clientBlockedBadEmailCount;
      const salesforceStatusPrunedCount = Math.max(
        0,
        Number(monitorForStep?.salesforceStatusPrunedCount || 0)
      );
      const fallbackPendingEligibleCount = Math.max(
        0,
        Math.min(approvedCount, monitorEnabledCount || approvedCount) -
          sentCount -
          queuedCount -
          blockedBadEmailCount
      );
      const monitorActionableCount = Number.isFinite(Number(monitorForStep?.actionableCount))
        ? Math.max(0, Number(monitorForStep.actionableCount))
        : null;
      const pendingEligibleCount =
        monitorActionableCount === null
          ? fallbackPendingEligibleCount
          : Math.max(monitorActionableCount, fallbackPendingEligibleCount);
      // Preview batching is computed from the same "ready to queue" pool shown in metrics.
      const previewEligibleMembers = approvedMembersForStep
        .filter((member) => getMemberEmailVerificationCategory(member) !== "bad")
        .slice(0, Math.max(0, Number(pendingEligibleCount || 0)));
      const previewBatches = buildBatchesFromMembers(previewEligibleMembers);
      const companyBatchSizes = previewBatches.map((batchEmails) => batchEmails.length);
      const fastDurationSeconds = estimateFastDurationSecondsFromBatchSizes(companyBatchSizes);
      return {
        ...option,
        persistedStepId,
        hasPersistedStepId: !!persistedStepId,
        approvedCount: monitorEnabledCount || approvedCount,
        sentCount,
        queuedCount,
        failedCount,
        blockedBadEmailCount,
        salesforceStatusPrunedCount,
        pendingEligibleCount,
        companyBatchSizes,
        previewBatches,
        fastDurationSeconds,
        isPaused: !!stepPauseMap[stepId],
        isPreparing: pendingManualStepIdSet.has(String(stepId || "").trim()),
      };
    });
  }, [
    sequenceStepApprovalOptions,
    campaignMonitor?.stepMetrics,
    campaignMonitor?.stepPauseMap,
    campaignMonitor?.runtime,
    allMembers,
    selectedCampaign?.sequences?.steps,
  ]);
  const liveMonitorStepProgressRows = useMemo(() => {
    return (runStepMetrics || [])
      .map((step, index) => {
        const orderIndex = Number(step?.orderIndex || index + 1) || index + 1;
        const hasEnabledLeadCount = Number.isFinite(Number(step?.approvedCount));
        const denominator = hasEnabledLeadCount
          ? Math.max(
              0,
              toSafeNumber(step?.approvedCount) - toSafeNumber(step?.blockedBadEmailCount)
            )
          : Math.max(0, toSafeNumber(step?.queuedCount) - toSafeNumber(step?.blockedBadEmailCount));
        const sentForDisplay = Math.max(0, Math.min(denominator, toSafeNumber(step?.sentCount)));
        const progressPercent =
          denominator > 0
            ? Math.max(0, Math.min(100, Math.round((sentForDisplay / denominator) * 100)))
            : 0;
        return {
          key: String(step?.stepId || `live-step-${orderIndex}`),
          orderIndex,
          label: `Step ${orderIndex}`,
          sentCount: sentForDisplay,
          queuedCount: denominator,
          progressPercent,
        };
      })
      .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
  }, [runStepMetrics]);
  const hasManualEnabledLeads = useMemo(
    () => (runStepMetrics || []).some((step) => Math.max(0, toSafeNumber(step?.approvedCount)) > 0),
    [runStepMetrics]
  );
  const runStepOrderById = useMemo(() => {
    const byId = new Map();
    (runStepMetrics || []).forEach((step, index) => {
      const stepId = String(step?.stepId || "").trim();
      const orderIndex = Number(step?.orderIndex || index + 1) || index + 1;
      if (!stepId) return;
      byId.set(stepId, orderIndex);
    });
    return byId;
  }, [runStepMetrics]);
  const runSequenceLabelById = useMemo(() => {
    const byId = new Map();
    (leadSequencePills || []).forEach((sequence) => {
      const sequenceId = String(sequence?.sequenceId || "").trim();
      if (!sequenceId) return;
      const letter = String(sequence?.letter || "").trim();
      const title = String(sequence?.title || "").trim();
      byId.set(sequenceId, letter ? `Sequence ${letter}: ${title}` : title || sequenceId);
    });
    return byId;
  }, [leadSequencePills]);
  const runSalesforcePrunedLeadLogs = useMemo(() => {
    return (allMembers || [])
      .map((member) => {
        const excludeReason = String(member?.excludeReason || "").trim();
        if (!excludeReason) return null;
        if (!excludeReason.toLowerCase().includes("salesforce status change")) return null;
        const stepApprovals =
          member?.stepApprovals && typeof member.stepApprovals === "object" ? member.stepApprovals : {};
        const manualStepLabels = Object.keys(stepApprovals)
          .filter((stepId) => !!stepApprovals[stepId])
          .map((stepId) => {
            const orderIndex = Number(runStepOrderById.get(String(stepId || "").trim()) || 0);
            return orderIndex > 0 ? `Step ${orderIndex}` : null;
          })
          .filter(Boolean);
        const sequenceLabels = (Array.isArray(member?.enabledSequenceIds) ? member.enabledSequenceIds : [])
          .map((sequenceId) => runSequenceLabelById.get(String(sequenceId || "").trim()) || "")
          .filter(Boolean);
        const queueScope = [manualStepLabels.join(", "), sequenceLabels.join(", ")]
          .filter(Boolean)
          .join(" | ");
        const email = String(member?.email || "").trim();
        const firstName = String(member?.firstName || "").trim();
        const lastName = String(member?.lastName || "").trim();
        const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
        const status = String(member?.status || "").trim();
        const eventTime = Math.max(
          0,
          Number(member?.excludedTime || 0),
          Number(member?.lastInclusionChangeTime || 0)
        );
        return {
          key: String(member?.memberId || member?._id || email || Math.random()),
          title: displayName ? `${displayName}${email ? ` (${email})` : ""}` : email || "Unknown lead",
          detail: `${excludeReason}.${queueScope ? `\n${queueScope}` : "\nQueue scope unavailable"}${
            status ? ` - Status: ${status}` : ""
          }`,
          eventTime,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Number(b?.eventTime || 0) - Number(a?.eventTime || 0));
  }, [allMembers, runStepOrderById, runSequenceLabelById]);
  const runVerificationBlockedLeadLogs = useMemo(() => {
    return (allMembers || [])
      .map((member) => {
        const stepApprovals =
          member?.stepApprovals && typeof member.stepApprovals === "object" ? member.stepApprovals : {};
        const manualStepLabels = Object.keys(stepApprovals)
          .filter((stepId) => !!stepApprovals[stepId])
          .map((stepId) => {
            const orderIndex = Number(runStepOrderById.get(String(stepId || "").trim()) || 0);
            return orderIndex > 0 ? `Step ${orderIndex}` : null;
          })
          .filter(Boolean);
        const sequenceLabels = (Array.isArray(member?.enabledSequenceIds) ? member.enabledSequenceIds : [])
          .map((sequenceId) => runSequenceLabelById.get(String(sequenceId || "").trim()) || "")
          .filter(Boolean);
        const queueScope = [manualStepLabels.join(", "), sequenceLabels.join(", ")]
          .filter(Boolean)
          .join(" | ");
        const email = String(member?.email || "").trim();
        const firstName = String(member?.firstName || "").trim();
        const lastName = String(member?.lastName || "").trim();
        const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
        const verificationCategory = getMemberEmailVerificationCategory(member);
        const excludeReason = String(member?.excludeReason || "").trim();
        const hasBlockedExcludeReason = excludeReason
          .toLowerCase()
          .includes("email verification blocked sending");
        if (verificationCategory !== "bad" && !hasBlockedExcludeReason) return null;
        const verificationDecision = String(member?.emailVerification?.decision || "")
          .trim()
          .replace(/_/g, " ");
        const verificationResult = String(member?.emailVerification?.result || "").trim();
        const verificationQuality = String(member?.emailVerification?.quality || "").trim();
        const verificationSummary = [verificationDecision, verificationResult, verificationQuality]
          .filter(Boolean)
          .join(" | ");
        const fallbackReason = verificationSummary
          ? `Blocked by email verification (${verificationSummary})`
          : "Blocked by email verification";
        const verifierResultStatus =
          verificationResult || verificationDecision || verificationQuality || "unknown";
        const eventTime = Math.max(
          0,
          Number(member?.emailVerification?.updatedTime || 0),
          Number(member?.emailVerification?.checkedTime || 0),
          Number(member?.excludedTime || 0),
          Number(member?.lastInclusionChangeTime || 0)
        );
        return {
          key: String(member?.memberId || member?._id || email || Math.random()),
          title: displayName ? `${displayName}${email ? ` (${email})` : ""}` : email || "Unknown lead",
          detail: `${excludeReason || fallbackReason}.${queueScope ? `\n${queueScope}` : "\nQueue scope unavailable"}${
            verifierResultStatus ? ` - Verifier result status: ${verifierResultStatus}` : ""
          }`,
          eventTime,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Number(b?.eventTime || 0) - Number(a?.eventTime || 0));
  }, [allMembers, runStepOrderById, runSequenceLabelById]);
  const runBounceLeadLogs = useMemo(() => {
    return (allMembers || [])
      .map((member) => {
        const eventType = String(member?.lastDeliveryEventType || "")
          .trim()
          .toLowerCase();
        const excludeReason = String(member?.excludeReason || "").trim();
        const isBounce =
          eventType === "bounce" || excludeReason.toLowerCase().includes("ses bounce detected");
        if (!isBounce) return null;
        const stepApprovals =
          member?.stepApprovals && typeof member.stepApprovals === "object" ? member.stepApprovals : {};
        const manualStepLabels = Object.keys(stepApprovals)
          .filter((stepId) => !!stepApprovals[stepId])
          .map((stepId) => {
            const orderIndex = Number(runStepOrderById.get(String(stepId || "").trim()) || 0);
            return orderIndex > 0 ? `Step ${orderIndex}` : null;
          })
          .filter(Boolean);
        const sequenceLabels = (Array.isArray(member?.enabledSequenceIds) ? member.enabledSequenceIds : [])
          .map((sequenceId) => runSequenceLabelById.get(String(sequenceId || "").trim()) || "")
          .filter(Boolean);
        const queueScope = [manualStepLabels.join(", "), sequenceLabels.join(", ")]
          .filter(Boolean)
          .join(" | ");
        const email = String(member?.email || "").trim();
        const firstName = String(member?.firstName || "").trim();
        const lastName = String(member?.lastName || "").trim();
        const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
        const bounceStatus = [String(member?.lastBounceType || "").trim(), String(member?.lastBounceSubType || "").trim()]
          .filter(Boolean)
          .join(" | ");
        const eventSummary =
          String(member?.lastDeliveryEventSummary || "").trim() ||
          excludeReason ||
          "SES bounce detected";
        const eventTime = Math.max(
          0,
          Number(member?.lastDeliveryEventTime || 0),
          Number(member?.excludedTime || 0),
          Number(member?.lastInclusionChangeTime || 0)
        );
        return {
          key: String(member?.memberId || member?._id || email || Math.random()),
          title: displayName ? `${displayName}${email ? ` (${email})` : ""}` : email || "Unknown lead",
          detail: `${eventSummary}.${queueScope ? `\n${queueScope}` : "\nQueue scope unavailable"}${
            bounceStatus ? ` - Bounce status: ${bounceStatus}` : ""
          }`,
          eventTime,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Number(b?.eventTime || 0) - Number(a?.eventTime || 0));
  }, [allMembers, runStepOrderById, runSequenceLabelById]);
  const runComplaintLeadLogs = useMemo(() => {
    return (allMembers || [])
      .map((member) => {
        const eventType = String(member?.lastDeliveryEventType || "")
          .trim()
          .toLowerCase();
        const excludeReason = String(member?.excludeReason || "").trim();
        const isComplaint =
          eventType === "complaint" || excludeReason.toLowerCase().includes("ses complaint detected");
        if (!isComplaint) return null;
        const stepApprovals =
          member?.stepApprovals && typeof member.stepApprovals === "object" ? member.stepApprovals : {};
        const manualStepLabels = Object.keys(stepApprovals)
          .filter((stepId) => !!stepApprovals[stepId])
          .map((stepId) => {
            const orderIndex = Number(runStepOrderById.get(String(stepId || "").trim()) || 0);
            return orderIndex > 0 ? `Step ${orderIndex}` : null;
          })
          .filter(Boolean);
        const sequenceLabels = (Array.isArray(member?.enabledSequenceIds) ? member.enabledSequenceIds : [])
          .map((sequenceId) => runSequenceLabelById.get(String(sequenceId || "").trim()) || "")
          .filter(Boolean);
        const queueScope = [manualStepLabels.join(", "), sequenceLabels.join(", ")]
          .filter(Boolean)
          .join(" | ");
        const email = String(member?.email || "").trim();
        const firstName = String(member?.firstName || "").trim();
        const lastName = String(member?.lastName || "").trim();
        const displayName = [firstName, lastName].filter(Boolean).join(" ").trim();
        const complaintStatus = String(member?.lastComplaintFeedbackType || "").trim();
        const eventSummary =
          String(member?.lastDeliveryEventSummary || "").trim() ||
          excludeReason ||
          "SES complaint detected";
        const eventTime = Math.max(
          0,
          Number(member?.lastDeliveryEventTime || 0),
          Number(member?.excludedTime || 0),
          Number(member?.lastInclusionChangeTime || 0)
        );
        return {
          key: String(member?.memberId || member?._id || email || Math.random()),
          title: displayName ? `${displayName}${email ? ` (${email})` : ""}` : email || "Unknown lead",
          detail: `${eventSummary}.${queueScope ? `\n${queueScope}` : "\nQueue scope unavailable"}${
            complaintStatus ? ` - Complaint status: ${complaintStatus}` : ""
          }`,
          eventTime,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Number(b?.eventTime || 0) - Number(a?.eventTime || 0));
  }, [allMembers, runStepOrderById, runSequenceLabelById]);
  const runLastSesEventLogs = useMemo(() => {
    return (monitorRecentActivity || [])
      .map((item, index) => {
        const status = String(item?.status || "")
          .trim()
          .toLowerCase();
        if (!["bounced", "complaint"].includes(status)) return null;
        const email = String(item?.memberEmail || "").trim();
        const stepOrderIndex = Math.max(0, Number(item?.stepOrderIndex || 0));
        const sourceLabel = String(item?.sourceLabel || "").trim() || "Manual step";
        const summary = String(item?.error || "").trim() || `SES ${status} detected`;
        const eventTime = Math.max(0, Number(item?.sentTime || item?.updatedTime || 0));
        return {
          key: `${String(item?.queueId || "ses")}-${index}-${eventTime}`,
          title: `${status === "complaint" ? "Complaint" : "Bounce"}${
            email ? ` - ${email}` : ""
          }`,
          detail: `${summary}.${stepOrderIndex > 0 ? `\nStep ${stepOrderIndex}` : ""}\n${sourceLabel}`,
          eventTime,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Number(b?.eventTime || 0) - Number(a?.eventTime || 0));
  }, [monitorRecentActivity]);
  const hasRunSafetyLogEntries =
    runSalesforcePrunedLeadLogs.length > 0 ||
    runVerificationBlockedLeadLogs.length > 0 ||
    runBounceLeadLogs.length > 0 ||
    runComplaintLeadLogs.length > 0 ||
    runLastSesEventLogs.length > 0;
  const runSequenceMetrics = useMemo(() => {
    const monitorSequences = Array.isArray(campaignMonitor?.runSequences)
      ? campaignMonitor.runSequences
      : [];
    const monitorPauseMap =
      campaignMonitor?.sequencePauseMap && typeof campaignMonitor.sequencePauseMap === "object"
        ? campaignMonitor.sequencePauseMap
        : {};
    const sequenceById = new Map(
      (sequencesDraft || []).map((entry, index) => [
        String(entry?.sequenceId || "").trim(),
        {
          ...(entry || {}),
          orderIndex: index,
        },
      ])
    );
    return monitorSequences.map((entry, index) => {
      const sequenceId = String(entry?.sequenceId || "").trim();
      const localSequence = sequenceById.get(sequenceId) || null;
      const orderIndex = Number(localSequence?.orderIndex || index);
      const sequenceOrdinalLabel = getSequenceLabelByIndex(orderIndex);
      const sequenceLetter = String(sequenceOrdinalLabel || "Sequence A").replace(/^Sequence\s+/, "") || "A";
      const stepBreakdowns = (Array.isArray(entry?.stepBreakdowns) ? entry.stepBreakdowns : []).map(
        (step, stepIndex) => {
          const stepId = String(step?.stepId || "").trim();
          const localStep = stepId ? emailStepsById.get(stepId) : null;
          const localNode = stepId
            ? (localSequence?.nodes || []).find(
                (node) => String(node?.stepId || "").trim() === stepId
              ) || null
            : null;
          const localNodeSpeedMode = normalizeSequenceNodeSpeedMode(localNode?.speedMode || "");
          const localFirstVariant = (localStep?.variants || [])[0] || {};
          const subject = String(step?.subject || localFirstVariant?.subject || "").trim();
          const body = String(step?.body || localFirstVariant?.body || "");
          const fallbackStepSpeedMode = String(
            step?.speedMode || localStep?.speedMode || localStep?.speed || "medium"
          ).trim();
          const effectiveSpeedMode = localNodeSpeedMode || fallbackStepSpeedMode;
          const effectiveTargetDurationSeconds =
            localNodeSpeedMode === "custom"
              ? Math.max(0, Number(localNode?.targetDurationSeconds || 0))
              : Math.max(0, Number(step?.targetDurationSeconds || localStep?.targetDurationSeconds || 0));
          return {
            ...(step || {}),
            stepId,
            stepIndex: Number(step?.stepIndex ?? stepIndex),
            stepOrderIndex: Number(step?.stepOrderIndex || localStep?.orderIndex || stepIndex + 1),
            stepName: String(step?.stepName || localStep?.name || ""),
            subject,
            body,
            speedMode: effectiveSpeedMode,
            targetDurationSeconds: effectiveTargetDurationSeconds,
            durationPresetMultiplier: Math.max(
              0.0001,
              Number(step?.durationPresetMultiplier || localStep?.durationPresetMultiplier || 1)
            ),
          };
        }
      );
      const sentCount = stepBreakdowns.reduce(
        (sum, step) => sum + Math.max(0, Number(step?.sentCount || 0)),
        0
      );
      const queuedCount = stepBreakdowns.reduce(
        (sum, step) => sum + Math.max(0, Number(step?.queuedCount || 0)),
        0
      );
      const blockedBadEmailCount = stepBreakdowns.reduce(
        (sum, step) => sum + Math.max(0, Number(step?.blockedCount || 0)),
        0
      );
      const pendingEligibleCount = stepBreakdowns.reduce(
        (sum, step) => sum + Math.max(0, Number(step?.eligibleCount || 0)),
        0
      );
      const enabledCount = Math.max(0, Number(entry?.enabledCount || 0));
      const completedCount = Math.max(0, Number(entry?.completedCount || 0));
      const inFlightStateCount = Math.max(0, Number(entry?.inFlightStateCount || 0));
      const progressedInFlightStateCount = Math.max(
        0,
        Number(entry?.progressedInFlightStateCount || 0)
      );
      const isCompleted =
        enabledCount > 0 &&
        completedCount >= enabledCount &&
        queuedCount <= 0 &&
        pendingEligibleCount <= 0;
      const hasStarted = !isCompleted && (queuedCount > 0 || progressedInFlightStateCount > 0);
      const isPaused = !!monitorPauseMap[sequenceId] && !isCompleted && hasStarted;
      const canStart = pendingEligibleCount > 0;
      return {
        sequenceId,
        sequenceName: String(entry?.sequenceName || localSequence?.name || "").trim() || "Untitled sequence",
        sequenceLetter,
        enabledCount,
        completedCount,
        sentCount,
        queuedCount,
        blockedBadEmailCount,
        pendingEligibleCount,
        inFlightStateCount,
        progressedInFlightStateCount,
        status: isCompleted ? "completed" : isPaused ? "paused" : "running",
        hasStarted,
        canStart,
        isCompleted,
        stepBreakdowns,
      };
    });
  }, [campaignMonitor?.runSequences, campaignMonitor?.sequencePauseMap, sequencesDraft, emailStepsById]);
  const runSequenceStepPreviewByKey = useMemo(() => {
    const previewByKey = new Map();
    (runSequenceMetrics || []).forEach((sequence) => {
      (sequence?.stepBreakdowns || []).forEach((step, stepIndex) => {
        const speedSettings = getStepSpeedSettings(step || {});
        const sentCount = Math.max(0, toSafeNumber(step?.sentCount));
        const queuedCount = Math.max(0, toSafeNumber(step?.queuedCount));
        const eligibleCount = Math.max(0, toSafeNumber(step?.eligibleCount));
        const blockedCount = Math.max(0, toSafeNumber(step?.blockedCount));
        const enabledCount = Math.max(0, toSafeNumber(step?.enabledCount));
        const totalForProgress = Math.max(0, enabledCount - blockedCount, sentCount + queuedCount + eligibleCount);
        const leadsRemaining = Math.max(0, totalForProgress - sentCount);
        const conservativeBatchSizes =
          leadsRemaining > 0 ? Array.from({ length: leadsRemaining }, () => 1) : [];
        const fastDurationSeconds = estimateFastDurationSecondsFromBatchSizes(conservativeBatchSizes);
        const effectiveDurationSeconds = Number(
          resolveDurationForStep(
            speedSettings.speed,
            fastDurationSeconds,
            Number(speedSettings.targetDurationSeconds || 0)
          ) || 0
        );
        const buildModePreview = (modeKey) => {
          const durationSeconds = resolveDurationForStep(
            modeKey,
            fastDurationSeconds,
            modeKey === "custom" ? Number(speedSettings.targetDurationSeconds || 0) : 0
          );
          const gapMinutes =
            leadsRemaining > 0
              ? Number(
                  (
                    durationSeconds /
                    60 /
                    Math.max(1, getSendIntervalsCount(leadsRemaining))
                  ).toFixed(4)
                )
              : 0;
          return {
            ...buildSpeedPreviewForInputs({
              selectedSchedule,
              endFridayAtNoon,
              scheduleBypassEnabled,
              gapMinutesInput: gapMinutes,
              leadsRemainingInput: leadsRemaining,
            }),
            mode: modeKey,
            durationSeconds,
            gapMinutes,
          };
        };
        const modePreviews = {
          fast: buildModePreview("fast"),
          medium: buildModePreview("medium"),
          slow: buildModePreview("slow"),
          custom: buildModePreview("custom"),
        };
        const selectedModePreview = modePreviews[speedSettings.speed] || modePreviews.medium;
        previewByKey.set(
          `${String(sequence?.sequenceId || "")}::${String(step?.stepId || stepIndex)}`,
          {
            speedSettings,
            leadsRemaining,
            fastDurationSeconds,
            effectiveDurationSeconds,
            modePreviews,
            customTargetTooLow:
              speedSettings.speed === "custom" &&
              Number(speedSettings.targetDurationSeconds || 0) > 0 &&
              Number(speedSettings.targetDurationSeconds || 0) < fastDurationSeconds,
            ...selectedModePreview,
          }
        );
      });
    });
    return previewByKey;
  }, [runSequenceMetrics, selectedSchedule, scheduleBypassEnabled, endFridayAtNoon]);
  const runSequenceRuntimePreviewById = useMemo(() => {
    const map = new Map();
    (runSequenceMetrics || []).forEach((sequence) => {
      const sequenceId = String(sequence?.sequenceId || "").trim();
      const orderedSteps = (sequence?.stepBreakdowns || [])
        .slice()
        .sort(
          (a, b) =>
            Number(a?.stepOrderIndex || a?.stepIndex || 0) - Number(b?.stepOrderIndex || b?.stepIndex || 0)
        );
      const sequenceEntry =
        (sequencesDraft || []).find(
          (entry) => String(entry?.sequenceId || "").trim() === String(sequenceId || "")
        ) || null;
      const waits = Array.isArray(sequenceEntry?.waits) ? sequenceEntry.waits : [];
      const stepDurationsMinutes = [];
      const waitDurationsMinutes = [];
      let isUpdating = false;
      orderedSteps.forEach((step, stepIndex) => {
        const stepId = String(step?.stepId || "").trim();
        const preview =
          runSequenceStepPreviewByKey.get(`${sequenceId}::${String(step?.stepId || stepIndex)}`) || {};
        stepDurationsMinutes.push(Math.max(0, Number(preview?.effectiveDurationSeconds || 0) / 60));
        if (stepIndex < orderedSteps.length - 1) {
          waitDurationsMinutes.push(convertSequenceWaitToMinutes((waits || [])[stepIndex] || {}));
        }
        const inputKey = getRunStepDurationInputKey(stepId, sequenceId);
        if (inputKey && !!runStepPreviewUpdatingByKey?.[inputKey]) isUpdating = true;
      });
      map.set(
        sequenceId,
        {
          isUpdating,
          ...buildSequenceRuntimePreviewForInputs({
            selectedSchedule,
            endFridayAtNoon,
            scheduleBypassEnabled,
            stepDurationsMinutesInput: stepDurationsMinutes,
            waitDurationsMinutesInput: waitDurationsMinutes,
          }),
        }
      );
    });
    return map;
  }, [
    runSequenceMetrics,
    runSequenceStepPreviewByKey,
    sequencesDraft,
    runStepPreviewUpdatingByKey,
    selectedSchedule,
    scheduleBypassEnabled,
    endFridayAtNoon,
  ]);
  const liveMonitorSequenceProgressRows = useMemo(() => {
    return (runSequenceMetrics || [])
      .map((sequence, index) => {
        const stepRows = (sequence?.stepBreakdowns || [])
          .map((step, index) => {
            const orderIndex = Number(step?.stepIndex || index) + 1;
            const enabledCount = Math.max(0, toSafeNumber(step?.enabledCount));
            const blockedCount = Math.max(0, toSafeNumber(step?.blockedCount));
            const sentCount = Math.max(0, toSafeNumber(step?.sentCount));
            const queuedCount = Math.max(0, toSafeNumber(step?.queuedCount));
            const eligibleCount = Math.max(0, toSafeNumber(step?.eligibleCount));
            const denominator = Math.max(
              0,
              enabledCount - blockedCount,
              sentCount + queuedCount + eligibleCount
            );
            const sentForDisplay = Math.max(0, Math.min(denominator, sentCount));
            const progressPercent =
              denominator > 0
                ? Math.max(0, Math.min(100, Math.round((sentForDisplay / denominator) * 100)))
                : 0;
            return {
              key: `${String(sequence?.sequenceId || "sequence")}::${String(step?.stepId || orderIndex)}`,
              orderIndex,
              label: `Step ${orderIndex}`,
              sentCount: sentForDisplay,
              queuedCount: denominator,
              progressPercent,
            };
          })
          .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
        return {
          key: String(sequence?.sequenceId || `live-sequence-${index}`),
          label: `Sequence ${String(sequence?.sequenceLetter || "").trim()}: ${
            String(sequence?.sequenceName || "").trim() || "Untitled sequence"
          }`,
          stepRows,
        };
      })
      .filter((row) => Array.isArray(row?.stepRows) && row.stepRows.length > 0);
  }, [runSequenceMetrics]);
  const runStepMetricsByOrderIndex = useMemo(() => {
    const map = new Map();
    (runStepMetrics || []).forEach((step) => {
      map.set(Number(step?.orderIndex || 0), step);
    });
    return map;
  }, [runStepMetrics]);
  const runStepMetricsByStepId = useMemo(() => {
    const map = new Map();
    (runStepMetrics || []).forEach((step) => {
      const persistedStepId = String(step?.persistedStepId || "").trim();
      const optionStepId = String(step?.stepId || "").trim();
      if (persistedStepId) map.set(persistedStepId, step);
      if (optionStepId) map.set(optionStepId, step);
    });
    return map;
  }, [runStepMetrics]);
  const getRunStepPreviewSyncAliasKeys = (stepId = "") => {
    const normalizedStepId = String(stepId || "").trim();
    if (!normalizedStepId) return [];
    const metric = runStepMetricsByStepId.get(normalizedStepId) || null;
    const aliasSet = new Set([
      normalizedStepId,
      String(metric?.persistedStepId || "").trim(),
      String(metric?.stepId || "").trim(),
    ]);
    return Array.from(aliasSet).filter(Boolean);
  };
  const getBackendMinimumCustomSecondsForStep = (stepId = "") => {
    const aliasKeys = getRunStepPreviewSyncAliasKeys(stepId);
    for (const aliasKey of aliasKeys) {
      const value = Number(backendStepScheduleMinimumCustomSecondsByStepId?.[aliasKey] || 0);
      if (Number.isFinite(value) && value > 0) return value;
    }
    return 0;
  };
  const runStepBackendPreviewRequests = useMemo(() => {
    const draftStepsByOrderIndex = new Map(
      (sequenceDraft?.steps || []).map((step, idx) => [Number(step?.orderIndex || idx + 1), step])
    );
    return (runStepMetrics || [])
      .map((step, idx) => {
        const persistedStepId = String(step?.persistedStepId || step?.stepId || "").trim();
        if (!persistedStepId) return null;
        const orderIndex = Number(step?.orderIndex || idx + 1) || idx + 1;
        const draftStep = draftStepsByOrderIndex.get(orderIndex) || {};
        const speedSettings = getStepSpeedSettings(draftStep);
        return {
          stepId: persistedStepId,
          stepPatch: {
            speed: normalizeStepSpeed(speedSettings.speed),
            speedMode: normalizeStepSpeed(speedSettings.speed),
            targetDurationSeconds:
              normalizeStepSpeed(speedSettings.speed) === "custom"
                ? Math.max(0, Number(speedSettings.targetDurationSeconds || 0))
                : 0,
          },
        };
      })
      .filter(Boolean);
  }, [runStepMetrics, sequenceDraft?.steps]);
  const runStepBackendPreviewRequestSignature = useMemo(() => {
    if (!runStepBackendPreviewRequests.length) return "";
    return JSON.stringify(
      runStepBackendPreviewRequests.map((entry) => ({
        stepId: String(entry?.stepId || "").trim(),
        speed: String(entry?.stepPatch?.speed || "").trim(),
        speedMode: String(entry?.stepPatch?.speedMode || "").trim(),
        targetDurationSeconds: Math.max(0, Number(entry?.stepPatch?.targetDurationSeconds || 0)),
      }))
    );
  }, [runStepBackendPreviewRequests]);
  useEffect(() => {
    if (!employee || !selectedCampaign?.campaignId) return;
    if (!runStepBackendPreviewRequests.length) {
      setBackendStepSchedulePreviewByStepId({});
      setBackendStepSchedulePreviewLoadingByStepId({});
      setBackendStepScheduleMinimumCustomSecondsByStepId({});
      return;
    }
    const requestId = backendStepSchedulePreviewRequestIdRef.current + 1;
    backendStepSchedulePreviewRequestIdRef.current = requestId;
    const timeoutId = setTimeout(async () => {
      try {
        const api = new ApiService({ employee });
        logSendSpeedApiDebug("REQ stepSchedulePreview batch:start", {
          campaignId: selectedCampaign.campaignId,
          requestId,
          requestCount: runStepBackendPreviewRequests.length,
          stepIds: runStepBackendPreviewRequests.map((entry) => String(entry?.stepId || "")),
        });
        const loadingPatch = runStepBackendPreviewRequests.reduce((acc, entry) => {
          acc[entry.stepId] = true;
          return acc;
        }, {});
        setBackendStepSchedulePreviewLoadingByStepId((prev) => ({ ...(prev || {}), ...loadingPatch }));
        const responses = await Promise.all(
          runStepBackendPreviewRequests.map(async (entry) => {
            logSendSpeedApiDebug("REQ stepSchedulePreview", {
              campaignId: selectedCampaign.campaignId,
              requestId,
              stepId: entry.stepId,
              stepPatch: entry.stepPatch || {},
            });
            const response = await api.post(
              `landmark/sendora/getCampaignStepSchedulePreview/${selectedCampaign.campaignId}/${entry.stepId}`,
              { step: entry.stepPatch }
            );
            logSendSpeedApiDebug("RES stepSchedulePreview", {
              campaignId: selectedCampaign.campaignId,
              requestId,
              stepId: entry.stepId,
              error: response?.error || "",
              hasSummary: !!response?.summary,
            });
            return { stepId: entry.stepId, response };
          })
        );
        if (backendStepSchedulePreviewRequestIdRef.current !== requestId) return;
        const nextPreviewByStepId = {};
        const nextMinimumByStepId = {};
        responses.forEach(({ stepId, response }) => {
          if (!response?.error && response?.summary) {
            nextPreviewByStepId[stepId] = response.summary;
            const minimumFromSummary = getBackendMinimumCustomTargetSeconds(response.summary);
            if (minimumFromSummary > 0) {
              nextMinimumByStepId[stepId] = minimumFromSummary;
            }
            return;
          }
          const minimumFromError = getBackendMinimumCustomTargetSecondsFromError(response?.error || "");
          if (minimumFromError > 0) {
            nextMinimumByStepId[stepId] = minimumFromError;
          }
        });
        setBackendStepSchedulePreviewByStepId(nextPreviewByStepId);
        setBackendStepScheduleMinimumCustomSecondsByStepId(nextMinimumByStepId);
        logSendSpeedApiDebug("RES stepSchedulePreview batch:done", {
          campaignId: selectedCampaign.campaignId,
          requestId,
          receivedCount: Object.keys(nextPreviewByStepId || {}).length,
        });
      } catch (error) {
        console.error("Could not load backend step schedule previews", error);
        logSendSpeedApiDebug("ERR stepSchedulePreview batch", {
          campaignId: selectedCampaign?.campaignId || "",
          requestId,
          message: String(error?.message || error || ""),
        });
      } finally {
        if (backendStepSchedulePreviewRequestIdRef.current !== requestId) return;
        const donePatch = runStepBackendPreviewRequests.reduce((acc, entry) => {
          acc[entry.stepId] = false;
          return acc;
        }, {});
        setBackendStepSchedulePreviewLoadingByStepId((prev) => ({ ...(prev || {}), ...donePatch }));
      }
    }, 250);
    return () => clearTimeout(timeoutId);
    // Intentionally key this fetch effect off a stable signature so rerenders with
    // equivalent payloads do not trigger repeated duplicate preview requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, selectedCampaign?.campaignId, runStepBackendPreviewRequestSignature]);
  useEffect(() => {
    setRunStepPreviewBackendSyncStateByStepId((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      const visited = new Set();
      Object.keys(next).forEach((stepId) => {
        if (visited.has(stepId)) return;
        const aliasKeys = getRunStepPreviewSyncAliasKeys(stepId);
        aliasKeys.forEach((aliasKey) => visited.add(aliasKey));
        const hasLoadingSeen = aliasKeys.some(
          (aliasKey) => String(next?.[aliasKey] || "") === "loading_seen"
        );
        const hasPending = aliasKeys.some((aliasKey) => String(next?.[aliasKey] || "") === "pending");
        const isAnyAliasLoading = aliasKeys.some(
          (aliasKey) => !!backendStepSchedulePreviewLoadingByStepId?.[aliasKey]
        );
        if (hasPending && isAnyAliasLoading) {
          aliasKeys.forEach((aliasKey) => {
            if (String(next?.[aliasKey] || "") === "loading_seen") return;
            next[aliasKey] = "loading_seen";
            changed = true;
          });
          return;
        }
        if (hasLoadingSeen && !isAnyAliasLoading) {
          aliasKeys.forEach((aliasKey) => {
            if (!Object.prototype.hasOwnProperty.call(next, aliasKey)) return;
            delete next[aliasKey];
            changed = true;
          });
        }
      });
      return changed ? next : prev;
    });
  }, [backendStepSchedulePreviewLoadingByStepId, runStepMetricsByStepId]);
  useEffect(() => {
    if (runStepBackendPreviewRequests.length) return;
    setRunStepPreviewBackendSyncStateByStepId({});
  }, [selectedCampaign?.campaignId, runStepBackendPreviewRequests.length]);
  const stepSpeedPreviewByOrderIndex = useMemo(() => {
    const map = new Map();
    (sequenceDraft?.steps || []).forEach((step, index) => {
      const orderIndex = Number(step?.orderIndex || index + 1);
      const metric = runStepMetricsByOrderIndex.get(orderIndex) || {};
      const leadsRemaining = Math.max(0, Number(metric?.pendingEligibleCount || 0));
      const { speed, targetDurationSeconds } = getStepSpeedSettings(step);
      const fastDurationSeconds = Math.max(0, Number(metric?.fastDurationSeconds || 0));
      const companyBatchSizes = Array.isArray(metric?.companyBatchSizes)
        ? metric.companyBatchSizes.map((value) => Math.max(0, Number(value || 0)))
        : [];
      const previewBatches = Array.isArray(metric?.previewBatches)
        ? metric.previewBatches.map((batchEmails) =>
            (Array.isArray(batchEmails) ? batchEmails : []).map((email) => normalizeEmail(email)).filter(Boolean)
          )
        : [];
      const buildModePreview = (modeKey) => {
        const durationSeconds = resolveDurationForStep(
          modeKey,
          fastDurationSeconds,
          modeKey === "custom" ? targetDurationSeconds : 0
        );
        const scheduleAvailabilityMultiplier = 1;
        const scheduleAdjustedDurationSeconds = durationSeconds;
        const gapMinutes =
          leadsRemaining > 0
            ? Number(
                (
                  scheduleAdjustedDurationSeconds /
                  60 /
                  Math.max(1, getSendIntervalsCount(leadsRemaining))
                ).toFixed(4)
              )
            : 0;
        return {
          ...buildSpeedPreviewForInputs({
            selectedSchedule,
            endFridayAtNoon,
            scheduleBypassEnabled,
            gapMinutesInput: gapMinutes,
            leadsRemainingInput: leadsRemaining,
          }),
          mode: modeKey,
          durationSeconds,
          scheduleAdjustedDurationSeconds,
          scheduleAvailabilityMultiplier,
          gapMinutes,
        };
      };
      const modePreviews = {
        fast: buildModePreview("fast"),
        medium: buildModePreview("medium"),
        slow: buildModePreview("slow"),
        custom: buildModePreview("custom"),
      };
      const selectedModePreview = modePreviews[speed] || modePreviews.medium;
      const effectiveDurationSeconds = Number(
        selectedModePreview?.scheduleAdjustedDurationSeconds || selectedModePreview?.durationSeconds || 0
      );
      const effectiveMultiplier =
        fastDurationSeconds > 0 ? effectiveDurationSeconds / fastDurationSeconds : 1;
      const interBatchWaitSeconds = Math.max(
        SENDORA_BATCH_MIN_WAIT_SECONDS,
        SENDORA_BATCH_MIN_WAIT_SECONDS * effectiveMultiplier
      );
      const intraBatchGapSeconds = Math.max(
        SENDORA_BATCH_MIN_INTRA_SEND_SECONDS,
        SENDORA_BATCH_MIN_INTRA_SEND_SECONDS * effectiveMultiplier
      );
      let runningOffsetSeconds = 0;
      const batchTimeline = companyBatchSizes.map((batchSize, batchIndex) => {
        const inBatchDurationSeconds = Math.max(0, (Math.max(0, batchSize - 1)) * intraBatchGapSeconds);
        const hasNextBatch = batchIndex < companyBatchSizes.length - 1;
        const gapAfterSeconds = hasNextBatch ? interBatchWaitSeconds : 0;
        const row = {
          batchIndex: batchIndex + 1,
          batchSize,
          emails: previewBatches[batchIndex] || [],
          startsAtOffsetSeconds: runningOffsetSeconds,
          inBatchDurationSeconds,
          gapAfterSeconds,
        };
        runningOffsetSeconds += inBatchDurationSeconds + gapAfterSeconds;
        return row;
      });
      const customScheduleAvailabilityMultiplier = Math.max(
        0.0001,
        Number(modePreviews?.custom?.scheduleAvailabilityMultiplier || 1)
      );
      const minCustomTargetSeconds = Math.max(0, fastDurationSeconds / customScheduleAvailabilityMultiplier);
      map.set(
        orderIndex,
        {
          ...selectedModePreview,
          modePreviews,
          mode: speed,
          companyBatchSizes,
          batchTimeline,
          batchCount: companyBatchSizes.length,
          interBatchWaitSeconds,
          intraBatchGapSeconds,
          activeDurationSeconds: runningOffsetSeconds,
          fastDurationSeconds,
          effectiveDurationSeconds,
          minCustomTargetSeconds,
          customTargetTooLow:
            speed === "custom" &&
            Number(targetDurationSeconds || 0) > 0 &&
            Number(targetDurationSeconds || 0) < minCustomTargetSeconds,
        }
      );
    });
    return map;
  }, [
    sequenceDraft?.steps,
    runStepMetricsByOrderIndex,
    selectedSchedule,
    scheduleBypassEnabled,
    endFridayAtNoon,
  ]);
  const stepLabelById = useMemo(() => {
    const map = new Map();
    (runStepMetrics || []).forEach((step) => {
      const stepId = String(step?.persistedStepId || step?.stepId || "").trim();
      if (!stepId) return;
      map.set(stepId, `Step ${Number(step?.orderIndex || 0) || 1}`);
    });
    return map;
  }, [runStepMetrics]);
  const monitorUpcomingQueueByStep = useMemo(() => {
    const groupedMap = new Map();
    (monitorUpcomingQueue || []).forEach((item) => {
      const stepId = String(item?.stepId || "").trim() || "__unknown__";
      if (!groupedMap.has(stepId)) groupedMap.set(stepId, []);
      groupedMap.get(stepId).push(item);
    });
    const orderedStepIds = (runStepMetrics || [])
      .map((step) => String(step?.persistedStepId || step?.stepId || "").trim())
      .filter(Boolean);
    const sortedKnown = orderedStepIds.map((stepId) => ({
        stepId,
        label: stepLabelById.get(stepId) || "Step",
        items: groupedMap.get(stepId) || [],
      }));
    const unknownGroups = Array.from(groupedMap.entries())
      .filter(([stepId]) => !orderedStepIds.includes(stepId))
      .map(([stepId, groupedItems]) => ({
        stepId,
        label: stepId === "__unknown__" ? "Unknown step" : stepLabelById.get(stepId) || "Step",
        items: groupedItems || [],
      }));
    return [...sortedKnown, ...unknownGroups];
  }, [monitorUpcomingQueue, runStepMetrics, stepLabelById]);
  const monitorRecentActivityByStep = useMemo(() => {
    const groupedMap = new Map();
    (monitorRecentActivity || []).forEach((item) => {
      const stepId = String(item?.stepId || "").trim() || "__unknown__";
      if (!groupedMap.has(stepId)) groupedMap.set(stepId, []);
      groupedMap.get(stepId).push(item);
    });
    const orderedStepIds = (runStepMetrics || [])
      .map((step) => String(step?.persistedStepId || step?.stepId || "").trim())
      .filter(Boolean);
    const sortedKnown = orderedStepIds.map((stepId) => ({
        stepId,
        label: stepLabelById.get(stepId) || "Step",
        items: groupedMap.get(stepId) || [],
      }));
    const unknownGroups = Array.from(groupedMap.entries())
      .filter(([stepId]) => !orderedStepIds.includes(stepId))
      .map(([stepId, groupedItems]) => ({
        stepId,
        label: stepId === "__unknown__" ? "Unknown step" : stepLabelById.get(stepId) || "Step",
        items: groupedItems || [],
      }));
    return [...sortedKnown, ...unknownGroups];
  }, [monitorRecentActivity, runStepMetrics, stepLabelById]);
  const sequenceVariantLabelMaps = useMemo(() => {
    const byStepId = new Map();
    const byStepOrderIndex = new Map();
    const sourceSteps = Array.isArray(selectedCampaign?.sequences?.steps)
      ? selectedCampaign.sequences.steps
      : Array.isArray(sequenceDraft?.steps)
      ? sequenceDraft.steps
      : [];
    (sourceSteps || []).forEach((step, stepIndex) => {
      const stepId = String(step?.stepId || "").trim();
      const stepOrderIndex = Number(step?.orderIndex || stepIndex + 1) || stepIndex + 1;
      const variantMap = new Map();
      (step?.variants || []).forEach((variant, variantIndex) => {
        const variantId = String(variant?.variantId || "").trim();
        if (!variantId) return;
        const fallbackLabel = `Variant ${String.fromCharCode(65 + Math.max(0, variantIndex))}`;
        const variantLabel = String(variant?.label || "").trim() || fallbackLabel;
        variantMap.set(variantId, variantLabel);
      });
      if (stepId) byStepId.set(stepId, variantMap);
      if (stepOrderIndex > 0) byStepOrderIndex.set(stepOrderIndex, variantMap);
    });
    return { byStepId, byStepOrderIndex };
  }, [selectedCampaign?.sequences?.steps, sequenceDraft?.steps]);
  const memberSentEmailRowsByKey = useMemo(() => {
    const byCanonicalMemberKey = new Map();
    (campaignSentEmails || []).forEach((item) => {
      const status = String(item?.status || "")
        .trim()
        .toLowerCase();
      if (!SENT_MONITOR_STATUSES.has(status)) return;
      const memberId = String(item?.memberId || "").trim();
      const memberEmail = normalizeEmail(item?.memberEmail || "");
      const canonicalMemberKey = memberId ? `id:${memberId}` : memberEmail ? `email:${memberEmail}` : "";
      if (!canonicalMemberKey) return;
      const stepId = String(item?.stepId || "").trim();
      const stepOrderIndex = Number(item?.stepOrderIndex || 0);
      const variantId = String(item?.variantId || "").trim();
      const sentEpoch = Number(item?.sentTime || item?.updatedTime || item?.scheduledTime || 0) || 0;
      const stepLabel =
        stepLabelById.get(stepId) || (stepOrderIndex > 0 ? `Step ${stepOrderIndex}` : "Step");
      const stepVariantMap =
        sequenceVariantLabelMaps.byStepId.get(stepId) ||
        sequenceVariantLabelMaps.byStepOrderIndex.get(stepOrderIndex) ||
        null;
      const variantLabel = variantId
        ? stepVariantMap?.get(variantId) || `Variant ${variantId.slice(0, 8)}`
        : "-";
      const payload = {
        ...item,
        memberId,
        memberEmail,
        stepId,
        stepOrderIndex,
        sentEpoch,
        stepLabel,
        variantLabel,
      };
      const existingRows = byCanonicalMemberKey.get(canonicalMemberKey) || [];
      existingRows.push(payload);
      byCanonicalMemberKey.set(canonicalMemberKey, existingRows);
    });
    const byMemberKey = new Map();
    byCanonicalMemberKey.forEach((rows, canonicalKey) => {
      const orderedRows = [...rows].sort((a, b) => Number(b?.sentEpoch || 0) - Number(a?.sentEpoch || 0));
      const withCountRows = orderedRows.map((row) => ({ ...row, sentCount: orderedRows.length }));
      byMemberKey.set(canonicalKey, withCountRows);
      const first = withCountRows[0] || {};
      const memberId = String(first?.memberId || "").trim();
      const memberEmail = normalizeEmail(first?.memberEmail || "");
      if (memberId) byMemberKey.set(`id:${memberId}`, withCountRows);
      if (memberEmail) byMemberKey.set(`email:${memberEmail}`, withCountRows);
    });
    return byMemberKey;
  }, [campaignSentEmails, stepLabelById, sequenceVariantLabelMaps]);
  const selectedVariantHistoryKey = useMemo(
    () =>
      `${selectedCampaignId}:${selectedStepIndex}:${selectedVariantIndex}:${
        selectedVariant?.variantId || ""
      }`,
    [selectedCampaignId, selectedStepIndex, selectedVariantIndex, selectedVariant?.variantId]
  );
  useEffect(() => {
    if (!ENABLE_MULTI_STEP_SEQUENCES && selectedStepIndex !== 0) {
      setSelectedStepIndex(0);
      setSelectedVariantIndex(0);
    }
  }, [selectedStepIndex]);
  useEffect(() => {
    const normalized = normalizeRichEditorHtml(selectedVariant?.body || "");
    bodyHistoryRef.current = {
      key: selectedVariantHistoryKey,
      stack: [normalized],
      index: 0,
    };
  }, [selectedVariantHistoryKey]);
  useEffect(() => {
    setDeliverabilitySuggestions([]);
    setDeliverabilitySuggestionSelections({});
    setDeliverabilityAppliedSuggestionKeys({});
    setDeliverabilitySuggestionsCollapsed(false);
  }, [selectedVariantHistoryKey]);
  const deliverabilityHasPendingSelections = useMemo(
    () =>
      deliverabilitySuggestions.some((item, index) => {
        const key = `${index}:${item}`;
        return !!deliverabilitySuggestionSelections[key] && !deliverabilityAppliedSuggestionKeys[key];
      }),
    [deliverabilitySuggestions, deliverabilitySuggestionSelections, deliverabilityAppliedSuggestionKeys]
  );
  const deliverabilityHasAnyAppliedSuggestions = useMemo(
    () => Object.values(deliverabilityAppliedSuggestionKeys).some(Boolean),
    [deliverabilityAppliedSuggestionKeys]
  );
  const selectedVariantLabel = useMemo(
    () => String.fromCharCode(65 + Math.max(0, Number(selectedVariantIndex) || 0)),
    [selectedVariantIndex]
  );
  const variantModalVariant = useMemo(() => {
    if (!variantSenderModal.isOpen) return null;
    return (
      sequenceDraft?.steps?.[variantSenderModal.stepIndex]?.variants?.[variantSenderModal.variantIndex] ||
      null
    );
  }, [variantSenderModal, sequenceDraft]);
  const senderAccountByEmail = useMemo(() => {
    const map = new Map();
    (senderAccounts || []).forEach((account) => {
      const key = normalizeEmail(account?.emailLowercase || account?.email || "");
      if (!key) return;
      map.set(key, account);
    });
    return map;
  }, [senderAccounts]);
  const senderSignaturePreferenceByEmail = useMemo(() => {
    const map = new Map();
    (senderSignaturePreferences || []).forEach((entry) => {
      const normalized = normalizeSenderSignaturePreference(entry);
      if (!normalized?.senderEmailLowercase) return;
      map.set(normalized.senderEmailLowercase, normalized);
    });
    return map;
  }, [senderSignaturePreferences]);
  const recipientNameByEmail = useMemo(() => {
    const map = new Map();
    const sourceMembers =
      Array.isArray(salesforceFilteredAllMembers) && salesforceFilteredAllMembers.length
        ? salesforceFilteredAllMembers
        : salesforceFilteredMembers;
    (sourceMembers || []).forEach((member) => {
      const email = normalizeEmail(member?.email || "");
      if (!email) return;
      const fullName = String(member?.fullName || "").trim();
      if (fullName) {
        map.set(email, fullName);
      }
    });
    return map;
  }, [salesforceFilteredAllMembers, salesforceFilteredMembers]);
  const senderNameByEmail = useMemo(() => {
    const map = new Map();
    const addAccount = (account = {}) => {
      const email = normalizeEmail(account?.emailLowercase || account?.email || "");
      if (!email) return;
      const firstName = String(account?.firstName || "").trim();
      const lastName = String(account?.lastName || "").trim();
      const fullName = `${firstName} ${lastName}`.trim() || String(account?.fullName || "").trim();
      if (fullName && !map.has(email)) {
        map.set(email, fullName);
      }
    };
    (senderAccounts || []).forEach(addAccount);
    normalizeSenderAccounts(
      sequenceDraft?.senderAccounts || [],
      sequenceDraft?.senderAccountEmails || []
    ).forEach(addAccount);
    return map;
  }, [senderAccounts, sequenceDraft?.senderAccounts, sequenceDraft?.senderAccountEmails]);
  const monitorPopupRecipientName = useMemo(() => {
    const email = normalizeEmail(selectedMonitorEmailPreview?.memberEmail || "");
    if (!email) return "";
    return String(recipientNameByEmail.get(email) || "").trim();
  }, [selectedMonitorEmailPreview?.memberEmail, recipientNameByEmail]);
  const monitorPopupSenderName = useMemo(() => {
    const email = normalizeEmail(selectedMonitorEmailPreview?.fromEmail || "");
    const fallbackName = String(selectedMonitorEmailPreview?.fromName || "").trim();
    if (!email) return fallbackName;
    return String(senderNameByEmail.get(email) || fallbackName || "").trim();
  }, [selectedMonitorEmailPreview?.fromEmail, selectedMonitorEmailPreview?.fromName, senderNameByEmail]);
  const sequenceSenderAccounts = useMemo(() => {
    const normalized = normalizeSenderAccounts(
      sequenceDraft?.senderAccounts || [],
      sequenceDraft?.senderAccountEmails || []
    );
    return normalized.map((entry) => {
      const matched = senderAccountByEmail.get(entry.emailLowercase) || entry;
      const preference = senderSignaturePreferenceByEmail.get(entry.emailLowercase) || null;
      return {
        ...matched,
        senderSignatureHtml: normalizeSenderSignatureHtml(
          matched?.senderSignatureHtml || resolveSenderSignatureHtmlFromPreference(preference)
        ),
      };
    });
  }, [sequenceDraft, senderAccountByEmail, senderSignaturePreferenceByEmail]);
  const sequenceSenderAccountEmailSet = useMemo(
    () =>
      new Set(
        (sequenceSenderAccounts || [])
          .map((entry) => normalizeEmail(entry?.emailLowercase || entry?.email || ""))
          .filter(Boolean)
      ),
    [sequenceSenderAccounts]
  );
  const hasSequenceSenderAccounts = sequenceSenderAccounts.length > 0;
  const signatureModalSenderAccounts = useMemo(
    () =>
      (sequenceSenderAccounts || []).filter((entry) =>
        normalizeEmail(entry?.emailLowercase || entry?.email || "")
      ),
    [sequenceSenderAccounts]
  );
  const selectedSignatureSenderAccount = useMemo(() => {
    const normalizedEmail = normalizeEmail(selectedSignatureSenderEmail || "");
    if (!normalizedEmail) return null;
    return (
      signatureModalSenderAccounts.find(
        (entry) => normalizeEmail(entry?.emailLowercase || entry?.email || "") === normalizedEmail
      ) || null
    );
  }, [selectedSignatureSenderEmail, signatureModalSenderAccounts]);
  const selectedSignatureSenderPreference = useMemo(() => {
    const normalizedEmail = normalizeEmail(selectedSignatureSenderEmail || "");
    if (!normalizedEmail) return null;
    const draftPreference = normalizeSenderSignaturePreference(
      senderSignatureDraftByEmail?.[normalizedEmail] || null
    );
    if (draftPreference) return draftPreference;
    return (
      senderSignaturePreferenceByEmail.get(normalizedEmail) ||
      createDefaultSenderSignaturePreference(normalizedEmail)
    );
  }, [
    selectedSignatureSenderEmail,
    senderSignaturePreferenceByEmail,
    senderSignatureDraftByEmail,
  ]);
  const selectedSignatureCustomSignatures = useMemo(
    () =>
      Array.isArray(selectedSignatureSenderPreference?.customSignatures)
        ? selectedSignatureSenderPreference.customSignatures
        : [],
    [selectedSignatureSenderPreference]
  );
  const selectedSignatureIdForSender = useMemo(
    () => String(selectedSignatureSenderPreference?.selectedSignatureId || "default").trim() || "default",
    [selectedSignatureSenderPreference]
  );
  const hasSignatureModalChanges = useMemo(() => {
    if (!isSenderSignatureModalOpen) return false;
    const allEmails = new Set([
      ...Object.keys(senderSignatureInitialByEmail || {}),
      ...Object.keys(senderSignatureDraftByEmail || {}),
    ]);
    for (const email of allEmails) {
      const left = serializeSenderSignaturePreference(senderSignatureInitialByEmail?.[email] || null);
      const right = serializeSenderSignaturePreference(senderSignatureDraftByEmail?.[email] || null);
      if (left !== right) return true;
    }
    return false;
  }, [
    isSenderSignatureModalOpen,
    senderSignatureInitialByEmail,
    senderSignatureDraftByEmail,
  ]);
  const campaignBrandVoiceCustomVoices = useMemo(
    () => normalizeCustomBrandVoices(selectedCampaign?.options?.brandVoiceCustomVoices || []),
    [selectedCampaign?.options?.brandVoiceCustomVoices]
  );
  const campaignBrandVoiceLibrary = useMemo(
    () => buildBrandVoiceLibrary(campaignBrandVoiceCustomVoices),
    [campaignBrandVoiceCustomVoices]
  );
  const selectedCampaignBrandVoiceId = useMemo(
    () =>
      resolveSelectedBrandVoiceId(
        selectedCampaign?.options?.selectedBrandVoiceId || "",
        campaignBrandVoiceLibrary
      ),
    [selectedCampaign?.options?.selectedBrandVoiceId, campaignBrandVoiceLibrary]
  );
  const selectedCampaignBrandVoice = useMemo(
    () =>
      campaignBrandVoiceLibrary.find(
        (entry) => String(entry?.brandVoiceId || "").trim() === selectedCampaignBrandVoiceId
      ) || null,
    [campaignBrandVoiceLibrary, selectedCampaignBrandVoiceId]
  );
  const modalBrandVoiceLibrary = useMemo(
    () => buildBrandVoiceLibrary(brandVoiceCustomDraft),
    [brandVoiceCustomDraft]
  );
  const selectedModalBrandVoiceId = useMemo(
    () => resolveSelectedBrandVoiceId(brandVoiceSelectedIdDraft || "", modalBrandVoiceLibrary),
    [brandVoiceSelectedIdDraft, modalBrandVoiceLibrary]
  );
  const selectedModalBrandVoice = useMemo(
    () =>
      modalBrandVoiceLibrary.find(
        (entry) => String(entry?.brandVoiceId || "").trim() === selectedModalBrandVoiceId
      ) || null,
    [modalBrandVoiceLibrary, selectedModalBrandVoiceId]
  );
  const selectedModalBrandVoiceIsTemplate = selectedModalBrandVoice?.sourceType === "template";
  const hasBrandVoiceModalChanges = useMemo(() => {
    if (!isBrandVoiceModalOpen) return false;
    return (
      String(brandVoiceSelectedIdDraft || "").trim() !== String(brandVoiceSelectedIdInitial || "").trim() ||
      serializeCustomBrandVoices(brandVoiceCustomDraft) !== serializeCustomBrandVoices(brandVoiceCustomInitial)
    );
  }, [
    isBrandVoiceModalOpen,
    brandVoiceSelectedIdDraft,
    brandVoiceSelectedIdInitial,
    brandVoiceCustomDraft,
    brandVoiceCustomInitial,
  ]);
  const normalizedCampaignAiVariables = useMemo(
    () => normalizeCampaignAiVariables(campaignAiVariablesDraft || []),
    [campaignAiVariablesDraft]
  );
  const selectedAiVariableDraft = useMemo(() => {
    const tokenKey = String(selectedAiVariableTokenKey || "").trim().toLowerCase();
    if (!tokenKey) return null;
    return (
      normalizedCampaignAiVariables.find(
        (entry) => String(entry?.tokenKey || "").trim().toLowerCase() === tokenKey
      ) || null
    );
  }, [normalizedCampaignAiVariables, selectedAiVariableTokenKey]);
  const hasAiVariablesModalChanges = useMemo(() => {
    if (!isAiVariablesModalOpen) return false;
    return (
      serializeCampaignAiVariables(campaignAiVariablesInitial || []) !==
      serializeCampaignAiVariables(campaignAiVariablesDraft || [])
    );
  }, [isAiVariablesModalOpen, campaignAiVariablesInitial, campaignAiVariablesDraft]);
  useEffect(() => {
    if (!isSenderSignatureModalOpen) return;
    const availableEmails = (signatureModalSenderAccounts || [])
      .map((entry) => normalizeEmail(entry?.emailLowercase || entry?.email || ""))
      .filter(Boolean);
    if (!availableEmails.length) {
      if (selectedSignatureSenderEmail) setSelectedSignatureSenderEmail("");
      return;
    }
    if (!availableEmails.includes(normalizeEmail(selectedSignatureSenderEmail || ""))) {
      setSelectedSignatureSenderEmail(availableEmails[0]);
    }
  }, [
    isSenderSignatureModalOpen,
    signatureModalSenderAccounts,
    selectedSignatureSenderEmail,
  ]);
  useEffect(() => {
    if (!isAiVariablesModalOpen) return;
    const tokenKeys = normalizeCampaignAiVariables(campaignAiVariablesDraft || [])
      .map((entry) => String(entry?.tokenKey || "").trim())
      .filter(Boolean);
    if (!tokenKeys.length) {
      setSelectedAiVariableTokenKey("aiVariable1");
      return;
    }
    const normalizedSelected = String(selectedAiVariableTokenKey || "").trim().toLowerCase();
    const hasSelected = tokenKeys.some(
      (tokenKey) => String(tokenKey || "").trim().toLowerCase() === normalizedSelected
    );
    if (!hasSelected) {
      setSelectedAiVariableTokenKey(tokenKeys[0]);
    }
  }, [isAiVariablesModalOpen, campaignAiVariablesDraft, selectedAiVariableTokenKey]);
  const isEmailEditorLocked = !hasSequenceSenderAccounts;
  const isSequenceAiToolbarLoading =
    autoGeneratingSpintax ||
    cleaningHtml ||
    fixingFormatting ||
    applyingCustomPrompt ||
    scanningDeliverability ||
    applyingDeliverabilitySuggestions;
  const selectedVariantHasSubject = useMemo(
    () => !!String(selectedVariant?.subject || "").trim(),
    [selectedVariant?.subject]
  );
  const hasMeaningfulVariantBody = (bodyValue = "") => {
    const normalizedBodyHtml = normalizeBodyToHtml(bodyValue || "");
    const withoutHighlights = stripVariableHighlightMarkup(normalizedBodyHtml);
    const plainText = String(withoutHighlights || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
    return !!plainText;
  };
  const selectedVariantHasBody = useMemo(
    () => hasMeaningfulVariantBody(selectedVariant?.body || ""),
    [selectedVariant?.body]
  );
  const hasAnyEmailVariantReady = useMemo(() => {
    return (sequenceDraft?.steps || []).some((step) =>
      (step?.variants || []).some((variant) => {
        const hasSubject = !!String(variant?.subject || "").trim();
        const hasBody = hasMeaningfulVariantBody(variant?.body || "");
        return hasSubject && hasBody;
      })
    );
  }, [sequenceDraft?.steps]);
  const canProgressBeyondEmails = useMemo(
    () => hasSequenceSenderAccounts && (selectedVariantHasSubject && selectedVariantHasBody || hasAnyEmailVariantReady),
    [
      hasSequenceSenderAccounts,
      selectedVariantHasSubject,
      selectedVariantHasBody,
      hasAnyEmailVariantReady,
    ]
  );
  const previewLeadOptions = useMemo(() => {
    const allMembersForPreview = Array.isArray(salesforceFilteredAllMembers)
      ? salesforceFilteredAllMembers
      : [];
    const isOttPreviewStep =
      Number(selectedStepIndex) > 0 &&
      String(selectedStep?.mode || "").trim().toLowerCase() === "ott";
    const ottSourceStepId = String(selectedStep?.ottSourceStepId || "").trim();
    if (!isOttPreviewStep || !ottSourceStepId) {
      return allMembersForPreview.slice(0, 15);
    }
    const memberById = new Map(
      allMembersForPreview
        .map((member) => [String(member?._id || "").trim(), member])
        .filter(([memberId]) => !!memberId)
    );
    const orderedWithOttHistory = [];
    const seenMemberIds = new Set();
    (campaignSentEmails || []).forEach((sentItem) => {
      if (String(sentItem?.stepId || "").trim() !== ottSourceStepId) return;
      const memberId = String(sentItem?.memberId || "").trim();
      if (!memberId || seenMemberIds.has(memberId)) return;
      const member = memberById.get(memberId);
      if (!member) return;
      orderedWithOttHistory.push(member);
      seenMemberIds.add(memberId);
    });
    if (orderedWithOttHistory.length) {
      return orderedWithOttHistory.slice(0, 15);
    }
    return allMembersForPreview.slice(0, 15);
  }, [salesforceFilteredAllMembers, campaignSentEmails, selectedStep, selectedStepIndex]);
  const previewSelectedLead = useMemo(
    () => previewLeadOptions.find((member) => member._id === previewForm.leadId) || null,
    [previewLeadOptions, previewForm.leadId]
  );
  useEffect(() => {
    if (!isPreviewModalOpen) return;
    const currentLeadId = String(previewForm?.leadId || "");
    const hasCurrentLead = previewLeadOptions.some(
      (member) => String(member?._id || "") === currentLeadId
    );
    if (hasCurrentLead) return;
    const fallbackLeadId = String(previewLeadOptions[0]?._id || "");
    setPreviewForm((prev) => {
      if (String(prev?.leadId || "") === fallbackLeadId) return prev;
      return { ...prev, leadId: fallbackLeadId };
    });
  }, [isPreviewModalOpen, previewForm?.leadId, previewLeadOptions]);
  const employeeDefaultEmail = useMemo(
    () => employee?.emailLowercase || employee?.email || "",
    [employee]
  );
  const previewSenderOptions = useMemo(() => {
    const variantOverride = normalizeEmail(
      selectedVariant?.senderAccount?.emailLowercase ||
        selectedVariant?.senderAccount?.email ||
        selectedVariant?.senderAccountEmail ||
        ""
    );
    if (variantOverride) return [variantOverride];
    const sequenceEmails = sequenceSenderAccounts.map((account) =>
      normalizeEmail(account?.emailLowercase || account?.email || "")
    );
    const fallback = defaultSenderAccountEmail || employeeDefaultEmail;
    return Array.from(new Set([...sequenceEmails, fallback].filter(Boolean)));
  }, [selectedVariant, sequenceSenderAccounts, defaultSenderAccountEmail, employeeDefaultEmail]);
  const previewRecipientOptions = useMemo(() => {
    const normalizedEmployeeDefault = normalizeLandmarkEmail(employeeDefaultEmail || "");
    const all = [
      normalizedEmployeeDefault,
      ...(employeeEmails || []).map((email) => normalizeLandmarkEmail(email)),
    ].filter(Boolean);
    return Array.from(new Set(all));
  }, [employeeDefaultEmail, employeeEmails]);
  const previewRecipientPreferenceStorageKey = useMemo(() => {
    const userKey = normalizeEmail(
      employee?.employeeId || employee?.emailLowercase || employee?.email || ""
    );
    if (!userKey) return "";
    return `${PREVIEW_RECIPIENT_PREF_STORAGE_KEY_PREFIX}${userKey}`;
  }, [employee?.employeeId, employee?.emailLowercase, employee?.email]);
  const isCustomPreviewRecipient = previewForm.sendTo === PREVIEW_RECIPIENT_CUSTOM_VALUE;
  const resolvedPreviewSendTo = isCustomPreviewRecipient
    ? String(previewForm.customSendTo || "").trim()
    : String(previewForm.sendTo || "").trim();
  const previewSendDisabledReason = (() => {
    if (!String(previewForm.sendFrom || "").trim()) return "Select a sender email first.";
    if (isCustomPreviewRecipient) {
      const customEmail = String(previewForm.customSendTo || "").trim();
      if (!customEmail) return "Enter a custom recipient email.";
      if (!isValidEmailAddress(customEmail)) return "Enter a valid custom recipient email.";
    }
    if (!resolvedPreviewSendTo) return "Enter a recipient email.";
    return "";
  })();
  const canSendPreviewTestEmail = !sendingTestEmail && !previewSendDisabledReason;
  const previewSenderAccount = useMemo(
    () => senderAccountByEmail.get(normalizeEmail(previewForm.sendFrom || "")) || null,
    [senderAccountByEmail, previewForm.sendFrom]
  );
  useEffect(() => {
    if (!isPreviewModalOpen || !previewRecipientPreferenceStorageKey) return;
    try {
      window.localStorage.setItem(
        previewRecipientPreferenceStorageKey,
        JSON.stringify({
          sendTo: String(previewForm.sendTo || ""),
          customSendTo: String(previewForm.customSendTo || ""),
        })
      );
    } catch (error) {
      // Ignore storage write failures.
    }
  }, [
    isPreviewModalOpen,
    previewRecipientPreferenceStorageKey,
    previewForm.sendTo,
    previewForm.customSendTo,
  ]);
  const mergedPreviewBodyTemplate = useMemo(() => {
    const normalizedBodyHtml = normalizeBodyToHtml(selectedVariant?.body || "");
    return applyMergeTokens(
      normalizedBodyHtml,
      previewSelectedLead || {},
      previewSenderAccount || employee || {}
    );
  }, [selectedVariant, previewSelectedLead, previewSenderAccount, employee]);
  const mergedPreviewSubjectTemplate = useMemo(
    () =>
      applyMergeTokens(
        String(selectedVariant?.subject || ""),
        previewSelectedLead || {},
        previewSenderAccount || employee || {}
      ),
    [selectedVariant, previewSelectedLead, previewSenderAccount, employee]
  );
  const resolvePreviewSpintaxBySeed = (seed) => {
    const resolvedBody = applySpintaxPreviewVariants(mergedPreviewBodyTemplate, seed, {
      flashSelection: false,
    });
    const resolvedSubject = applySpintaxPreviewVariants(mergedPreviewSubjectTemplate, seed, {
      flashSelection: false,
    });
    return {
      body: resolvedBody,
      subject: resolvedSubject,
      signature: `${resolvedSubject}||${resolvedBody}`,
    };
  };
  const currentPreviewSpintaxSignature = useMemo(
    () => resolvePreviewSpintaxBySeed(previewSpintaxSeed).signature,
    [previewSpintaxSeed, mergedPreviewBodyTemplate, mergedPreviewSubjectTemplate]
  );
  const hasPreviewSpintax = useMemo(() => {
    const spintaxPattern = /\{[^{}]*\|[^{}]*\}/;
    return (
      spintaxPattern.test(String(mergedPreviewBodyTemplate || "")) ||
      spintaxPattern.test(String(mergedPreviewSubjectTemplate || ""))
    );
  }, [mergedPreviewBodyTemplate, mergedPreviewSubjectTemplate]);
  const previewRenderedSubject = String(previewRenderState?.subject || "").trim();
  const previewRenderedBodyHtml = String(previewRenderState?.html || "");
  const previewRenderedError = String(previewRenderState?.error || "");
  const displaySubject = previewSpintaxDisplayOverride?.subject ?? previewRenderedSubject;
  const displayBodyHtml = previewSpintaxDisplayOverride?.html ?? previewRenderedBodyHtml;
  const previewCampaignId = String(selectedCampaign?.campaignId || "").trim();
  useEffect(() => {
    if (!isPreviewModalOpen || !employee || !previewCampaignId) return;
    const campaignId = previewCampaignId;
    const sendFromEmail = String(previewForm?.sendFrom || "").trim();
    if (!campaignId || !sendFromEmail) {
      setPreviewRenderState((prev) => ({
        ...prev,
        loading: false,
        subject: "",
        html: "",
        text: "",
        error: "",
      }));
      return;
    }
    let cancelled = false;
    setPreviewSpintaxDisplayOverride(null);
    setPreviewRenderState((prev) => ({ ...prev, loading: true, error: "" }));
    const api = new ApiService({ employee });
    api
      .post("landmark/sendora/renderEmailPreview", {
        campaignId,
        sendFromEmail,
        sendFromFirstName: String(previewSenderAccount?.firstName || "").trim(),
        sendFromLastName: String(previewSenderAccount?.lastName || "").trim(),
        leadId: String(previewForm?.leadId || "").trim(),
        previewStepId: String(selectedStep?.stepId || "").trim(),
        previewStepMode: String(selectedStep?.mode || "").trim(),
        previewOttSourceStepId: String(selectedStep?.ottSourceStepId || "").trim(),
        subjectTemplate: selectedVariant?.subject || "",
        bodyTemplate: selectedVariant?.body || "",
        spintaxSeed: previewSpintaxSeed,
      })
      .then((response) => {
        if (cancelled) return;
        if (response?.error) {
          setPreviewRenderState((prev) => ({
            ...prev,
            loading: false,
            subject: "",
            html: "",
            text: "",
            error: String(response.error || "Unable to render preview"),
          }));
          return;
        }
        setPreviewRenderState({
          loading: false,
          subject: String(response?.subject || ""),
          html: String(response?.html || ""),
          text: String(response?.text || ""),
          error: "",
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.log("Sendora preview render error", error);
        setPreviewRenderState((prev) => ({
          ...prev,
          loading: false,
          subject: "",
          html: "",
          text: "",
          error: "Unable to render preview",
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [
    isPreviewModalOpen,
    employee,
    previewCampaignId,
    previewForm?.sendFrom,
    previewForm?.leadId,
    selectedStep?.stepId,
    selectedStep?.mode,
    selectedStep?.ottSourceStepId,
    selectedVariant?.subject,
    selectedVariant?.body,
    previewSenderAccount?.firstName,
    previewSenderAccount?.lastName,
  ]);
  const previewDiceIcons = useMemo(
    () => [DiceOneIcon, DiceTwoIcon, DiceThreeIcon, DiceFourIcon, DiceFiveIcon, DiceSixIcon],
    []
  );
  const PreviewDiceIcon = previewDiceIcons[previewDiceFaceIndex] || DiceFiveIcon;
  useEffect(() => {
    return () => {
      if (previewDiceRollTimerRef.current) {
        clearTimeout(previewDiceRollTimerRef.current);
      }
      if (previewDiceSwapTimerRef.current) {
        clearTimeout(previewDiceSwapTimerRef.current);
      }
    };
  }, []);
  const previewDocumentHtml = useMemo(
    () => buildEmailPreviewDocument(displayBodyHtml),
    [displayBodyHtml]
  );
  const variableFieldOptions = useMemo(
    () => [...RECIPIENT_VARIABLE_TOKENS],
    []
  );
  const senderVariableOptions = useMemo(
    () => [...SENDER_VARIABLE_TOKENS],
    []
  );
  const aiVariableOptions = useMemo(
    () =>
      normalizeCampaignAiVariables(campaignAiVariablesDraft || [])
        .filter((entry) => entry?.isActive !== false)
        .map((entry) => String(entry?.tokenKey || "").trim())
        .filter(Boolean),
    [campaignAiVariablesDraft]
  );
  const ottEmailVariableOptions = useMemo(() => [...OTT_EMAIL_VARIABLE_TOKENS], []);
  const ottSenderVariableOptions = useMemo(
    () =>
      senderVariableOptions.map((fieldName) => {
        const trimmed = String(fieldName || "").trim();
        if (!trimmed) return "";
        const suffix = trimmed.replace(/^sender/i, "");
        return `previousSender${suffix}`;
      }).filter(Boolean),
    [senderVariableOptions]
  );
  const isSenderAccountFormComplete = useMemo(() => {
    const email = normalizeEmail(senderAccountForm?.email || "");
    const firstName = String(senderAccountForm?.firstName || "").trim();
    const lastName = String(senderAccountForm?.lastName || "").trim();
    return !!email && email.includes("@") && !!firstName && !!lastName;
  }, [senderAccountForm]);
  const sendingInfrastructureOptionsForUi = useMemo(() => {
    const fallback = SENDORA_SENDING_INFRASTRUCTURES;
    const list = (Array.isArray(sendingInfrastructureOptions) ? sendingInfrastructureOptions : [])
      .map((entry) => ({
        key: normalizeInfrastructureKey(entry?.key || ""),
        label: String(entry?.label || "").trim(),
      }))
      .filter((entry) => !!entry.key)
      .map((entry) => ({
        ...entry,
        label: entry.label || fallback.find((candidate) => candidate.key === entry.key)?.label || entry.key,
      }));
    return list.length ? list : fallback;
  }, [sendingInfrastructureOptions]);
  const senderDomainSuffixesInAccounts = useMemo(
    () =>
      Array.from(
        new Set(
          (senderAccounts || [])
            .map((entry) => getDomainSuffixFromEmail(entry?.emailLowercase || entry?.email || ""))
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [senderAccounts]
  );
  const domainInfrastructureRulesForUi = useMemo(() => {
    const existingRules = normalizeDomainInfrastructureRules(domainInfrastructureRules || []);
    const ruleMap = new Map(existingRules.map((entry) => [entry.domainSuffix, entry]));
    senderDomainSuffixesInAccounts.forEach((domainSuffix) => {
      if (ruleMap.has(domainSuffix)) return;
      const fallbackRule = normalizeDomainInfrastructureRule({ domainSuffix });
      if (!fallbackRule) return;
      ruleMap.set(domainSuffix, fallbackRule);
    });
    return Array.from(ruleMap.values()).sort((a, b) => {
      const left = normalizeDomainSuffix(a?.domainSuffix || "");
      const right = normalizeDomainSuffix(b?.domainSuffix || "");
      const leftPriority = SENDORA_DOMAIN_DISPLAY_ORDER.indexOf(left);
      const rightPriority = SENDORA_DOMAIN_DISPLAY_ORDER.indexOf(right);
      const leftHasPriority = leftPriority >= 0;
      const rightHasPriority = rightPriority >= 0;
      if (leftHasPriority && rightHasPriority) return leftPriority - rightPriority;
      if (leftHasPriority) return -1;
      if (rightHasPriority) return 1;
      return left.localeCompare(right);
    });
  }, [domainInfrastructureRules, senderDomainSuffixesInAccounts]);

  const todayDateInput = useMemo(
    () => epochToDateInput(Math.floor(Date.now() / 1000)),
    []
  );

  const timezoneOptions = useMemo(() => {
    const currentTimezone = selectedSchedule?.timezone || "";
    if (!currentTimezone) return TIMEZONE_OPTIONS;
    const hasCurrent = TIMEZONE_OPTIONS.some((tz) => tz.value === currentTimezone);
    return hasCurrent
      ? TIMEZONE_OPTIONS
      : [{ value: currentTimezone, label: currentTimezone }, ...TIMEZONE_OPTIONS];
  }, [selectedSchedule]);
  const selectedCampaignSpeed = normalizeCampaignSpeed(editForm.campaignPriority || "medium");
  const effectiveDailyLimit = Math.max(1, toSafeNumber(editForm.dailyLimit, 5000));
  const gapBetweenEmailsLabel = useMemo(
    () => formatGapBetweenEmailsLabel(editForm.minTimeGapMinutes),
    [editForm.minTimeGapMinutes]
  );

  useEffect(() => {
    const computedGap = computeMinGapMinutesForSpeed(
      selectedCampaignSpeed,
      selectedSchedule,
      effectiveDailyLimit,
      scheduleBypassEnabled
    );
    setEditForm((prev) => ({
      ...prev,
      campaignPriority: selectedCampaignSpeed,
      minTimeGapMinutes: computedGap,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCampaignSpeed,
    selectedSchedule?.fromTime,
    selectedSchedule?.toTime,
    scheduleBypassEnabled,
    effectiveDailyLimit,
  ]);

  const speedPreview = useMemo(() => {
    const leadsRemaining = Math.max(
      0,
      toSafeNumber(unsentLeadCount, toSafeNumber(selectedCampaignStats.includedLeads, 0))
    );
    return buildSpeedPreviewForInputs({
      selectedSchedule,
      endFridayAtNoon,
      scheduleBypassEnabled,
      gapMinutesInput: editForm.minTimeGapMinutes,
      leadsRemainingInput: leadsRemaining,
    });
  }, [
    scheduleBypassEnabled,
    selectedSchedule,
    endFridayAtNoon,
    editForm.minTimeGapMinutes,
    selectedCampaignStats.includedLeads,
    unsentLeadCount,
  ]);
  const openEstimatedCompletionTimelineModal = async ({
    title = "Estimated completion timeline",
    leadsRemaining = 0,
    gapMinutes = 0,
    completionTimeLabel = "",
    stepId = "",
    stepPatch = null,
  } = {}) => {
    setEstimatedCompletionTimelineModal({
      isOpen: true,
      loading: true,
      title: String(title || "Estimated completion timeline"),
      timezone: "UTC",
      hasScheduleWindow: true,
      rows: [],
      leadsRemaining: Math.max(0, Number(leadsRemaining || 0)),
      completionTimeLabel: String(completionTimeLabel || ""),
    });
    const normalizedStepId = String(stepId || "").trim();
    if (employee && selectedCampaign?.campaignId && normalizedStepId) {
      try {
        const api = new ApiService({ employee });
        const response = await api.post(
          `landmark/sendora/getCampaignStepSchedulePreview/${selectedCampaign.campaignId}/${normalizedStepId}`,
          {
            step:
              stepPatch && typeof stepPatch === "object"
                ? {
                    speed: normalizeStepSpeed(stepPatch?.speed || ""),
                    speedMode: normalizeStepSpeed(stepPatch?.speedMode || stepPatch?.speed || ""),
                    targetDurationSeconds: Math.max(0, Number(stepPatch?.targetDurationSeconds || 0)),
                  }
                : {},
          }
        );
        if (!response?.error && response?.summary) {
          const summary = response.summary || {};
          setEstimatedCompletionTimelineModal({
            isOpen: true,
            loading: false,
            title: String(title || "Estimated completion timeline"),
            timezone: String(summary?.timezone || "UTC"),
            hasScheduleWindow: true,
            rows: Array.isArray(summary?.timelineRows)
              ? summary.timelineRows.map((row) => ({
                  dateLabel: String(row?.dateLabel || ""),
                  windowLabel: String(row?.windowLabel || ""),
                  count: Number(row?.count || 0),
                  firstSendLabel: formatCompletionTimeLabel(
                    new Date(Math.max(0, Number(row?.firstScheduledTime || 0)) * 1000),
                    String(summary?.timezone || "UTC")
                  ),
                  lastSendLabel: formatCompletionTimeLabel(
                    new Date(Math.max(0, Number(row?.lastScheduledTime || 0)) * 1000),
                    String(summary?.timezone || "UTC")
                  ),
                }))
              : [],
            leadsRemaining: Math.max(0, Number(summary?.queuedCount || leadsRemaining || 0)),
            completionTimeLabel:
              Number(summary?.lastScheduledTime || 0) > 0
                ? formatCompletionTimeLabel(
                    new Date(Number(summary.lastScheduledTime) * 1000),
                    String(summary?.timezone || "UTC")
                  )
                : String(completionTimeLabel || ""),
          });
          return;
        }
      } catch (error) {
        console.error("Could not load backend schedule timeline preview", error);
      }
    }
    const timeline = buildSendTimelineForInputs({
      selectedSchedule,
      endFridayAtNoon,
      scheduleBypassEnabled,
      gapMinutesInput: gapMinutes,
      leadsRemainingInput: leadsRemaining,
    });
    setEstimatedCompletionTimelineModal({
      isOpen: true,
      loading: false,
      title: String(title || "Estimated completion timeline"),
      timezone: String(timeline?.timezone || "UTC"),
      hasScheduleWindow: timeline?.hasScheduleWindow !== false,
      rows: Array.isArray(timeline?.rows) ? timeline.rows : [],
      leadsRemaining: Math.max(0, Number(leadsRemaining || 0)),
      completionTimeLabel: String(completionTimeLabel || ""),
    });
  };
  const closeEstimatedCompletionTimelineModal = () =>
    setEstimatedCompletionTimelineModal((prev) => ({ ...prev, isOpen: false }));
  const estimatedCompletionTimelineDays = useMemo(() => {
    const groupedDays = [];
    const dayIndexByLabel = new Map();
    (Array.isArray(estimatedCompletionTimelineModal.rows) ? estimatedCompletionTimelineModal.rows : []).forEach(
      (row, index) => {
        const dateLabel = String(row?.dateLabel || "Unknown day");
        const existingIndex = dayIndexByLabel.get(dateLabel);
        if (typeof existingIndex === "number") {
          const existingDay = groupedDays[existingIndex];
          existingDay.rows.push({
            ...row,
            rowIndex: index,
          });
          return;
        }
        dayIndexByLabel.set(dateLabel, groupedDays.length);
        groupedDays.push({
          dateLabel,
          rows: [
            {
              ...row,
              rowIndex: index,
            },
          ],
        });
      }
    );
    return groupedDays;
  }, [estimatedCompletionTimelineModal.rows]);

  const isDefaultStartDate = useMemo(
    () => !String(campaignDateWindow.startDate || "").trim(),
    [campaignDateWindow.startDate]
  );
  const isDefaultEndDate = useMemo(
    () => campaignDateWindow.noEndDate,
    [campaignDateWindow.noEndDate]
  );
  const isUsMainlandEtPtBusinessHours = useMemo(
    () => selectedSchedule?.timezone === US_MAINLAND_ET_PT_BUSINESS_HOURS_TIMEZONE,
    [selectedSchedule?.timezone]
  );

  const openDatePicker = (inputRef) => {
    const input = inputRef?.current;
    if (!input) return;
    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  const resetStartDateToDefault = () => {
    setCampaignDateWindow((prev) => ({
      ...prev,
      startDate: "",
    }));
  };

  const resetEndDateToDefault = () => {
    setCampaignDateWindow((prev) => ({
      ...prev,
      endDate: "",
      noEndDate: true,
    }));
  };

  const activeTabIndex = useMemo(
    () => Math.max(0, DETAIL_TABS.findIndex((tab) => tab.key === activeTab)),
    [activeTab]
  );
  const lastGuidedTabIndex = DETAIL_TABS.length - 1;
  const effectiveMaxReachedTabIndex = canProgressBeyondEmails
    ? lastGuidedTabIndex
    : maxReachedTabIndex;
  const hasCompletedGuidedSteps = canProgressBeyondEmails;
  const queuedUnpausedStepTargets = useMemo(
    () =>
      (runStepMetrics || [])
        .filter(
          (step) =>
            !!step?.hasPersistedStepId &&
            Number(step?.queuedCount || 0) > 0 &&
            !step?.isPaused
        )
        .map((step) => ({
          stepId: step.persistedStepId,
          orderIndex: Number(step?.orderIndex || 0) || 1,
        })),
    [runStepMetrics]
  );
  useEffect(() => {
    setMaxReachedTabIndex((prev) => Math.max(prev, activeTabIndex));
  }, [activeTabIndex]);
  useEffect(() => {
    if (activeTab !== "speed") return;
    setActiveTab("emails");
  }, [activeTab]);

  const handleSelectDetailTab = (tabKey) => {
    const requestedIndex = DETAIL_TABS.findIndex((tab) => tab.key === tabKey);
    if (requestedIndex < 0) return;
    if (["schedule", "options", "run"].includes(tabKey) && !canProgressBeyondEmails) {
      toast.error(
        "Before continuing, add at least one sending account and make sure the email subject and body are not empty."
      );
      return;
    }
    if (requestedIndex > effectiveMaxReachedTabIndex + 1) return;
    if (activeTab === "emails" && tabKey !== "emails" && isSequenceDirty) {
      const reason = getSequenceDirtyReason(
        currentSequenceSignature,
        String(lastSavedSequenceSignature || "")
      );
      console.error("[Sendora] Unsaved confirm reason", reason);
      const confirmed = window.confirm(
        "You have unsaved email changes. Leave Emails without saving?"
      );
      if (!confirmed) return;
    }
    if (activeTab === "sequences" && tabKey !== "sequences" && isSequenceDefinitionsDirty) {
      const confirmed = window.confirm(
        "You have unsaved sequence changes. Leave Sequences without saving?"
      );
      if (!confirmed) return;
    }
    setActiveTab(tabKey);
  };

  const renderTabControls = () => (
    <div className="sendora-tabs-wrap sendora-tabs-wrap-card">
      <div className="sendora-tabs-row">
        {DETAIL_TABS.map((tab, tabIndex) => (
          (() => {
            const isLockedBySequence = tabIndex > effectiveMaxReachedTabIndex + 1;
            const isLockedByEmailRequirements =
              ["schedule", "options", "run"].includes(tab.key) && !canProgressBeyondEmails;
            const isLocked = isLockedBySequence || isLockedByEmailRequirements;
            return (
          <button
            type="button"
            key={tab.key}
            className={`sendora-tab-btn ${activeTab === tab.key ? "is-active" : ""} ${
              isLocked ? "is-locked" : ""
            }`}
            onClick={() => handleSelectDetailTab(tab.key)}
            disabled={isLocked}
          >
            <span className="sendora-tab-step-number">{tabIndex + 1}</span>
            <span className="sendora-tab-step-label">{tab.label}</span>
          </button>
            );
          })()
        ))}
      </div>
    </div>
  );

  const syncSelectedVariantBody = (nextBodyHtml) => {
    if (!selectedVariant) return;
    handleUpdateVariantField(selectedStepIndex, selectedVariantIndex, "body", nextBodyHtml);
  };

  const pushBodyHistorySnapshot = (bodyHtml) => {
    if (applyingBodyHistoryRef.current) return;
    const normalized = normalizeRichEditorHtml(String(bodyHtml || ""));
    const history = bodyHistoryRef.current;
    const currentEntry = history.stack[history.index] ?? null;
    if (currentEntry === normalized) return;
    const nextStack = history.stack.slice(0, history.index + 1);
    nextStack.push(normalized);
    const HISTORY_LIMIT = 120;
    if (nextStack.length > HISTORY_LIMIT) {
      nextStack.splice(0, nextStack.length - HISTORY_LIMIT);
    }
    history.stack = nextStack;
    history.index = nextStack.length - 1;
  };

  const applyBodyHistoryEntry = (entryHtml) => {
    if (!editorRef.current) return;
    const normalized = normalizeRichEditorHtml(String(entryHtml || ""));
    applyingBodyHistoryRef.current = true;
    const highlighted = highlightVariablesInEditorHtml(normalizeBodyToHtml(normalized));
    if (editorRef.current.innerHTML !== highlighted) {
      editorRef.current.innerHTML = highlighted;
    }
    syncSelectedVariantBody(normalized);
    editorRef.current.focus();
    restoreEditorSelection("body");
    captureEditorSelection("body");
    setTimeout(() => {
      applyingBodyHistoryRef.current = false;
    }, 0);
  };

  const handleBodyHistoryUndo = () => {
    const history = bodyHistoryRef.current;
    if (history.index <= 0) return;
    history.index -= 1;
    applyBodyHistoryEntry(history.stack[history.index] || "");
  };

  const handleBodyHistoryRedo = () => {
    const history = bodyHistoryRef.current;
    if (history.index >= history.stack.length - 1) return;
    history.index += 1;
    applyBodyHistoryEntry(history.stack[history.index] || "");
  };

  const ensureBodyEditorDefaultTypographyWrapper = ({
    syncVariant = false,
    focusCaret = false,
  } = {}) => {
    if (!editorRef.current || typeof window === "undefined") return false;
    const rawCurrentHtml = stripZeroWidthSpaces(
      stripVariableHighlightMarkup(editorRef.current?.innerHTML || "")
    );
    if (!isRichEditorHtmlEffectivelyEmpty(rawCurrentHtml)) return false;
    const wrapperHtml = buildDefaultBodyTypographyHtml(
      editorFontFamilySelectionValue || EDITOR_DISPLAY_FONT_FAMILY,
      editorFontSizeSelectionValue || String(EDITOR_DISPLAY_FONT_SIZE_PT)
    );
    const highlighted = highlightVariablesInEditorHtml(wrapperHtml);
    if (editorRef.current.innerHTML !== highlighted) {
      editorRef.current.innerHTML = highlighted;
    }
    if (syncVariant) {
      syncSelectedVariantBody(wrapperHtml);
      pushBodyHistorySnapshot(wrapperHtml);
    }
    if (focusCaret) {
      const targetNode =
        editorRef.current.querySelector("div, p, span") || editorRef.current;
      const selection = window.getSelection?.();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(targetNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      captureEditorSelection("body");
    }
    return true;
  };

  const handleRichEditorInput = () => {
    const nextBodyHtml = normalizeRichEditorHtml(
      stripZeroWidthSpaces(stripVariableHighlightMarkup(editorRef.current?.innerHTML || ""))
    );
    if (isRichEditorHtmlEffectivelyEmpty(nextBodyHtml)) {
      ensureBodyEditorDefaultTypographyWrapper({ syncVariant: true, focusCaret: false });
      return;
    }
    syncSelectedVariantBody(nextBodyHtml);
    pushBodyHistorySnapshot(nextBodyHtml);
  };

  const handleRichEditorKeyDown = (event) => {
    if (event.key === "|") {
      const insertedPipe = insertSpintaxPipeAtSelection("body");
      if (insertedPipe) {
        event.preventDefault();
        return;
      }
    }
    const modifier = event.metaKey || event.ctrlKey;
    if (!modifier) return;
    const key = String(event.key || "").toLowerCase();
    const isUndo = key === "z" && !event.shiftKey;
    const isRedo = (key === "z" && event.shiftKey) || key === "y";
    if (!isUndo && !isRedo) return;
    event.preventDefault();
    if (isUndo) {
      handleBodyHistoryUndo();
      return;
    }
    handleBodyHistoryRedo();
  };

  const handleRichEditorBlur = () => {
    if (!editorRef.current) return;
    cleanupEmptySpintaxPairs(editorRef.current, { onlyWhenSelectionOutside: false });
    if (editorRef.current.querySelector(".sendora-token.is-spintax-delimiter")) return;
    const highlighted = highlightVariablesInEditorHtml(
      normalizeBodyToHtml(stripVariableHighlightMarkup(editorRef.current.innerHTML || ""))
    );
    if (editorRef.current.innerHTML !== highlighted) {
      editorRef.current.innerHTML = highlighted;
    }
  };

  const handleEditorCommand = (command) => {
    if (isCodeView || !editorRef.current) return;
    restoreEditorSelection("body");
    const inlineBefore = getEditorInlineStyleSelectionState();
    editorRef.current.focus();
    if (command === "createLink") {
      const url = window.prompt("Enter link URL");
      if (!url) return;
      document.execCommand("createLink", false, url);
    } else {
      document.execCommand(command, false, null);
    }
    if (command === "bold") {
      const nextBold = !inlineBefore.bold;
      applySelectionStyleToVariableTokens((style) => {
        style.fontWeight = nextBold ? "700" : "400";
      });
    } else if (command === "italic") {
      const nextItalic = !inlineBefore.italic;
      applySelectionStyleToVariableTokens((style) => {
        style.fontStyle = nextItalic ? "italic" : "normal";
      });
    } else if (command === "underline") {
      const nextUnderline = !inlineBefore.underline;
      applySelectionStyleToVariableTokens((style) => {
        style.textDecoration = nextUnderline ? "underline" : "none";
      });
    }
    handleRichEditorInput();
    syncEditorInlineStyleControlsFromSelection();
  };

  const handleEditorIndent = () => {
    if (isCodeView || !editorRef.current) return;
    const selection = window.getSelection?.();
    if (!selection) return;
    const savedRange = bodySelectionRangeRef.current;
    const isSavedRangeValid =
      savedRange &&
      editorRef.current.contains(savedRange.startContainer) &&
      editorRef.current.contains(savedRange.endContainer);
    const liveRange =
      selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const isLiveRangeValid =
      liveRange &&
      editorRef.current.contains(liveRange.startContainer) &&
      editorRef.current.contains(liveRange.endContainer);
    const preferredRange =
      isLiveRangeValid && !liveRange.collapsed
        ? liveRange.cloneRange()
        : isSavedRangeValid && !savedRange.collapsed
        ? savedRange.cloneRange()
        : null;
    if (preferredRange) {
      selection.removeAllRanges();
      selection.addRange(preferredRange);
    } else {
      restoreEditorSelection("body");
    }
    editorRef.current.focus();
    if (!selection.rangeCount) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(editorRef.current);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
    }
    let range = selection.getRangeAt(0);
    const isWithinEditor =
      editorRef.current.contains(range.startContainer) && editorRef.current.contains(range.endContainer);
    if (!isWithinEditor) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(editorRef.current);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
      range = selection.getRangeAt(0);
    }
    const INDENT_TEXT = "\u00A0\u00A0\u00A0\u00A0";
    if (range.collapsed) {
      const indentTextNode = document.createTextNode("\u00A0\u00A0\u00A0\u00A0");
      range.insertNode(indentTextNode);
      const nextRange = document.createRange();
      nextRange.setStartAfter(indentTextNode);
      nextRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(nextRange);
    } else {
      const selectedText = String(range.toString() || "");
      if (selectedText.includes("\n")) {
        const indentedText = selectedText
          .split("\n")
          .map((line) => `${INDENT_TEXT}${line}`)
          .join("\n");
        const insertedByExecCommand = document.execCommand("insertText", false, indentedText);
        if (!insertedByExecCommand) {
          insertTextAtSelection(editorRef.current, indentedText);
        }
      } else {
        document.execCommand("indent", false, null);
      }
    }
    captureEditorSelection("body");
    handleRichEditorInput();
    syncEditorInlineStyleControlsFromSelection();
  };

  const handleEditorFontFamilyChange = (value) => {
    const nextFamily = String(value || "").trim();
    if (!nextFamily) return;
    setEditorFontFamilySelectionValue(nextFamily);
    if (isCodeView || !editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("fontName", false, nextFamily);
    applySelectionStyleToVariableTokens((style) => {
      style.fontFamily = nextFamily;
    });
    handleRichEditorInput();
    syncEditorFontControlsFromSelection();
  };

  const handleEditorFontSizeChange = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    const nextSize = parsed;
    setEditorFontSizeSelectionValue(String(nextSize));
    if (isCodeView || !editorRef.current) return;
    editorRef.current.focus();
    document.execCommand("fontSize", false, "7");
    const fontNodes = editorRef.current.querySelectorAll('font[size="7"]');
    fontNodes.forEach((node) => {
      node.removeAttribute("size");
      node.style.fontSize = `${nextSize}pt`;
    });
    applySelectionStyleToVariableTokens((style) => {
      style.fontSize = `${nextSize}pt`;
    });
    handleRichEditorInput();
    syncEditorFontControlsFromSelection();
  };

  const handleCleanHtmlWithAi = async () => {
    if (!employee || !selectedVariant) return;
    const htmlToClean = String(selectedVariant.body || "").trim();
    if (!htmlToClean) {
      toast.error("No HTML to clean.");
      return;
    }
    try {
      setCleaningHtml(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/sendora/cleanHtml",
        {
          html: htmlToClean,
        },
        null,
        { timeout: SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS }
      );
      const cleanedHtml = String(response?.cleanedHtml || "").trim();
      if (!cleanedHtml) {
        toast.error("No cleaned HTML returned.");
        return;
      }
      syncSelectedVariantBody(cleanedHtml);
      if (!isCodeView && editorRef.current) {
        editorRef.current.innerHTML = highlightVariablesInEditorHtml(
          normalizeBodyToHtml(cleanedHtml)
        );
        captureEditorSelection("body");
      }
      toast.success("HTML cleaned for email compatibility.");
    } catch (error) {
      console.log("Sendora clean HTML error", error);
      toast.error("Could not clean HTML right now.");
    } finally {
      setCleaningHtml(false);
    }
  };

  const handleFixFormattingWithAi = async () => {
    if (!employee || !selectedVariant) return;
    const htmlToFormat = String(selectedVariant.body || "").trim();
    if (!htmlToFormat) {
      toast.error("No HTML to format.");
      return;
    }
    try {
      setFixingFormatting(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/sendora/fixFormatting",
        {
          html: htmlToFormat,
        },
        null,
        { timeout: SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS }
      );
      const formattedHtml = String(response?.formattedHtml || "").trim();
      if (!formattedHtml) {
        toast.error("No formatted HTML returned.");
        return;
      }
      syncSelectedVariantBody(formattedHtml);
      if (!isCodeView && editorRef.current) {
        editorRef.current.innerHTML = highlightVariablesInEditorHtml(
          normalizeBodyToHtml(formattedHtml)
        );
        captureEditorSelection("body");
      }
      if (formattedHtml === htmlToFormat) {
        toast.success("Formatting already looks good.");
      } else {
        toast.success("Formatting improved for email compatibility.");
      }
    } catch (error) {
      console.log("Sendora fix formatting error", error);
      toast.error("Could not fix formatting right now.");
    } finally {
      setFixingFormatting(false);
    }
  };

  const handleGenerateAutoSpintaxWithAi = async () => {
    if (!employee || !selectedVariant) return;
    let htmlToEdit = normalizeRichEditorHtml(String(selectedVariant.body || ""));
    if (!isCodeView && editorRef.current) {
      htmlToEdit = normalizeRichEditorHtml(
        stripZeroWidthSpaces(stripVariableHighlightMarkup(editorRef.current.innerHTML || ""))
      );
    }
    if (!htmlToEdit.trim()) {
      toast.error("No email body content to process.");
      return;
    }
    try {
      setAutoGeneratingSpintax(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/sendora/generateAutoSpintax",
        {
          html: htmlToEdit,
        },
        null,
        { timeout: SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS }
      );
      const updatedHtml = String(response?.updatedHtml || "").trim();
      if (!updatedHtml) {
        toast.error("No generated spintax HTML returned.");
        return;
      }
      syncSelectedVariantBody(updatedHtml);
      if (!isCodeView && editorRef.current) {
        editorRef.current.innerHTML = highlightVariablesInEditorHtml(
          normalizeBodyToHtml(updatedHtml)
        );
        captureEditorSelection("body");
      }
      if (updatedHtml === htmlToEdit) {
        toast.success("No safe spintax targets found. Copy left unchanged.");
        return;
      }
      const estimatedInsertedBlocks = Math.max(0, Number(response?.estimatedInsertedBlocks || 0));
      if (estimatedInsertedBlocks > 0) {
        toast.success(
          `Generated spintax for ${estimatedInsertedBlocks} phrase${
            estimatedInsertedBlocks === 1 ? "" : "s"
          }.`
        );
      } else {
        toast.success("Generated and applied spintax.");
      }
    } catch (error) {
      console.log("Sendora auto spintax generation error", error);
      toast.error("Could not generate spintax right now.");
    } finally {
      setAutoGeneratingSpintax(false);
    }
  };

  const handleApplyCustomPromptWithAi = async () => {
    if (!employee || !selectedVariant) return;
    const promptText = String(customPromptText || "").trim();
    const htmlToEdit = String(selectedVariant.body || "").trim();
    if (!promptText) {
      toast.error("Enter a prompt first.");
      return;
    }
    if (!htmlToEdit) {
      toast.error("No HTML to modify.");
      return;
    }
    try {
      setApplyingCustomPrompt(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/sendora/applyCustomPrompt",
        {
          html: htmlToEdit,
          prompt: promptText,
        },
        null,
        { timeout: SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS }
      );
      const updatedHtml = String(response?.updatedHtml || "").trim();
      if (!updatedHtml) {
        toast.error("No updated HTML returned.");
        return;
      }
      syncSelectedVariantBody(updatedHtml);
      if (!isCodeView && editorRef.current) {
        editorRef.current.innerHTML = highlightVariablesInEditorHtml(
          normalizeBodyToHtml(updatedHtml)
        );
        captureEditorSelection("body");
      }
      if (updatedHtml === htmlToEdit) {
        toast.success("No changes needed.");
      } else {
        toast.success("Custom AI prompt applied.");
      }
      setCustomPromptOpen(false);
      setCustomPromptText("");
    } catch (error) {
      console.log("Sendora custom prompt error", error);
      toast.error("Could not apply custom prompt right now.");
    } finally {
      setApplyingCustomPrompt(false);
    }
  };

  const handleScanDeliverabilityWithAi = async () => {
    if (!employee || !selectedVariant) return;
    const htmlToScan = String(selectedVariant.body || "").trim();
    if (!htmlToScan) {
      toast.error("No HTML to scan.");
      return;
    }
    try {
      setScanningDeliverability(true);
      setDeliverabilityAppliedSuggestionKeys({});
      setDeliverabilitySuggestionsCollapsed(false);
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/sendora/scanDeliverability",
        {
          html: htmlToScan,
        },
        null,
        { timeout: SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS }
      );
      const suggestions = Array.isArray(response?.suggestions)
        ? response.suggestions
            .map((item) => String(item || "").trim())
            .filter(Boolean)
        : [];
      if (!suggestions.length) {
        toast.error("No deliverability suggestions returned.");
        return;
      }
      setDeliverabilitySuggestions(suggestions);
      const initialSelections = {};
      suggestions.forEach((item, index) => {
        initialSelections[`${index}:${item}`] = true;
      });
      setDeliverabilitySuggestionSelections(initialSelections);
      setDeliverabilityAppliedSuggestionKeys({});
      setDeliverabilitySuggestionsCollapsed(false);
      toast.success("Deliverability scan complete.");
    } catch (error) {
      console.log("Sendora deliverability scan error", error);
      toast.error("Could not run deliverability scan right now.");
    } finally {
      setScanningDeliverability(false);
    }
  };

  const handleToggleDeliverabilitySuggestion = (key) => {
    setDeliverabilitySuggestionSelections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleApplyDeliverabilitySuggestions = async () => {
    if (!employee || !selectedVariant) return;
    const htmlToEdit = String(selectedVariant.body || "").trim();
    if (!htmlToEdit) {
      toast.error("No HTML to modify.");
      return;
    }
    const selectedSuggestions = deliverabilitySuggestions.filter((item, index) => {
      const key = `${index}:${item}`;
      return !!deliverabilitySuggestionSelections[key] && !deliverabilityAppliedSuggestionKeys[key];
    });
    if (!selectedSuggestions.length) {
      toast.error("Select at least one unapplied suggestion.");
      return;
    }
    try {
      setApplyingDeliverabilitySuggestions(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/sendora/applyDeliverabilitySuggestions",
        {
          html: htmlToEdit,
          suggestions: selectedSuggestions,
        },
        null,
        { timeout: SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS }
      );
      const updatedHtml = String(response?.updatedHtml || "").trim();
      if (!updatedHtml) {
        toast.error("No updated HTML returned.");
        return;
      }
      syncSelectedVariantBody(updatedHtml);
      if (!isCodeView && editorRef.current) {
        editorRef.current.innerHTML = highlightVariablesInEditorHtml(
          normalizeBodyToHtml(updatedHtml)
        );
        captureEditorSelection("body");
      }
      if (updatedHtml === htmlToEdit) {
        toast.success("Suggestions already satisfied.");
      } else {
        toast.success("Selected suggestions applied.");
      }
      setDeliverabilityAppliedSuggestionKeys((prev) => {
        const next = { ...prev };
        deliverabilitySuggestions.forEach((item, index) => {
          const key = `${index}:${item}`;
          if (deliverabilitySuggestionSelections[key]) {
            next[key] = true;
          }
        });
        return next;
      });
    } catch (error) {
      console.log("Sendora apply deliverability suggestions error", error);
      toast.error("Could not apply suggestions right now.");
    } finally {
      setApplyingDeliverabilitySuggestions(false);
    }
  };

  const toggleCodeView = () => {
    const switchingToCode = !isCodeView;
    if (switchingToCode && selectedVariant) {
      let sourceHtml = normalizeRichEditorHtml(selectedVariant.body || "");
      if (editorRef.current) {
        sourceHtml = normalizeRichEditorHtml(
          stripZeroWidthSpaces(stripVariableHighlightMarkup(editorRef.current.innerHTML || ""))
        );
      }
      const beautified = formatHtmlForCodeView(sourceHtml);
      if (beautified !== sourceHtml) {
        syncSelectedVariantBody(beautified);
      }
    }
    if (switchingToCode) {
      setOpenVariableMenu(false);
    }
    setIsCodeView((prev) => !prev);
  };

  const insertVariableToken = (fieldName, targetOverride = null, options = {}) => {
    const { closeMenu = true } = options || {};
    const token = `{{${fieldName}}}`;
    const target = targetOverride === "subject" || targetOverride === "body"
      ? targetOverride
      : editorInsertTarget === "subject"
      ? "subject"
      : "body";
    if (closeMenu) setOpenVariableMenu(false);
    if (target === "subject") {
      if (subjectEditorRef.current) {
        subjectEditorRef.current.focus();
        restoreEditorSelection("subject");
        const tokenClass = getTokenHighlightClass(fieldName);
        insertInlineTokenAtSelection(subjectEditorRef.current, token, tokenClass);
        captureEditorSelection("subject");
        handleSubjectEditorInput();
      } else {
        const current = selectedVariant?.subject || "";
        handleUpdateVariantField(selectedStepIndex, selectedVariantIndex, "subject", `${current}${token}`);
      }
      return;
    }

    if (isCodeView) {
      const input = codeEditorRef.current;
      const current = selectedVariant?.body || "";
      const start = Number(input?.selectionStart ?? current.length);
      const end = Number(input?.selectionEnd ?? current.length);
      const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
      syncSelectedVariantBody(next);
      setTimeout(() => {
        if (!input) return;
        input.focus();
        const cursor = start + token.length;
        input.setSelectionRange(cursor, cursor);
      }, 0);
      return;
    }

    if (editorRef.current) {
      editorRef.current.focus();
      restoreEditorSelection("body");
      const tokenClass = getTokenHighlightClass(fieldName);
      const insertedInline = insertInlineTokenAtSelection(editorRef.current, token, tokenClass);
      if (!insertedInline) {
        const insertedByExecCommand = document.execCommand("insertText", false, token);
        if (!insertedByExecCommand) {
          insertTextAtSelection(editorRef.current, token);
        }
      }
      captureEditorSelection("body");
      handleRichEditorInput();
      return;
    }

    syncSelectedVariantBody(`${selectedVariant?.body || ""}${token}`);
  };
  const handleInsertOttReplyVariables = () => {
    if (isEmailEditorLocked || !isSelectedStepOttMode) return;
    const ottTokenChip = (tokenName) =>
      `<span contenteditable="false" class="sendora-token is-ott">{{${tokenName}}}</span>`;
    const previousContextHeaderHtml = `<div style="border-top: 1px solid #E1E1E1; padding-top: 3pt;">
  <p style="margin: 0; font-family: Arial, sans-serif; font-size: 10pt; line-height: 1.4;">
    <strong>From:</strong> ${ottTokenChip("previousSenderName")} &lt;<a href="mailto:{{previousSenderEmail}}">${ottTokenChip("previousSenderEmail")}</a>&gt;<br>
    <strong>Sent:</strong> ${ottTokenChip("previousEmailSentDate")}<br>
    <strong>To:</strong> ${ottTokenChip("email")}<br>
    <strong>Subject:</strong> ${ottTokenChip("previousEmailSubject")}
  </p>
</div>`;
    const previousContextHeaderWithBreakHtml = `<br/>${previousContextHeaderHtml}`;
    const previousContextHeaderMarker = "<strong>From:</strong> {{previousSenderName}}";
    const previousEmailBodyToken = "{{previousEmailBody}}";
    const previousEmailSubjectToken = "{{previousEmailSubject}}";
    const currentBody = String(selectedVariant?.body || "");
    const hasPreviousEmailBodyToken = currentBody.includes(previousEmailBodyToken);
    const hasPreviousContextHeader = currentBody.includes(previousContextHeaderMarker);
    let nextBody = currentBody;
    if (!hasPreviousContextHeader && hasPreviousEmailBodyToken) {
      nextBody = currentBody.replace(
        previousEmailBodyToken,
        `${previousContextHeaderWithBreakHtml}<br/>${previousEmailBodyToken}`
      );
    } else if (!hasPreviousEmailBodyToken) {
      nextBody = `${currentBody}${previousContextHeaderWithBreakHtml}<br/>${previousEmailBodyToken}`;
    }
    if (nextBody !== currentBody) {
      syncSelectedVariantBody(nextBody);
      if (!isCodeView && editorRef.current) {
        editorRef.current.innerHTML = highlightVariablesInEditorHtml(normalizeBodyToHtml(nextBody));
        captureEditorSelection("body");
      }
    }
    const currentSubject = String(selectedVariant?.subject || "").trim();
    const hasPreviousSubjectToken = currentSubject.includes(previousEmailSubjectToken);
    const hasReplyPrefix = /^re:\s*/i.test(currentSubject);
    let nextSubject = currentSubject;
    if (!hasPreviousSubjectToken) {
      const withoutReplyPrefix = currentSubject.replace(/^re:\s*/i, "").trim();
      nextSubject = withoutReplyPrefix
        ? `RE: ${previousEmailSubjectToken} ${withoutReplyPrefix}`
        : `RE: ${previousEmailSubjectToken}`;
    } else if (!hasReplyPrefix) {
      nextSubject = `RE: ${currentSubject}`;
    }
    if (nextSubject !== currentSubject) {
      handleUpdateVariantField(selectedStepIndex, selectedVariantIndex, "subject", nextSubject.trim());
    }
  };

  const handleInsertSpintaxStart = () => {
    const target = editorInsertTarget === "subject" ? "subject" : "body";
    const targetEditorRef = target === "subject" ? subjectEditorRef : editorRef;
    const targetEditor = targetEditorRef.current;
    if (isCodeView || !targetEditor) return;
    targetEditor.focus();
    restoreEditorSelection(target);
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount) return;
    let range = selection.getRangeAt(0);
    const isWithinEditor =
      targetEditor.contains(range.startContainer) &&
      targetEditor.contains(range.endContainer);
    if (!isWithinEditor) {
      const fallbackRange = document.createRange();
      fallbackRange.selectNodeContents(targetEditor);
      fallbackRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(fallbackRange);
      range = selection.getRangeAt(0);
    }
    const hasSelectedText = !range.collapsed && String(range.toString() || "").length > 0;
    const leftDelimiter = document.createElement("span");
    leftDelimiter.className = "sendora-token is-spintax-delimiter";
    leftDelimiter.setAttribute("contenteditable", "false");
    leftDelimiter.textContent = "{";
    const pipeDelimiter = document.createElement("span");
    pipeDelimiter.className = "sendora-token is-spintax-delimiter";
    pipeDelimiter.setAttribute("contenteditable", "false");
    pipeDelimiter.textContent = "|";
    const rightDelimiter = document.createElement("span");
    rightDelimiter.className = "sendora-token is-spintax-delimiter";
    rightDelimiter.setAttribute("contenteditable", "false");
    rightDelimiter.textContent = "}";
    const caretAnchor = document.createTextNode("\u200B");
    const selectedFragment = hasSelectedText ? range.extractContents() : null;
    if (!hasSelectedText) {
      range.collapse(false);
    }
    const fragment = document.createDocumentFragment();
    fragment.appendChild(leftDelimiter);
    if (selectedFragment) {
      fragment.appendChild(selectedFragment);
      fragment.appendChild(pipeDelimiter);
    }
    fragment.appendChild(caretAnchor);
    fragment.appendChild(rightDelimiter);
    range.insertNode(fragment);
    const nextRange = document.createRange();
    nextRange.setStart(caretAnchor, 1);
    nextRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(nextRange);
    const restoreRange = nextRange.cloneRange();
    setTimeout(() => {
      if (!targetEditorRef.current) return;
      targetEditorRef.current.focus();
      const liveSelection = window.getSelection?.();
      if (!liveSelection) return;
      liveSelection.removeAllRanges();
      liveSelection.addRange(restoreRange);
      captureEditorSelection(target);
    }, 0);
    captureEditorSelection(target);
    if (target === "subject") {
      handleSubjectEditorInput();
    } else {
      handleRichEditorInput();
    }
  };

  const handleGenerateSpintaxSuggestions = async () => {
    if (!employee || !editorRef.current) return;
    const selectedText = String(spintaxSuggestionPopup.selectionText || "").trim();
    if (!selectedText) return;
    try {
      setGeneratingSpintaxSuggestions(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        "landmark/sendora/generateSpintaxSuggestions",
        {
          selectionText: selectedText,
          fullContextText: spintaxSuggestionPopup.fullContextText || "",
          selectionStart: Number(spintaxSuggestionPopup.selectionStart) || 0,
          selectionEnd: Number(spintaxSuggestionPopup.selectionEnd) || 0,
        },
        null,
        { timeout: SENDORA_SEQUENCE_AI_TOOL_TIMEOUT_MS }
      );
      const generated = Array.isArray(response?.variations) ? response.variations : [];
      const options = Array.from(
        new Set(
          [selectedText, ...generated]
            .map((item) => String(item || "").trim())
            .filter(Boolean)
        )
      );
      if (options.length < 2) {
        toast.error("No usable spintax suggestions returned.");
        return;
      }
      const beforeInsertHtml = normalizeRichEditorHtml(
        stripZeroWidthSpaces(stripVariableHighlightMarkup(editorRef.current?.innerHTML || ""))
      );
      pushBodyHistorySnapshot(beforeInsertHtml);
      editorRef.current.focus();
      restoreEditorSelection("body");
      const inserted = insertSpintaxTokenAtSelection(editorRef.current, options);
      if (!inserted) {
        toast.error("Could not insert generated spintax.");
        return;
      }
      captureEditorSelection("body");
      handleRichEditorInput();
      setSpintaxSuggestionPopup((prev) => ({ ...prev, visible: false }));
      hideSpintaxVariantRemoveControls();
    } catch (error) {
      console.log("Sendora spintax suggestion error", error);
      toast.error("Unable to generate spintax suggestions right now.");
    } finally {
      setGeneratingSpintaxSuggestions(false);
    }
  };

  const openPreviewModal = () => {
    const variantOverride = normalizeEmail(
      selectedVariant?.senderAccount?.emailLowercase ||
        selectedVariant?.senderAccount?.email ||
        selectedVariant?.senderAccountEmail ||
        ""
    );
    const sequenceFirst = normalizeEmail(
      sequenceSenderAccounts?.[0]?.emailLowercase || sequenceSenderAccounts?.[0]?.email || ""
    );
    const preferredSendFrom =
      variantOverride || sequenceFirst || defaultSenderAccountEmail || employeeDefaultEmail || "";
    const fallbackSendTo =
      normalizeLandmarkEmail(employeeDefaultEmail || "") ||
      previewRecipientOptions[0] ||
      "";
    let preferredSendTo = fallbackSendTo;
    let preferredCustomSendTo = "";
    if (previewRecipientPreferenceStorageKey) {
      try {
        const storedRaw = window.localStorage.getItem(previewRecipientPreferenceStorageKey);
        const stored = storedRaw ? JSON.parse(storedRaw) : null;
        const storedSendTo = String(stored?.sendTo || "").trim();
        const storedCustomSendTo = String(stored?.customSendTo || "").trim();
        if (storedSendTo === PREVIEW_RECIPIENT_CUSTOM_VALUE) {
          preferredSendTo = PREVIEW_RECIPIENT_CUSTOM_VALUE;
          preferredCustomSendTo = storedCustomSendTo;
        } else if (storedSendTo && previewRecipientOptions.includes(storedSendTo)) {
          preferredSendTo = storedSendTo;
        }
      } catch (error) {
        // Ignore storage read/parse failures.
      }
    }
    setPreviewForm({
      sendFrom: preferredSendFrom || previewSenderOptions[0] || "",
      sendTo: preferredSendTo,
      customSendTo: preferredCustomSendTo,
      leadId: previewLeadOptions[0]?._id || "",
    });
    setPreviewRenderState({
      loading: true,
      subject: "",
      html: "",
      text: "",
      error: "",
    });
    setPreviewSpintaxSeed(Date.now());
    setIsPreviewModalOpen(true);
  };

  const handleRandomizePreviewSpintax = () => {
    let nextSeed = Date.now();
    let nextSignature = resolvePreviewSpintaxBySeed(nextSeed).signature;
    let attempts = 0;
    while (nextSignature === currentPreviewSpintaxSignature && attempts < 40) {
      nextSeed = Date.now() + Math.floor(Math.random() * 1000000) + attempts;
      nextSignature = resolvePreviewSpintaxBySeed(nextSeed).signature;
      attempts += 1;
    }
    const flashBody = applySpintaxPreviewVariants(mergedPreviewBodyTemplate, nextSeed, {
      flashSelection: true,
      flashKey: `r${nextSeed}`,
    });
    const flashSubject = applySpintaxPreviewVariants(mergedPreviewSubjectTemplate, nextSeed, {
      flashSelection: true,
      flashKey: `r${nextSeed}`,
    });
    setPreviewSpintaxDisplayOverride({ html: flashBody, subject: flashSubject });
    setPreviewSpintaxSeed(nextSeed);
    setPreviewDiceRolling(true);
    let nextDiceFace = Math.floor(Math.random() * 6);
    let faceAttempts = 0;
    while (nextDiceFace === previewDiceFaceIndex && faceAttempts < 20) {
      nextDiceFace = Math.floor(Math.random() * 6);
      faceAttempts += 1;
    }
    if (previewDiceSwapTimerRef.current) {
      clearTimeout(previewDiceSwapTimerRef.current);
    }
    if (previewDiceRollTimerRef.current) {
      clearTimeout(previewDiceRollTimerRef.current);
    }
    previewDiceSwapTimerRef.current = setTimeout(() => {
      setPreviewDiceFaceIndex(nextDiceFace);
      previewDiceSwapTimerRef.current = null;
    }, 200);
    previewDiceRollTimerRef.current = setTimeout(() => {
      setPreviewDiceRolling(false);
      previewDiceRollTimerRef.current = null;
    }, 400);
  };

  const handleSendTestEmailPreview = () => {
    if (!employee || !selectedCampaign) return;
    if (!previewForm.sendFrom?.trim()) {
      toast.error("Select a send from email");
      return;
    }
    if (!resolvedPreviewSendTo) {
      toast.error("Enter a recipient email");
      return;
    }
    if (isCustomPreviewRecipient && !isValidEmailAddress(resolvedPreviewSendTo)) {
      toast.error("Enter a valid recipient email");
      return;
    }
    const payload = {
      campaignId: selectedCampaign.campaignId,
      sendFromEmail: previewForm.sendFrom.trim(),
      sendFromFirstName: String(previewSenderAccount?.firstName || "").trim(),
      sendFromLastName: String(previewSenderAccount?.lastName || "").trim(),
      sendToEmail: resolvedPreviewSendTo,
      leadId: previewForm.leadId || "",
      previewStepId: String(selectedStep?.stepId || "").trim(),
      previewStepMode: String(selectedStep?.mode || "").trim(),
      previewOttSourceStepId: String(selectedStep?.ottSourceStepId || "").trim(),
      subjectTemplate: selectedVariant?.subject || "",
      bodyTemplate: selectedVariant?.body || "",
      spintaxSeed: previewSpintaxSeed,
    };
    setSendingTestEmail(true);
    const api = new ApiService({ employee });
    api
      .post("landmark/sendora/sendTestEmail", payload)
      .then((response) => {
        if (response?.error) {
          toast.error(response.error);
          return;
        }
        if (response?.messageId) {
          // Useful for delivery tracing without adding toast noise.
          console.log("[Sendora] Test email messageId:", response.messageId);
        }
        toast.success("Test email sent");
      })
      .catch((err) => {
        console.error(err);
        toast.error("Could not send test email");
      })
      .finally(() => setSendingTestEmail(false));
  };

  const handleUpdateSelectedSchedule = (field, value) => {
    setScheduleDrafts((prev) => {
      if (!prev.length) return prev;
      const nextFirst = { ...prev[0], [field]: value };
      return [nextFirst, ...prev.slice(1)];
    });
  };

  const handleToggleSelectedScheduleDay = (day, checked) => {
    setScheduleDrafts((prev) => {
      if (!prev.length) return prev;
      const firstSchedule = { ...prev[0] };
      const days = new Set(Array.isArray(firstSchedule.days) ? firstSchedule.days : []);
      if (checked) days.add(day);
      else days.delete(day);
      firstSchedule.days = Array.from(days);
      return [firstSchedule, ...prev.slice(1)];
    });
  };

  const handleToggleScheduleBypass = (checked) => {
    setScheduleDrafts((prev) => {
      if (!prev.length) return prev;
      const firstSchedule = { ...prev[0], isActive: !checked, bypassSchedule: checked };
      return [firstSchedule, ...prev.slice(1)];
    });
  };

  const addCollaboratorToEditForm = (rawEmail) => {
    const nextEmail = normalizeLandmarkEmail(rawEmail);
    if (!nextEmail) return;
    setEditForm((prev) => {
      if ((prev.collaboratorEmails || []).includes(nextEmail)) return prev;
      return {
        ...prev,
        collaboratorEmails: [...(prev.collaboratorEmails || []), nextEmail],
      };
    });
  };

  const removeCollaboratorFromForm = (email) => {
    setEditForm((prev) => ({
      ...prev,
      collaboratorEmails: (prev.collaboratorEmails || []).filter((e) => e !== email),
    }));
  };

  const handleAddSelectedCollaborator = () => {
    if (!editForm.selectedCollaboratorEmail) return;
    addCollaboratorToEditForm(editForm.selectedCollaboratorEmail);
    setEditForm((prev) => ({ ...prev, selectedCollaboratorEmail: "" }));
  };

  const commitEmailPills = (type, rawValue) => {
    const key = type === "cc" ? "ccEmails" : "bccEmails";
    const inputKey = type === "cc" ? "ccInput" : "bccInput";
    const incoming = splitEmailsFromInput(rawValue);
    if (!incoming.length) {
      setEditForm((prev) => ({ ...prev, [inputKey]: "" }));
      return;
    }
    setEditForm((prev) => ({
      ...prev,
      [key]: Array.from(new Set([...(prev[key] || []), ...incoming])),
      [inputKey]: "",
    }));
  };

  const handleEmailPillInputChange = (type, value) => {
    const inputKey = type === "cc" ? "ccInput" : "bccInput";
    setEditForm((prev) => ({ ...prev, [inputKey]: value }));
  };

  const handleEmailPillInputKeyDown = (type, event) => {
    if ([" ", "Enter", "Tab", ",", ";"].includes(event.key)) {
      event.preventDefault();
      const inputKey = type === "cc" ? "ccInput" : "bccInput";
      commitEmailPills(type, editForm[inputKey] || "");
    }
  };

  const handleRemovePillEmail = (type, emailToRemove) => {
    const key = type === "cc" ? "ccEmails" : "bccEmails";
    const normalizedTarget = normalizeEmail(emailToRemove);
    setEditForm((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((email) => normalizeEmail(email) !== normalizedTarget),
    }));
  };

  const addSequenceSenderAccountByEmail = (rawEmail) => {
    const nextEmail = normalizeEmail(rawEmail);
    if (!nextEmail) return;
    setSequenceDraft((prev) => {
      const accountToAdd = senderAccountByEmail.get(nextEmail) || buildSenderAccountFromEmail(nextEmail);
      const mergedAccounts = normalizeSenderAccounts(
        [...(prev.senderAccounts || []), accountToAdd].filter(Boolean),
        prev.senderAccountEmails || []
      );
      return {
        ...prev,
        senderAccounts: mergedAccounts,
        senderAccountEmails: mergedAccounts.map((entry) => entry.emailLowercase),
      };
    });
  };

  const handleAddExistingSenderAccountFromModal = () => {
    const nextEmail = normalizeEmail(selectedSenderAccountEmail);
    if (!nextEmail) {
      toast.error("Select an existing sender account");
      return;
    }
    if (sequenceSenderAccountEmailSet.has(nextEmail)) {
      toast.error("That sender account is already added to this sequence");
      return;
    }
    addSequenceSenderAccountByEmail(nextEmail);
    toast.success("Sender account added to sequence");
    setIsSenderAccountModalOpen(false);
    setSelectedSenderAccountEmail("");
  };

  const handleRemoveSequenceSenderAccount = (emailToRemove) => {
    const target = normalizeEmail(emailToRemove);
    setSequenceDraft((prev) => {
      const nextSenderAccounts = normalizeSenderAccounts(
        (prev.senderAccounts || []).filter(
          (entry) => normalizeEmail(entry?.emailLowercase || entry?.email || "") !== target
        ),
        (prev.senderAccountEmails || []).filter((email) => normalizeEmail(email) !== target)
      );
      return {
        ...prev,
        senderAccounts: nextSenderAccounts,
        senderAccountEmails: nextSenderAccounts.map((entry) => entry.emailLowercase),
      };
    });
  };

  const openVariantSenderModal = (stepIndex, variantIndex) => {
    setVariantSenderModal({
      isOpen: true,
      stepIndex,
      variantIndex,
    });
  };

  const closeVariantSenderModal = () => {
    setVariantSenderModal({ isOpen: false, stepIndex: -1, variantIndex: -1 });
  };

  const handleSetVariantSenderOverride = (emailValue) => {
    if (!variantSenderModal.isOpen) return;
    const nextEmail = normalizeEmail(emailValue);
    const currentEmail = normalizeEmail(
      variantModalVariant?.senderAccount?.emailLowercase ||
        variantModalVariant?.senderAccount?.email ||
        variantModalVariant?.senderAccountEmail ||
        ""
    );
    if (currentEmail === nextEmail) return;
    const nextAccount = nextEmail
      ? senderAccountByEmail.get(nextEmail) || buildSenderAccountFromEmail(nextEmail)
      : null;
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const step = { ...(steps[variantSenderModal.stepIndex] || {}) };
      const variants = [...(step.variants || [])];
      const target = { ...(variants[variantSenderModal.variantIndex] || {}) };
      target.senderAccount = nextAccount;
      target.senderAccountEmail = nextEmail;
      variants[variantSenderModal.variantIndex] = target;
      step.variants = variants;
      steps[variantSenderModal.stepIndex] = step;
      return { ...prev, steps };
    });
    toast.success(nextEmail ? "Variant sender override updated" : "Variant sender reset to default");
  };

  const handleResetVariantSenderOverride = () => {
    if (!variantSenderModal.isOpen) return;
    const currentEmail = normalizeEmail(
      variantModalVariant?.senderAccount?.emailLowercase ||
        variantModalVariant?.senderAccount?.email ||
        variantModalVariant?.senderAccountEmail ||
        ""
    );
    if (!currentEmail) return;
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const step = { ...(steps[variantSenderModal.stepIndex] || {}) };
      const variants = [...(step.variants || [])];
      const target = { ...(variants[variantSenderModal.variantIndex] || {}) };
      target.senderAccount = null;
      target.senderAccountEmail = "";
      variants[variantSenderModal.variantIndex] = target;
      step.variants = variants;
      steps[variantSenderModal.stepIndex] = step;
      return { ...prev, steps };
    });
    toast.success("Variant sender reset to default");
  };

  const syncSenderAccountsFromResponse = (response = {}) => {
    const normalizedPreferences = (Array.isArray(response?.senderSignaturePreferences)
      ? response.senderSignaturePreferences
      : []
    )
      .map((entry) => normalizeSenderSignaturePreference(entry))
      .filter(Boolean);
    const preferenceByEmail = new Map(
      normalizedPreferences.map((entry) => [entry.senderEmailLowercase, entry])
    );
    const normalizedAccounts = (Array.isArray(response?.accounts) ? response.accounts : [])
      .map((entry) => normalizeSenderAccount(entry))
      .filter(Boolean)
      .map((entry) => {
        const pref = preferenceByEmail.get(entry.emailLowercase) || null;
        return {
          ...entry,
          senderSignatureHtml: normalizeSenderSignatureHtml(
            entry?.senderSignatureHtml || resolveSenderSignatureHtmlFromPreference(pref)
          ),
        };
      });
    setSenderAccounts(normalizedAccounts);
    setSenderSignaturePreferences(normalizedPreferences);
    setDefaultSenderAccountEmail(normalizeEmail(response?.defaultAccountEmail || ""));
    const normalizedInfrastructureOptions = (
      Array.isArray(response?.sendingInfrastructureOptions)
        ? response.sendingInfrastructureOptions
        : SENDORA_SENDING_INFRASTRUCTURES
    )
      .map((entry) => ({
        key: normalizeInfrastructureKey(entry?.key || ""),
        label: String(entry?.label || "").trim(),
      }))
      .filter((entry) => !!entry.key)
      .map((entry) => ({
        ...entry,
        label:
          entry.label ||
          SENDORA_SENDING_INFRASTRUCTURES.find((candidate) => candidate.key === entry.key)?.label ||
          entry.key,
      }));
    setSendingInfrastructureOptions(
      normalizedInfrastructureOptions.length
        ? normalizedInfrastructureOptions
        : SENDORA_SENDING_INFRASTRUCTURES
    );
    const domainRules = normalizeDomainInfrastructureRules(
      response?.senderDomainInfrastructurePreferences || []
    );
    setDomainInfrastructureRules(domainRules);
    lastSavedDomainInfrastructureSignatureRef.current = buildDomainInfrastructureSignature(domainRules);
    setSequenceDraft((prev) => {
      const currentSenderAccounts = normalizeSenderAccounts(
        prev?.senderAccounts || [],
        prev?.senderAccountEmails || []
      );
      const accountByEmail = new Map(
        normalizedAccounts.map((entry) => [
          normalizeEmail(entry?.emailLowercase || entry?.email || ""),
          entry,
        ])
      );
      const nextSenderAccounts = currentSenderAccounts.map((entry) => {
        const email = normalizeEmail(entry?.emailLowercase || entry?.email || "");
        return accountByEmail.get(email) || entry;
      });
      const nextSteps = (prev?.steps || []).map((step) => ({
        ...step,
        variants: (step?.variants || []).map((variant) => {
          const variantSenderEmail = normalizeEmail(
            variant?.senderAccount?.emailLowercase ||
              variant?.senderAccount?.email ||
              variant?.senderAccountEmail ||
              ""
          );
          if (!variantSenderEmail) return variant;
          const refreshedSender = accountByEmail.get(variantSenderEmail);
          if (!refreshedSender) return variant;
          return {
            ...variant,
            senderAccount: refreshedSender,
            senderAccountEmail: variantSenderEmail,
          };
        }),
      }));
      return {
        ...prev,
        steps: nextSteps,
        senderAccounts: nextSenderAccounts,
        senderAccountEmails: nextSenderAccounts.map((entry) =>
          normalizeEmail(entry?.emailLowercase || entry?.email || "")
        ),
      };
    });
  };

  const handleSetDomainInfrastructureDefault = (domainSuffix, nextDefaultInfrastructure) => {
    const normalizedDomain = normalizeDomainSuffix(domainSuffix);
    const normalizedDefault = normalizeInfrastructureKey(nextDefaultInfrastructure);
    if (!normalizedDomain || !normalizedDefault) return;
    setDomainInfrastructureRules((prev) => {
      const ruleMap = new Map(
        normalizeDomainInfrastructureRules(prev || []).map((entry) => [entry.domainSuffix, entry])
      );
      const currentRule =
        ruleMap.get(normalizedDomain) || normalizeDomainInfrastructureRule({ domainSuffix: normalizedDomain });
      if (!currentRule) return prev;
      if (!currentRule.allowedInfrastructures.includes(normalizedDefault)) return prev;
      ruleMap.set(normalizedDomain, {
        ...currentRule,
        defaultInfrastructure: normalizedDefault,
      });
      return Array.from(ruleMap.values());
    });
  };

  const upsertSenderSignaturePreference = async (
    senderEmail,
    selectedSignatureId,
    customSignatures,
    { successMessage = "" } = {}
  ) => {
    if (!employee) return false;
    const senderEmailLowercase = normalizeEmail(senderEmail);
    if (!senderEmailLowercase) return false;
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/sendora/upsertSenderSignaturePreference", {
        senderEmail: senderEmailLowercase,
        selectedSignatureId: String(selectedSignatureId || "default").trim() || "default",
        customSignatures: (Array.isArray(customSignatures) ? customSignatures : [])
          .map((entry) => normalizeCustomSenderSignature(entry))
          .filter((entry) => !!entry?.signatureId),
      });
      if (response?.error) {
        toast.error(response.error);
        return false;
      }
      syncSenderAccountsFromResponse(response);
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Could not save sender signatures");
      return false;
    }
  };

  const openSenderSignatureModal = () => {
    if (!hasSequenceSenderAccounts) return;
    const nextDraftByEmail = {};
    (signatureModalSenderAccounts || []).forEach((account) => {
      const senderEmail = normalizeEmail(account?.emailLowercase || account?.email || "");
      if (!senderEmail) return;
      const pref =
        normalizeSenderSignaturePreference(senderSignaturePreferenceByEmail.get(senderEmail) || null) ||
        createDefaultSenderSignaturePreference(senderEmail);
      if (!pref) return;
      nextDraftByEmail[senderEmail] = pref;
    });
    setSenderSignatureDraftByEmail(nextDraftByEmail);
    setSenderSignatureInitialByEmail(JSON.parse(JSON.stringify(nextDraftByEmail)));
    setSenderSignatureCodeViewById({});
    const firstSenderEmail = normalizeEmail(
      signatureModalSenderAccounts?.[0]?.emailLowercase ||
        signatureModalSenderAccounts?.[0]?.email ||
        ""
    );
    setSelectedSignatureSenderEmail(firstSenderEmail);
    setIsSenderSignatureModalOpen(true);
  };

  const closeSenderSignatureModal = () => {
    if (savingSenderSignatures) return;
    setIsSenderSignatureModalOpen(false);
    setSenderSignatureDraftByEmail({});
    setSenderSignatureInitialByEmail({});
    setSenderSignatureCodeViewById({});
    setSelectedSignatureSenderEmail("");
  };

  const openBrandVoiceModal = () => {
    if (!selectedCampaign?.campaignId) return;
    const seededCustomVoices = normalizeCustomBrandVoices(
      selectedCampaign?.options?.brandVoiceCustomVoices || []
    );
    const seededLibrary = buildBrandVoiceLibrary(seededCustomVoices);
    const seededSelectedId = resolveSelectedBrandVoiceId(
      selectedCampaign?.options?.selectedBrandVoiceId || "",
      seededLibrary
    );
    setBrandVoiceCustomDraft(seededCustomVoices);
    setBrandVoiceCustomInitial(JSON.parse(JSON.stringify(seededCustomVoices)));
    setBrandVoiceSelectedIdDraft(seededSelectedId);
    setBrandVoiceSelectedIdInitial(seededSelectedId);
    setIsBrandVoiceModalOpen(true);
  };

  const closeBrandVoiceModal = () => {
    if (savingBrandVoices) return;
    setIsBrandVoiceModalOpen(false);
    setBrandVoiceCustomDraft([]);
    setBrandVoiceCustomInitial([]);
    setBrandVoiceSelectedIdDraft("");
    setBrandVoiceSelectedIdInitial("");
  };

  const handleAddCustomBrandVoice = () => {
    const nextIndex = Math.max(1, (brandVoiceCustomDraft || []).length + 1);
    const nextVoice = createDefaultCustomBrandVoice(nextIndex);
    setBrandVoiceCustomDraft((prev) =>
      normalizeCustomBrandVoices([...(Array.isArray(prev) ? prev : []), nextVoice])
    );
    if (nextVoice?.brandVoiceId) {
      setBrandVoiceSelectedIdDraft(nextVoice.brandVoiceId);
    }
  };

  const handleDeleteCustomBrandVoice = (brandVoiceId) => {
    const normalizedId = String(brandVoiceId || "").trim();
    if (!normalizedId) return;
    setBrandVoiceCustomDraft((prev) => {
      const nextDraft = normalizeCustomBrandVoices(
        (Array.isArray(prev) ? prev : []).filter((entry) => entry?.brandVoiceId !== normalizedId)
      );
      if (String(brandVoiceSelectedIdDraft || "").trim() === normalizedId) {
        const nextLibrary = buildBrandVoiceLibrary(nextDraft);
        setBrandVoiceSelectedIdDraft(resolveSelectedBrandVoiceId("", nextLibrary));
      }
      return nextDraft;
    });
  };

  const handleUpdateSelectedCustomBrandVoice = (updates = {}) => {
    const targetId = String(selectedModalBrandVoiceId || "").trim();
    if (!targetId) return;
    setBrandVoiceCustomDraft((prev) =>
      normalizeCustomBrandVoices(
        (Array.isArray(prev) ? prev : []).map((entry) => {
          if (String(entry?.brandVoiceId || "").trim() !== targetId) return entry;
          return normalizeBrandVoice({
            ...entry,
            ...updates,
            sourceType: "custom",
            updatedTime: Math.floor(Date.now() / 1000),
          });
        })
      )
    );
  };

  const handleSaveBrandVoiceModal = async () => {
    if (!employee || !selectedCampaign?.campaignId || !hasBrandVoiceModalChanges) return;
    try {
      setSavingBrandVoices(true);
      const payloadCustomVoices = normalizeCustomBrandVoices(brandVoiceCustomDraft || []);
      const payloadLibrary = buildBrandVoiceLibrary(payloadCustomVoices);
      const payloadSelectedId = resolveSelectedBrandVoiceId(brandVoiceSelectedIdDraft, payloadLibrary);
      const api = new ApiService({ employee });
      const response = await api.post("landmark/sendora/updateCampaign", {
        campaignId: selectedCampaign.campaignId,
        options: {
          ...(selectedCampaign?.options || {}),
          brandVoiceCustomVoices: payloadCustomVoices,
          selectedBrandVoiceId: payloadSelectedId,
        },
      });
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      const savedCustomVoices = normalizeCustomBrandVoices(
        response?.options?.brandVoiceCustomVoices || payloadCustomVoices
      );
      const savedLibrary = buildBrandVoiceLibrary(savedCustomVoices);
      const savedSelectedId = resolveSelectedBrandVoiceId(
        response?.options?.selectedBrandVoiceId || payloadSelectedId,
        savedLibrary
      );
      setBrandVoiceCustomDraft(savedCustomVoices);
      setBrandVoiceCustomInitial(savedCustomVoices);
      setBrandVoiceSelectedIdDraft(savedSelectedId);
      setBrandVoiceSelectedIdInitial(savedSelectedId);
      setSelectedCampaign((prev) =>
        prev
          ? {
              ...prev,
              options: {
                ...(prev.options || {}),
                brandVoiceCustomVoices: savedCustomVoices,
                selectedBrandVoiceId: savedSelectedId,
              },
            }
          : prev
      );
      toast.success("Brand voice settings saved");
      setIsBrandVoiceModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Could not save brand voice settings");
    } finally {
      setSavingBrandVoices(false);
    }
  };

  const openAiVariablesModal = () => {
    if (!selectedCampaign?.campaignId) return;
    const seeded = normalizeCampaignAiVariables(campaignAiVariablesDraft || []);
    const nextDraft = seeded.length ? seeded : [createDefaultCampaignAiVariable(1)];
    setCampaignAiVariablesDraft(nextDraft);
    setCampaignAiVariablesInitial(JSON.parse(JSON.stringify(nextDraft)));
    setSelectedAiVariableTokenKey(String(nextDraft?.[0]?.tokenKey || "aiVariable1"));
    setIsAiVariablesModalOpen(true);
  };

  const closeAiVariablesModal = () => {
    if (savingCampaignAiVariables) return;
    setIsAiVariablesModalOpen(false);
    setSelectedAiVariableTokenKey("");
  };

  const handleAddCampaignAiVariable = () => {
    const nextIndex = Math.max(
      1,
      ...normalizeCampaignAiVariables(campaignAiVariablesDraft || []).map((entry) =>
        parseAiVariableTokenIndex(entry?.tokenKey || "", 1)
      )
    ) + 1;
    const newVariable = createDefaultCampaignAiVariable(nextIndex);
    setCampaignAiVariablesDraft((prev) =>
      normalizeCampaignAiVariables([...(Array.isArray(prev) ? prev : []), newVariable])
    );
    setSelectedAiVariableTokenKey(newVariable.tokenKey);
  };

  const handleUpdateSelectedAiVariablePrompt = (prompt) => {
    const selectedTokenKey = String(selectedAiVariableTokenKey || "").trim().toLowerCase();
    if (!selectedTokenKey) return;
    setCampaignAiVariablesDraft((prev) =>
      normalizeCampaignAiVariables(
        (Array.isArray(prev) ? prev : []).map((entry) => {
          const tokenKey = String(entry?.tokenKey || "").trim().toLowerCase();
          if (tokenKey !== selectedTokenKey) return entry;
          return normalizeCampaignAiVariable({
            ...entry,
            prompt: String(prompt || "").slice(0, AI_VARIABLE_PROMPT_MAX_CHARS),
            updatedTime: Math.floor(Date.now() / 1000),
          });
        })
      )
    );
  };

  const handleSaveAiVariablesModal = async () => {
    if (!employee || !selectedCampaign?.campaignId || !hasAiVariablesModalChanges) return;
    try {
      setSavingCampaignAiVariables(true);
      const payloadAiVariables = normalizeCampaignAiVariables(campaignAiVariablesDraft || []);
      const api = new ApiService({ employee });
      const response = await api.post("landmark/sendora/updateCampaign", {
        campaignId: selectedCampaign.campaignId,
        options: {
          ...(selectedCampaign?.options || {}),
          aiVariables: payloadAiVariables,
        },
      });
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      const savedAiVariables = normalizeCampaignAiVariables(response?.options?.aiVariables || payloadAiVariables);
      setCampaignAiVariablesDraft(savedAiVariables);
      setCampaignAiVariablesInitial(savedAiVariables);
      setSelectedCampaign((prev) =>
        prev
          ? {
              ...prev,
              options: {
                ...(prev.options || {}),
                aiVariables: savedAiVariables,
              },
            }
          : prev
      );
      toast.success("AI variables saved");
      setIsAiVariablesModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Could not save AI variables");
    } finally {
      setSavingCampaignAiVariables(false);
    }
  };

  const toggleSenderSignatureCodeView = (signatureId) => {
    const key = String(signatureId || "").trim() || "default";
    setSenderSignatureCodeViewById((prev) => ({
      ...(prev || {}),
      [key]: !(prev || {})[key],
    }));
  };

  const updateSenderSignatureDraftForSelectedSender = (updater) => {
    const senderEmail = normalizeEmail(selectedSignatureSenderEmail);
    if (!senderEmail) return;
    const currentPreference =
      normalizeSenderSignaturePreference(senderSignatureDraftByEmail?.[senderEmail] || null) ||
      createDefaultSenderSignaturePreference(senderEmail);
    if (!currentPreference) return;
    const nextPreference = normalizeSenderSignaturePreference(updater(currentPreference) || null);
    if (!nextPreference) return;
    setSenderSignatureDraftByEmail((prev) => ({
      ...(prev || {}),
      [senderEmail]: nextPreference,
    }));
  };

  const handleSelectSenderSignatureThumbnail = (signatureId) => {
    const selectedSignatureId = String(signatureId || "default").trim() || "default";
    updateSenderSignatureDraftForSelectedSender((preference) => {
      if (selectedSignatureId === String(preference?.selectedSignatureId || "default")) return preference;
      return {
        ...preference,
        selectedSignatureId,
      };
    });
  };

  const handleAddCustomSenderSignature = () => {
    const now = Math.floor(Date.now() / 1000);
    const newSignature = normalizeCustomSenderSignature({
      signatureId: `sig_${makeId("sender")}`,
      html: normalizeSenderSignatureHtml(DEFAULT_SENDER_SIGNATURE_TEMPLATE),
      createdTime: now,
      updatedTime: now,
    });
    updateSenderSignatureDraftForSelectedSender((preference) => ({
      ...preference,
      selectedSignatureId: newSignature.signatureId,
      customSignatures: [
        ...(Array.isArray(preference?.customSignatures) ? preference.customSignatures : []),
        newSignature,
      ],
    }));
  };

  const handleDeleteCustomSenderSignature = (signatureId) => {
    const normalizedSignatureId = String(signatureId || "").trim();
    if (!normalizedSignatureId) return;
    updateSenderSignatureDraftForSelectedSender((preference) => {
      const nextCustomSignatures = (Array.isArray(preference?.customSignatures)
        ? preference.customSignatures
        : []
      ).filter((entry) => String(entry?.signatureId || "").trim() !== normalizedSignatureId);
      const nextSelectedSignatureId =
        String(preference?.selectedSignatureId || "default").trim() === normalizedSignatureId
          ? "default"
          : String(preference?.selectedSignatureId || "default").trim() || "default";
      return {
        ...preference,
        selectedSignatureId: nextSelectedSignatureId,
        customSignatures: nextCustomSignatures,
      };
    });
  };

  const handleSaveCustomSenderSignatureHtml = (signatureId, html) => {
    const normalizedSignatureId = String(signatureId || "").trim();
    if (!normalizedSignatureId) return;
    updateSenderSignatureDraftForSelectedSender((preference) => ({
      ...preference,
      customSignatures: (Array.isArray(preference?.customSignatures)
        ? preference.customSignatures
        : []
      ).map((entry) => {
        if (String(entry?.signatureId || "").trim() !== normalizedSignatureId) return entry;
        return normalizeCustomSenderSignature({
          ...entry,
          html: normalizeSenderSignatureHtml(html),
          updatedTime: Math.floor(Date.now() / 1000),
        });
      }),
    }));
  };

  const handleSaveSenderSignatureModal = async () => {
    if (!hasSignatureModalChanges) return;
    const changedEmails = Array.from(
      new Set([
        ...Object.keys(senderSignatureInitialByEmail || {}),
        ...Object.keys(senderSignatureDraftByEmail || {}),
      ])
    ).filter((email) => {
      const left = serializeSenderSignaturePreference(senderSignatureInitialByEmail?.[email] || null);
      const right = serializeSenderSignaturePreference(senderSignatureDraftByEmail?.[email] || null);
      return left !== right;
    });
    if (!changedEmails.length) return;
    setSavingSenderSignatures(true);
    let allSaved = true;
    for (const senderEmail of changedEmails) {
      const pref = normalizeSenderSignaturePreference(senderSignatureDraftByEmail?.[senderEmail] || null);
      if (!pref?.senderEmailLowercase) continue;
      // eslint-disable-next-line no-await-in-loop
      const ok = await upsertSenderSignaturePreference(
        pref.senderEmailLowercase,
        pref.selectedSignatureId || "default",
        pref.customSignatures || []
      );
      if (!ok) {
        allSaved = false;
        break;
      }
    }
    setSavingSenderSignatures(false);
    if (!allSaved) return;
    closeSenderSignatureModal();
    toast.success("Sender signatures saved");
  };

  const handleSaveSenderAccount = async (event) => {
    event.preventDefault();
    if (!employee) return;
    const payload = {
      email: normalizeEmail(senderAccountForm.email),
      firstName: String(senderAccountForm.firstName || "").trim(),
      lastName: String(senderAccountForm.lastName || "").trim(),
    };
    if (!payload.email) {
      toast.error("Sender email is required");
      return;
    }
    if (!payload.firstName || !payload.lastName) {
      toast.error("Sender first and last name are required");
      return;
    }
    try {
      const api = new ApiService({ employee });
      const response = await api.post("landmark/sendora/upsertSenderAccount", payload);
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      syncSenderAccountsFromResponse(response);
      addSequenceSenderAccountByEmail(payload.email);
      setIsSenderAccountModalOpen(false);
      setSenderAccountForm({ email: "", firstName: "", lastName: "" });
      setSelectedSenderAccountEmail("");
      toast.success("Sender account saved and added");
    } catch (err) {
      console.error(err);
      toast.error("Could not save sender account");
    }
  };

  const handleCreateCampaign = async (event) => {
    event.preventDefault();
    if (!employee) return;
    if (!createModalForm.name.trim()) {
      toast.error("Campaign name is required");
      return;
    }
    if (!createModalForm.salesforceCampaignId) {
      toast.error("Please select a Salesforce campaign");
      return;
    }

    const payload = {
      name: createModalForm.name.trim(),
      notes: createModalForm.notes.trim(),
      salesforceCampaignId: createModalForm.salesforceCampaignId,
    };

    try {
      setSavingCampaign(true);
      const api = new ApiService({ employee });
      const response = await api.post("landmark/sendora/createCampaign", payload, null, {
        timeout: SENDORA_CREATE_CAMPAIGN_TIMEOUT_MS,
      });
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      toast.success("Campaign created");
      setIsCreateModalOpen(false);
      setCreateModalForm({
        name: "",
        notes: "",
        salesforceCampaignId: "",
      });
      await loadCampaigns();
      if (response?.campaign?.campaignId) {
        pendingCampaignTabOverrideRef.current = "emails";
        setSelectedCampaignId(response.campaign.campaignId);
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not create campaign");
    } finally {
      setSavingCampaign(false);
    }
  };

  const handleCreateSalesforceCampaignChange = (salesforceCampaignId) => {
    const selectedSalesforceCampaign = createModalSalesforceCampaigns.find(
      (campaign) => campaign.id === salesforceCampaignId
    );
    const nextCampaignName = selectedSalesforceCampaign
      ? getUniqueCampaignNameFromBase(selectedSalesforceCampaign.name || "", campaigns)
      : "";
    setCreateModalForm((prev) => ({
      ...prev,
      salesforceCampaignId,
      name: nextCampaignName,
    }));
  };
  const openCreateCampaignModal = () => {
    loadSalesforceCampaigns({ force: true });
    setIsCreateModalOpen(true);
  };
  const closeCreateCampaignModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleSaveCampaignSettings = async () => {
    if (!employee || !selectedCampaign) return;
    try {
      setSavingCampaign(true);
      const api = new ApiService({ employee });
      const payload = {
        campaignId: selectedCampaign.campaignId,
        name: editForm.name.trim(),
        notes: editForm.notes,
        salesforceCampaignId: editForm.salesforceCampaignId,
        collaboratorEmails: editForm.collaboratorEmails || [],
        options: {
          cc: editForm.ccEmails || [],
          bcc: editForm.bccEmails || [],
          dailyLimit: toSafeNumber(editForm.dailyLimit, 5000),
          campaignPriority: normalizeCampaignSpeed(editForm.campaignPriority || "medium"),
          minTimeGapMinutes: toSafeNumber(editForm.minTimeGapMinutes, 0.096),
          stopSendingOnReply: !!editForm.stopSendingOnReply,
          stopCampaignForCompanyOnReply: !!editForm.stopCampaignForCompanyOnReply,
          stopSendingOnAutoReply: !!editForm.stopSendingOnAutoReply,
          prioritizeNewLeads: !!editForm.prioritizeNewLeads,
          enableMillionVerifier: !!runSettingsForm.enableMillionVerifier,
          emailVerificationMode: normalizeEmailVerificationMode(
            runSettingsForm.emailVerificationMode
          ),
          syncInvalidMailboxToSalesforce: !!runSettingsForm.syncInvalidMailboxToSalesforce,
          syncSalesforceStatusAfterSend: !!runSettingsForm.syncSalesforceStatusAfterSend,
          postSendSalesforceStatus: String(runSettingsForm.postSendSalesforceStatus || "").trim(),
          updateSalesforceOutreachCounters: !!runSettingsForm.updateSalesforceOutreachCounters,
          pruneQueuedLeadsOnSalesforceStatusChange:
            !!runSettingsForm.pruneQueuedLeadsOnSalesforceStatusChange,
        },
      };
      const payloadSignature = JSON.stringify(payload);
      const response = await api.post("landmark/sendora/updateCampaign", payload);
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      lastSavedCampaignSettingsSignatureRef.current = payloadSignature;
      toast.success("Changes saved", { id: "sendora-options-saved" });
      await refreshSelectedCampaignData(selectedCampaign.campaignId, { preserveActiveTab: true });
    } catch (err) {
      console.error(err);
      toast.error("Could not save campaign settings");
    } finally {
      setSavingCampaign(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "options") return;
    if (!employee || !selectedCampaign?.campaignId) return;
    if (savingCampaign) return;

    const payload = {
      campaignId: selectedCampaign.campaignId,
      name: editForm.name.trim(),
      notes: editForm.notes,
      salesforceCampaignId: editForm.salesforceCampaignId,
      collaboratorEmails: editForm.collaboratorEmails || [],
      options: {
        cc: editForm.ccEmails || [],
        bcc: editForm.bccEmails || [],
        dailyLimit: toSafeNumber(editForm.dailyLimit, 5000),
        campaignPriority: normalizeCampaignSpeed(editForm.campaignPriority || "medium"),
        minTimeGapMinutes: toSafeNumber(editForm.minTimeGapMinutes, 0.096),
        stopSendingOnReply: !!editForm.stopSendingOnReply,
        stopCampaignForCompanyOnReply: !!editForm.stopCampaignForCompanyOnReply,
        stopSendingOnAutoReply: !!editForm.stopSendingOnAutoReply,
        prioritizeNewLeads: !!editForm.prioritizeNewLeads,
        enableMillionVerifier: !!runSettingsForm.enableMillionVerifier,
        emailVerificationMode: normalizeEmailVerificationMode(
          runSettingsForm.emailVerificationMode
        ),
        syncInvalidMailboxToSalesforce: !!runSettingsForm.syncInvalidMailboxToSalesforce,
        syncSalesforceStatusAfterSend: !!runSettingsForm.syncSalesforceStatusAfterSend,
        postSendSalesforceStatus: String(runSettingsForm.postSendSalesforceStatus || "").trim(),
        updateSalesforceOutreachCounters: !!runSettingsForm.updateSalesforceOutreachCounters,
        pruneQueuedLeadsOnSalesforceStatusChange:
          !!runSettingsForm.pruneQueuedLeadsOnSalesforceStatusChange,
      },
    };
    const nextSignature = JSON.stringify(payload);

    if (campaignSettingsSeededCampaignIdRef.current !== selectedCampaign.campaignId) {
      campaignSettingsSeededCampaignIdRef.current = selectedCampaign.campaignId;
      lastSavedCampaignSettingsSignatureRef.current = nextSignature;
      return;
    }
    if (nextSignature === lastSavedCampaignSettingsSignatureRef.current) return;

    if (campaignSettingsAutosaveTimerRef.current) {
      clearTimeout(campaignSettingsAutosaveTimerRef.current);
    }

    campaignSettingsAutosaveTimerRef.current = setTimeout(async () => {
      try {
        setSavingCampaign(true);
        const api = new ApiService({ employee });
        const response = await api.post("landmark/sendora/updateCampaign", payload);
        if (response?.error) {
          toast.error(response.error);
          return;
        }
        lastSavedCampaignSettingsSignatureRef.current = nextSignature;
        toast.success("Changes saved", { id: "sendora-options-saved" });
      } catch (error) {
        console.error(error);
        toast.error("Could not save campaign settings");
      } finally {
        setSavingCampaign(false);
      }
    }, 700);

    return () => {
      if (campaignSettingsAutosaveTimerRef.current) {
        clearTimeout(campaignSettingsAutosaveTimerRef.current);
      }
    };
  }, [
    activeTab,
    employee,
    selectedCampaign?.campaignId,
    editForm.name,
    editForm.notes,
    editForm.salesforceCampaignId,
    editForm.collaboratorEmails,
    editForm.ccEmails,
    editForm.bccEmails,
    editForm.dailyLimit,
    editForm.campaignPriority,
    editForm.minTimeGapMinutes,
    editForm.stopSendingOnReply,
    editForm.stopCampaignForCompanyOnReply,
    editForm.stopSendingOnAutoReply,
    editForm.prioritizeNewLeads,
    runSettingsForm.enableMillionVerifier,
    runSettingsForm.emailVerificationMode,
    runSettingsForm.syncInvalidMailboxToSalesforce,
    runSettingsForm.syncSalesforceStatusAfterSend,
    runSettingsForm.postSendSalesforceStatus,
    runSettingsForm.updateSalesforceOutreachCounters,
    runSettingsForm.pruneQueuedLeadsOnSalesforceStatusChange,
    savingCampaign,
  ]);

  useEffect(() => {
    if (!employee) return;
    if (savingDomainInfrastructure) return;
    const normalizedRules = normalizeDomainInfrastructureRules(domainInfrastructureRules || []);
    const nextSignature = buildDomainInfrastructureSignature(normalizedRules);
    if (nextSignature === lastSavedDomainInfrastructureSignatureRef.current) return;
    if (domainInfrastructureAutosaveTimerRef.current) {
      clearTimeout(domainInfrastructureAutosaveTimerRef.current);
    }
    domainInfrastructureAutosaveTimerRef.current = setTimeout(async () => {
      try {
        setSavingDomainInfrastructure(true);
        const api = new ApiService({ employee });
        let latestResponse = null;
        for (const rule of normalizedRules) {
          // eslint-disable-next-line no-await-in-loop
          const response = await api.post("landmark/sendora/upsertDomainInfrastructurePreference", {
            domainSuffix: String(rule?.domainSuffix || ""),
            defaultInfrastructure: String(rule?.defaultInfrastructure || ""),
            allowedInfrastructures: Array.isArray(rule?.allowedInfrastructures)
              ? rule.allowedInfrastructures
              : [],
          });
          if (response?.error) {
            throw new Error(String(response.error || "Could not save sending infrastructure"));
          }
          latestResponse = response;
        }
        if (latestResponse) {
          syncSenderAccountsFromResponse(latestResponse);
        } else {
          lastSavedDomainInfrastructureSignatureRef.current = nextSignature;
        }
        toast.success("Sending infrastructure settings saved", {
          id: "sendora-infrastructure-saved",
        });
      } catch (error) {
        console.error(error);
        toast.error("Could not save sending infrastructure settings");
      } finally {
        setSavingDomainInfrastructure(false);
      }
    }, 700);
    return () => {
      if (domainInfrastructureAutosaveTimerRef.current) {
        clearTimeout(domainInfrastructureAutosaveTimerRef.current);
      }
    };
  }, [employee, domainInfrastructureRules, savingDomainInfrastructure]);

  const handleSaveSchedule = async ({
    showSuccessToast = false,
    successToastId = "",
    refreshAfterSave = false,
    showErrorToast = true,
  } = {}) => {
    if (!employee || !selectedCampaign) return;
    try {
      setSavingSchedule(true);
      const now = Math.floor(Date.now() / 1000);
      const schedulePayload = (scheduleDrafts || []).map((schedule) => {
        const bypassSchedule =
          schedule?.bypassSchedule === true || schedule?.isActive === false;
        return {
          ...DEFAULT_SCHEDULE,
          ...schedule,
          isActive: !bypassSchedule,
          bypassSchedule,
          pauseSendingAfterFridayNoon: !!endFridayAtNoon,
          scheduleId: schedule.scheduleId || makeId("sch"),
          createdTime: schedule.createdTime || now,
          updatedTime: now,
        };
      });
      const startDateEpoch = dateInputToEpoch(campaignDateWindow.startDate);
      const endDateEpoch = campaignDateWindow.noEndDate
        ? 0
        : dateInputToEpoch(campaignDateWindow.endDate);
      const api = new ApiService({ employee });
      const response = await api.post("landmark/sendora/updateCampaign", {
        campaignId: selectedCampaign.campaignId,
        schedules: schedulePayload,
        dateWindow: {
          startDate: startDateEpoch > 0 ? startDateEpoch : 0,
          endDate: endDateEpoch > 0 ? endDateEpoch : 0,
        },
      });
      if (response?.error) {
        if (showErrorToast) {
          toast.error(response.error);
        }
        return;
      }
      if (showSuccessToast) {
        if (String(successToastId || "").trim()) {
          toast.success("Schedule saved", { id: String(successToastId || "").trim() });
        } else {
          toast.success("Schedule saved");
        }
      }
      lastSavedScheduleSignatureRef.current = buildScheduleAutoSaveSignature(
        scheduleDrafts,
        campaignDateWindow,
        endFridayAtNoon
      );
      if (refreshAfterSave) {
        await refreshSelectedCampaignData(selectedCampaign.campaignId, {
          preserveActiveTab: true,
        });
      }
    } catch (err) {
      console.error(err);
      if (showErrorToast) {
        toast.error("Could not save schedule");
      }
    } finally {
      setSavingSchedule(false);
    }
  };

  useEffect(() => {
    if (activeTab !== "schedule") return;
    if (!employee || !selectedCampaign?.campaignId) return;
    if (!scheduleDrafts.length) return;
    if (savingSchedule) return;

    const nextSignature = buildScheduleAutoSaveSignature(
      scheduleDrafts,
      campaignDateWindow,
      endFridayAtNoon
    );
    if (nextSignature === lastSavedScheduleSignatureRef.current) return;

    if (scheduleAutosaveTimerRef.current) {
      clearTimeout(scheduleAutosaveTimerRef.current);
    }

    scheduleAutosaveTimerRef.current = setTimeout(() => {
      handleSaveSchedule({
        showSuccessToast: true,
        successToastId: "sendora-schedule-autosave-saved",
        showErrorToast: true,
      });
    }, 700);

    return () => {
      if (scheduleAutosaveTimerRef.current) {
        clearTimeout(scheduleAutosaveTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    employee,
    selectedCampaign?.campaignId,
    scheduleDrafts,
    campaignDateWindow.startDate,
    campaignDateWindow.endDate,
    campaignDateWindow.noEndDate,
    endFridayAtNoon,
    savingSchedule,
  ]);

  const handleSaveSequence = async ({ silent = false, refreshAfterSave = true } = {}) => {
    if (!employee || !selectedCampaign) return;
    try {
      setSavingSequence(true);
      const now = Math.floor(Date.now() / 1000);
      const orderedSteps = (sequenceDraft.steps || []).map((step, stepIndex) => ({
        ...step,
        stepId: String(step?.stepId || "").trim() || `legacy_step_${stepIndex + 1}`,
        orderIndex: stepIndex + 1,
      }));
      const seenStepIds = new Set();
      for (const step of orderedSteps) {
        const normalizedStepId = String(step?.stepId || "").trim();
        const mode =
          Number(step?.orderIndex || 0) === 1
            ? "standard"
            : String(step?.mode || "").trim().toLowerCase() === "ott"
            ? "ott"
            : "standard";
        const sourceStepId = String(step?.ottSourceStepId || "").trim();
        if (mode === "ott" && (!sourceStepId || !seenStepIds.has(sourceStepId))) {
          toast.error(`Step ${Number(step?.orderIndex || 1)} OTT source must reference an earlier step`);
          return;
        }
        seenStepIds.add(normalizedStepId);
      }
      const normalizedSteps = (sequenceDraft.steps || []).map((step, stepIndex) => ({
        ...step,
        stepId: String(step?.stepId || "").trim() || `legacy_step_${stepIndex + 1}`,
        orderIndex: stepIndex + 1,
        mode:
          stepIndex === 0
            ? "standard"
            : String(step?.mode || "").trim().toLowerCase() === "ott"
            ? "ott"
            : "standard",
        ottSourceStepId:
          stepIndex > 0 && String(step?.mode || "").trim().toLowerCase() === "ott"
            ? String(step?.ottSourceStepId || "").trim()
            : "",
        speed: normalizeStepSpeed(step?.speedMode || step?.speed || "medium"),
        speedMode: normalizeStepSpeed(step?.speedMode || step?.speed || "medium"),
        dailyLimit: Math.max(1, Number(step?.dailyLimit || 5000) || 5000),
        targetDurationSeconds: Math.max(0, Number(step?.targetDurationSeconds || 0) || 0),
        durationPresetMultiplier: Math.max(
          0.0001,
          Number(
            step?.durationPresetMultiplier ||
              CAMPAIGN_SPEED_PRESETS[
                normalizeStepSpeed(step?.speedMode || step?.speed || "medium")
              ]?.multiplier ||
              1
          ) || 1
        ),
        pacingSnapshot:
          step?.pacingSnapshot && typeof step?.pacingSnapshot === "object" ? step.pacingSnapshot : {},
        updatedTime: now,
        variants: (() => {
          const variants = (step.variants || []).map((variant, variantIndex) => {
            const isSelectedVariant =
              stepIndex === selectedStepIndex && variantIndex === selectedVariantIndex;
            const bodyFromEditor =
              isSelectedVariant &&
              activeTab === "emails" &&
              !isCodeView &&
              editorRef.current
                ? normalizeRichEditorHtml(
                    stripZeroWidthSpaces(
                      stripVariableHighlightMarkup(editorRef.current.innerHTML || "")
                    )
                  )
                : null;
            const subjectFromEditor =
              isSelectedVariant &&
              activeTab === "emails" &&
              subjectEditorRef.current
                ? String(subjectEditorRef.current?.innerText || "")
                    .replace(/[\r\n]+/g, " ")
                : null;
            return {
              ...variant,
              isActive: !!variant.isActive,
              senderAccount: normalizeSenderAccount(variant?.senderAccount || {}),
              senderAccountEmail: normalizeEmail(
                variant?.senderAccount?.emailLowercase ||
                  variant?.senderAccount?.email ||
                  variant?.senderAccountEmail ||
                  ""
              ),
              subject: subjectFromEditor != null ? subjectFromEditor : (variant?.subject ?? ""),
              body: bodyFromEditor != null ? bodyFromEditor : (variant?.body ?? ""),
              updatedTime: now,
            };
          });
          if (!variants.some((variant) => variant.isActive) && variants.length) {
            variants[0] = { ...variants[0], isActive: true };
          }
          return variants;
        })(),
      }));
      const api = new ApiService({ employee });
      const normalizedSenderAccounts = normalizeSenderAccounts(
        sequenceDraft.senderAccounts || [],
        sequenceDraft.senderAccountEmails || []
      );
      const normalizedSenderAccountEmails = normalizedSenderAccounts.map(
        (entry) => entry.emailLowercase
      );
      const response = await api.post("landmark/sendora/updateCampaign", {
        campaignId: selectedCampaign.campaignId,
        sequences: {
          steps: normalizedSteps,
          sequenceDefinitions: (sequencesDraft || []).map((entry, index) => ({
            sequenceId: String(entry?.sequenceId || "").trim() || makeId("sqn"),
            name: String(entry?.name || "").trim() || `Sequence ${String.fromCharCode(65 + index)}`,
            nodes: (entry?.nodes || [])
              .map((node, nodeIndex) => ({
                ...normalizeSequenceNode(
                  {
                    ...node,
                    nodeId: String(node?.nodeId || "").trim() || makeId("sqn_node"),
                  },
                  nodeIndex
                ),
                orderHint: nodeIndex,
              }))
              .filter((node) => !!node.stepId)
              .map((node, nodeIndex) => normalizeSequenceNode(node, nodeIndex)),
            waits: (entry?.waits || []).map((wait) => ({
              amount: Math.max(1, Number(wait?.amount || 1) || 1),
              unit: SEQUENCE_WAIT_UNITS.includes(String(wait?.unit || "")) ? wait.unit : "days",
            })),
          })),
          senderAccounts: normalizedSenderAccounts,
          senderAccountEmails: normalizedSenderAccountEmails,
        },
        options: {
          ...(selectedCampaign?.options || {}),
          senderAccounts: normalizedSenderAccounts,
          senderAccountEmails: normalizedSenderAccountEmails,
        },
      });
      if (response?.error) {
        if (!silent) {
          toast.error(response.error);
        }
        return;
      }
      const canonicalSavedSequence = normalizeSequence(
        {
          steps: normalizedSteps,
          sequenceDefinitions: (sequencesDraft || []).map((entry) => ({ ...(entry || {}) })),
          senderAccounts: normalizedSenderAccounts,
          senderAccountEmails: normalizedSenderAccountEmails,
        },
        normalizedSenderAccounts,
        normalizedSenderAccountEmails
      );
      const sigAfterSave = buildSequenceSaveSignature(canonicalSavedSequence);
      setLastSavedSequenceSignature(sigAfterSave);
      setLastSavedSequenceDefinitionsSignature(
        buildSequenceDefinitionsSaveSignature(sequencesDraft || [])
      );
      lastSavedSenderAccountsSignatureRef.current = buildSenderAccountsSignature(canonicalSavedSequence);
      setSequenceDraft(canonicalSavedSequence);
      if (!silent) {
        if (activeTab === "emails") {
          toast.success("Emails saved");
        } else {
          const savedSequenceCount = Math.max(0, Number((sequencesDraft || []).length || 0));
          toast.success(savedSequenceCount === 1 ? "Sequence saved" : "Sequences saved");
        }
      }
      if (refreshAfterSave) {
        await loadCampaignById(selectedCampaign.campaignId, {
          preserveActiveTab: true,
          preserveSequenceSelection: true,
          preserveSequenceDraft: true,
        });
        await loadCampaigns();
      }
    } catch (err) {
      console.error(err);
      if (!silent) {
        toast.error("Could not save sequence");
      }
    } finally {
      setSavingSequence(false);
    }
  };

  const hasInvalidRunStepDailyLimitInput = useMemo(
    () =>
      (sequenceDraft?.steps || []).some((step) => {
        const speed = normalizeStepSpeed(step?.speedMode || step?.speed || "medium");
        if (speed !== "custom") return false;
        const stepId = String(step?.stepId || "").trim();
        if (!stepId) return false;
        const inputParts = runStepDailyLimitInputs?.[stepId] || {};
        const customTargetSeconds = durationPartsToSeconds(inputParts);
        const metric = runStepMetricsByStepId.get(stepId) || {};
        const minSeconds = Math.max(0, Number(metric?.fastDurationSeconds || 0));
        return customTargetSeconds < minSeconds;
      }),
    [sequenceDraft?.steps, runStepDailyLimitInputs, runStepMetricsByStepId]
  );

  useEffect(() => {
    if (!selectedCampaignId || !employee) return;
    if (activeTab !== "run") return;
    if (hasInvalidRunStepDailyLimitInput) return;
    if (!(isSequenceDirty || isSequenceDefinitionsDirty) || savingSequence || runningMemberAction) {
      return;
    }
    if (sequenceRunStepAutosaveTimerRef.current) {
      clearTimeout(sequenceRunStepAutosaveTimerRef.current);
    }
    sequenceRunStepAutosaveTimerRef.current = setTimeout(() => {
      sequenceRunStepAutosaveTimerRef.current = null;
      handleSaveSequence({ silent: true, refreshAfterSave: false });
    }, 700);
    return () => {
      if (sequenceRunStepAutosaveTimerRef.current) {
        clearTimeout(sequenceRunStepAutosaveTimerRef.current);
        sequenceRunStepAutosaveTimerRef.current = null;
      }
    };
  }, [
    selectedCampaignId,
    employee,
    activeTab,
    isSequenceDirty,
    isSequenceDefinitionsDirty,
    currentSequenceSignature,
    currentSequenceDefinitionsSignature,
    hasInvalidRunStepDailyLimitInput,
    savingSequence,
    runningMemberAction,
    sequencesDraft,
  ]);

  const setRunStepPreviewUpdating = (stepId = "", sequenceId = "", isUpdating = true) => {
    const key = getRunStepDurationInputKey(stepId, sequenceId);
    if (!key) return;
    setRunStepPreviewUpdatingByKey((prev) => {
      if (isUpdating) {
        if (prev?.[key]) return prev;
        return { ...prev, [key]: true };
      }
      if (!prev?.[key]) return prev;
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });
  };

  const clearRunStepPreviewUpdatingForStep = (stepId = "", sequenceIds = []) => {
    const normalizedStepId = String(stepId || "").trim();
    if (!normalizedStepId) return;
    const keys = new Set();
    const manualKey = getRunStepDurationInputKey(normalizedStepId, "");
    if (manualKey) keys.add(manualKey);
    (Array.isArray(sequenceIds) ? sequenceIds : []).forEach((sequenceId) => {
      const key = getRunStepDurationInputKey(normalizedStepId, sequenceId);
      if (key) keys.add(key);
    });
    if (!keys.size) return;
    setRunStepPreviewUpdatingByKey((prev) => {
      let changed = false;
      const next = { ...(prev || {}) };
      keys.forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(next, key)) return;
        delete next[key];
        changed = true;
      });
      return changed ? next : prev;
    });
  };

  const applySpeedChangeRequeuePlan = async (stepId = "") => {
    const normalizedStepId = String(stepId || "").trim();
    if (!employee || !selectedCampaign || !normalizedStepId) {
      clearRunStepPreviewUpdatingForStep(normalizedStepId);
      return;
    }
    const queuedSequenceIds = Array.from(
      speedChangeSequenceIdsRef.current?.[normalizedStepId] || new Set()
    ).filter(Boolean);
    speedChangeSequenceIdsRef.current[normalizedStepId] = new Set();
    const stepMetric = runStepMetricsByStepId.get(normalizedStepId) || null;
    const stepOrderIndex = Number(stepMetric?.orderIndex || 0) || null;
    const stepLabel = stepOrderIndex ? `Step ${stepOrderIndex}` : "Step";
    const impactedSequences = (runSequenceMetrics || []).filter((sequence) => {
      const sequenceId = String(sequence?.sequenceId || "").trim();
      if (queuedSequenceIds.includes(sequenceId)) return true;
      return (sequence?.stepBreakdowns || []).some(
        (entry) => String(entry?.stepId || "").trim() === normalizedStepId
      );
    });
    const impactedSequenceIds = Array.from(
      new Set(
        (impactedSequences || [])
          .map((entry) => String(entry?.sequenceId || "").trim())
          .filter(Boolean)
      )
    );
    const previewSequenceIdsForReset = Array.from(
      new Set([...(queuedSequenceIds || []), ...(impactedSequenceIds || [])])
    );

    let didPauseAny = false;
    let didClearAny = false;
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      if (stepMetric && Number(stepMetric?.queuedCount || 0) > 0) {
        if (!stepMetric?.isPaused) {
          logSendSpeedApiDebug("REQ setCampaignStepPause", {
            campaignId: selectedCampaign.campaignId,
            stepId: normalizedStepId,
            action: "pause",
          });
          const pauseResponse = await api.post(
            `landmark/sendora/setCampaignStepPause/${selectedCampaign.campaignId}/${normalizedStepId}`,
            { action: "pause" }
          );
          logSendSpeedApiDebug("RES setCampaignStepPause", {
            campaignId: selectedCampaign.campaignId,
            stepId: normalizedStepId,
            error: pauseResponse?.error || "",
          });
          if (!pauseResponse?.error) {
            didPauseAny = true;
            toast.info(`${stepLabel} paused so new speed settings can be applied`);
          }
        }
        logSendSpeedApiDebug("REQ clearCampaignStepQueue", {
          campaignId: selectedCampaign.campaignId,
          stepId: normalizedStepId,
        });
        const clearResponse = await api.post(
          `landmark/sendora/clearCampaignStepQueue/${selectedCampaign.campaignId}/${normalizedStepId}`,
          {}
        );
        logSendSpeedApiDebug("RES clearCampaignStepQueue", {
          campaignId: selectedCampaign.campaignId,
          stepId: normalizedStepId,
          error: clearResponse?.error || "",
        });
        if (!clearResponse?.error) {
          didClearAny = true;
        }
      }

      for (const sequence of impactedSequences) {
        const sequenceId = String(sequence?.sequenceId || "").trim();
        if (!sequenceId || sequence?.status === "completed") continue;
        const sequenceLabel = `Sequence ${String(sequence?.sequenceLetter || "").trim()}: ${
          String(sequence?.sequenceName || "").trim() || "Untitled sequence"
        }`;
        const stepBreakdown = (sequence?.stepBreakdowns || []).find(
          (entry) => String(entry?.stepId || "").trim() === normalizedStepId
        );
        const hasQueuedForStep = Number(stepBreakdown?.queuedCount || 0) > 0;
        const shouldPauseSequence =
          (Number(sequence?.queuedCount || 0) > 0 || sequence?.hasStarted) &&
          sequence?.status !== "paused";
        if (shouldPauseSequence) {
          logSendSpeedApiDebug("REQ setCampaignSequencePause", {
            campaignId: selectedCampaign.campaignId,
            sequenceId,
            stepId: normalizedStepId,
            action: "pause",
          });
          const pauseResponse = await api.post(
            `landmark/sendora/setCampaignSequencePause/${selectedCampaign.campaignId}/${sequenceId}`,
            { action: "pause" }
          );
          logSendSpeedApiDebug("RES setCampaignSequencePause", {
            campaignId: selectedCampaign.campaignId,
            sequenceId,
            stepId: normalizedStepId,
            error: pauseResponse?.error || "",
          });
          if (!pauseResponse?.error) {
            didPauseAny = true;
            toast.info(`${sequenceLabel} paused so new speed settings can be applied`);
          }
        }
        if (hasQueuedForStep) {
          logSendSpeedApiDebug("REQ clearCampaignSequenceStepQueue", {
            campaignId: selectedCampaign.campaignId,
            sequenceId,
            stepId: normalizedStepId,
          });
          const clearResponse = await api.post(
            `landmark/sendora/clearCampaignSequenceStepQueue/${selectedCampaign.campaignId}/${sequenceId}/${normalizedStepId}`,
            {}
          );
          logSendSpeedApiDebug("RES clearCampaignSequenceStepQueue", {
            campaignId: selectedCampaign.campaignId,
            sequenceId,
            stepId: normalizedStepId,
            error: clearResponse?.error || "",
          });
          if (!clearResponse?.error) {
            didClearAny = true;
          }
        }
      }
    } catch (error) {
      console.error(error);
      logSendSpeedApiDebug("ERR applySpeedChangeRequeuePlan", {
        campaignId: selectedCampaign?.campaignId || "",
        stepId: normalizedStepId,
        message: String(error?.message || error || ""),
      });
      toast.error("Could not apply speed-change pause/requeue updates");
    } finally {
      setRunningMemberAction(false);
    }
    if (didPauseAny || didClearAny) {
      toast.success(
        "Speed updated. Restart paused steps/sequences to requeue with new timing. Sent leads are not requeued."
      );
      await loadCampaignMonitorSnapshot(selectedCampaign.campaignId);
    }
    clearRunStepPreviewUpdatingForStep(normalizedStepId, previewSequenceIdsForReset);
  };

  const queueSpeedChangeRequeuePlan = (stepId = "", sequenceId = "") => {
    const normalizedStepId = String(stepId || "").trim();
    const normalizedSequenceId = String(sequenceId || "").trim();
    if (!normalizedStepId) return;
    logSendSpeedApiDebug("QUEUE speed-change", {
      stepId: normalizedStepId,
      sequenceId: normalizedSequenceId,
    });
    setRunStepPreviewUpdating(normalizedStepId, normalizedSequenceId, true);
    if (runStepMetricsByStepId.get(normalizedStepId)) {
      setRunStepPreviewBackendSyncStateByStepId((prev) => {
        const next = { ...(prev || {}) };
        getRunStepPreviewSyncAliasKeys(normalizedStepId).forEach((aliasKey) => {
          next[aliasKey] = "pending";
        });
        return next;
      });
    }
    if (!speedChangeSequenceIdsRef.current[normalizedStepId]) {
      speedChangeSequenceIdsRef.current[normalizedStepId] = new Set();
    }
    if (normalizedSequenceId) {
      speedChangeSequenceIdsRef.current[normalizedStepId].add(normalizedSequenceId);
    }
    if (speedChangeRequeueTimersRef.current[normalizedStepId]) {
      clearTimeout(speedChangeRequeueTimersRef.current[normalizedStepId]);
    }
    speedChangeRequeueTimersRef.current[normalizedStepId] = setTimeout(() => {
      delete speedChangeRequeueTimersRef.current[normalizedStepId];
      applySpeedChangeRequeuePlan(normalizedStepId);
    }, 900);
  };

  const handleUpdateSequenceNodeSpeed = (sequenceId = "", stepId = "", patch = {}) => {
    const normalizedSequenceId = String(sequenceId || "").trim();
    const normalizedStepId = String(stepId || "").trim();
    if (!normalizedSequenceId || !normalizedStepId) return false;
    let didUpdate = false;
    setSequencesDraft((prev) =>
      (prev || []).map((entry) => {
        if (String(entry?.sequenceId || "").trim() !== normalizedSequenceId) return entry;
        const nodes = Array.isArray(entry?.nodes) ? entry.nodes : [];
        const nextNodes = nodes.map((node, nodeIndex) => {
          if (String(node?.stepId || "").trim() !== normalizedStepId) return node;
          const currentSpeedMode = normalizeSequenceNodeSpeedMode(node?.speedMode || "");
          const nextSpeedMode = Object.prototype.hasOwnProperty.call(patch, "speed")
            ? normalizeStepSpeed(patch?.speed || "")
            : currentSpeedMode;
          const currentTargetSeconds = Math.max(0, Number(node?.targetDurationSeconds || 0) || 0);
          const nextTargetRaw = Object.prototype.hasOwnProperty.call(patch, "targetDurationSeconds")
            ? Number(patch?.targetDurationSeconds || 0)
            : currentTargetSeconds;
          const nextTargetSeconds = Math.max(
            0,
            Number.isFinite(nextTargetRaw) ? Math.round(nextTargetRaw) : 0
          );
          didUpdate = true;
          return normalizeSequenceNode(
            {
              ...node,
              speedMode: nextSpeedMode,
              targetDurationSeconds: nextSpeedMode === "custom" ? nextTargetSeconds : 0,
            },
            nodeIndex
          );
        });
        return { ...entry, nodes: nextNodes };
      })
    );
    return didUpdate;
  };

  const handleUpdateRunStepSpeed = (stepIndex, patch = {}, { sequenceId = "" } = {}) => {
    const step = (sequenceDraft?.steps || [])[stepIndex] || null;
    const stepId = String(step?.stepId || "").trim();
    const normalizedSequenceId = String(sequenceId || "").trim();
    const didUpdateSequenceNode =
      !!normalizedSequenceId && !!stepId
        ? handleUpdateSequenceNodeSpeed(normalizedSequenceId, stepId, patch)
        : false;
    if (!didUpdateSequenceNode) {
      handleUpdateStepSpeed(stepIndex, patch);
    }
    if (activeTab !== "run" || !stepId) return;
    queueSpeedChangeRequeuePlan(stepId, normalizedSequenceId);
  };

  const handleTriggerStep = async (stepId, stepOrderIndex = null, eligibleLeadCount = null) => {
    const normalizedStepId = String(stepId || "").trim();
    if (!employee || !selectedCampaign || !normalizedStepId) return;
    if (isSequenceDirty) {
      toast.error("Save sequence changes before starting a step");
      return;
    }
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/triggerCampaignStep/${selectedCampaign.campaignId}/${normalizedStepId}`,
        {}
      );
      if (response?.error) {
        if (String(response.error || "").toLowerCase().includes("step not found")) {
          const stepLabel =
            Number(stepOrderIndex || 0) > 0
              ? `Step ${Number(stepOrderIndex)}`
              : "That step";
          toast.error(`${stepLabel} was not found in saved sequence. Save sequence and try again.`);
        } else {
          toast.error(response.error);
        }
        return;
      }
      const summary = response?.summary || {};
      const queuedCount = Number(summary?.queuedCount || 0);
      const expectedQueuedCount = Number.isFinite(Number(eligibleLeadCount))
        ? Math.max(0, Number(eligibleLeadCount))
        : Math.max(0, queuedCount);
      const stepLabel = Number(stepOrderIndex || 0) > 0 ? `Step ${Number(stepOrderIndex)}` : "step";
      toast.success(
        `Queuing ${expectedQueuedCount} lead${expectedQueuedCount === 1 ? "" : "s"} for ${stepLabel}`
      );
      loadCampaignMonitorSnapshot(selectedCampaign.campaignId).catch((error) => {
        console.error(error);
      });
    } catch (error) {
      console.error(error);
      toast.error("Could not trigger step");
    } finally {
      setRunningMemberAction(false);
    }
  };

  const handleSetStepPause = async (stepId, action = "pause", stepOrderIndex = null) => {
    const normalizedStepId = String(stepId || "").trim();
    const normalizedAction = String(action || "").trim().toLowerCase();
    if (!employee || !selectedCampaign || !normalizedStepId) return;
    if (!["pause", "resume"].includes(normalizedAction)) return;
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/setCampaignStepPause/${selectedCampaign.campaignId}/${normalizedStepId}`,
        {
          action: normalizedAction,
        }
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      const label =
        Number(stepOrderIndex || 0) > 0 ? `Step ${Number(stepOrderIndex)}` : "Step";
      toast.success(
        normalizedAction === "pause" ? `${label} paused` : `${label} resumed`
      );
      loadCampaignMonitorSnapshot(selectedCampaign.campaignId).catch((error) => {
        console.error(error);
      });
      await loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true });
    } catch (error) {
      console.error(error);
      toast.error(`Could not ${normalizedAction} step`);
    } finally {
      setRunningMemberAction(false);
    }
  };

  const handleClearStepQueue = async (stepId, stepOrderIndex = null, queuedCount = 0) => {
    const normalizedStepId = String(stepId || "").trim();
    if (!employee || !selectedCampaign || !normalizedStepId) return;
    const label =
      Number(stepOrderIndex || 0) > 0 ? `Step ${Number(stepOrderIndex)}` : "This step";
    const queuedTotal = Math.max(0, Number(queuedCount || 0));
    const confirmed = window.confirm(
      `${label}: clear ${queuedTotal} queued email${queuedTotal === 1 ? "" : "s"}? This does not mark leads as sent. You can start the step again to re-queue enabled unsent leads.`
    );
    if (!confirmed) return;
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/clearCampaignStepQueue/${selectedCampaign.campaignId}/${normalizedStepId}`,
        {}
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      const canceledCount = Number(response?.queueClearSummary?.canceledQueueJobs || 0);
      toast.success(
        `${label}: cleared ${canceledCount} queued email${canceledCount === 1 ? "" : "s"}`
      );
      loadCampaignMonitorSnapshot(selectedCampaign.campaignId).catch((error) => {
        console.error(error);
      });
      await loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true });
    } catch (error) {
      console.error(error);
      toast.error("Could not clear step queue");
    } finally {
      setRunningMemberAction(false);
    }
  };

  const handleTriggerSequence = async (sequenceId) => {
    const normalizedSequenceId = String(sequenceId || "").trim();
    if (!employee || !selectedCampaign || !normalizedSequenceId) return;
    if (isSequenceDefinitionsDirty) {
      toast.error("Save sequence changes before starting sequence");
      return;
    }
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/triggerCampaignSequence/${selectedCampaign.campaignId}/${normalizedSequenceId}`,
        {}
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      if (response?.summary?.queuedToExistingSequenceStart) {
        toast.success("Sequence start queued - existing background process will handle it");
        await loadCampaignMonitorSnapshot(selectedCampaign.campaignId);
        await loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true });
        return;
      }
      if (response?.summary?.backgroundStarted) {
        toast.success("Sequence start requested - processing in background");
        await loadCampaignMonitorSnapshot(selectedCampaign.campaignId);
        await loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true });
        return;
      }
      const queuedCount = Math.max(0, Number(response?.summary?.queuedCount || 0));
      const eligibleStates = Math.max(0, Number(response?.summary?.eligibleStates || 0));
      const verificationBlockedCount = Math.max(
        0,
        Number(response?.verificationSummary?.blockedCount || 0)
      );
      if (queuedCount > 0) {
        toast.success(
          `Sequence started - queued ${queuedCount} email${queuedCount === 1 ? "" : "s"}`
        );
      } else {
        const blockedSuffix =
          verificationBlockedCount > 0
            ? `; ${verificationBlockedCount} blocked by verification`
            : "";
        toast.success(
          `Sequence started - 0 emails queued (${eligibleStates} due state${
            eligibleStates === 1 ? "" : "s"
          }${blockedSuffix})`
        );
      }
      await loadCampaignMonitorSnapshot(selectedCampaign.campaignId);
      await loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true });
    } catch (error) {
      console.error(error);
      toast.error("Could not start sequence");
    } finally {
      setRunningMemberAction(false);
    }
  };

  const handleSetSequencePause = async (sequenceId, action = "pause") => {
    const normalizedSequenceId = String(sequenceId || "").trim();
    const normalizedAction = String(action || "").trim().toLowerCase();
    if (!employee || !selectedCampaign || !normalizedSequenceId) return;
    if (!["pause", "resume"].includes(normalizedAction)) return;
    if (isSequenceDefinitionsDirty) {
      toast.error("Save sequence changes before updating sequence run state");
      return;
    }
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/setCampaignSequencePause/${selectedCampaign.campaignId}/${normalizedSequenceId}`,
        { action: normalizedAction }
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      toast.success(normalizedAction === "pause" ? "Sequence paused" : "Sequence resumed");
      await loadCampaignMonitorSnapshot(selectedCampaign.campaignId);
      await loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true });
    } catch (error) {
      console.error(error);
      toast.error(`Could not ${normalizedAction} sequence`);
    } finally {
      setRunningMemberAction(false);
    }
  };

  const handleClearSequence = async (sequenceId, sequenceName = "", sequenceLetter = "") => {
    const normalizedSequenceId = String(sequenceId || "").trim();
    if (!employee || !selectedCampaign || !normalizedSequenceId) return;
    if (isSequenceDefinitionsDirty) {
      toast.error("Save sequence changes before clearing sequence queue");
      return;
    }
    const normalizedSequenceName = String(sequenceName || "").trim() || "Untitled sequence";
    const normalizedSequenceLetter = String(sequenceLetter || "").trim();
    const label = normalizedSequenceLetter
      ? `Sequence ${normalizedSequenceLetter} (${normalizedSequenceName})`
      : normalizedSequenceName;
    const confirmed = window.confirm(
      `Disable ${label} and clear all queued sequence emails?`
    );
    if (!confirmed) return;
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/clearCampaignSequence/${selectedCampaign.campaignId}/${normalizedSequenceId}`,
        {}
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      toast.success("Sequence queue cleared");
      await loadCampaignMonitorSnapshot(selectedCampaign.campaignId);
      await loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true });
    } catch (error) {
      console.error(error);
      toast.error("Could not clear sequence queue");
    } finally {
      setRunningMemberAction(false);
    }
  };

  const sanitizeSequenceChain = (sequenceEntry = {}) => {
    const nodes = Array.isArray(sequenceEntry?.nodes) ? sequenceEntry.nodes : [];
    const stepById = new Map(
      (sequenceStepsForUi || []).map((step, index) => [
        String(step?.stepId || ""),
        { ...step, orderIndex: Number(step?.orderIndex || index + 1) || index + 1 },
      ])
    );
    const keptNodes = [];
    const seen = new Set();
    nodes.forEach((node) => {
      const stepId = String(node?.stepId || "").trim();
      if (!stepId || seen.has(stepId)) return;
      const step = stepById.get(stepId);
      if (!step) return;
      const isOtt = String(step?.mode || "").trim().toLowerCase() === "ott";
      const sourceStepId = String(step?.ottSourceStepId || "").trim();
      if (isOtt && (!sourceStepId || !seen.has(sourceStepId))) return;
      seen.add(stepId);
      keptNodes.push(
        normalizeSequenceNode(
          {
            ...node,
            nodeId: String(node?.nodeId || "").trim() || makeId("sqn_node"),
            stepId,
          },
          keptNodes.length
        )
      );
    });
    const normalizedWaits = Array.from(
      { length: Math.max(0, keptNodes.length - 1) },
      (_, waitIndex) => {
        const source = (sequenceEntry?.waits || [])[waitIndex] || {};
        const amount = Math.max(1, Number(source?.amount || 1) || 1);
        const unit = SEQUENCE_WAIT_UNITS.includes(String(source?.unit || ""))
          ? source.unit
          : "days";
        return { amount, unit };
      }
    );
    return { ...sequenceEntry, nodes: keptNodes, waits: normalizedWaits };
  };

  const handleAddSequenceChain = () => {
    setSequencesDraft((prev) => {
      const next = [...(prev || [])];
      const defaultEntry = createDefaultSequenceChain(sequenceStepsForUi, next.length);
      next.push(defaultEntry);
      setActiveSequenceId(defaultEntry.sequenceId);
      return next;
    });
  };

  const handleSelectSequenceChain = (sequenceId = "") => {
    setActiveSequenceId(String(sequenceId || ""));
  };

  const handleRenameSequenceChain = (sequenceId = "", nextName = "") => {
    const normalizedName = String(nextName || "").trimStart();
    setSequencesDraft((prev) =>
      (prev || []).map((entry) =>
        entry.sequenceId === sequenceId ? { ...entry, name: normalizedName || "Untitled sequence" } : entry
      )
    );
  };

  const handleDeleteSequenceChain = (sequenceId = "") => {
    setSequencesDraft((prev) => {
      const next = (prev || []).filter((entry) => entry.sequenceId !== sequenceId);
      if (activeSequenceId === sequenceId) {
        setActiveSequenceId(next[0]?.sequenceId || "");
      }
      return next;
    });
  };

  const handleUpdateSequenceWait = (sequenceId = "", waitIndex = 0, patch = {}) => {
    setSequencesDraft((prev) =>
      (prev || []).map((entry) => {
        if (entry.sequenceId !== sequenceId) return entry;
        const waits = [...(entry?.waits || [])];
        const current = waits[waitIndex] || { amount: 1, unit: "days" };
        const amount = Object.prototype.hasOwnProperty.call(patch, "amount")
          ? patch.amount === ""
            ? ""
            : Math.max(1, Math.trunc(Number(patch.amount || 1) || 1))
          : current.amount;
        const unit = Object.prototype.hasOwnProperty.call(patch, "unit")
          ? (SEQUENCE_WAIT_UNITS.includes(String(patch.unit || "")) ? patch.unit : "days")
          : current.unit;
        waits[waitIndex] = { amount, unit };
        return { ...entry, waits };
      })
    );
  };

  const handleRemoveSequenceNode = (sequenceId = "", nodeIndex = 0) => {
    setSequencesDraft((prev) =>
      (prev || []).map((entry) => {
        if (entry.sequenceId !== sequenceId) return entry;
        const nodes = [...(entry?.nodes || [])];
        if (nodeIndex < 0 || nodeIndex >= nodes.length) return entry;
        nodes.splice(nodeIndex, 1);
        const waits = [...(entry?.waits || [])];
        if (waits.length) {
          if (nodeIndex === 0) {
            waits.splice(0, 1);
          } else if (nodeIndex >= nodes.length) {
            waits.splice(nodeIndex - 1, 1);
          } else {
            const preserved = waits[nodeIndex - 1] || waits[nodeIndex] || { amount: 1, unit: "days" };
            waits.splice(nodeIndex - 1, 2, preserved);
          }
        }
        return sanitizeSequenceChain({ ...entry, nodes, waits });
      })
    );
  };

  const handleAddSequenceNode = (sequenceId = "", stepId = "") => {
    const normalizedStepId = String(stepId || "").trim();
    if (!normalizedStepId) return;
    setSequencesDraft((prev) =>
      (prev || []).map((entry) => {
        if (entry.sequenceId !== sequenceId) return entry;
        const alreadyExists = (entry?.nodes || []).some((node) => node.stepId === normalizedStepId);
        if (alreadyExists) return entry;
        const nodes = [
          ...(entry?.nodes || []),
          normalizeSequenceNode(
            { nodeId: makeId("sqn_node"), stepId: normalizedStepId, speedMode: "", targetDurationSeconds: 0 },
            (entry?.nodes || []).length
          ),
        ];
        const waits = [...(entry?.waits || [])];
        if (nodes.length > 1) waits.push({ amount: 1, unit: "days" });
        return sanitizeSequenceChain({ ...entry, nodes, waits });
      })
    );
  };

  const handleSwapSequenceNodeStep = (sequenceId = "", nodeIndex = 0, stepId = "") => {
    const normalizedStepId = String(stepId || "").trim();
    if (!normalizedStepId) return;
    setSequencesDraft((prev) =>
      (prev || []).map((entry) => {
        if (entry.sequenceId !== sequenceId) return entry;
        const nodes = [...(entry?.nodes || [])];
        const current = nodes[nodeIndex];
        if (!current) return entry;
        const isUsedElsewhere = nodes.some(
          (node, idx) => idx !== nodeIndex && String(node?.stepId || "") === normalizedStepId
        );
        if (isUsedElsewhere) return entry;
        nodes[nodeIndex] = { ...current, stepId: normalizedStepId };
        return sanitizeSequenceChain({ ...entry, nodes, waits: [...(entry?.waits || [])] });
      })
    );
  };

  const handleAddStep = () => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      steps.push(createDefaultStep(steps.length + 1));
      return { ...prev, steps };
    });
    setSelectedStepIndex(sequenceDraft.steps.length);
    setSelectedVariantIndex(0);
  };

  const handleDuplicateStep = (index) => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const sourceStep = steps[index];
      if (!sourceStep) return prev;
      const now = Math.floor(Date.now() / 1000);
      const duplicatedStep = {
        ...sourceStep,
        stepId: makeId("stp"),
        name: String(sourceStep?.name || `Step ${index + 1}`),
        mode:
          Number(index + 2) === 1
            ? "standard"
            : String(sourceStep?.mode || "").trim().toLowerCase() === "ott"
            ? "ott"
            : "standard",
        ottSourceStepId: String(sourceStep?.ottSourceStepId || "").trim(),
        ottUsePreviousSender: sourceStep?.ottUsePreviousSender !== false,
        variants: (sourceStep?.variants || []).map((variant, variantIndex) => ({
          ...variant,
          variantId: makeId("vrn"),
          label: String(variant?.label || `Variant ${String.fromCharCode(65 + variantIndex)}`),
          createdTime: now,
          updatedTime: now,
        })),
        createdTime: now,
        updatedTime: now,
      };
      steps.splice(index + 1, 0, duplicatedStep);
      const reordered = steps.map((step, stepIndex) => ({
        ...step,
        orderIndex: stepIndex + 1,
        mode:
          stepIndex === 0
            ? "standard"
            : String(step?.mode || "").trim().toLowerCase() === "ott"
            ? "ott"
            : "standard",
        ottSourceStepId:
          stepIndex === 0
            ? ""
            : String(step?.ottSourceStepId || "").trim(),
      }));
      return { ...prev, steps: reordered };
    });
    setSelectedStepIndex(index + 1);
    setSelectedVariantIndex(0);
  };

  const handleRemoveStep = (index) => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      if (steps.length <= 1) return prev;
      steps.splice(index, 1);
      const reordered = steps.map((step, i) => ({
        ...step,
        orderIndex: i + 1,
      }));
      return { ...prev, steps: reordered };
    });
    if (selectedStepIndex >= index) {
      setSelectedStepIndex((prev) => Math.max(0, prev - 1));
      setSelectedVariantIndex(0);
    }
  };

  const handleUpdateStepMode = (stepIndex, modeValue) => {
    if (stepIndex === 0) return;
    const mode = String(modeValue || "").trim().toLowerCase() === "ott" ? "ott" : "standard";
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const target = { ...(steps[stepIndex] || {}) };
      target.mode = mode;
      if (mode !== "ott") {
        target.ottSourceStepId = "";
      } else {
        if (target?.ottUsePreviousSender === undefined || target?.ottUsePreviousSender === null) {
          target.ottUsePreviousSender = true;
        }
        const priorStepId = String(steps[stepIndex - 1]?.stepId || "").trim();
        if (!String(target?.ottSourceStepId || "").trim() && priorStepId) {
          target.ottSourceStepId = priorStepId;
        }
      }
      steps[stepIndex] = target;
      return { ...prev, steps };
    });
  };

  const handleUpdateStepOttSource = (stepIndex, sourceStepId) => {
    const normalizedSourceStepId = String(sourceStepId || "").trim();
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const target = { ...(steps[stepIndex] || {}) };
      target.ottSourceStepId = normalizedSourceStepId;
      steps[stepIndex] = target;
      return { ...prev, steps };
    });
  };
  const handleUpdateStepOttUsePreviousSender = (stepIndex, enabled) => {
    if (stepIndex <= 0) return;
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const target = { ...(steps[stepIndex] || {}) };
      target.ottUsePreviousSender = enabled !== false;
      steps[stepIndex] = target;
      return { ...prev, steps };
    });
  };

  const handleUpdateStepSpeed = (stepIndex, patch = {}) => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const target = { ...(steps[stepIndex] || {}) };
      const nextSpeed = normalizeStepSpeed(
        Object.prototype.hasOwnProperty.call(patch, "speed")
          ? patch.speed
          : target?.speedMode || target?.speed || "medium"
      );
      const nextTargetDurationSecondsRaw = Object.prototype.hasOwnProperty.call(
        patch,
        "targetDurationSeconds"
      )
        ? Number(patch.targetDurationSeconds)
        : Number(target?.targetDurationSeconds || 0);
      const nextTargetDurationSeconds = Math.max(
        0,
        Number.isFinite(nextTargetDurationSecondsRaw) ? Math.round(nextTargetDurationSecondsRaw) : 0
      );
      target.speed = nextSpeed;
      target.speedMode = nextSpeed;
      target.targetDurationSeconds = nextTargetDurationSeconds;
      target.durationPresetMultiplier = Number(CAMPAIGN_SPEED_PRESETS[nextSpeed]?.multiplier || 1);
      steps[stepIndex] = target;
      return { ...prev, steps };
    });
  };

  const handleRunStepDailyLimitInputChange = (stepIndex, partKey, rawValue, { sequenceId = "" } = {}) => {
    const step = (sequenceDraft?.steps || [])[stepIndex] || null;
    const stepId = String(step?.stepId || "").trim();
    if (!stepId) return;
    const normalizedSequenceId = String(sequenceId || "").trim();
    const sequenceNode =
      normalizedSequenceId
        ? ((sequencesDraft || []).find(
            (entry) => String(entry?.sequenceId || "").trim() === normalizedSequenceId
          )?.nodes || []).find((node) => String(node?.stepId || "").trim() === stepId) || null
        : null;
    const nextPartValue =
      partKey === "days"
        ? clampInt(rawValue, 0, 7)
        : partKey === "hours"
        ? clampInt(rawValue, 0, 24)
        : clampInt(rawValue, 0, 60);
    const inputKey = getRunStepDurationInputKey(stepId, normalizedSequenceId);
    if (!inputKey) return;
    setRunStepDailyLimitInputs((prev) => {
      const fallbackTargetSeconds =
        normalizeSequenceNodeSpeedMode(sequenceNode?.speedMode || "") === "custom"
          ? Number(sequenceNode?.targetDurationSeconds || 0)
          : Number(step?.targetDurationSeconds || 0);
      const previousParts = prev?.[inputKey] || secondsToDurationParts(fallbackTargetSeconds || 0);
      const mergedParts = {
        days: clampInt(previousParts?.days, 0, 7),
        hours: clampInt(previousParts?.hours, 0, 24),
        minutes: clampInt(previousParts?.minutes, 0, 60),
        [partKey]: nextPartValue,
      };
      handleUpdateRunStepSpeed(stepIndex, {
        speed: "custom",
        targetDurationSeconds: durationPartsToSeconds(mergedParts),
      }, { sequenceId });
      return {
        ...prev,
        [inputKey]: mergedParts,
      };
    });
  };

  useEffect(() => {
    const nextInputs = {};
    (sequenceDraft?.steps || []).forEach((step) => {
      const stepId = String(step?.stepId || "").trim();
      if (!stepId) return;
      const speedSettings = getStepSpeedSettings(step);
      const key = getRunStepDurationInputKey(stepId, "");
      if (!key) return;
      nextInputs[key] = secondsToDurationParts(Number(speedSettings.targetDurationSeconds || 0));
    });
    (sequencesDraft || []).forEach((sequence) => {
      const sequenceId = String(sequence?.sequenceId || "").trim();
      if (!sequenceId) return;
      (sequence?.nodes || []).forEach((node) => {
        const stepId = String(node?.stepId || "").trim();
        if (!stepId) return;
        const speedMode = normalizeSequenceNodeSpeedMode(node?.speedMode || "");
        if (speedMode !== "custom") return;
        const key = getRunStepDurationInputKey(stepId, sequenceId);
        if (!key) return;
        nextInputs[key] = secondsToDurationParts(Number(node?.targetDurationSeconds || 0));
      });
    });
    setRunStepDailyLimitInputs(nextInputs);
  }, [sequenceDraft?.steps, sequencesDraft]);

  const handleAddVariant = (stepIndex) => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const step = { ...steps[stepIndex] };
      const variants = [...(step.variants || [])];
      const now = Math.floor(Date.now() / 1000);
      variants.push({
        ...createDefaultVariant(now),
        label: `Variant ${String.fromCharCode(65 + variants.length)}`,
      });
      step.variants = variants;
      steps[stepIndex] = step;
      return { ...prev, steps };
    });
    setSelectedStepIndex(stepIndex);
    setSelectedVariantIndex((sequenceDraft.steps?.[stepIndex]?.variants?.length || 0));
  };

  const handleRemoveVariant = (stepIndex, variantIndex) => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const step = { ...steps[stepIndex] };
      const variants = [...(step.variants || [])];
      if (variants.length <= 1) return prev;
      variants.splice(variantIndex, 1);
      if (!variants.some((variant) => variant.isActive) && variants.length) {
        variants[0] = { ...variants[0], isActive: true };
      }
      step.variants = variants;
      steps[stepIndex] = step;
      return { ...prev, steps };
    });
  };

  const handleDuplicateVariant = (stepIndex, variantIndex) => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const step = { ...steps[stepIndex] };
      const variants = [...(step.variants || [])];
      const sourceVariant = variants[variantIndex];
      if (!sourceVariant) return prev;
      const now = Math.floor(Date.now() / 1000);
      const duplicatedVariant = {
        ...sourceVariant,
        variantId: makeId("vrn"),
        label: `Variant ${String.fromCharCode(65 + variants.length)}`,
        createdTime: now,
        updatedTime: now,
      };
      variants.splice(variantIndex + 1, 0, duplicatedVariant);
      step.variants = variants;
      steps[stepIndex] = step;
      return { ...prev, steps };
    });
    setSelectedStepIndex(stepIndex);
    setSelectedVariantIndex(variantIndex + 1);
  };

  const handleToggleVariantActive = (stepIndex, variantIndex, checked) => {
    if (!checked) {
      const variants = sequenceDraft.steps?.[stepIndex]?.variants || [];
      const target = variants[variantIndex];
      const activeCount = variants.filter((variant) => variant.isActive).length;
      if (target?.isActive && activeCount <= 1) {
        toast.error("Each step needs at least one active variant");
        return;
      }
    }

    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const step = { ...steps[stepIndex] };
      const variants = [...(step.variants || [])];
      if (!variants[variantIndex]) return prev;
      variants[variantIndex] = {
        ...variants[variantIndex],
        isActive: checked,
      };
      step.variants = variants;
      steps[stepIndex] = step;
      return { ...prev, steps };
    });
  };

  const handleUpdateVariantField = (stepIndex, variantIndex, field, value) => {
    setSequenceDraft((prev) => {
      const steps = [...(prev.steps || [])];
      const step = { ...steps[stepIndex] };
      const variants = [...(step.variants || [])];
      variants[variantIndex] = {
        ...variants[variantIndex],
        [field]: value,
      };
      step.variants = variants;
      steps[stepIndex] = step;
      return { ...prev, steps };
    });
  };
  const handleSelectSequenceStep = (stepIndex) => {
    if (Number(selectedStepIndex) === Number(stepIndex)) {
      return;
    }
    setSelectedStepIndex(stepIndex);
    setSelectedVariantIndex(0);
  };

  const handlePauseAllQueuedSteps = async () => {
    if (!employee || !selectedCampaign?.campaignId) return;
    if (!queuedUnpausedStepTargets.length) return;
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      await Promise.all(
        queuedUnpausedStepTargets.map((target) =>
          api.post(
            `landmark/sendora/setCampaignStepPause/${selectedCampaign.campaignId}/${target.stepId}`,
            { action: "pause" }
          )
        )
      );
      toast.success(
        `Paused ${queuedUnpausedStepTargets.length} step${
          queuedUnpausedStepTargets.length === 1 ? "" : "s"
        }`
      );
      await Promise.all([
        loadCampaignById(selectedCampaign.campaignId, { preserveActiveTab: true }),
        loadCampaignMonitorSnapshot(selectedCampaign.campaignId),
      ]);
    } catch (error) {
      console.error(error);
      toast.error("Could not pause all queued steps");
    } finally {
      setRunningMemberAction(false);
    }
  };

  const handleCampaignRowLifecycleAction = async (event, campaign, action) => {
    event.stopPropagation();
    if (!employee || !campaign?.campaignId || !action) return;
    try {
      setCampaignRowActionId(campaign.campaignId);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/setCampaignLifecycle/${campaign.campaignId}`,
        { action }
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      const actionToLabel = {
        start: "started",
        pause: "paused",
        resume: "resumed",
        stop: "stopped",
      };
      toast.success(`Campaign ${actionToLabel[action] || "updated"}`);
      await loadCampaigns();
    } catch (err) {
      console.error(err);
      toast.error("Could not update lifecycle");
    } finally {
      setCampaignRowActionId("");
    }
  };

  const openRenameCampaignModal = (event, campaign) => {
    event.stopPropagation();
    if (!campaign?.campaignId) return;
    setOpenCampaignMenuId("");
    setRenameCampaignId(campaign.campaignId);
    setRenameCampaignName(campaign.name || "");
    setIsRenameCampaignModalOpen(true);
  };

  const handleRenameCampaign = async () => {
    if (!employee || !renameCampaignId) return;
    const nextName = renameCampaignName.trim();
    if (!nextName) {
      toast.error("Campaign name is required");
      return;
    }
    try {
      setCampaignRowActionId(renameCampaignId);
      const api = new ApiService({ employee });
      const response = await api.post("landmark/sendora/updateCampaign", {
        campaignId: renameCampaignId,
        name: nextName,
      });
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      toast.success("Campaign renamed");
      setIsRenameCampaignModalOpen(false);
      setRenameCampaignId("");
      setRenameCampaignName("");
      await loadCampaigns();
      if (selectedCampaignId === renameCampaignId) {
        await loadCampaignById(renameCampaignId, { preserveActiveTab: true });
      }
    } catch (err) {
      console.error(err);
      toast.error("Could not rename campaign");
    } finally {
      setCampaignRowActionId("");
    }
  };

  const handleDeleteCampaignRow = async (event, campaign) => {
    event.stopPropagation();
    if (!employee || !campaign?.campaignId) return;
    setOpenCampaignMenuId("");
    const ok = window.confirm(
      "Delete this campaign? Sending will be stopped and campaign history will be retained."
    );
    if (!ok) return;
    try {
      setCampaignRowActionId(campaign.campaignId);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/deleteCampaign/${campaign.campaignId}`,
        {}
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      toast.success("Campaign deleted");
      if (selectedCampaignId === campaign.campaignId) {
        setSelectedCampaignId("");
        setSelectedCampaign(null);
      }
      await loadCampaigns();
    } catch (err) {
      console.error(err);
      toast.error("Could not delete campaign");
    } finally {
      setCampaignRowActionId("");
    }
  };

  const handleDuplicateCampaignRow = async (event, campaign) => {
    event.stopPropagation();
    if (!employee || !campaign?.campaignId) return;
    if (!campaign?.salesforceCampaignId) {
      toast.error("Campaign must be linked to Salesforce to duplicate");
      return;
    }
    setOpenCampaignMenuId("");
    const duplicateName = getUniqueCampaignNameFromBase(
      `${campaign.name || "Campaign"} Copy`,
      campaigns
    );
    try {
      setCampaignRowActionId(campaign.campaignId);
      const api = new ApiService({ employee });
      const createResponse = await api.post(
        "landmark/sendora/createCampaign",
        {
          name: duplicateName,
          notes: campaign.notes || "",
          salesforceCampaignId: campaign.salesforceCampaignId,
        },
        null,
        {
          timeout: SENDORA_CREATE_CAMPAIGN_TIMEOUT_MS,
        }
      );
      if (createResponse?.error) {
        toast.error(createResponse.error);
        return;
      }
      const duplicatedCampaignId = createResponse?.campaign?.campaignId;
      if (duplicatedCampaignId) {
        await api.post("landmark/sendora/updateCampaign", {
          campaignId: duplicatedCampaignId,
          notes: campaign.notes || "",
          collaboratorEmails: campaign.collaboratorEmails || [],
          options: campaign.options || {},
          schedules: campaign.schedules || [],
          dateWindow: campaign.dateWindow || {},
          sequences: campaign.sequences || {},
        });
      }
      toast.success("Campaign duplicated");
      await loadCampaigns();
    } catch (err) {
      console.error(err);
      toast.error("Could not duplicate campaign");
    } finally {
      setCampaignRowActionId("");
    }
  };

  const handleDeleteCampaign = async () => {
    if (!employee || !selectedCampaign) return;
    const ok = window.confirm(
      "Delete this campaign? Sending will be stopped and campaign history will be retained."
    );
    if (!ok) return;
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/deleteCampaign/${selectedCampaign.campaignId}`,
        {}
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      toast.success("Campaign deleted");
      setSelectedCampaignId("");
      await loadCampaigns();
    } catch (err) {
      console.error(err);
      toast.error("Could not delete campaign");
    } finally {
      setRunningMemberAction(false);
    }
  };

  const handleRefreshMembers = async () => {
    if (!employee || !selectedCampaign) return;
    try {
      setRunningMemberAction(true);
      setLoadingMembers(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/syncCampaignMembers/${selectedCampaign.campaignId}`,
        {},
        null,
        { timeout: 180000 }
      );
      const syncSummary = response && typeof response === "object" ? response : null;
      if (!syncSummary) {
        toast.error("Salesforce sync did not return a valid response");
        return;
      }
      if (syncSummary.error) {
        toast.error(syncSummary.error);
        return;
      }
      toast.success(
        `Synced Salesforce members (${Number(syncSummary?.createdCount || 0)} new, ${
          Number(syncSummary?.deactivatedCount || 0)
        } removed)`
      );
      await refreshSelectedCampaignData(selectedCampaign.campaignId);
    } catch (err) {
      console.error(err);
      toast.error("Could not refresh members from Salesforce");
    } finally {
      setRunningMemberAction(false);
      setLoadingMembers(false);
    }
  };

  const handleMemberIncludeExclude = async (mode) => {
    if (!employee || !selectedCampaign || !selectedMemberIds.length) {
      toast.error("Select members first");
      return;
    }
    if (mode === "include" && !selectedLeadSequenceId && !selectedApprovalStepIds.length) {
      toast.error("Select at least one step to enable");
      return;
    }
    if (mode === "include" && isSequenceDirty) {
      toast.error("Save sequence changes before enabling steps for leads");
      return;
    }
    if ((mode === "include" || mode === "exclude") && !!selectedLeadSequenceId && isSequenceDefinitionsDirty) {
      toast.error("Save sequence chain changes before updating lead sequence assignments");
      return;
    }
    const savedSteps = Array.isArray(selectedCampaign?.sequences?.steps)
      ? selectedCampaign.sequences.steps
      : [];
    const savedStepIds = new Set(
      savedSteps.map((step) => String(step?.stepId || "").trim()).filter(Boolean)
    );
    const optionByStepId = new Map(
      (sequenceStepApprovalOptions || []).map((option) => [
        String(option?.stepId || "").trim(),
        option,
      ])
    );
    const savedStepIdByOrder = new Map(
      savedSteps.map((step, index) => [
        Number(step?.orderIndex || index + 1),
        String(step?.stepId || "").trim(),
      ])
    );
    const persistedSelectedStepIds = Array.from(
      new Set(
        (selectedApprovalStepIds || [])
          .map((stepId) => String(stepId || "").trim())
          .filter(Boolean)
          .map((stepId) => {
            if (savedStepIds.has(stepId)) return stepId;
            const option = optionByStepId.get(stepId);
            const orderIndex = Number(option?.orderIndex || 0);
            return savedStepIdByOrder.get(orderIndex) || "";
          })
          .filter(Boolean)
      )
    );
    if (mode === "include" && !persistedSelectedStepIds.length) {
      toast.error("No saved steps selected. Save sequence and try again.");
      return;
    }
    const isSequenceModeAction = mode === "include" && !!selectedLeadSequenceId;
    const isSequenceDisableAction = mode === "exclude" && !!selectedLeadSequenceId;
    const endpoint = isSequenceModeAction
      ? "includeMembersSequence"
      : isSequenceDisableAction
      ? "excludeMembersSequence"
      : mode === "include"
      ? "includeMembers"
      : "excludeMembers";
    const payload = isSequenceModeAction
      ? { memberIds: selectedMemberIds, sequenceId: selectedLeadSequenceId }
      : isSequenceDisableAction
      ? { memberIds: selectedMemberIds, sequenceId: selectedLeadSequenceId }
      : mode === "include"
      ? { memberIds: selectedMemberIds, stepIds: persistedSelectedStepIds }
      : {
          memberIds: selectedMemberIds,
          reason: "Unqueued by user",
        };

    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const response = await api.post(
        `landmark/sendora/${endpoint}/${selectedCampaign.campaignId}`,
        payload
      );
      if (response?.error) {
        toast.error(response.error);
        return;
      }
      const rawModifiedCount = Number(response?.modifiedCount);
      const rawMatchedCount = Number(response?.matchedCount);
      const requestedCount = Math.max(0, Number(selectedMemberIds.length || 0));
      const matchedCount = Number.isFinite(rawMatchedCount) ? Math.max(0, rawMatchedCount) : 0;
      const modifiedCount = Number.isFinite(rawModifiedCount) ? Math.max(0, rawModifiedCount) : 0;
      const count = matchedCount > 0 ? matchedCount : modifiedCount > 0 ? modifiedCount : requestedCount;
      const nounSingular = mode === "include" ? "lead" : "member";
      const nounPlural = mode === "include" ? "leads" : "members";
      toast.success(
        `${mode === "include" ? "Enabled" : "Disabled"} ${count} ${
          count === 1 ? nounSingular : nounPlural
        }`
      );
      const selectedIdSet = new Set(selectedMemberIds.map((id) => String(id)));
      const validStepIds = Array.from(savedStepIds);
      const nextStepApprovalsForInclude = validStepIds.reduce((acc, stepId) => {
        acc[stepId] = persistedSelectedStepIds.includes(stepId);
        return acc;
      }, {});
      const updateMemberCollection = (collection = []) =>
        (Array.isArray(collection) ? collection : []).map((member) => {
          const memberId = String(member?._id || "");
          if (!selectedIdSet.has(memberId)) return member;
          const existingSequenceIds = new Set(
            (Array.isArray(member?.enabledSequenceIds) ? member.enabledSequenceIds : [])
              .map((sequenceId) => String(sequenceId || "").trim())
              .filter(Boolean)
          );
          if (isSequenceModeAction && selectedLeadSequenceId) {
            existingSequenceIds.add(String(selectedLeadSequenceId || "").trim());
          } else if (isSequenceDisableAction && selectedLeadSequenceId) {
            existingSequenceIds.delete(String(selectedLeadSequenceId || "").trim());
          }
          return {
            ...member,
            queuePreference:
              !isSequenceModeAction && !isSequenceDisableAction
                ? mode === "include"
                  ? "manual_queued"
                  : "manual_unqueued"
                : member?.queuePreference || "auto",
            excludeReason: mode === "exclude" ? "Unqueued by user" : "",
            enabledSequenceIds: Array.from(existingSequenceIds),
            stepApprovals:
              mode === "include" && !isSequenceModeAction
                ? nextStepApprovalsForInclude
                : member?.stepApprovals || {},
          };
        });
      setMembers((prev) => updateMemberCollection(prev));
      setAllMembers((prev) => updateMemberCollection(prev));
      if (response?.stats) {
        setSelectedCampaign((prev) =>
          prev ? { ...prev, stats: { ...(prev.stats || {}), ...(response.stats || {}) } } : prev
        );
        setCampaigns((prev) =>
          (Array.isArray(prev) ? prev : []).map((campaign) =>
            String(campaign?.campaignId || "") === String(selectedCampaign?.campaignId || "")
              ? { ...campaign, stats: { ...(campaign?.stats || {}), ...(response.stats || {}) } }
              : campaign
          )
        );
      }
      setMemberSelection({});
      setCampaignMonitor((prev) => {
        if (!prev) return prev;
        const currentPendingCount = Math.max(0, Number(prev?.metrics?.pendingQueueCount || 0));
        const nextPendingCount =
          mode === "include"
            ? currentPendingCount + count
            : Math.max(0, currentPendingCount - count);
        return {
          ...prev,
          metrics: {
            ...((prev && prev.metrics) || {}),
            pendingQueueCount: nextPendingCount,
          },
        };
      });
      loadCampaignMonitorSnapshot(selectedCampaign.campaignId).catch((error) => {
        console.error(error);
      });
    } catch (err) {
      console.error(err);
      toast.error("Could not update members");
    } finally {
      setRunningMemberAction(false);
    }
  };
  const handleBatchUpdateSalesforceStatus = async () => {
    if (!employee || !selectedCampaign || !selectedMemberIds.length) {
      toast.error("Select leads first");
      return;
    }
    const normalizedTargetStatus = String(bulkSalesforceStatusTarget || "").trim();
    if (!normalizedTargetStatus) {
      toast.error("Select a Salesforce status");
      return;
    }
    try {
      setRunningMemberAction(true);
      const api = new ApiService({ employee });
      const normalizedSelectedIds = Array.from(
        new Set(selectedMemberIds.map((id) => String(id || "").trim()).filter(Boolean))
      );
      let totalQueuedCount = 0;
      let totalMissingCount = 0;
      let salesforceWritesSuppressed = false;
      for (
        let offset = 0;
        offset < normalizedSelectedIds.length;
        offset += BULK_SALESFORCE_STATUS_REQUEST_CHUNK_SIZE
      ) {
        const chunkMemberIds = normalizedSelectedIds.slice(
          offset,
          offset + BULK_SALESFORCE_STATUS_REQUEST_CHUNK_SIZE
        );
        const isFinalChunk =
          offset + BULK_SALESFORCE_STATUS_REQUEST_CHUNK_SIZE >= normalizedSelectedIds.length;
        if (!chunkMemberIds.length) continue;
        const response = await api.post(
          `landmark/sendora/batchUpdateMembersSalesforceStatus/${selectedCampaign.campaignId}`,
          {
            memberIds: chunkMemberIds,
            targetStatus: normalizedTargetStatus,
            triggerFlush: isFinalChunk,
          }
        );
        if (response?.error) {
          toast.error(response.error);
          return;
        }
        totalQueuedCount += Math.max(0, Number(response?.queuedCount || 0));
        totalMissingCount += Math.max(0, Number(response?.missingSalesforceMemberIdCount || 0));
        if (isFinalChunk) {
          const flushSummary = response?.flushSummary || {};
          if (
            !!flushSummary?.suppressSalesforcePostSendChanges ||
            !!flushSummary?.dryRunSendMode ||
            !!flushSummary?.recipientOverrideMode ||
            (!!flushSummary?.skipped && !flushSummary?.deferredByRequest)
          ) {
            salesforceWritesSuppressed = true;
          }
        }
      }
      const selectedIdSet = new Set(selectedMemberIds.map((id) => String(id || "").trim()).filter(Boolean));
      const updateCollection = (collection = []) =>
        (Array.isArray(collection) ? collection : []).map((member) => {
          const memberId = String(member?._id || "").trim();
          if (!selectedIdSet.has(memberId)) return member;
          return {
            ...member,
            status: normalizedTargetStatus,
          };
        });
      setMembers((prev) => updateCollection(prev));
      setAllMembers((prev) => updateCollection(prev));
      setMemberSelection({});
      const queuedSummaryMessage =
        totalMissingCount > 0
          ? `Queued ${totalQueuedCount} status update${
              totalQueuedCount === 1 ? "" : "s"
            } (${totalMissingCount} missing Salesforce ID)`
          : `Queued ${totalQueuedCount} Salesforce status update${totalQueuedCount === 1 ? "" : "s"}`;
      if (totalMissingCount > 0) {
        toast.success(queuedSummaryMessage);
      } else {
        toast.success(queuedSummaryMessage);
      }
      if (salesforceWritesSuppressed) {
        toast.error(
          "Salesforce writes are currently disabled in this mode. Updates were queued only."
        );
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not queue Salesforce status updates");
    } finally {
      setRunningMemberAction(false);
    }
  };
  const handleOpenBulkSalesforceStatusConfirm = () => {
    if (!employee || !selectedCampaign || !selectedMemberIds.length) {
      toast.error("Select leads first");
      return;
    }
    const normalizedTargetStatus = String(bulkSalesforceStatusTarget || "").trim();
    if (!normalizedTargetStatus) {
      toast.error("Select a Salesforce status");
      return;
    }
    setIsBulkSalesforceStatusConfirmModalOpen(true);
  };
  const handleConfirmBulkSalesforceStatusUpdate = async () => {
    setIsBulkSalesforceStatusConfirmModalOpen(false);
    await handleBatchUpdateSalesforceStatus();
  };
  const handleOpenCampaignContactUploadModal = () => {
    if (!selectedCampaignSalesforceId) {
      toast.error("This campaign is not linked to a Salesforce campaign");
      return;
    }
    campaignContactUploadFileInputRef.current?.click();
  };
  const handlePickCampaignContactUploadFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCampaignContactUploadSelectedFile(file);
    setIsCampaignContactUploadModalMinimized(false);
    setIsCampaignContactUploadModalOpen(true);
    if (campaignContactUploadFileInputRef.current) {
      campaignContactUploadFileInputRef.current.value = "";
    }
  };
  const handleCloseCampaignContactUploadModal = () => {
    if (isCampaignContactUploadProcessing) {
      setIsCampaignContactUploadModalOpen(false);
      setIsCampaignContactUploadModalMinimized(true);
      return;
    }
    setIsCampaignContactUploadModalOpen(false);
    setIsCampaignContactUploadModalMinimized(false);
  };
  const handleRestoreCampaignContactUploadModal = () => {
    setIsCampaignContactUploadModalMinimized(false);
    setIsCampaignContactUploadModalOpen(true);
  };
  const handleCampaignUploadProcessingChange = (isProcessing) => {
    setIsCampaignContactUploadProcessing(Boolean(isProcessing));
    if (!isProcessing) {
      setIsCampaignContactUploadModalMinimized(false);
    }
  };
  const handleCampaignUploadCompleted = () => {
    setIsCampaignContactUploadModalMinimized(false);
  };

  const handleToggleApprovalStepSelection = (stepId, checked) => {
    const normalizedStepId = String(stepId || "");
    if (!normalizedStepId) return;
    let nextStepIdsSnapshot = [];
    let didChange = false;
    setSelectedApprovalStepIds((prev) => {
      const nextSet = new Set(
        (Array.isArray(prev) ? prev : []).map((value) => String(value || "")).filter(Boolean)
      );
      if (checked) {
        nextSet.add(normalizedStepId);
      } else {
        nextSet.delete(normalizedStepId);
      }
      sequenceStepApprovalOptions.forEach((option) => {
        if (option.mode !== "ott") return;
        if (!nextSet.has(option.stepId)) return;
        if (!nextSet.has(option.ottSourceStepId)) {
          nextSet.delete(option.stepId);
        }
      });
      const next = Array.from(nextSet);
      const current = Array.isArray(prev) ? prev : [];
      didChange =
        next.length !== current.length || next.some((value, index) => value !== current[index]);
      nextStepIdsSnapshot = next;
      return next;
    });
    if (selectedLeadSequenceId && didChange) {
      const sourceSequence = (sequencesDraft || []).find(
        (entry) => String(entry?.sequenceId || "") === String(selectedLeadSequenceId || "")
      );
      if (sourceSequence) {
        const optionByStepId = new Map(
          (sequenceStepApprovalOptions || []).map((option) => [
            String(option?.stepId || ""),
            option,
          ])
        );
        const selectedSet = new Set(
          (nextStepIdsSnapshot || []).map((value) => String(value || "")).filter(Boolean)
        );
        const orderedStepIds = (sequenceStepApprovalOptions || [])
          .slice()
          .sort((a, b) => Number(a?.orderIndex || 0) - Number(b?.orderIndex || 0))
          .map((option) => String(option?.stepId || ""))
          .filter((stepId) => selectedSet.has(stepId));
        const finalStepIds = [];
        const finalSet = new Set();
        orderedStepIds.forEach((stepId) => {
          const option = optionByStepId.get(stepId);
          if (!option) return;
          if (option.mode === "ott" && !finalSet.has(String(option?.ottSourceStepId || ""))) return;
          finalStepIds.push(stepId);
          finalSet.add(stepId);
        });
        const sourceWaitByEdge = new Map();
        const sourceNodes = sourceSequence?.nodes || [];
        const sourceWaits = sourceSequence?.waits || [];
        for (let idx = 0; idx < sourceNodes.length - 1; idx += 1) {
          const fromStepId = String(sourceNodes[idx]?.stepId || "");
          const toStepId = String(sourceNodes[idx + 1]?.stepId || "");
          if (!fromStepId || !toStepId) continue;
          sourceWaitByEdge.set(`${fromStepId}->${toStepId}`, sourceWaits[idx] || { amount: 1, unit: "days" });
        }
        const customNodes = finalStepIds.map((nextStepId, nodeIndex) => {
          const sourceNode =
            sourceNodes.find((node) => String(node?.stepId || "") === String(nextStepId || "")) || {};
          return normalizeSequenceNode(
            {
              nodeId: makeId("sqn_node"),
              stepId: nextStepId,
              speedMode: sourceNode?.speedMode || "",
              targetDurationSeconds: sourceNode?.targetDurationSeconds || 0,
            },
            nodeIndex
          );
        });
        const customWaits = Array.from({ length: Math.max(0, customNodes.length - 1) }, (_, idx) => {
          const fromStepId = String(customNodes[idx]?.stepId || "");
          const toStepId = String(customNodes[idx + 1]?.stepId || "");
          return sourceWaitByEdge.get(`${fromStepId}->${toStepId}`) || { amount: 1, unit: "days" };
        });
        const customSequence = {
          sequenceId: makeId("sqn"),
          name: "Custom",
          nodes: customNodes,
          waits: customWaits,
        };
        setSelectedLeadSequenceId(customSequence.sequenceId);
        setSequencesDraft((prev) => [...(prev || []), customSequence]);
      }
      if (!sourceSequence) {
        setSelectedLeadSequenceId("");
      }
    }
  };

  const handleOpenLeadSequenceWaitEditor = (edge = {}) => {
    if (!selectedLeadSequence) return;
    const wait = edge?.wait || { amount: 1, unit: "days" };
    setLeadSequenceWaitEditor({
      isOpen: true,
      sequenceId: String(selectedLeadSequence?.sequenceId || ""),
      fromStepId: String(edge?.fromStepId || ""),
      toStepId: String(edge?.toStepId || ""),
      edgeIndex: Number(edge?.edgeIndex || 0),
      amount: String(Math.max(1, Math.trunc(Number(wait?.amount || 1) || 1)),
      ),
      unit: SEQUENCE_WAIT_UNITS.includes(String(wait?.unit || "")) ? wait.unit : "days",
    });
  };

  const handleCloseLeadSequenceWaitEditor = () => {
    setLeadSequenceWaitEditor((prev) => ({ ...prev, isOpen: false }));
  };

  const handleApplyLeadSequenceWaitEditor = () => {
    const sequenceId = String(leadSequenceWaitEditor?.sequenceId || "");
    const edgeIndex = Number(leadSequenceWaitEditor?.edgeIndex || -1);
    if (!sequenceId || edgeIndex < 0) {
      handleCloseLeadSequenceWaitEditor();
      return;
    }
    const parsedAmount = Math.trunc(Number(leadSequenceWaitEditor?.amount || ""));
    const amount = Number.isFinite(parsedAmount) && parsedAmount > 0 ? parsedAmount : 1;
    const unit = SEQUENCE_WAIT_UNITS.includes(String(leadSequenceWaitEditor?.unit || ""))
      ? leadSequenceWaitEditor.unit
      : "days";
    handleUpdateSequenceWait(sequenceId, edgeIndex, { amount, unit });
    handleCloseLeadSequenceWaitEditor();
  };

  const handleToggleMemberSelection = (memberId, checked) => {
    setMemberSelection((prev) => ({
      ...prev,
      [memberId]: checked,
    }));
  };

  const handleToggleSelectAllVisible = (checked) => {
    const next = {};
    visibleMembers.forEach((m) => {
      next[m._id] = checked;
    });
    setMemberSelection(next);
  };

  const allVisibleSelected =
    visibleMembers.length > 0 &&
    visibleMembers.every((m) => memberSelection[m._id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <img src={BlueSquaresLoading} alt="Loading" style={{ width: 70, maxWidth: "100%" }} />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper sendora-wrapper">
        <div className="sendora-navbar">
          <div className="sendora-navbar-left">
            {/* <img
              src={landmarkAltLogo}
              className="sendora-navbar-landmark-logo"
              alt="Landmark Ventures"
            /> */}
            <img src={sendoraLogo} className="sendora-navbar-sendora-logo" alt="Sendora" />
            <div className="sendora-navbar-wordmark-stack">
              <span className="sendora-navbar-wordmark">sendora</span>
              <span className="sendora-navbar-subtitle">a landmark creation</span>
            </div>
          </div>
          <div className="sendora-navbar-right">
            {isAuthenticated && employee && (
              <>
                <span className="sendora-navbar-version-text">v1.2.0</span>
                {sendoraNavbarModePills.length > 0 && (
                  <div className="sendora-navbar-mode-pill-wrap">
                    {sendoraNavbarModePills.map((pill) => (
                      <span key={pill.key} className="sendora-navbar-mode-pill">
                        {pill.label}
                      </span>
                    ))}
                  </div>
                )}
                <div className="existing-accounts-container">
                  <EmployeeInfo
                    landmarkEmployee={employee}
                    disableHover={true}
                    showLogout={true}
                    logout={logout}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="sendora-body">
          {!isAuthenticated ? (
            <div className="text-center text-muted p-5">
              Please sign in to access Sendora.
            </div>
          ) : (
            <>
              {!selectedCampaign && (
                <>
              <Card className="admin-card p-3 mb-3">
                <div className="section-top-row">
                  <div>
                    <div className="section-title">Your Campaigns</div>
                    <div className="section-desc">
                      Create Sendora campaigns linked to Salesforce campaigns. After
                      a campaign starts, newly synced leads stay excluded until you
                      append them.
                    </div>
                  </div>
                  <div className="section-actions">
                    <Button
                      className="sendora-btn"
                      onClick={openCreateCampaignModal}
                    >
                      <img
                        src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgaWQ9InBsdXMiPgo8cGF0aCBpZD0iVmVjdG9yIiBkPSJNMTEuOTk5OSA4LjY2OTI3SDguNjY2NTlWMTIuMDAyNkM4LjY2NjU5IDEyLjM2OTMgOC4zNjY1OSAxMi42NjkzIDcuOTk5OTIgMTIuNjY5M0M3LjYzMzI1IDEyLjY2OTMgNy4zMzMyNSAxMi4zNjkzIDcuMzMzMjUgMTIuMDAyNlY4LjY2OTI3SDMuOTk5OTJDMy42MzMyNSA4LjY2OTI3IDMuMzMzMjUgOC4zNjkyNyAzLjMzMzI1IDguMDAyNkMzLjMzMzI1IDcuNjM1OTQgMy42MzMyNSA3LjMzNTk0IDMuOTk5OTIgNy4zMzU5NEg3LjMzMzI1VjQuMDAyNkM3LjMzMzI1IDMuNjM1OTQgNy42MzMyNSAzLjMzNTk0IDcuOTk5OTIgMy4zMzU5NEM4LjM2NjU5IDMuMzM1OTQgOC42NjY1OSAzLjYzNTk0IDguNjY2NTkgNC4wMDI2VjcuMzM1OTRIMTEuOTk5OUMxMi4zNjY2IDcuMzM1OTQgMTIuNjY2NiA3LjYzNTk0IDEyLjY2NjYgOC4wMDI2QzEyLjY2NjYgOC4zNjkyNyAxMi4zNjY2IDguNjY5MjcgMTEuOTk5OSA4LjY2OTI3WiIgZmlsbD0id2hpdGUiLz4KPC9nPgo8L3N2Zz4="
                        alt="plusIcon"
                        className="jsx-1762102436 jss94"
                      />{" "}
                      Add new campaign
                    </Button>
                  </div>
                </div>
                {
                  <>
                    <div className="table-controls">
                      <input
                        className="form-control"
                        placeholder="Search campaigns"
                        value={campaignSearch}
                        onChange={(e) => setCampaignSearch(e.target.value)}
                      />
                    </div>

                    <div className="table-wrap campaign-table-wrap">
                      <div className="campaign-table-scroll">
                      <table className="sendora-table">
                        <thead>
                          <tr>
                            <th>Campaign</th>
                            <th>Status</th>
                            <th className="sendora-campaign-progress-col">Progress</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingCampaigns ? (
                            <tr>
                              <td colSpan={5}>
                                <div className="loading-inline">
                                  <img src={BlueSquaresLoading} alt="Loading" />
                                </div>
                              </td>
                            </tr>
                          ) : campaignRows.length ? (
                            campaignRows.map((campaign) => {
                              const isSelected = selectedCampaignId === campaign.campaignId;
                              const stats = campaign.stats || {};
                              const campaignStatus = campaign.lifecycle?.status || "draft";
                              const statusBadgeMeta = getCampaignStatusBadgeMeta(campaignStatus);
                              const { progressPercent, scheduledLeads, sentLeads } =
                                getCampaignProgressSnapshot(campaign, stats);
                              const manualSteps = Array.isArray(campaign?.sequences?.steps)
                                ? campaign.sequences.steps
                                : [];
                              const sequenceDefinitions = Array.isArray(
                                campaign?.sequences?.sequenceDefinitions
                              )
                                ? campaign.sequences.sequenceDefinitions
                                : [];
                              const hasManualStepsCreated = manualSteps.length > 0;
                              const hasSequencesCreated = sequenceDefinitions.length > 0;
                              const manualStepOrderById = new Map(
                                manualSteps
                                  .map((step, index) => {
                                    const stepId = String(step?.stepId || "").trim();
                                    if (!stepId) return null;
                                    const orderIndex = Number(step?.orderIndex || index + 1) || index + 1;
                                    return [stepId, orderIndex];
                                  })
                                  .filter(Boolean)
                              );
                              const ownerHandle = getCampaignPersonHandle(campaign?.owner || {});
                              const ownerEmployeeId = String(campaign?.owner?.employeeId || "").trim();
                              const ownerEmailLower = normalizeEmail(campaign?.owner?.email || "");
                              const currentEmployeeId = String(employee?.employeeId || "").trim();
                              const currentEmployeeEmailLower = normalizeEmail(
                                employee?.emailLowercase || employee?.email || ""
                              );
                              const isOwnerCurrentUser =
                                (ownerEmployeeId && ownerEmployeeId === currentEmployeeId) ||
                                (ownerEmailLower && ownerEmailLower === currentEmployeeEmailLower);
                              const collaboratorHandles = Array.from(
                                new Set(
                                  (campaign?.collaborators || [])
                                    .map((entry) => getCampaignPersonHandle(entry))
                                    .filter(Boolean)
                                )
                              );
                              const shouldShowOwner = !!ownerHandle && !isOwnerCurrentUser;
                              const shouldShowCollaborators = collaboratorHandles.length > 0;
                              const shouldShowCampaignMeta =
                                shouldShowOwner || shouldShowCollaborators;
                              const campaignStepProgressRows = (
                                Array.isArray(campaign?.stepProgress) ? campaign.stepProgress : []
                              )
                                .map((stepMetric, index) => {
                                  const enabledCount = Math.max(0, toSafeNumber(stepMetric?.enabledCount));
                                  const blockedCount = Math.max(
                                    0,
                                    toSafeNumber(
                                      stepMetric?.blockedBadEmailCount ?? stepMetric?.blockedCount
                                    )
                                  );
                                  const sentCount = Math.max(0, toSafeNumber(stepMetric?.sentCount));
                                  const queuedCount = Math.max(0, toSafeNumber(stepMetric?.queuedCount));
                                  const eligibleCount = Math.max(
                                    0,
                                    toSafeNumber(stepMetric?.eligibleCount)
                                  );
                                  const denominator = Math.max(
                                    0,
                                    enabledCount - blockedCount,
                                    sentCount + queuedCount + eligibleCount,
                                    toSafeNumber(stepMetric?.queuedMinusBlockedBadEmailCount)
                                  );
                                  const sentForDisplay = Math.max(
                                    0,
                                    Math.min(denominator, sentCount)
                                  );
                                  const percent =
                                    denominator > 0
                                      ? Math.max(
                                          0,
                                          Math.min(100, Math.round((sentForDisplay / denominator) * 100))
                                        )
                                      : 0;
                                  const stepId = String(stepMetric?.stepId || "").trim();
                                  const orderIndex =
                                    Number(
                                      stepMetric?.orderIndex ||
                                        manualStepOrderById.get(stepId) ||
                                        index + 1
                                    ) || index + 1;
                                  return {
                                    key: stepId || `step-${orderIndex}`,
                                    label: `Step ${orderIndex}`,
                                    orderIndex,
                                    sentCount: sentForDisplay,
                                    queuedCount: denominator,
                                    progressPercent: percent,
                                  };
                                })
                                .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
                              const campaignStepProgressFallbackRows = manualSteps
                                .map((step, index) => {
                                  const orderIndex = Number(step?.orderIndex || index + 1) || index + 1;
                                  return {
                                    key: String(step?.stepId || `step-${orderIndex}`),
                                    label: `Step ${orderIndex}`,
                                    orderIndex,
                                    sentCount: 0,
                                    queuedCount: 0,
                                    progressPercent: 0,
                                  };
                                })
                                .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
                              const stepProgressRowsForRender =
                                campaignStepProgressRows.length > 0
                                  ? campaignStepProgressRows
                                  : campaignStepProgressFallbackRows;
                              const sequenceProgressById = new Map(
                                (
                                  Array.isArray(campaign?.sequenceProgress)
                                    ? campaign.sequenceProgress
                                    : Array.isArray(campaign?.runSequences)
                                      ? campaign.runSequences
                                      : []
                                )
                                  .map((sequence) => {
                                    const sequenceId = String(sequence?.sequenceId || "").trim();
                                    if (!sequenceId) return null;
                                    return [sequenceId, sequence];
                                  })
                                  .filter(Boolean)
                              );
                              const fallbackCampaignSequenceProgressRows = sequenceDefinitions
                                .map((sequence, index) => {
                                  const sequenceId = String(sequence?.sequenceId || "").trim();
                                  const sequenceProgress = sequenceProgressById.get(sequenceId) || {};
                                  const sequenceLabel = getSequenceLabelByIndex(index);
                                  const sequenceTitle =
                                    String(
                                      sequence?.title ||
                                        sequence?.name ||
                                        sequenceProgress?.sequenceName ||
                                        sequenceProgress?.title ||
                                        ""
                                    ).trim() ||
                                    "Untitled sequence";
                                  const rawStepBreakdowns = Array.isArray(sequenceProgress?.stepBreakdowns)
                                    ? sequenceProgress.stepBreakdowns
                                    : [];
                                  const stepRows = (
                                    rawStepBreakdowns.length
                                      ? rawStepBreakdowns
                                      : Array.isArray(sequence?.nodes)
                                        ? sequence.nodes
                                        : []
                                  )
                                    .map((step, stepIndex) => {
                                      const stepId = String(step?.stepId || "").trim();
                                      const orderIndex = Number(step?.stepIndex ?? stepIndex) + 1;
                                      const enabledCount = Math.max(0, toSafeNumber(step?.enabledCount));
                                      const blockedCount = Math.max(
                                        0,
                                        toSafeNumber(step?.blockedCount ?? step?.blockedBadEmailCount)
                                      );
                                      const sentCount = Math.max(0, toSafeNumber(step?.sentCount));
                                      const queuedCount = Math.max(
                                        0,
                                        toSafeNumber(step?.queuedCount)
                                      );
                                      const eligibleCount = Math.max(
                                        0,
                                        toSafeNumber(step?.eligibleCount)
                                      );
                                      const denominator = Math.max(
                                        0,
                                        enabledCount - blockedCount,
                                        sentCount + queuedCount + eligibleCount
                                      );
                                      const sentForDisplay = Math.max(
                                        0,
                                        Math.min(denominator, sentCount)
                                      );
                                      const progressPercent =
                                        denominator > 0
                                          ? Math.max(
                                              0,
                                              Math.min(100, Math.round((sentForDisplay / denominator) * 100))
                                            )
                                          : 0;
                                      return {
                                        key: `${sequenceId || `sequence-${index}`}::${String(
                                          stepId || step?.nodeId || orderIndex
                                        )}`,
                                        orderIndex,
                                        label: `Step ${orderIndex}`,
                                        sentCount: sentForDisplay,
                                        queuedCount: denominator,
                                        progressPercent,
                                      };
                                    })
                                    .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
                                  return {
                                    key: sequenceId || `sequence-${index}`,
                                    label: `${sequenceLabel}: ${sequenceTitle}`,
                                    stepRows,
                                  };
                                })
                                .filter((row) => Array.isArray(row?.stepRows) && row.stepRows.length > 0);
                              const isSelectedCampaignRow =
                                String(campaign?.campaignId || "").trim() ===
                                String(selectedCampaignId || "").trim();
                              const campaignSequenceProgressRows =
                                isSelectedCampaignRow && liveMonitorSequenceProgressRows.length > 0
                                  ? liveMonitorSequenceProgressRows
                                  : fallbackCampaignSequenceProgressRows;
                              const isActionRunning = campaignRowActionId === campaign.campaignId;
                              return (
                                <tr
                                  key={campaign.campaignId}
                                  className={isSelected ? "row-selected" : ""}
                                  onClick={() => setSelectedCampaignId(campaign.campaignId)}
                                >
                                  <td>
                                    <div className="table-title">{campaign.name}</div>
                                    <div className="table-subtext">
                                      {campaign.notes || campaign.description}
                                    </div>
                                    {campaign?.salesforceCampaignId && (
                                      <a
                                        className="sendora-sf-campaign-link"
                                        href={`https://landmarkventures.lightning.force.com/lightning/r/Campaign/${campaign.salesforceCampaignId}/view`}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        <img
                                          src={SalesforceLogo}
                                          alt=""
                                          aria-hidden="true"
                                          className="sendora-sf-logo"
                                        />
                                        {campaign.salesforceCampaignName ||
                                          campaign.salesforceCampaignId}
                                      </a>
                                    )}
                                    {shouldShowCampaignMeta && (
                                      <div className="sendora-campaign-meta-line">
                                        {shouldShowOwner && <span>Owner: {ownerHandle}</span>}
                                        {shouldShowCollaborators && (
                                          <span>Collaborators: {collaboratorHandles.join(", ")}</span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <span
                                      className={`sendora-status-badge ${statusBadgeMeta.className}`}
                                    >
                                      {statusBadgeMeta.label}
                                    </span>
                                  </td>
                                  <td className="sendora-campaign-progress-col">
                                    <div className="sendora-progress-cell">
                                      {hasSequencesCreated && !!campaignSequenceProgressRows.length && (
                                        <div className="sendora-live-monitor-sequence-progress sendora-campaign-sequence-progress">
                                          {campaignSequenceProgressRows.map((sequenceRow) => (
                                            <div
                                              className="sendora-live-monitor-sequence-progress-row"
                                              key={sequenceRow.key}
                                            >
                                              <div className="sendora-live-monitor-sequence-progress-title">
                                                {sequenceRow.label}
                                              </div>
                                              <div className="sendora-live-monitor-sequence-progress-steps">
                                                {(sequenceRow.stepRows || []).map((stepRow) => (
                                                  <div
                                                    className="sendora-live-monitor-sequence-progress-step"
                                                    key={stepRow.key}
                                                  >
                                                    <div className="sendora-progress-label">
                                                      {formatMetricNumber(stepRow.sentCount)} /{" "}
                                                      {formatMetricNumber(stepRow.queuedCount)} (
                                                      {stepRow.progressPercent}%)
                                                    </div>
                                                    <div className="sendora-progress-track">
                                                      <div
                                                        className="sendora-progress-fill"
                                                        style={{ width: `${stepRow.progressPercent}%` }}
                                                      />
                                                    </div>
                                                    <div className="sendora-live-monitor-sequence-progress-step-label">
                                                      {stepRow.label}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {hasManualStepsCreated &&
                                        stepProgressRowsForRender.map((stepRow) => (
                                          <div className="sendora-progress-row" key={stepRow.key}>
                                            <div className="sendora-progress-row-label">{stepRow.label}</div>
                                            <div className="sendora-progress-row-bar-wrap">
                                              <div className="sendora-progress-label">
                                                {formatMetricNumber(stepRow.sentCount)} /{" "}
                                                {formatMetricNumber(stepRow.queuedCount)} (
                                                {stepRow.progressPercent}%)
                                              </div>
                                              <div className="sendora-progress-track">
                                                <div
                                                  className="sendora-progress-fill"
                                                  style={{ width: `${stepRow.progressPercent}%` }}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      {!hasManualStepsCreated &&
                                        !hasSequencesCreated &&
                                        scheduledLeads > 0 && (
                                          <div className="sendora-progress-row" key="campaign-overall">
                                            <div className="sendora-progress-row-label">Campaign</div>
                                            <div className="sendora-progress-row-bar-wrap">
                                              <div className="sendora-progress-label">
                                                {formatMetricNumber(sentLeads)} /{" "}
                                                {formatMetricNumber(scheduledLeads)} ({progressPercent}%)
                                              </div>
                                              <div className="sendora-progress-track">
                                                <div
                                                  className="sendora-progress-fill"
                                                  style={{ width: `${progressPercent}%` }}
                                                />
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </td>
                                  <td>{toLocalIsoDate(campaign.createdTime)}</td>
                                  <td className="sendora-actions-cell">
                                    <div className="sendora-actions-group">
                                      <div className="campaign-row-menu">
                                        <button
                                          type="button"
                                          className="sendora-row-icon-btn sendora-row-menu-btn"
                                          aria-label="Campaign menu"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            const triggerRect =
                                              event.currentTarget.getBoundingClientRect();
                                            setOpenCampaignMenuPosition({
                                              top: triggerRect.bottom + 6,
                                              left: triggerRect.right,
                                            });
                                            setOpenCampaignMenuId((prev) =>
                                              prev === campaign.campaignId
                                                ? ""
                                                : campaign.campaignId
                                            );
                                          }}
                                          disabled={isActionRunning}
                                        >
                                          <svg
                                            stroke="currentColor"
                                            fill="none"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            height="20"
                                            width="20"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <circle cx="12" cy="12" r="1" />
                                            <circle cx="19" cy="12" r="1" />
                                            <circle cx="5" cy="12" r="1" />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={5} className="empty-cell">
                                No campaigns found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  </>
                }
              </Card>
                </>
              )}

              {selectedCampaign && (
                <>
                  <Card className="admin-card p-3 mb-3">
                    <div className="sendora-detail-header">
                      <div className="sendora-detail-left">
                        <Button
                          variant="outlined"
                          className="sendora-btn-outline sendora-back-btn"
                          aria-label="Back"
                          onClick={() => {
                            setSelectedCampaignId("");
                            setSelectedCampaign(null);
                            setCampaignSearch("");
                          }}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="feather feather-chevron-left fea text-dark icon-xs"
                            style={{ strokeWidth: "3px", marginTop: "-2px" }}
                          >
                            <g>
                              <polyline points="15 18 9 12 15 6" />
                            </g>
                          </svg>
                        </Button>
                        <div>
                          <div className="section-title mb-0">{selectedCampaign.name}</div>
                          <div className="table-subtext">
                            {selectedCampaign?.salesforceCampaignId ? (
                              <a
                                className="sendora-sf-campaign-link"
                                href={`https://landmarkventures.lightning.force.com/lightning/r/Campaign/${selectedCampaign.salesforceCampaignId}/view`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={SalesforceLogo}
                                  alt=""
                                  aria-hidden="true"
                                  className="sendora-sf-logo"
                                />
                                {selectedCampaign.salesforceCampaignName ||
                                  selectedCampaign.salesforceCampaignId ||
                                  "Salesforce campaign"}
                              </a>
                            ) : (
                              "No Salesforce campaign"
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="sendora-detail-right">
                        <button
                          type="button"
                          className="sendora-monitor-toggle-btn"
                          onClick={() => setCampaignMonitorExpanded((prev) => !prev)}
                        >
                          <span
                            className={`sendora-monitor-connection-dot ${
                              campaignMonitorConnected ? "is-live" : "is-offline"
                            }`}
                          />
                          <span>{campaignMonitorExpanded ? "Hide live feed" : "Show live feed"}</span>
                          <i
                            className={`fa fa-chevron-${campaignMonitorExpanded ? "up" : "down"}`}
                            aria-hidden="true"
                          />
                        </button>
                        <div className="sendora-header-metrics">
                          <div className="sendora-header-metric">
                            <div className="sendora-header-metric-value">
                              {liveSentCount}
                            </div>
                            <div className="sendora-header-metric-label">Sent</div>
                          </div>
                          <div className="sendora-header-metric">
                            <div className="sendora-header-metric-value">
                              {liveQueuedCount}
                            </div>
                            <div className="sendora-header-metric-label">Queued</div>
                          </div>
                        </div>
                        {queuedUnpausedStepTargets.length > 0 && (
                          <Button
                            className="sendora-btn play-btn"
                            onClick={handlePauseAllQueuedSteps}
                            disabled={runningMemberAction || !hasCompletedGuidedSteps}
                          >
                            <i className="fa fa-pause-circle" aria-hidden="true" />
                            <span>Pause all steps</span>
                          </Button>
                        )}
                      </div>
                    </div>
                    {campaignMonitorExpanded && (
                      <div className="sendora-live-monitor">
                        <div className="sendora-live-monitor-top-row">
                          <div className="sendora-live-monitor-title-wrap">
                            <div className="sendora-live-monitor-title-row">
                              <div className="sendora-live-monitor-title">Live campaign monitor</div>
                              <img
                                src={BlueRipple}
                                alt="Live campaign monitor pulse"
                                className="sendora-live-monitor-title-icon"
                              />
                            </div>
                          </div>
                          <div className="sendora-live-monitor-statline">
                            <span>Progress {liveProgressPercent}%</span>
                            <span>Bounced {formatMetricNumber(monitorMetrics?.bouncedCount)}</span>
                            <span>Complaints {formatMetricNumber(monitorMetrics?.complaintCount)}</span>
                            <span>Failed {formatMetricNumber(monitorMetrics?.failedCount)}</span>
                            <span>Canceled {formatMetricNumber(monitorMetrics?.canceledCount)}</span>
                          </div>
                        </div>
                        {!!liveMonitorSequenceProgressRows.length && (
                          <div className="sendora-live-monitor-sequence-progress">
                            {liveMonitorSequenceProgressRows.map((sequenceRow) => (
                              <div
                                className="sendora-live-monitor-sequence-progress-row"
                                key={sequenceRow.key}
                              >
                                <div className="sendora-live-monitor-sequence-progress-title">
                                  {sequenceRow.label}
                                </div>
                                <div className="sendora-live-monitor-sequence-progress-steps">
                                  {(sequenceRow.stepRows || []).map((stepRow) => (
                                    <div
                                      className="sendora-live-monitor-sequence-progress-step"
                                      key={stepRow.key}
                                    >
                                      <div className="sendora-progress-label">
                                        {formatMetricNumber(stepRow.sentCount)} /{" "}
                                        {formatMetricNumber(stepRow.queuedCount)} (
                                        {stepRow.progressPercent}%)
                                      </div>
                                      <div className="sendora-progress-track">
                                        <div
                                          className="sendora-progress-fill"
                                          style={{ width: `${stepRow.progressPercent}%` }}
                                        />
                                      </div>
                                      <div className="sendora-live-monitor-sequence-progress-step-label">
                                        {stepRow.label}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {hasManualEnabledLeads && !!liveMonitorStepProgressRows.length && (
                          <div className="sendora-live-monitor-step-progress">
                            <div className="sendora-progress-cell">
                              {liveMonitorStepProgressRows.map((stepRow) => (
                                <div className="sendora-progress-row" key={stepRow.key}>
                                  <div className="sendora-progress-row-label">{stepRow.label}</div>
                                  <div className="sendora-progress-row-bar-wrap">
                                    <div className="sendora-progress-label">
                                      {formatMetricNumber(stepRow.sentCount)} /{" "}
                                      {formatMetricNumber(stepRow.queuedCount)} (
                                      {stepRow.progressPercent}%)
                                    </div>
                                    <div className="sendora-progress-track">
                                      <div
                                        className="sendora-progress-fill"
                                        style={{ width: `${stepRow.progressPercent}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="sendora-live-monitor-column sendora-live-monitor-feed-column">
                          <label className="sendora-run-feed-mode-toggle sendora-live-monitor-detailed-toggle">
                            <input
                              type="checkbox"
                              checked={showVerboseRunFeed}
                              onChange={(e) => setShowVerboseRunFeed(e.target.checked)}
                            />
                            <span>Detailed</span>
                          </label>
                          {showVerboseRunFeed ? (
                            <div className="sendora-live-monitor-list sendora-live-monitor-log-list">
                              {monitorRunLogs.length ? (
                                monitorRunLogs.map((logEntry) => {
                                  const metaSummary = summarizeRunLogMeta(logEntry.meta || {});
                                  return (
                                    <div
                                      className={`sendora-live-monitor-log-row is-${String(
                                        logEntry.level || "info"
                                      ).replace(/[^a-z0-9_-]/gi, "")}`}
                                      key={logEntry.key}
                                    >
                                      <span className="sendora-live-monitor-log-time">
                                        {formatMonitorEpoch(logEntry.time)}
                                      </span>
                                      <span className="sendora-live-monitor-log-message">
                                        <span>{logEntry.message}</span>
                                        {!!metaSummary && (
                                          <span className="sendora-live-monitor-log-meta">
                                            {metaSummary}
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="sendora-live-monitor-empty">
                                  No run log updates yet
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="sendora-basic-run-feed">
                              {basicRunFeedState.hasActiveSequenceStart ? (
                                (basicRunFeedState.lines || []).map((line) => (
                                  <div
                                    className={`sendora-basic-run-feed-line ${
                                      line.status === "warning" ? "is-warning" : ""
                                    }`}
                                    key={line.key}
                                  >
                                    <span className="sendora-basic-run-feed-text">
                                      {line.text}
                                      {line.status === "loading" &&
                                        line.key === "verification" &&
                                        !line.detail && (
                                          <span className="sendora-verification-may-take-note">
                                            (this may take a few minutes)
                                          </span>
                                        )}
                                    </span>
                                    <span className="sendora-basic-run-feed-right">
                                      {!!line.detail && (
                                        <span className="sendora-basic-run-feed-detail">{line.detail}</span>
                                      )}
                                      {line.status === "loading" ? (
                                        <img
                                          src={BlueCasualLoader}
                                          alt={`${line.text} in progress`}
                                          className="sendora-basic-run-feed-loader"
                                        />
                                      ) : line.status === "done" ? (
                                        <i
                                          className="fa fa-check-circle sendora-basic-run-feed-check"
                                          aria-hidden="true"
                                        />
                                      ) : line.status === "warning" ? (
                                        <i
                                          className="fa fa-exclamation-circle sendora-basic-run-feed-warning"
                                          aria-hidden="true"
                                        />
                                      ) : null}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <div className="sendora-basic-run-feed-line">
                                  <span className="sendora-basic-run-feed-text">
                                    Start a sequence or step to see step-by-step queueing progress.
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="sendora-live-monitor-grid">
                          <div className="sendora-live-monitor-column">
                            <div className="sendora-live-monitor-upcoming-header">
                              <div className="sendora-live-monitor-column-title-row">
                                <div className="sendora-live-monitor-column-title">Upcoming queue</div>
                                <label className="sendora-live-monitor-split-toggle">
                                  <input
                                    type="checkbox"
                                    checked={splitMonitorByStep}
                                    onChange={(e) => setSplitMonitorByStep(!!e.target.checked)}
                                  />
                                  <span>Separate by step</span>
                                </label>
                              </div>
                              {effectiveCampaignStatus === "paused" && (
                                <div className="sendora-live-monitor-paused-indicator">
                                  <span>Campaign paused - queue dispatch is halted</span>
                                </div>
                              )}
                            </div>
                            {splitMonitorByStep ? (
                              <div className="sendora-live-monitor-step-groups">
                                {monitorUpcomingQueueByStep.length ? (
                                  monitorUpcomingQueueByStep.map((group) => {
                                    const isManualStepPaused = !!campaignMonitor?.stepPauseMap?.[
                                      String(group?.stepId || "").trim()
                                    ];
                                    const pausedSequenceNames = Array.from(
                                      new Set(
                                        (group?.items || [])
                                          .filter((item) => {
                                            const sequenceId = String(item?.sequenceId || "").trim();
                                            if (!sequenceId) return false;
                                            return !!campaignMonitor?.sequencePauseMap?.[sequenceId];
                                          })
                                          .map((item) => String(item?.sequenceName || "").trim())
                                          .filter(Boolean)
                                      )
                                    );
                                    const normalizedStepId = String(group?.stepId || "").trim();
                                    const hasSchedulePausedItems = (group?.items || []).some((item) =>
                                      isQueueItemPausedBySchedule(item)
                                    );
                                    const isGroupPaused = isManualStepPaused || hasSchedulePausedItems;
                                    const stepMetricForGroup = runStepMetricsByStepId.get(
                                      String(group?.stepId || "").trim()
                                    );
                                    const totalQueuedCount = Math.max(
                                      0,
                                      Number(
                                        stepMetricForGroup?.queuedCount ?? group?.items?.length ?? 0
                                      )
                                    );
                                    return (
                                      <div
                                        className={`sendora-live-monitor-list sendora-live-monitor-list-split ${
                                          isGroupPaused ? "is-paused-step" : ""
                                        }`}
                                        key={`upcoming-group-${group.stepId}`}
                                      >
                                        <div
                                          className={`sendora-live-monitor-split-label ${
                                            isGroupPaused || pausedSequenceNames.length > 0
                                              ? "has-stack"
                                              : ""
                                          }`}
                                        >
                                          <div className="sendora-live-monitor-split-label-main">
                                            <span>{group.label}</span>
                                            <span className="sendora-live-monitor-split-count">
                                              {`${formatMetricNumber(totalQueuedCount)} email${
                                                totalQueuedCount === 1 ? "" : "s"
                                              }`}
                                            </span>
                                          </div>
                                          {(isGroupPaused || pausedSequenceNames.length > 0) && (
                                            <div className="sendora-live-monitor-split-paused-stack">
                                              {isGroupPaused && (
                                                <div className="sendora-live-monitor-step-paused-banner is-manual">
                                                  <i className="fa fa-pause-circle" aria-hidden="true" />
                                                  <span>
                                                    {isManualStepPaused
                                                      ? "Manual sending paused"
                                                      : "Step paused by schedule window - queue dispatch deferred"}
                                                  </span>
                                                </div>
                                              )}
                                              {pausedSequenceNames.map((sequenceName) => (
                                                <div
                                                  className="sendora-live-monitor-step-paused-banner is-sequence"
                                                  key={`paused-sequence-${normalizedStepId}-${sequenceName}`}
                                                >
                                                  <i className="fa fa-pause-circle" aria-hidden="true" />
                                                  <span>{`${sequenceName}: Main sending paused`}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        {(group.items || []).slice(0, 20).map((item) => (
                                          <button
                                            type="button"
                                            className={`sendora-live-monitor-row sendora-live-monitor-row-btn ${
                                              selectedMonitorEmailPreview?.source === "upcoming" &&
                                              selectedMonitorEmailPreview?.queueId === item.queueId
                                                ? "is-selected"
                                                : ""
                                            }`}
                                            key={item.queueId}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openMonitorEmailPreview(item, "upcoming", event);
                                            }}
                                          >
                                            <div className="sendora-live-monitor-row-main">
                                              <div className="sendora-live-monitor-row-line is-primary">
                                                <span className="sendora-live-monitor-email">
                                                  {item.memberEmail || "-"}
                                                </span>
                                                <span className="sendora-live-monitor-row-time">
                                                  {formatMonitorEpoch(item.scheduledTime)}
                                                </span>
                                              </div>
                                              <div className="sendora-live-monitor-row-line is-secondary">
                                                <span className="sendora-live-monitor-from">
                                                  via {item.fromEmail || "-"}
                                                </span>
                                                <span className="sendora-live-monitor-row-pills">
                                                  {(isQueueItemPausedByStepOrSequence(item) ||
                                                    isQueueItemPausedBySchedule(item)) && (
                                                    <i
                                                      className="fa fa-pause-circle"
                                                      aria-hidden="true"
                                                    />
                                                  )}
                                                  <span className={getMonitorSourcePillClassName(item)}>
                                                    {String(item?.sourceLabel || "Manual step")}
                                                  </span>
                                                  <span
                                                    className={`sendora-live-monitor-status-pill is-${String(
                                                      item.status || ""
                                                    ).replace(/[^a-z0-9_-]/gi, "")}`}
                                                  >
                                                    {item.status}
                                                  </span>
                                                </span>
                                              </div>
                                            </div>
                                          </button>
                                        ))}
                                        {!group.items?.length && (
                                          <div className="sendora-live-monitor-empty">
                                            {totalQueuedCount > 0
                                              ? "Queued emails exist, but none are in the current preview window"
                                              : "No emails queued"}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="sendora-live-monitor-list">
                                    <div className="sendora-live-monitor-empty">
                                      {showQueuePopulateHint
                                        ? `No queued emails - ${queuePopulateActionText} campaign to populate queue`
                                        : "No upcoming queued emails"}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="sendora-live-monitor-list">
                                {monitorUpcomingQueue.length ? (
                                  monitorUpcomingQueue.slice(0, 20).map((item) => (
                                    <button
                                      type="button"
                                      className={`sendora-live-monitor-row sendora-live-monitor-row-btn ${
                                        selectedMonitorEmailPreview?.source === "upcoming" &&
                                        selectedMonitorEmailPreview?.queueId === item.queueId
                                          ? "is-selected"
                                          : ""
                                      }`}
                                      key={item.queueId}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openMonitorEmailPreview(item, "upcoming", event);
                                      }}
                                    >
                                      <div className="sendora-live-monitor-row-main">
                                        <div className="sendora-live-monitor-row-line is-primary">
                                          <span className="sendora-live-monitor-email">
                                            {item.memberEmail || "-"}
                                          </span>
                                          <span className="sendora-live-monitor-row-time">
                                            {formatMonitorEpoch(item.scheduledTime)}
                                          </span>
                                        </div>
                                        <div className="sendora-live-monitor-row-line is-secondary">
                                          <span className="sendora-live-monitor-from">
                                            via {item.fromEmail || "-"}
                                          </span>
                                          <span className="sendora-live-monitor-row-pills">
                                            {(isQueueItemPausedByStepOrSequence(item) ||
                                              isQueueItemPausedBySchedule(item)) && (
                                              <i className="fa fa-pause-circle" aria-hidden="true" />
                                            )}
                                            <span className={getMonitorSourcePillClassName(item)}>
                                              {String(item?.sourceLabel || "Manual step")}
                                            </span>
                                            <span
                                              className={`sendora-live-monitor-status-pill is-${String(
                                                item.status || ""
                                              ).replace(/[^a-z0-9_-]/gi, "")}`}
                                            >
                                              {item.status}
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="sendora-live-monitor-empty">
                                    {showQueuePopulateHint
                                      ? `No queued emails - ${queuePopulateActionText} campaign to populate queue`
                                      : "No upcoming queued emails"}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="sendora-live-monitor-column">
                            <div className="sendora-live-monitor-column-title">Recent activity</div>
                            {splitMonitorByStep ? (
                              <div className="sendora-live-monitor-step-groups">
                                {monitorRecentActivityByStep.length ? (
                                  monitorRecentActivityByStep.map((group) => {
                                    const normalizedStepId = String(group?.stepId || "").trim();
                                    const stepMetric = runStepMetricsByStepId.get(normalizedStepId) || null;
                                    const totalSentCount = stepMetric
                                      ? Math.max(0, Number(stepMetric?.sentCount || 0))
                                      : Math.max(0, Number(group?.items?.length || 0));
                                    return (
                                      <div
                                        className="sendora-live-monitor-list sendora-live-monitor-list-split"
                                        key={`recent-group-${group.stepId}`}
                                      >
                                        <div className="sendora-live-monitor-split-label">
                                          <span>{group.label}</span>
                                          <span className="sendora-live-monitor-split-count">
                                            {`${formatMetricNumber(totalSentCount)} sent`}
                                          </span>
                                        </div>
                                        {(group.items || []).slice(0, 20).map((item) => (
                                          <button
                                            type="button"
                                            className={`sendora-live-monitor-row sendora-live-monitor-row-btn ${
                                              selectedMonitorEmailPreview?.source === "recent" &&
                                              selectedMonitorEmailPreview?.queueId === item.queueId
                                                ? "is-selected"
                                                : ""
                                            }`}
                                            key={`${item.queueId}-${item.updatedTime}`}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openMonitorEmailPreview(item, "recent", event);
                                            }}
                                          >
                                            <div className="sendora-live-monitor-row-main">
                                              <div className="sendora-live-monitor-row-line is-primary">
                                                <span className="sendora-live-monitor-email">
                                                  {item.memberEmail || "-"}
                                                </span>
                                                <span className="sendora-live-monitor-row-time">
                                                  {formatMonitorEpoch(item.sentTime || item.updatedTime)}
                                                </span>
                                              </div>
                                              <div className="sendora-live-monitor-row-line is-secondary">
                                                <span className="sendora-live-monitor-from">
                                                  via {item.fromEmail || "-"}
                                                </span>
                                                <span className="sendora-live-monitor-row-pills">
                                                  <span className={getMonitorSourcePillClassName(item)}>
                                                    {String(item?.sourceLabel || "Manual step")}
                                                  </span>
                                                  <span
                                                    className={`sendora-live-monitor-status-pill is-${String(
                                                      item.status || ""
                                                    ).replace(/[^a-z0-9_-]/gi, "")}`}
                                                  >
                                                    {item.status}
                                                  </span>
                                                </span>
                                              </div>
                                            </div>
                                          </button>
                                        ))}
                                        {!group.items?.length && (
                                          <div className="sendora-live-monitor-empty">
                                            No send activity yet
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="sendora-live-monitor-list">
                                    <div className="sendora-live-monitor-empty">No send activity yet</div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="sendora-live-monitor-list">
                                {monitorRecentActivity.length ? (
                                  monitorRecentActivity.slice(0, 20).map((item) => (
                                    <button
                                      type="button"
                                      className={`sendora-live-monitor-row sendora-live-monitor-row-btn ${
                                        selectedMonitorEmailPreview?.source === "recent" &&
                                        selectedMonitorEmailPreview?.queueId === item.queueId
                                          ? "is-selected"
                                          : ""
                                      }`}
                                      key={`${item.queueId}-${item.updatedTime}`}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openMonitorEmailPreview(item, "recent", event);
                                      }}
                                    >
                                      <div className="sendora-live-monitor-row-main">
                                        <div className="sendora-live-monitor-row-line is-primary">
                                          <span className="sendora-live-monitor-email">
                                            {item.memberEmail || "-"}
                                          </span>
                                          <span className="sendora-live-monitor-row-time">
                                            {formatMonitorEpoch(item.sentTime || item.updatedTime)}
                                          </span>
                                        </div>
                                        <div className="sendora-live-monitor-row-line is-secondary">
                                          <span className="sendora-live-monitor-from">
                                            via {item.fromEmail || "-"}
                                          </span>
                                          <span className="sendora-live-monitor-row-pills">
                                            <span className={getMonitorSourcePillClassName(item)}>
                                              {String(item?.sourceLabel || "Manual step")}
                                            </span>
                                            <span
                                              className={`sendora-live-monitor-status-pill is-${String(
                                                item.status || ""
                                              ).replace(/[^a-z0-9_-]/gi, "")}`}
                                            >
                                              {item.status}
                                            </span>
                                          </span>
                                        </div>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="sendora-live-monitor-empty">No send activity yet</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>

                  {activeTab === "speed" && (
                    <Card className="admin-card p-3 mb-3">
                      {renderTabControls()}
                      <div className="section-top-row">
                        <div>
                          <div className="section-title">Campaign speed</div>
                          <div className="section-desc">
                            Set the daily volume of your campaign and view estimated completion times.
                          </div>
                        </div>
                      </div>

                      <div className="options-sections speed-sections-grid">
                        <div className="speed-main-column">
                          <div className="speed-controls-panel">
                            <div className="options-rows">
                            <div className="options-row">
                              <div className="options-row-label">Campaign speed</div>
                              <div className="options-row-input">
                                <select
                                  className="form-control"
                                  value={selectedCampaignSpeed}
                                  onChange={(e) => {
                                    const nextSpeed = normalizeCampaignSpeed(e.target.value);
                                    setEditForm((prev) => ({
                                      ...prev,
                                      campaignPriority: nextSpeed,
                                      dailyLimit:
                                        nextSpeed === "custom"
                                          ? 100000
                                          : CAMPAIGN_SPEED_PRESETS[nextSpeed]?.dailyLimit ||
                                            prev.dailyLimit,
                                    }));
                                  }}
                                >
                                  <option value="slow">Slow (~1000 sends a day)</option>
                                  <option value="medium">Medium (~5000 sends a day)</option>
                                  <option value="fast">Fast (~50,000 sends a day)</option>
                                  <option value="custom">Custom</option>
                                </select>
                              </div>
                            </div>
                            <div className="options-row">
                              <div className="options-row-label">Daily limit</div>
                              <div className="options-row-input">
                                <input
                                  className="form-control"
                                  type="number"
                                  min={1}
                                  value={editForm.dailyLimit}
                                  onChange={(e) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      campaignPriority: "custom",
                                      dailyLimit: e.target.value,
                                    }))
                                  }
                                />
                                <div className="speed-gap-hint">{gapBetweenEmailsLabel}</div>
                              </div>
                            </div>
                          </div>
                          </div>

                          <div className="admin-card p-2 mb-2 sendora-sub-card options-section-card speed-preview-panel">
                            <div className="options-section-header">
                              <div className="options-section-title">Campaign Preview</div>
                              <div className="options-section-desc">
                                Estimated completion based on current speed, schedule, and pending
                                leads.
                              </div>
                            </div>
                            <div className="speed-preview-compact-grid">
                              {!scheduleBypassEnabled && (
                                <div className="speed-preview-compact-item is-wide">
                                  <div className="speed-preview-compact-label">Starts sending</div>
                                  <div className="speed-preview-compact-value">
                                    {speedPreview.startTimeLabel || "Unavailable"}
                                  </div>
                                </div>
                              )}
                              <div className="speed-preview-compact-item">
                                <div className="speed-preview-compact-label">Leads</div>
                                <div className="speed-preview-compact-value">
                                  {Number(speedPreview.leadsRemaining || 0).toLocaleString()}
                                </div>
                              </div>
                              <div className="speed-preview-compact-item">
                                <div className="speed-preview-compact-label">Runtime</div>
                                <div className="speed-preview-compact-value">
                                  {speedPreview.durationLabel}
                                </div>
                              </div>
                              <div className="speed-preview-compact-item is-wide is-highlight">
                                <div className="speed-preview-compact-label">Estimated completion</div>
                                <div className="speed-preview-compact-value">
                                  <button
                                    type="button"
                                    className="btn btn-link p-0"
                                    onClick={() =>
                                      openEstimatedCompletionTimelineModal({
                                        title: "Campaign estimated completion timeline",
                                        leadsRemaining: Number(speedPreview.leadsRemaining || 0),
                                        gapMinutes: Number(speedPreview.gapMinutes || 0),
                                        completionTimeLabel: String(
                                          speedPreview.completionTimeLabel || ""
                                        ),
                                      })
                                    }
                                  >
                                    {speedPreview.completionTimeLabel}
                                  </button>
                                </div>
                                <div className="speed-preview-compact-note">
                                  {scheduleBypassEnabled
                                    ? "Based on bypassed schedule (24/7 pacing)"
                                    : "Based on active schedule windows"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}

                  {activeTab === "options" && (
                  <Card className="admin-card p-3 mb-3">
                    {renderTabControls()}
                    <div className="section-top-row">
                      <div>
                        <div className="section-title">Campaign settings</div>
                        <div className="section-desc">
                          Configure sending options. These settings apply across all
                          sequence steps and variants.
                        </div>
                      </div>
                    </div>

                    <div className="options-sections">
                      <div className="admin-card p-2 mb-2 sendora-sub-card options-section-card">
                        <div className="options-section-header">
                          <div className="options-section-title">Run Settings</div>
                          <div className="options-section-desc">
                            Controls that run automatically when the campaign sends emails.
                          </div>
                        </div>
                        <div className="options-rows">
                          <div className="options-row run-settings-row">
                            <div className="options-row-label">Email quality checks</div>
                            <div className="options-row-input">
                              <label className="checkbox-inline run-settings-check">
                                <input
                                  type="checkbox"
                                  checked={runSettingsForm.enableMillionVerifier}
                                  onChange={(e) =>
                                    setRunSettingsForm((prev) => ({
                                      ...prev,
                                      enableMillionVerifier: e.target.checked,
                                    }))
                                  }
                                />
                                <span className="run-settings-check-content">
                                  <span>
                                    Verify recipient email deliverability with{" "}
                                    <a
                                      href="https://www.millionverifier.com/"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      MillionVerifier
                                    </a>{" "}
                                    before sending. (recommended)
                                  </span>
                                </span>
                              </label>
                              <label className="checkbox-inline run-settings-check is-sub-option">
                                <input
                                  type="checkbox"
                                  checked={runSettingsForm.syncInvalidMailboxToSalesforce}
                                  onChange={(e) =>
                                    setRunSettingsForm((prev) => ({
                                      ...prev,
                                      syncInvalidMailboxToSalesforce: e.target.checked,
                                    }))
                                  }
                                />
                                <span>
                                  If MillionVerifier flags an email as invalid/bad, mark the Salesforce
                                  Contact as email bounced. (recommended)
                                </span>
                              </label>
                            </div>
                          </div>
                          <div className="options-row run-settings-row">
                            <div className="options-row-label">Salesforce updates</div>
                            <div className="options-row-input">
                              <label className="checkbox-inline run-settings-check">
                                <input
                                  type="checkbox"
                                  checked={runSettingsForm.syncSalesforceStatusAfterSend}
                                  onChange={(e) =>
                                    setRunSettingsForm((prev) => ({
                                      ...prev,
                                      syncSalesforceStatusAfterSend: e.target.checked,
                                    }))
                                  }
                                />
                                <span>
                                  Update Salesforce campaign member status after each email is sent.
                                </span>
                              </label>
                              <div className="run-settings-status-picker">
                                <div className="field-help">Status to apply in Salesforce</div>
                                <select
                                  className="form-control"
                                  value={runSettingsForm.postSendSalesforceStatus}
                                  disabled={!runSettingsForm.syncSalesforceStatusAfterSend}
                                  onChange={(e) =>
                                    setRunSettingsForm((prev) => ({
                                      ...prev,
                                      postSendSalesforceStatus: e.target.value,
                                    }))
                                  }
                                >
                                  {runSettingsSalesforceStatusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <label className="checkbox-inline run-settings-check mt-2">
                                <input
                                  type="checkbox"
                                  checked={runSettingsForm.updateSalesforceOutreachCounters}
                                  onChange={(e) =>
                                    setRunSettingsForm((prev) => ({
                                      ...prev,
                                      updateSalesforceOutreachCounters: e.target.checked,
                                    }))
                                  }
                                />
                                <span>
                                  Update Salesforce campaign member last sent / outreach counter
                                </span>
                              </label>
                              <label className="checkbox-inline run-settings-check mt-2">
                                <input
                                  type="checkbox"
                                  checked={
                                    runSettingsForm.pruneQueuedLeadsOnSalesforceStatusChange
                                  }
                                  onChange={(e) =>
                                    setRunSettingsForm((prev) => ({
                                      ...prev,
                                      pruneQueuedLeadsOnSalesforceStatusChange:
                                        e.target.checked,
                                    }))
                                  }
                                />
                                <div className="run-settings-check-text">
                                  <span>
                                    Remove queued leads if their Salesforce status differs from
                                    their enable-time status, excluding{" "}
                                    {campaignSalesforceStatusProfile.defaultStatus || "Unsent"}
                                    {" and "}
                                    {campaignSalesforceStatusProfile.sentStatus || "Invited"}.
                                    (recommended)
                                  </span>
                                  <div className="field-help">
                                    This prevents emails being sent to leads after their status
                                    has been marked as Attending/Declined (for example)
                                  </div>
                                </div>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="admin-card p-2 mb-2 sendora-sub-card options-section-card">
                        <div className="options-section-header">
                          <div className="options-section-title">Campaign Details</div>
                          <div className="options-section-desc">
                            Basic campaign metadata and access controls.
                          </div>
                        </div>
                        <div className="options-rows">
                          <div className="options-row">
                            <div className="options-row-label">Campaign name</div>
                            <div className="options-row-input">
                              <input
                                className="form-control"
                                value={editForm.name}
                                onChange={(e) =>
                                  setEditForm((prev) => ({ ...prev, name: e.target.value }))
                                }
                              />
                            </div>
                          </div>
                          <div className="options-row">
                            <div className="options-row-label">Linked Salesforce campaign</div>
                            <div className="options-row-input">
                              <div className="sendora-sf-campaign-value">
                                {selectedCampaign?.salesforceCampaignId ? (
                                  <a
                                    className="sendora-sf-campaign-link"
                                    href={`https://landmarkventures.lightning.force.com/lightning/r/Campaign/${selectedCampaign.salesforceCampaignId}/view`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <img
                                      src={SalesforceLogo}
                                      alt=""
                                      aria-hidden="true"
                                      className="sendora-sf-logo"
                                    />
                                    {selectedCampaign?.salesforceCampaignName ||
                                      selectedCampaign?.salesforceCampaignId ||
                                      "Salesforce campaign"}
                                  </a>
                                ) : (
                                  "No Salesforce campaign"
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="options-row">
                            <div className="options-row-label">Notes (internal Sendora use only)</div>
                            <div className="options-row-input">
                              <textarea
                                className="form-control"
                                rows={2}
                                value={editForm.notes}
                                onChange={(e) =>
                                  setEditForm((prev) => ({ ...prev, notes: e.target.value }))
                                }
                              />
                            </div>
                          </div>
                          <div className="options-row">
                            <div className="options-row-label">Collaborators</div>
                            <div className="options-row-input">
                              <div className="collaborator-controls">
                                <select
                                  className="form-control"
                                  value={editForm.selectedCollaboratorEmail}
                                  onChange={(e) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      selectedCollaboratorEmail: e.target.value,
                                    }))
                                  }
                                >
                                  <option value="">Select existing employee email</option>
                                  {employeeEmails.map((email) => (
                                    <option key={email} value={email}>
                                      {email}
                                    </option>
                                  ))}
                                </select>
                                {!!editForm.selectedCollaboratorEmail && (
                                  <Button
                                    className="sendora-btn collaborator-add-btn"
                                    onClick={handleAddSelectedCollaborator}
                                  >
                                    Add
                                  </Button>
                                )}
                              </div>
                              <div className="collaborator-chip-row">
                                {(editForm.collaboratorEmails || []).map((email) => (
                                  <span key={email} className="collaborator-chip">
                                    <span className="collaborator-chip-label">{email}</span>
                                    <button
                                      type="button"
                                      className="collaborator-chip-remove"
                                      onClick={() => removeCollaboratorFromForm(email)}
                                      aria-label={`Remove collaborator ${email}`}
                                      title="Remove"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div className="field-help">
                                Collaborators can fully edit and operate this campaign.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="admin-card p-2 mb-2 sendora-sub-card options-section-card">
                        <div className="options-section-header">
                          <div className="options-section-title">Recipients</div>
                          <div className="options-section-desc">
                            Configure recipient copy settings.
                          </div>
                        </div>
                        <div className="options-rows">
                          <div className="options-row">
                            <div className="options-row-label">
                              <span className="sendora-label-with-tooltip">
                                <span>CC</span>
                                <CustomTooltip
                                  content={CC_TOOLTIP_TEXT}
                                  placement="right"
                                  tooltipClassName="sendora-email-help-tooltip"
                                >
                                  <button
                                    type="button"
                                    className="sendora-label-help-btn"
                                    aria-label="What CC means"
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    <i className="fa fa-info-circle" aria-hidden="true" />
                                  </button>
                                </CustomTooltip>
                              </span>
                            </div>
                            <div className="options-row-input">
                              <div className="sendora-pill-input-wrap">
                                <div className="collaborator-chip-row">
                                  {(editForm.ccEmails || []).map((email) => (
                                    <span key={`cc-${email}`} className="collaborator-chip">
                                      <span className="collaborator-chip-label">{email}</span>
                                      <button
                                        type="button"
                                        className="collaborator-chip-remove"
                                        onClick={() => handleRemovePillEmail("cc", email)}
                                        aria-label={`Remove CC ${email}`}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    className="sendora-pill-input"
                                    value={editForm.ccInput}
                                    onChange={(e) => handleEmailPillInputChange("cc", e.target.value)}
                                    onKeyDown={(e) => handleEmailPillInputKeyDown("cc", e)}
                                    onBlur={() => commitEmailPills("cc", editForm.ccInput)}
                                    placeholder="Type email and press space"
                                  />
                                </div>
                              </div>
                              <div className="field-help">
                                CC recipients are copied on all emails sent in this campaign.
                              </div>
                            </div>
                          </div>
                          <div className="options-row">
                            <div className="options-row-label">
                              <span className="sendora-label-with-tooltip">
                                <span>BCC</span>
                                <CustomTooltip
                                  content={BCC_TOOLTIP_TEXT}
                                  placement="right"
                                  tooltipClassName="sendora-email-help-tooltip"
                                >
                                  <button
                                    type="button"
                                    className="sendora-label-help-btn"
                                    aria-label="What BCC means"
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    <i className="fa fa-info-circle" aria-hidden="true" />
                                  </button>
                                </CustomTooltip>
                              </span>
                            </div>
                            <div className="options-row-input">
                              <div className="sendora-pill-input-wrap">
                                <div className="collaborator-chip-row">
                                  {(editForm.bccEmails || []).map((email) => (
                                    <span key={`bcc-${email}`} className="collaborator-chip">
                                      <span className="collaborator-chip-label">{email}</span>
                                      <button
                                        type="button"
                                        className="collaborator-chip-remove"
                                        onClick={() => handleRemovePillEmail("bcc", email)}
                                        aria-label={`Remove BCC ${email}`}
                                        title="Remove"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ))}
                                  <input
                                    className="sendora-pill-input"
                                    value={editForm.bccInput}
                                    onChange={(e) => handleEmailPillInputChange("bcc", e.target.value)}
                                    onKeyDown={(e) => handleEmailPillInputKeyDown("bcc", e)}
                                    onBlur={() => commitEmailPills("bcc", editForm.bccInput)}
                                    placeholder="Type email and press space"
                                  />
                                </div>
                              </div>
                              <div className="field-help">
                                BCC recipients are hidden copies on all emails sent in this campaign.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {SHOW_BEHAVIOR_OPTIONS && (
                        <div className="admin-card p-2 mb-2 sendora-sub-card options-section-card">
                          <div className="options-section-header">
                            <div className="options-section-title">Behavior</div>
                            <div className="options-section-desc">
                              Fine-tune campaign stop and prioritization logic.
                            </div>
                          </div>
                          <div className="options-rows">
                            <div className="options-row">
                              <div className="options-row-label">Rules</div>
                              <div className="options-row-input">
                                <div className="checkbox-row options-behavior-list">
                                  <label className="checkbox-inline">
                                    <input
                                      type="checkbox"
                                      checked={editForm.stopSendingOnReply}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({
                                          ...prev,
                                          stopSendingOnReply: e.target.checked,
                                        }))
                                      }
                                    />
                                    <span>Stop sending emails on reply</span>
                                  </label>
                                  <label className="checkbox-inline">
                                    <input
                                      type="checkbox"
                                      checked={editForm.stopCampaignForCompanyOnReply}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({
                                          ...prev,
                                          stopCampaignForCompanyOnReply: e.target.checked,
                                        }))
                                      }
                                    />
                                    <span>Stop campaign for company on reply</span>
                                  </label>
                                  <label className="checkbox-inline">
                                    <input
                                      type="checkbox"
                                      checked={editForm.stopSendingOnAutoReply}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({
                                          ...prev,
                                          stopSendingOnAutoReply: e.target.checked,
                                        }))
                                      }
                                    />
                                    <span>Stop sending on auto-reply</span>
                                  </label>
                                  <label className="checkbox-inline">
                                    <input
                                      type="checkbox"
                                      checked={editForm.prioritizeNewLeads}
                                      onChange={(e) =>
                                        setEditForm((prev) => ({
                                          ...prev,
                                          prioritizeNewLeads: e.target.checked,
                                        }))
                                      }
                                    />
                                    <span>Prioritize new leads</span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="admin-card p-2 mb-2 sendora-sub-card options-section-card">
                        <div className="options-section-header">
                          <div className="options-section-title">Sending Infastructure</div>
                          <div className="options-section-desc">
                            Set the sending infastructure used by sender domain.
                          </div>
                        </div>
                        <div className="sending-infrastructure-rule-list">
                          {(domainInfrastructureRulesForUi || []).map((rule) => {
                            const domainSuffix = String(rule?.domainSuffix || "");
                            const allowedSet = new Set(
                              Array.isArray(rule?.allowedInfrastructures)
                                ? rule.allowedInfrastructures
                                : []
                            );
                            const selectableDefaultOptions = sendingInfrastructureOptionsForUi.filter(
                              (option) => allowedSet.has(option.key)
                            );
                            return (
                              <div key={domainSuffix} className="sending-infrastructure-rule-item">
                                <div className="sending-infrastructure-rule-domain">@{domainSuffix}</div>
                                <div className="sending-infrastructure-rule-controls">
                                  <div className="sending-infrastructure-rule-default">
                                    <label>Send via</label>
                                    <select
                                      className="form-control"
                                      value={String(rule?.defaultInfrastructure || "")}
                                      onChange={(e) =>
                                        handleSetDomainInfrastructureDefault(
                                          domainSuffix,
                                          e.target.value
                                        )
                                      }
                                    >
                                      {selectableDefaultOptions.map((option) => (
                                        <option key={`${domainSuffix}-${option.key}`} value={option.key}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {savingDomainInfrastructure && (
                          <div className="field-help mt-2">Saving infrastructure settings...</div>
                        )}
                      </div>
                    </div>
                  </Card>
                  )}

                  {activeTab === "emails" && (
                  <Card className="admin-card p-3 mb-3">
                    {renderTabControls()}
                    <div className="section-top-row email-section-top-row">
                      <div>
                        <div className="section-title">Email builder</div>
                      </div>
                      <button
                        type="button"
                        className="sendora-link-toggle-btn"
                        onClick={() => setShowEmailAdvancedOptions((prev) => !prev)}
                      >
                        {showEmailAdvancedOptions ? "Hide advanced options" : "Show advanced options"}
                      </button>
                    </div>
                    {showEmailAdvancedOptions && (
                      <div className="admin-card p-2 mb-2 sendora-sub-card email-sender-card sendora-brand-voice-card">
                        <div className="email-sender-inline">
                          <div className="email-sender-header">
                            <div className="email-sender-title">Brand voice</div>
                            <div className="email-sender-description">
                              Choose a brand voice that can be used to help generate or guide copy.
                            </div>
                          </div>
                          <div className="email-sender-controls">
                            <div className="email-sender-description">
                              Active: {selectedCampaignBrandVoice?.name || "None selected"}
                            </div>
                            <Button
                              variant="outlined"
                              className="email-add-sender-btn sendora-btn-outline"
                              onClick={openBrandVoiceModal}
                              title="Select brand voice"
                              disabled={!selectedCampaign?.campaignId}
                            >
                              <i className="fa fa-commenting-o" aria-hidden="true" />
                              <span>Select brand voice</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="admin-card p-2 mb-2 sendora-sub-card email-sender-card">
                      <div className="email-sender-inline">
                        <div className="email-sender-header">
                          <div className="email-sender-title">Sending accounts</div>
                          <div className="email-sender-description">
                            Select one or more sender accounts. If multiple accounts are selected,
                            each email is sent from a randomly chosen sender.
                          </div>
                        </div>
                        <div className="email-sender-controls">
                          {hasSequenceSenderAccounts && (
                            <div className="collaborator-chip-row email-sender-chip-row">
                              {(sequenceSenderAccounts || []).map((account) => {
                                const emailKey = normalizeEmail(
                                  account?.emailLowercase || account?.email || ""
                                );
                                return (
                                  <span key={emailKey} className="collaborator-chip">
                                    <span className="collaborator-chip-label">
                                      {formatSenderAccountLabel(account)}
                                    </span>
                                    <button
                                      type="button"
                                      className="collaborator-chip-remove"
                                      onClick={() => handleRemoveSequenceSenderAccount(emailKey)}
                                      aria-label={`Remove sender ${emailKey}`}
                                      title="Remove"
                                    >
                                      ×
                                    </button>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          <Button
                            variant="outlined"
                            className={`email-add-sender-btn ${
                              hasSequenceSenderAccounts
                                ? "sendora-btn-outline"
                                : "sendora-btn email-add-sender-btn--highlight"
                            }`}
                            onClick={() => setIsSenderAccountModalOpen(true)}
                            title="Add sender account"
                          >
                            <i className="fa fa-plus" aria-hidden="true" />
                            <span>Add sender</span>
                          </Button>
                          {hasSequenceSenderAccounts && (
                            <Button
                              variant="outlined"
                              className="email-add-sender-btn sendora-btn-outline"
                              onClick={openSenderSignatureModal}
                              title="Edit sender signatures"
                            >
                              <i className="fa fa-pencil" aria-hidden="true" />
                              <span>Edit signatures</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`email-layout-grid ${
                        isEmailEditorLocked ? "is-editor-locked" : ""
                      }`}
                    >
                      <div
                        className={`email-left-pane ${isEmailEditorLocked ? "is-disabled" : ""}`}
                      >
                        {sequenceStepsForUi.map((step, stepIndex) => {
                          const isSelectedStep = selectedStepIndex === stepIndex;
                          const stepLabel = ENABLE_MULTI_STEP_SEQUENCES
                            ? `Step ${step.orderIndex}`
                            : "Emails";
                          const normalizedStepMode =
                            stepIndex === 0
                              ? "standard"
                              : String(step?.mode || "").trim().toLowerCase() === "ott"
                              ? "ott"
                              : "standard";
                          const priorStepOptions = (sequenceStepsForUi || [])
                            .filter((candidate) => Number(candidate?.orderIndex || 0) < Number(step?.orderIndex || 0))
                            .map((candidate) => ({
                              value: String(candidate?.stepId || ""),
                              label: `Step ${Number(candidate?.orderIndex || 0) || 1}`,
                            }));
                          return (
                            <div
                              key={step.stepId || stepIndex}
                              className={`email-step-panel ${
                                isSelectedStep && hasSequenceSenderAccounts ? "is-active" : ""
                              }`}
                            >
                              <div
                                className="email-step-panel-head"
                                onClick={(event) => {
                                  if (isEmailEditorLocked) return;
                                  const target = event?.target;
                                  if (target?.closest?.(".email-step-actions")) return;
                                  if (
                                    target?.closest?.(
                                      "button, input, select, textarea, a, label, .email-variant-item"
                                    )
                                  ) {
                                    return;
                                  }
                                  handleSelectSequenceStep(stepIndex);
                                }}
                              >
                                <button
                                  type="button"
                                  className={`email-step-select ${
                                    !hasSequenceSenderAccounts ? "is-no-senders" : ""
                                  }`}
                                  disabled={isEmailEditorLocked}
                                  onClick={() => handleSelectSequenceStep(stepIndex)}
                                >
                                  {stepLabel}
                                </button>
                                <div className="email-step-actions">
                                  {ENABLE_MULTI_STEP_SEQUENCES && stepIndex > 0 && (
                                    <div className="email-step-ott-config-wrap">
                                      <CustomTooltip
                                        content="OTT uses this step as a reply on top of an earlier step's sent email."
                                        placement="top"
                                      >
                                        <label
                                          className="checkbox-inline mb-0 email-step-ott-toggle"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={normalizedStepMode === "ott"}
                                            onChange={(e) =>
                                              handleUpdateStepMode(
                                                stepIndex,
                                                e.target.checked ? "ott" : "standard"
                                              )
                                            }
                                          />
                                          <span className="email-step-ott-label">OTT</span>
                                        </label>
                                      </CustomTooltip>
                                      {normalizedStepMode === "ott" && (
                                        <div className="email-step-ott-inline">
                                          <span className="email-step-ott-inline-prefix">:</span>
                                          <select
                                            className="email-step-ott-inline-select"
                                            value={String(step?.ottSourceStepId || "")}
                                            onChange={(e) =>
                                              handleUpdateStepOttSource(stepIndex, e.target.value)
                                            }
                                          >
                                            {priorStepOptions.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {ENABLE_MULTI_STEP_SEQUENCES && (
                                    <button
                                      type="button"
                                      className="email-step-trash email-step-duplicate-btn"
                                      title="Duplicate step"
                                      onClick={() => handleDuplicateStep(stepIndex)}
                                    >
                                      <svg
                                        viewBox="0 0 512 512"
                                        width="15"
                                        height="15"
                                        aria-hidden="true"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="32"
                                      >
                                        <rect x="128" y="128" width="336" height="336" rx="57" ry="57" />
                                        <path d="M383.5,128l.5-24a56.16,56.16,0,0,0-56-56H112a64.19,64.19,0,0,0-64,64V328a56.16,56.16,0,0,0,56,56h24" />
                                        <line x1="296" y1="216" x2="296" y2="376" />
                                        <line x1="376" y1="296" x2="216" y2="296" />
                                      </svg>
                                    </button>
                                  )}
                                  {ENABLE_MULTI_STEP_SEQUENCES &&
                                    sequenceDraft.steps.length > 1 && (
                                    <button
                                      type="button"
                                      className="email-step-trash"
                                      onClick={() => handleRemoveStep(stepIndex)}
                                    >
                                      <i className="fa fa-trash" aria-hidden="true" />
                                    </button>
                                    )}
                                </div>
                              </div>

                              <div className="email-variant-list">
                                {(step.variants || []).map((variant, variantIndex) => {
                                  const variantLabel = String.fromCharCode(65 + variantIndex);
                                  const hasMultipleVariants = (step.variants || []).length > 1;
                                  const isSelectedVariant =
                                    isSelectedStep && selectedVariantIndex === variantIndex;
                                  const senderOverrideEmail = normalizeEmail(
                                    variant?.senderAccount?.emailLowercase ||
                                      variant?.senderAccount?.email ||
                                      variant?.senderAccountEmail ||
                                      ""
                                  );
                                  const senderOverrideAccount = senderOverrideEmail
                                    ? normalizeSenderAccount(variant?.senderAccount || {}) ||
                                      senderAccountByEmail.get(senderOverrideEmail) || {
                                        emailLowercase: senderOverrideEmail,
                                      }
                                    : null;
                                  const senderOverrideText = senderOverrideAccount
                                    ? formatSenderAccountLabel(senderOverrideAccount)
                                    : "";
                                  return (
                                    <button
                                      type="button"
                                      key={variant.variantId || `${stepIndex}-${variantIndex}`}
                                      className={`email-variant-item ${
                                        isSelectedVariant ? "is-active" : ""
                                      }`}
                                      disabled={isEmailEditorLocked}
                                      onClick={() => {
                                        setSelectedStepIndex(stepIndex);
                                        setSelectedVariantIndex(variantIndex);
                                      }}
                                    >
                                      {hasMultipleVariants && (
                                        <span className="variant-badge">{variantLabel}</span>
                                      )}
                                      <span className="email-variant-content">
                                        <span className="email-variant-subject">
                                          {variant.subject ||
                                            "No subject"}
                                        </span>
                                        {!!senderOverrideText && (
                                          <span className="email-variant-sender-override">
                                            {senderOverrideText}
                                          </span>
                                        )}
                                      </span>
                                      <span className="email-variant-actions">
                                        {(step.variants || []).length > 1 && (
                                          <label
                                            className="variant-toggle-wrap"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={variant.isActive !== false}
                                              disabled={isEmailEditorLocked}
                                              onChange={(e) =>
                                                handleToggleVariantActive(
                                                  stepIndex,
                                                  variantIndex,
                                                  e.target.checked
                                                )
                                              }
                                            />
                                            <span className="variant-toggle-slider" />
                                          </label>
                                        )}
                                        <CustomTooltip content="Duplicate variant" placement="top">
                                          <span
                                            className="email-variant-duplicate"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDuplicateVariant(stepIndex, variantIndex);
                                            }}
                                          >
                                            <svg
                                              viewBox="0 0 512 512"
                                              width="15"
                                              height="15"
                                              aria-hidden="true"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="32"
                                            >
                                              <rect x="128" y="128" width="336" height="336" rx="57" ry="57" />
                                              <path d="M383.5,128l.5-24a56.16,56.16,0,0,0-56-56H112a64.19,64.19,0,0,0-64,64V328a56.16,56.16,0,0,0,56,56h24" />
                                              <line x1="296" y1="216" x2="296" y2="376" />
                                              <line x1="376" y1="296" x2="216" y2="296" />
                                            </svg>
                                          </span>
                                        </CustomTooltip>
                                        <CustomTooltip content="Variant settings" placement="top">
                                          <span
                                            className="email-variant-config"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openVariantSenderModal(stepIndex, variantIndex);
                                            }}
                                          >
                                            <i className="fa fa-cog" aria-hidden="true" />
                                          </span>
                                        </CustomTooltip>
                                        {(step.variants || []).length > 1 && (
                                          <CustomTooltip content="Delete variant" placement="top">
                                            <span
                                              className="email-variant-remove"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveVariant(stepIndex, variantIndex);
                                              }}
                                            >
                                              <i className="fa fa-trash" aria-hidden="true" />
                                            </span>
                                          </CustomTooltip>
                                        )}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>

                              <Button
                                variant="outlined"
                                className="sendora-btn-outline w-100 sendora-add-variant-btn"
                                onClick={() => handleAddVariant(stepIndex)}
                                disabled={isEmailEditorLocked}
                              >
                                + Add variant
                              </Button>

                            </div>
                          );
                        })}

                        {ENABLE_MULTI_STEP_SEQUENCES && (
                          <Button
                            variant="outlined"
                            className="sendora-btn-outline w-100"
                            onClick={handleAddStep}
                          >
                            + Add step
                          </Button>
                        )}
                      </div>

                      <div className={`email-right-pane ${isEmailEditorLocked ? "is-disabled" : ""}`}>
                        <div className="admin-card p-3">
                          {(selectedStep?.variants || []).length > 1 && (
                            <span className="variant-badge sequence-editor-corner-variant-badge">
                              {selectedVariantLabel}
                            </span>
                          )}
                          <div className="sequence-editor-head">
                            {isSelectedStepOttMode && (
                              <div className="sequence-ott-options-row">
                                <div className="sequence-ott-options-left">
                                  <div className="sequence-ott-options-label">OTT options</div>
                                  <label className="sequence-ott-option-checkbox">
                                    <input
                                      type="checkbox"
                                      checked={selectedStep?.ottUsePreviousSender !== false}
                                      onChange={(event) =>
                                        handleUpdateStepOttUsePreviousSender(
                                          selectedStepIndex,
                                          event?.target?.checked !== false
                                        )
                                      }
                                      disabled={isEmailEditorLocked}
                                    />
                                    <span>Send from the same account as the previous email</span>
                                  </label>
                                  <div className="sequence-ott-option-help">
                                    Recommended. Uses the sender from the most recent linked-step email sent to this
                                    recipient.
                                  </div>
                                </div>
                                <Button
                                  variant="outlined"
                                  className="sendora-btn-outline sequence-ott-insert-btn"
                                  onClick={handleInsertOttReplyVariables}
                                  disabled={isEmailEditorLocked}
                                >
                                  <i className="fa fa-reply" aria-hidden="true" /> Insert previous email context
                                </Button>
                              </div>
                            )}
                            <div className="sequence-subject-row">
                              <div className="form-group sequence-subject-group">
                                <label>Subject</label>
                                <div
                                  ref={subjectEditorRef}
                                  className="form-control sequence-subject-editor"
                                  contentEditable={!isEmailEditorLocked}
                                  suppressContentEditableWarning
                                  onMouseDown={(event) => handleTokenMouseDown(event, "subject")}
                                  onFocus={() => {
                                    setEditorInsertTarget("subject");
                                    captureEditorSelection("subject");
                                  }}
                                  onMouseUp={() => captureEditorSelection("subject")}
                                  onKeyUp={() => captureEditorSelection("subject")}
                                  onKeyDown={handleSubjectEditorKeyDown}
                                  onPaste={handleSubjectEditorPaste}
                                  onInput={() => {
                                    handleSubjectEditorInput();
                                    captureEditorSelection("subject");
                                  }}
                                  onBlur={handleSubjectEditorBlur}
                                />
                              </div>
                              <Button
                                variant="outlined"
                                className="sendora-btn-outline sequence-preview-btn"
                                onClick={openPreviewModal}
                                disabled={isEmailEditorLocked}
                              >
                                <i className="fa fa-eye" aria-hidden="true" /> Preview
                              </Button>
                            </div>
                          </div>

                          <div className="sequence-editor-toolbar-row">
                          <div className="sequence-editor-toolbar">
                            <div className="variable-dropdown">
                              <button
                                type="button"
                                className="sequence-editor-tool sequence-variable-btn"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setOpenVariableMenu((prev) => !prev);
                                }}
                                disabled={isCodeView || isEmailEditorLocked}
                              >
                                <i className="fa fa-bolt" aria-hidden="true" /> Insert variable
                              </button>
                              {openVariableMenu && !isCodeView && !isEmailEditorLocked && (
                                <div className="variable-menu">
                                  <div
                                    className={`variable-menu-panels ${
                                      isSelectedStepOttMode ? "has-ott-variables" : ""
                                    }`}
                                  >
                                    <div className="variable-menu-panel">
                                      <div className="variable-menu-title variable-menu-title-recipient">
                                        Recipient variables
                                      </div>
                                      {variableFieldOptions.map((fieldName) => (
                                        <button
                                          type="button"
                                          key={`recipient-${fieldName}`}
                                          className="variable-menu-item"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => insertVariableToken(fieldName)}
                                        >
                                          {`{{${fieldName}}}`}
                                        </button>
                                      ))}
                                    </div>
                                    <div className="variable-menu-panel">
                                      <div className="variable-menu-title variable-menu-title-sender">
                                        Sender variables
                                      </div>
                                      {senderVariableOptions.map((fieldName) => (
                                        <button
                                          type="button"
                                          key={`sender-${fieldName}`}
                                          className="variable-menu-item variable-menu-item-sender"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => insertVariableToken(fieldName)}
                                        >
                                          {`{{${fieldName}}}`}
                                        </button>
                                      ))}
                                    </div>
                                    {!!aiVariableOptions.length && (
                                      <div className="variable-menu-panel">
                                        <div className="variable-menu-title variable-menu-title-ai">
                                          AI variables
                                        </div>
                                        {aiVariableOptions.map((fieldName) => (
                                          <button
                                            type="button"
                                            key={`ai-${fieldName}`}
                                            className="variable-menu-item variable-menu-item-ai"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => insertVariableToken(fieldName)}
                                          >
                                            {`{{${fieldName}}}`}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    {isSelectedStepOttMode && (
                                      <div className="variable-menu-panel">
                                        <div className="variable-menu-title variable-menu-title-ott">
                                          OTT variables
                                        </div>
                                        {ottEmailVariableOptions.map((fieldName) => (
                                          <button
                                            type="button"
                                            key={`ott-context-${fieldName}`}
                                            className="variable-menu-item variable-menu-item-ott"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => insertVariableToken(fieldName)}
                                          >
                                            {`{{${fieldName}}}`}
                                          </button>
                                        ))}
                                        <div className="variable-menu-ott-subcolumns">
                                          <div className="variable-menu-ott-subcolumn">
                                            <div className="variable-menu-subtitle">Previous sender</div>
                                            {ottSenderVariableOptions.map((fieldName) => (
                                              <button
                                                type="button"
                                                key={`ott-sender-${fieldName}`}
                                                className="variable-menu-item variable-menu-item-ott"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => insertVariableToken(fieldName)}
                                              >
                                                {`{{${fieldName}}}`}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="sequence-editor-tool sequence-variable-btn sequence-spintax-btn"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleInsertSpintaxStart}
                              disabled={isCodeView || isEmailEditorLocked}
                              title="Insert spintax"
                              aria-label="Insert spintax"
                            >
                              <span className="sequence-spintax-icon">S</span>
                              Insert Spintax
                            </button>
                            <select
                              className="sequence-editor-select"
                              value={editorFontFamilySelectionValue}
                              onChange={(e) => handleEditorFontFamilyChange(e.target.value)}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <option value="" />
                              {EDITOR_FONT_FAMILY_OPTIONS.map((fontName) => (
                                <option key={fontName} value={fontName}>
                                  {fontName}
                                </option>
                              ))}
                            </select>
                            <select
                              className="sequence-editor-select sequence-editor-size-select"
                              value={editorFontSizeSelectionValue}
                              onChange={(e) => handleEditorFontSizeChange(e.target.value)}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <option value="">Font size</option>
                              {EDITOR_FONT_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                  {size}pt
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className={`sequence-editor-tool ${
                                editorInlineStyleState.bold ? "is-active" : ""
                              }`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleEditorCommand("bold")}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <b>B</b>
                            </button>
                            <button
                              type="button"
                              className={`sequence-editor-tool ${
                                editorInlineStyleState.italic ? "is-active" : ""
                              }`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleEditorCommand("italic")}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <i>I</i>
                            </button>
                            <button
                              type="button"
                              className={`sequence-editor-tool ${
                                editorInlineStyleState.underline ? "is-active" : ""
                              }`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handleEditorCommand("underline")}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <u>U</u>
                            </button>
                            <button
                              type="button"
                              className="sequence-editor-tool"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleEditorIndent}
                              disabled={isCodeView || isEmailEditorLocked}
                              title="Indent"
                              aria-label="Indent"
                            >
                              <i className="fa fa-indent" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="sequence-editor-tool"
                              onClick={() => handleEditorCommand("insertUnorderedList")}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <i className="fa fa-list-ul" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="sequence-editor-tool"
                              onClick={() => handleEditorCommand("insertOrderedList")}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <i className="fa fa-list-ol" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="sequence-editor-tool"
                              onClick={() => handleEditorCommand("createLink")}
                              disabled={isCodeView || isEmailEditorLocked}
                            >
                              <i className="fa fa-link" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className={`sequence-editor-tool ${
                                isCodeView ? "is-active" : ""
                              }`}
                              onClick={toggleCodeView}
                              disabled={isEmailEditorLocked}
                            >
                              <i className="fa fa-code" aria-hidden="true" />
                            </button>
                          </div>
                          <div
                            className={`sequence-editor-toolbar sequence-ai-toolbar ${
                              isSequenceAiToolbarLoading ? "is-loading-shine" : ""
                            }`}
                          >
                            <button
                              type="button"
                              className="sequence-editor-tool sequence-ai-tool-btn sequence-generate-spintax-btn"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleGenerateAutoSpintaxWithAi}
                              disabled={
                                autoGeneratingSpintax ||
                                cleaningHtml ||
                                fixingFormatting ||
                                applyingCustomPrompt ||
                                scanningDeliverability ||
                                applyingDeliverabilitySuggestions ||
                                isEmailEditorLocked
                              }
                              title="Generate spintax automatically"
                              aria-label="Generate spintax automatically"
                            >
                              <i className="fa fa-random" aria-hidden="true" />
                              <span>{autoGeneratingSpintax ? "Generating..." : "Generate spintax"}</span>
                            </button>
                            <button
                              type="button"
                              className="sequence-editor-tool sequence-ai-tool-btn sequence-clean-html-btn"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleCleanHtmlWithAi}
                              disabled={
                                cleaningHtml ||
                                fixingFormatting ||
                                scanningDeliverability ||
                                applyingDeliverabilitySuggestions ||
                                isEmailEditorLocked
                              }
                              title="Clean HTML for email compatibility"
                              aria-label="Clean HTML"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M13.8019 3.07009L11.8374 8.03851C11.7168 8.34473 11.5367 8.62467 11.3078 8.86122C11.0789 9.0979 10.8054 9.287 10.5031 9.41768C10.2008 9.54835 9.87566 9.61801 9.54638 9.62265C9.21709 9.6273 8.89013 9.56684 8.58427 9.44476L8.57958 9.44288L6.36016 8.54391C6.09297 8.82711 5.84558 9.11913 5.6195 9.4209L17 13.9733V12.7996L14.8261 11.9419L14.8218 11.9401C14.2072 11.6944 13.7151 11.2148 13.4535 10.6067C13.192 9.99865 13.1823 9.31164 13.4265 8.6964L13.4281 8.69234L15.4336 3.70459C15.4786 3.59592 15.5013 3.47932 15.5002 3.36171C15.4991 3.2441 15.4743 3.12791 15.4273 3.0201C15.3803 2.91228 15.312 2.81505 15.2266 2.73421C15.1412 2.65337 15.0403 2.59059 14.93 2.54961L14.9289 2.5492C14.5129 2.39401 13.9994 2.60364 13.8019 3.07009ZM19.0591 15.2969C19.0198 14.9589 19 14.6096 19 14.2507L19 12.6324L19 12.631C19.0003 12.2825 18.8965 11.9415 18.7019 11.6524C18.507 11.3627 18.2299 11.138 17.9063 11.0071L15.5644 10.0831L15.5629 10.0825C15.4407 10.0332 15.3429 9.93757 15.2908 9.81645C15.2387 9.69534 15.2365 9.55857 15.2848 9.43591L15.2854 9.43438L17.2836 4.46462C17.43 4.10908 17.5036 3.72777 17.5001 3.34324C17.4965 2.9568 17.4151 2.57504 17.2606 2.22079C17.1062 1.86654 16.8819 1.54706 16.6012 1.28145C16.3206 1.01596 15.9894 0.809731 15.6273 0.675088C14.1404 0.120832 12.5346 0.906039 11.952 2.3099L11.9457 2.3255L9.97636 7.30602C9.95224 7.36731 9.91624 7.42323 9.87045 7.47057C9.82465 7.5179 9.76995 7.55572 9.70949 7.58186C9.64904 7.60799 9.58401 7.62192 9.51815 7.62285C9.45292 7.62377 9.38814 7.61192 9.32747 7.58796L6.95105 6.62541L6.94731 6.6239C6.62733 6.4958 6.27664 6.46503 5.93924 6.53547C5.60184 6.6059 5.29275 6.77441 5.05075 7.01984L5.04603 7.02466C3.18512 8.9376 2.00014 11.3045 2 14.2502C1.99763 17.0263 3.07259 19.6951 4.99854 21.6945C5.18704 21.8901 5.44705 22.0007 5.71875 22.0007H10.4977H10.5021H21.75C22.2157 22.0007 22.6198 21.6792 22.7244 21.2254C22.8291 20.7716 22.6066 20.3056 22.1879 20.1017C20.4276 19.2443 19.3556 17.5633 19.0744 15.4212C19.0719 15.3793 19.0668 15.3378 19.0591 15.2969ZM17.1747 16.1972L4.60661 11.1698C4.21265 12.1039 4 13.1244 4 14.2507C3.99812 16.3699 4.76809 18.4132 6.15773 20.0007H8.28848C7.60644 19.0184 7.10383 17.9155 6.81123 16.7428C6.67753 16.2069 7.00355 15.6641 7.53941 15.5304C8.07527 15.3967 8.61805 15.7228 8.75174 16.2586C9.10696 17.6824 9.84983 18.9771 10.8945 20.0007H18.8889C18.0041 18.9296 17.4386 17.6196 17.1747 16.1972Z"
                                  fill="currentColor"
                                />
                              </svg>
                              <span>{cleaningHtml ? "Cleaning..." : "Clean HTML"}</span>
                            </button>
                            <button
                              type="button"
                              className="sequence-editor-tool sequence-ai-tool-btn sequence-fix-formatting-btn"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleFixFormattingWithAi}
                              disabled={
                                fixingFormatting ||
                                cleaningHtml ||
                                scanningDeliverability ||
                                applyingDeliverabilitySuggestions ||
                                isEmailEditorLocked
                              }
                              title="Fix email formatting with AI"
                              aria-label="Fix formatting"
                            >
                              <i className="fa fa-magic" aria-hidden="true" />
                              <span>{fixingFormatting ? "Fixing..." : "Fix formatting"}</span>
                            </button>
                            <button
                              type="button"
                              className={`sequence-editor-tool sequence-ai-tool-btn ${
                                isAiVariablesModalOpen ? "is-active" : ""
                              }`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={openAiVariablesModal}
                              disabled={
                                cleaningHtml ||
                                fixingFormatting ||
                                applyingCustomPrompt ||
                                scanningDeliverability ||
                                applyingDeliverabilitySuggestions ||
                                isEmailEditorLocked
                              }
                              title="Configure campaign AI variables"
                              aria-label="AI Variables"
                            >
                              <i className="fa fa-magic" aria-hidden="true" />
                              <span>AI Variables</span>
                            </button>
                            <button
                              type="button"
                              className={`sequence-editor-tool sequence-ai-tool-btn ${
                                customPromptOpen ? "is-active" : ""
                              }`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setCustomPromptOpen((prev) => !prev)}
                              disabled={
                                cleaningHtml ||
                                fixingFormatting ||
                                applyingCustomPrompt ||
                                scanningDeliverability ||
                                applyingDeliverabilitySuggestions ||
                                isEmailEditorLocked
                              }
                              title="Apply custom AI prompt"
                              aria-label="Custom prompt"
                            >
                              <i className="fa fa-terminal" aria-hidden="true" />
                              <span>Custom prompt</span>
                            </button>
                            <button
                              type="button"
                              className="sequence-editor-tool sequence-ai-tool-btn sequence-deliverability-scan-btn"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={handleScanDeliverabilityWithAi}
                              disabled={
                                cleaningHtml ||
                                fixingFormatting ||
                                applyingCustomPrompt ||
                                scanningDeliverability ||
                                applyingDeliverabilitySuggestions ||
                                isEmailEditorLocked
                              }
                              title="Run deliverability scan"
                              aria-label="Deliverability scan"
                            >
                              <i className="fa fa-shield" aria-hidden="true" />
                              <span>
                                {scanningDeliverability
                                  ? "Scanning..."
                                  : "Deliverability scan"}
                              </span>
                            </button>
                          </div>
                          </div>
                          {customPromptOpen && (
                            <div className="sequence-ai-custom-prompt-wrap">
                              <textarea
                                className="sequence-ai-custom-prompt-input"
                                placeholder="Describe how AI should modify this email..."
                                value={customPromptText}
                                onChange={(e) => setCustomPromptText(e.target.value)}
                                rows={4}
                                disabled={isEmailEditorLocked}
                              />
                              <button
                                type="button"
                                className="sequence-editor-tool sequence-ai-tool-btn sequence-ai-custom-prompt-submit"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleApplyCustomPromptWithAi}
                                disabled={
                                  !String(customPromptText || "").trim() ||
                                  applyingCustomPrompt ||
                                  cleaningHtml ||
                                  fixingFormatting ||
                                  scanningDeliverability ||
                                  applyingDeliverabilitySuggestions ||
                                  isEmailEditorLocked
                                }
                              >
                                <i className="fa fa-paper-plane" aria-hidden="true" />
                                <span>
                                  {applyingCustomPrompt ? "Applying..." : "Submit custom prompt"}
                                </span>
                              </button>
                            </div>
                          )}
                          {!!deliverabilitySuggestions.length && (
                            <div className="sequence-ai-deliverability-results">
                              <div className="sequence-ai-deliverability-header">
                                <div className="sequence-ai-deliverability-title">
                                  Deliverability suggestions
                                </div>
                                <button
                                  type="button"
                                  className="sequence-ai-deliverability-collapse-btn"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() =>
                                    setDeliverabilitySuggestionsCollapsed((prev) => !prev)
                                  }
                                  aria-label={
                                    deliverabilitySuggestionsCollapsed
                                      ? "Expand deliverability suggestions"
                                      : "Collapse deliverability suggestions"
                                  }
                                >
                                  <i
                                    className={`fa ${
                                      deliverabilitySuggestionsCollapsed
                                        ? "fa-chevron-down"
                                        : "fa-chevron-up"
                                    }`}
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                              {!deliverabilitySuggestionsCollapsed && (
                                <>
                                  <ul>
                                    {deliverabilitySuggestions.map((item, index) => {
                                      const suggestionKey = `${index}:${item}`;
                                      const isApplied = !!deliverabilityAppliedSuggestionKeys[suggestionKey];
                                      return (
                                        <li key={`deliverability-suggestion-${index}`}>
                                          <label className="sequence-ai-deliverability-check-row">
                                            {isApplied ? (
                                              <span className="sequence-ai-deliverability-applied-check">
                                                <i className="fa fa-check" aria-hidden="true" />
                                              </span>
                                            ) : (
                                              <input
                                                type="checkbox"
                                                checked={
                                                  !!deliverabilitySuggestionSelections[suggestionKey]
                                                }
                                                onChange={() =>
                                                  handleToggleDeliverabilitySuggestion(suggestionKey)
                                                }
                                                disabled={
                                                  scanningDeliverability ||
                                                  applyingDeliverabilitySuggestions ||
                                                  cleaningHtml ||
                                                  fixingFormatting ||
                                                  applyingCustomPrompt ||
                                                  isEmailEditorLocked
                                                }
                                              />
                                            )}
                                            <span>{item}</span>
                                          </label>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                  {deliverabilityHasPendingSelections ||
                                  !deliverabilityHasAnyAppliedSuggestions ? (
                                    <button
                                      type="button"
                                      className="sequence-editor-tool sequence-ai-tool-btn sequence-ai-deliverability-apply-btn"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={handleApplyDeliverabilitySuggestions}
                                      disabled={
                                        scanningDeliverability ||
                                        applyingDeliverabilitySuggestions ||
                                        cleaningHtml ||
                                        fixingFormatting ||
                                        applyingCustomPrompt ||
                                        isEmailEditorLocked
                                      }
                                    >
                                      <i className="fa fa-check-square-o" aria-hidden="true" />
                                      <span>
                                        {applyingDeliverabilitySuggestions
                                          ? "Applying suggestions..."
                                          : "Apply suggestions"}
                                      </span>
                                    </button>
                                  ) : (
                                    <div className="sequence-ai-deliverability-applied-status">
                                      <i className="fa fa-check-circle" aria-hidden="true" />
                                      <span>Suggestions applied</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}

                          {isCodeView ? (
                            <Editor
                              value={selectedVariant?.body || ""}
                              onValueChange={(code) => {
                                if (isEmailEditorLocked) return;
                                syncSelectedVariantBody(code);
                              }}
                              highlight={(code) => {
                                try {
                                  return highlight(code, languages.markup);
                                } catch (error) {
                                  console.error("Sendora code view highlight error", error);
                                  return code;
                                }
                              }}
                              padding={12}
                              textareaRef={(node) => {
                                codeEditorRef.current = node;
                              }}
                              onFocus={() => setEditorInsertTarget("body")}
                              className="sequence-code-editor sequence-code-editor-simple"
                              style={{
                                backgroundColor: isEmailEditorLocked ? "#f1f5f9" : "#fff",
                                border: "1px solid rgba(15, 23, 42, 0.1)",
                                borderRadius: "0 0 10px 10px",
                                minHeight: "380px",
                                fontSize: 13,
                                lineHeight: 1.5,
                                color: "#0f172a",
                                fontFamily:
                                  "ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace",
                              }}
                            />
                          ) : (
                            <div
                              ref={editorRef}
                              className="sequence-rich-editor"
                              contentEditable={!isEmailEditorLocked}
                              suppressContentEditableWarning
                              onMouseDown={handleRichEditorMouseDown}
                              style={{
                                fontFamily: EDITOR_DISPLAY_FONT_FAMILY,
                                fontSize: `${EDITOR_DISPLAY_FONT_SIZE_PT}pt`,
                              }}
                              onFocus={() => {
                                setEditorInsertTarget("body");
                                ensureBodyEditorDefaultTypographyWrapper({
                                  syncVariant: true,
                                  focusCaret: true,
                                });
                                captureEditorSelection("body");
                                syncEditorFontControlsFromSelection();
                                syncEditorInlineStyleControlsFromSelection();
                                updateSpintaxVariantRemoveControls();
                              }}
                              onMouseUp={() => {
                                captureEditorSelection("body");
                                const removedEmptySpintax = cleanupEmptySpintaxPairs(
                                  editorRef.current,
                                  { onlyWhenSelectionOutside: true }
                                );
                                if (removedEmptySpintax) {
                                  handleRichEditorInput();
                                  captureEditorSelection("body");
                                }
                                activeVariableTokenRef.current = null;
                                syncEditorFontControlsFromSelection();
                                syncEditorInlineStyleControlsFromSelection();
                                updateSpintaxSuggestionPopupFromSelection();
                                updateSpintaxVariantRemoveControls();
                              }}
                              onKeyUp={() => {
                                captureEditorSelection("body");
                                activeVariableTokenRef.current = null;
                                syncEditorFontControlsFromSelection();
                                syncEditorInlineStyleControlsFromSelection();
                                updateSpintaxSuggestionPopupFromSelection();
                                updateSpintaxVariantRemoveControls();
                              }}
                              onKeyDown={handleRichEditorKeyDown}
                              onInput={() => {
                                handleRichEditorInput();
                                captureEditorSelection("body");
                                activeVariableTokenRef.current = null;
                                syncEditorFontControlsFromSelection();
                                syncEditorInlineStyleControlsFromSelection();
                                hideSpintaxSuggestionPopup();
                                hideSpintaxVariantRemoveControls();
                              }}
                              onBlur={(event) => {
                                handleRichEditorBlur(event);
                                setTimeout(() => {
                                  if (
                                    document.activeElement &&
                                    document.activeElement.closest(
                                      ".sendora-spintax-suggestion-popover"
                                    )
                                  ) {
                                    return;
                                  }
                                  if (
                                    document.activeElement &&
                                    document.activeElement.closest(
                                      ".sendora-spintax-variant-remove-btn"
                                    )
                                  ) {
                                    return;
                                  }
                                  hideSpintaxSuggestionPopup();
                                  hideSpintaxVariantRemoveControls();
                                }, 0);
                              }}
                            />
                          )}

                          <div className="email-save-row">
                            <Button
                              variant="contained"
                              className="sendora-btn"
                              onClick={handleSaveSequence}
                              disabled={savingSequence}
                            >
                              {savingSequence
                                ? (sequenceDraft?.steps || []).length === 1
                                  ? "Saving email..."
                                  : "Saving emails..."
                                : (sequenceDraft?.steps || []).length === 1
                                ? "Save email"
                                : "Save emails"}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                  )}

                  {activeTab === "sequences" && (
                  <Card className="admin-card p-3 mb-3">
                    {renderTabControls()}
                    <div className="section-top-row">
                      <div>
                        <div className="section-title">Sequences</div>
                      </div>
                    </div>
                    <div className="sequence-layout-grid">
                      <div className="sequence-left-pane">
                        {(sequencesDraft || []).map((entry, sequenceIndex) => {
                          const isActive = entry.sequenceId === activeSequenceId;
                          const sequenceDisplayName =
                            String(entry?.name || "").trim() || getSequenceLabelByIndex(sequenceIndex);
                          const sequenceOrdinalLabel = getSequenceLabelByIndex(sequenceIndex);
                          return (
                            <div
                              key={entry.sequenceId}
                              className={`sequence-item ${isActive ? "is-active" : ""}`}
                              onClick={() => handleSelectSequenceChain(entry.sequenceId)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  handleSelectSequenceChain(entry.sequenceId);
                                }
                              }}
                            >
                              <div className="sequence-item-head email-step-panel-head">
                                <button type="button" className="sequence-item-select">
                                  {sequenceOrdinalLabel}
                                </button>
                                <button
                                  type="button"
                                  className="sequence-item-delete"
                                  title="Delete sequence"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleDeleteSequenceChain(entry.sequenceId);
                                  }}
                                >
                                  <i className="fa fa-trash" aria-hidden="true" />
                                </button>
                              </div>
                              <input
                                type="text"
                                className="form-control sequence-item-name"
                                value={sequenceDisplayName}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) =>
                                  handleRenameSequenceChain(entry.sequenceId, event.target.value)
                                }
                              />
                            </div>
                          );
                        })}
                        <Button
                          variant="outlined"
                          className={`sequence-add-btn w-100 ${
                            (sequencesDraft || []).length
                              ? "sendora-btn-outline"
                              : "sendora-btn sequence-add-btn--highlight"
                          }`}
                          onClick={handleAddSequenceChain}
                        >
                          + Add sequence
                        </Button>
                      </div>
                      <div className="sequence-right-pane">
                        {!activeSequence ? (
                          <div className="admin-card p-3 sequence-empty-state">
                            (Optional) Add a sequence to configure wait times between steps and automate sending.
                          </div>
                        ) : (
                          <div className="admin-card p-3 sequence-editor-card">
                            <span className="variant-badge sequence-editor-corner-variant-badge">
                              {(() => {
                                const activeIndex = (sequencesDraft || []).findIndex(
                                  (entry) => entry.sequenceId === activeSequenceId
                                );
                                const label = getSequenceLabelByIndex(Math.max(0, activeIndex));
                                return String(label || "Sequence A").replace(/^Sequence\s+/, "") || "A";
                              })()}
                            </span>
                            {!sequenceChainValidation.isValid && (
                              <div className="sequence-chain-validation">{sequenceChainValidation.reason}</div>
                            )}
                            <div className="sequence-chain-list">
                              {(activeSequence?.nodes || []).map((node, nodeIndex) => {
                                const step = emailStepsById.get(String(node?.stepId || ""));
                                const firstVariant = (step?.variants || [])[0] || {};
                                const isOtt = String(step?.mode || "").trim().toLowerCase() === "ott";
                                const ottSourceStep = isOtt
                                  ? emailStepsById.get(String(step?.ottSourceStepId || ""))
                                  : null;
                                const subject = String(firstVariant?.subject || "").trim() || "No subject";
                                const bodyPreview = htmlToPreviewText(firstVariant?.body || "");
                                const waitSettings = (activeSequence?.waits || [])[nodeIndex] || {
                                  amount: 1,
                                  unit: "days",
                                };
                                const swapOptions = [
                                  ...(sequenceStepsForUi || []).filter((candidate) => {
                                    const candidateId = String(candidate?.stepId || "");
                                    if (candidateId === String(node?.stepId || "")) return true;
                                    return !(activeSequence?.nodes || []).some(
                                      (entry, index) =>
                                        index !== nodeIndex &&
                                        String(entry?.stepId || "") === candidateId
                                    );
                                  }),
                                ];
                                return (
                                  <Fragment key={node.nodeId || `${activeSequence.sequenceId}-${nodeIndex}`}>
                                    <div className="sequence-chain-step-card">
                                      <div className="sequence-chain-step-head">
                                        <div className="sequence-chain-step-title-row">
                                          <div className="sequence-chain-step-title">
                                            {`Step ${Number(step?.orderIndex || nodeIndex + 1) || nodeIndex + 1}`}
                                          </div>
                                          <label
                                            className="sequence-chain-step-swap-trigger"
                                            title="Swap step"
                                          >
                                            <i className="fa fa-chevron-down" aria-hidden="true" />
                                            <select
                                              className="sequence-chain-step-swap-native"
                                              value={String(node?.stepId || "")}
                                              onChange={(event) =>
                                                handleSwapSequenceNodeStep(
                                                  activeSequence.sequenceId,
                                                  nodeIndex,
                                                  event.target.value
                                                )
                                              }
                                            >
                                              {swapOptions.map((option) => (
                                                <option
                                                  key={`${activeSequence.sequenceId}-${nodeIndex}-${option.stepId}`}
                                                  value={String(option?.stepId || "")}
                                                >
                                                  {`Step ${Number(option?.orderIndex || 0) || 1}`}
                                                </option>
                                              ))}
                                            </select>
                                          </label>
                                        </div>
                                        <div className="sequence-chain-step-actions">
                                          <button
                                            type="button"
                                            className="sequence-chain-step-remove"
                                            onClick={() =>
                                              handleRemoveSequenceNode(activeSequence.sequenceId, nodeIndex)
                                            }
                                            title="Remove step from sequence"
                                          >
                                            <i className="fa fa-trash" aria-hidden="true" />
                                          </button>
                                        </div>
                                      </div>
                                      {isOtt && (
                                        <div className="sequence-chain-step-ott">
                                          {`OTT: Step ${Number(ottSourceStep?.orderIndex || 0) || "?"}`}
                                        </div>
                                      )}
                                      <div className="sequence-chain-step-subject">{subject}</div>
                                      <div className="sequence-chain-step-body">{bodyPreview || "No body content."}</div>
                                    </div>
                                    {nodeIndex < (activeSequence?.nodes || []).length - 1 && (
                                      <div className="sequence-chain-connector">
                                        <div className="sequence-chain-delay-container">
                                          <div className="sequence-chain-delay-row">
                                            <span className="sequence-chain-delay-label">Wait</span>
                                            <div className="sequence-chain-delay-controls">
                                              <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                className="form-control"
                                                value={
                                                  waitSettings?.amount === ""
                                                    ? ""
                                                    : String(
                                                      Math.max(
                                                        1,
                                                        Math.trunc(Number(waitSettings?.amount || 1) || 1)
                                                      )
                                                    )
                                                }
                                                onChange={(event) => {
                                                  const rawValue = String(event.target.value || "");
                                                  if (!/^\d*$/.test(rawValue)) return;
                                                  handleUpdateSequenceWait(
                                                    activeSequence.sequenceId,
                                                    nodeIndex,
                                                    { amount: rawValue === "" ? "" : rawValue }
                                                  );
                                                }}
                                                onBlur={(event) => {
                                                  const rawValue = String(event.target.value || "").trim();
                                                  const parsedValue = Math.trunc(Number(rawValue));
                                                  handleUpdateSequenceWait(activeSequence.sequenceId, nodeIndex, {
                                                    amount:
                                                      rawValue && Number.isFinite(parsedValue) && parsedValue > 0
                                                        ? parsedValue
                                                        : 1,
                                                  });
                                                }}
                                              />
                                              <select
                                                className="form-control"
                                                value={String(waitSettings?.unit || "days")}
                                                onChange={(event) =>
                                                  handleUpdateSequenceWait(
                                                    activeSequence.sequenceId,
                                                    nodeIndex,
                                                    { unit: event.target.value }
                                                  )
                                                }
                                              >
                                                {SEQUENCE_WAIT_UNITS.map((unit) => (
                                                  <option
                                                    key={`${activeSequence.sequenceId}-wait-unit-${nodeIndex}-${unit}`}
                                                    value={unit}
                                                  >
                                                    {unit}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="sequence-chain-connector-chevron">
                                          <i className="fa fa-chevron-down" aria-hidden="true" />
                                        </div>
                                      </div>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </div>
                            {!!activeSequenceUnusedEmailSteps.length && (
                              <div className="sequence-chain-add-step-row" ref={sequenceAddStepMenuRef}>
                                <Button
                                  variant="outlined"
                                  className="sendora-btn-outline sequence-chain-add-step-btn"
                                  onClick={() => setIsSequenceAddStepMenuOpen((prev) => !prev)}
                                >
                                  <i className="fa fa-plus" aria-hidden="true" />
                                  <span>Add step</span>
                                </Button>
                                {isSequenceAddStepMenuOpen && (
                                  <div className="sequence-chain-add-step-menu">
                                    {activeSequenceUnusedEmailSteps.map((step) => {
                                      const orderIndex = Number(step?.orderIndex || 0) || 1;
                                      const isOtt = String(step?.mode || "").trim().toLowerCase() === "ott";
                                      const sourceStepId = String(step?.ottSourceStepId || "").trim();
                                      const sourceStep = sourceStepId ? emailStepsById.get(sourceStepId) : null;
                                      const sourceOrderIndex =
                                        Number(sourceStep?.orderIndex || 0) || null;
                                      const usedStepIds = new Set(
                                        (activeSequence?.nodes || []).map((node) =>
                                          String(node?.stepId || "")
                                        )
                                      );
                                      const isDisabled =
                                        isOtt && (!sourceStepId || !usedStepIds.has(sourceStepId));
                                      const stepLabel = isOtt
                                        ? `Step ${orderIndex} (OTT: Step ${sourceOrderIndex || "?"})`
                                        : `Step ${orderIndex}`;
                                      return (
                                        <button
                                          type="button"
                                          key={`unused-step-${step.stepId}`}
                                          className={`sequence-chain-add-step-menu-item ${
                                            isDisabled ? "is-disabled" : ""
                                          }`}
                                          disabled={isDisabled}
                                          onClick={() => {
                                            if (isDisabled) return;
                                            handleAddSequenceNode(activeSequence.sequenceId, step.stepId);
                                            setIsSequenceAddStepMenuOpen(false);
                                          }}
                                        >
                                          {stepLabel}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="email-save-row">
                          <Button
                            variant="contained"
                            className="sendora-btn"
                            onClick={handleSaveSequence}
                            disabled={savingSequence}
                          >
                            {savingSequence ? "Saving sequences..." : "Save sequences"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                  )}

                  {activeTab === "run" && (
                  <Card className="admin-card p-3 mb-3">
                    {renderTabControls()}
                    <div className="section-top-row">
                      <div>
                        {runSequenceMetrics.length > 0 && (
                          <>
                            <div className="section-title mb-2">Sequence sending controls</div>
                            <div className="section-desc mb-2">
                              Start, pause, or resume each sequence and track progress across every
                              sequence step.
                            </div>
                            <div className="sendora-run-steps-grid mb-4">
                              {runSequenceMetrics.map((sequence) => {
                                const sequenceRuntimePreview =
                                  runSequenceRuntimePreviewById.get(String(sequence?.sequenceId || "").trim()) ||
                                  {};
                                const sequenceDraftEntry =
                                  (sequencesDraft || []).find(
                                    (entry) =>
                                      String(entry?.sequenceId || "").trim() ===
                                      String(sequence?.sequenceId || "").trim()
                                  ) || null;
                                const sequenceWaits = Array.isArray(sequenceDraftEntry?.waits)
                                  ? sequenceDraftEntry.waits
                                  : [];
                                const orderedSequenceSteps = (sequence?.stepBreakdowns || [])
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      Number(a?.stepOrderIndex || a?.stepIndex || 0) -
                                      Number(b?.stepOrderIndex || b?.stepIndex || 0)
                                  );
                                return (
                                  <div
                                    className="sendora-run-step-card"
                                    key={`run-sequence-${sequence.sequenceId}`}
                                  >
                                  <div className="sendora-run-step-header">
                                    <div className="sendora-run-step-title">
                                      {`Sequence ${sequence.sequenceLetter}: ${sequence.sequenceName}`}
                                    </div>
                                    {sequence.status === "completed" && (
                                      <div className="sendora-live-monitor-paused-indicator is-completed">
                                        <span>Sequence completed</span>
                                      </div>
                                    )}
                                    {sequence.status === "paused" && (
                                      <div className="sendora-live-monitor-paused-indicator">
                                        <span>Sequence paused</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="sendora-run-step-body">
                                    <div className="sendora-run-step-metrics">
                                      <div className="sendora-run-step-metric is-enabled">
                                        <span>Enabled leads</span>
                                        <strong>{sequence.enabledCount}</strong>
                                      </div>
                                      <div className="sendora-run-step-metric is-sent">
                                        <span>Emails sent</span>
                                        <strong>{sequence.sentCount}</strong>
                                      </div>
                                      <div className="sendora-run-step-metric is-queued">
                                        <span>Queued</span>
                                        <strong>{sequence.queuedCount}</strong>
                                      </div>
                                      {Number(sequence.blockedBadEmailCount || 0) > 0 && (
                                        <div className="sendora-run-step-metric is-blocked-bad-email">
                                          <span>Blocked (bad email)</span>
                                          <strong>{sequence.blockedBadEmailCount}</strong>
                                        </div>
                                      )}
                                      <div className="sendora-run-step-metric is-ready-to-queue">
                                        <span>Ready to queue</span>
                                        <strong>{sequence.pendingEligibleCount}</strong>
                                      </div>
                                      <div className="sendora-run-step-metric is-sent">
                                        <span>Completed leads</span>
                                        <strong>{sequence.completedCount}</strong>
                                      </div>
                                    </div>
                                    <div className="sendora-sequence-run-step-list">
                                      {orderedSequenceSteps.map((sequenceStep, sequenceStepIndex) => {
                                          const stepOrderIndex =
                                            Number(
                                              sequenceStep?.stepOrderIndex ||
                                                sequenceStep?.stepIndex ||
                                                sequenceStepIndex + 1
                                            ) || sequenceStepIndex + 1;
                                          const sentCount = Math.max(
                                            0,
                                            toSafeNumber(sequenceStep?.sentCount)
                                          );
                                          const queuedCount = Math.max(
                                            0,
                                            toSafeNumber(sequenceStep?.queuedCount)
                                          );
                                          const eligibleCount = Math.max(
                                            0,
                                            toSafeNumber(sequenceStep?.eligibleCount)
                                          );
                                          const blockedCount = Math.max(
                                            0,
                                            toSafeNumber(sequenceStep?.blockedCount)
                                          );
                                          const enabledCount = Math.max(
                                            0,
                                            toSafeNumber(sequenceStep?.enabledCount)
                                          );
                                          const totalForProgress = Math.max(
                                            0,
                                            enabledCount - blockedCount,
                                            sentCount + queuedCount + eligibleCount
                                          );
                                          const sentForDisplay = Math.max(
                                            0,
                                            Math.min(totalForProgress, sentCount)
                                          );
                                          const progressPercent =
                                            totalForProgress > 0
                                              ? Math.max(
                                                  0,
                                                  Math.min(
                                                    100,
                                                    Math.round((sentForDisplay / totalForProgress) * 100)
                                                  )
                                                )
                                              : 0;
                                          const sequenceStepPreview =
                                            runSequenceStepPreviewByKey.get(
                                              `${String(sequence?.sequenceId || "")}::${String(
                                                sequenceStep?.stepId || sequenceStepIndex
                                              )}`
                                            ) || {};
                                          const sequenceStepId = String(sequenceStep?.stepId || "").trim();
                                          const sequencePreviewUpdatingKey = getRunStepDurationInputKey(
                                            sequenceStepId,
                                            sequence?.sequenceId || ""
                                          );
                                          const isSequencePreviewUpdating = !!runStepPreviewUpdatingByKey?.[
                                            sequencePreviewUpdatingKey
                                          ];
                                          const isSequencePreviewAwaitingBackendSync =
                                            getRunStepPreviewSyncAliasKeys(sequenceStepId).some(
                                              (aliasKey) =>
                                                !!runStepPreviewBackendSyncStateByStepId?.[aliasKey]
                                            );
                                          const sequenceDraftStepIndex = (sequenceDraft?.steps || []).findIndex(
                                            (draftStep) =>
                                              String(draftStep?.stepId || "").trim() === sequenceStepId
                                          );
                                          const sequenceDraftStep =
                                            sequenceDraftStepIndex >= 0
                                              ? (sequenceDraft?.steps || [])[sequenceDraftStepIndex]
                                              : null;
                                          const sequenceNode =
                                            (sequencesDraft || [])
                                              .find(
                                                (entry) =>
                                                  String(entry?.sequenceId || "").trim() ===
                                                  String(sequence?.sequenceId || "").trim()
                                              )
                                              ?.nodes?.find(
                                                (node) =>
                                                  String(node?.stepId || "").trim() === sequenceStepId
                                              ) || null;
                                          const sequenceNodeSpeedMode = normalizeSequenceNodeSpeedMode(
                                            sequenceNode?.speedMode || ""
                                          );
                                          const sequenceStepSpeedSettings = getStepSpeedSettings(
                                            sequenceNodeSpeedMode
                                              ? {
                                                  ...(sequenceDraftStep || sequenceStep || {}),
                                                  speedMode: sequenceNodeSpeedMode,
                                                  speed: sequenceNodeSpeedMode,
                                                  targetDurationSeconds:
                                                    sequenceNodeSpeedMode === "custom"
                                                      ? Math.max(
                                                          0,
                                                          Number(sequenceNode?.targetDurationSeconds || 0)
                                                        )
                                                      : Number(
                                                          sequenceDraftStep?.targetDurationSeconds ||
                                                            sequenceStep?.targetDurationSeconds ||
                                                            0
                                                        ),
                                                }
                                              : sequenceDraftStep || sequenceStep || {}
                                          );
                                          const sequenceStepMetric =
                                            runStepMetricsByStepId.get(sequenceStepId) || null;
                                          const ottSourceStepId = String(
                                            sequenceStep?.ottSourceStepId ||
                                              sequenceDraftStep?.ottSourceStepId ||
                                              ""
                                          ).trim();
                                          const isOttSequenceStep =
                                            String(sequenceStep?.mode || "")
                                              .trim()
                                              .toLowerCase() === "ott" ||
                                            String(sequenceDraftStep?.mode || "")
                                              .trim()
                                              .toLowerCase() === "ott";
                                          const ottSourceOrderIndex = ottSourceStepId
                                            ? (() => {
                                                const sourceRunStep = (sequence?.stepBreakdowns || []).find(
                                                  (candidate) =>
                                                    String(candidate?.stepId || "").trim() === ottSourceStepId
                                                );
                                                const runOrderIndex = Number(
                                                  sourceRunStep?.stepOrderIndex ||
                                                    sourceRunStep?.stepIndex ||
                                                    0
                                                );
                                                if (runOrderIndex > 0) return runOrderIndex;
                                                const sourceDraftStep = (sequenceDraft?.steps || []).find(
                                                  (candidate) =>
                                                    String(candidate?.stepId || "").trim() === ottSourceStepId
                                                );
                                                const draftOrderIndex = Number(
                                                  sourceDraftStep?.orderIndex || 0
                                                );
                                                return draftOrderIndex > 0 ? draftOrderIndex : null;
                                              })()
                                            : null;
                                          const ottBadgeLabel =
                                            isOttSequenceStep && Number(ottSourceOrderIndex || 0) > 0
                                              ? `OTT: Step ${Number(ottSourceOrderIndex)}`
                                              : "OTT";
                                          const sequenceStepSpeedPreview =
                                            sequenceStepPreview?.modePreviews
                                              ? sequenceStepPreview
                                              : stepSpeedPreviewByOrderIndex.get(
                                                  Number(sequenceStepMetric?.orderIndex || stepOrderIndex)
                                                ) || null;
                                          const sequenceBackendMinimumCustomTargetSeconds =
                                            getBackendMinimumCustomSecondsForStep(sequenceStepId);
                                          const sequenceRequiredCustomTargetSeconds =
                                            sequenceBackendMinimumCustomTargetSeconds > 0
                                              ? sequenceBackendMinimumCustomTargetSeconds
                                              : Math.max(
                                                  0,
                                                  Number(
                                                    sequenceStepSpeedPreview?.minCustomTargetSeconds || 0
                                                  )
                                                );
                                          const sequenceCustomTargetTooLow =
                                            sequenceStepSpeedSettings.speed === "custom" &&
                                            sequenceRequiredCustomTargetSeconds > 0 &&
                                            Number(
                                              sequenceStepSpeedSettings.targetDurationSeconds || 0
                                            ) < sequenceRequiredCustomTargetSeconds;
                                          const hasSequenceDraftStep = sequenceDraftStepIndex >= 0;
                                          const sequenceModePreview =
                                            sequenceStepSpeedPreview?.modePreviews || {};
                                          const getSequenceModeDurationLabel = (preview = {}) => {
                                            const scheduleAdjustedLabel = String(
                                              preview?.durationLabel || ""
                                            ).trim();
                                            if (scheduleAdjustedLabel && scheduleAdjustedLabel !== "0m") {
                                              return scheduleAdjustedLabel;
                                            }
                                            return formatDurationMinutesWithSubMinuteLabel(
                                              Number(preview?.durationSeconds || 0) / 60
                                            );
                                          };
                                          const sequenceFastOptionLabel = `Fast (${getSequenceModeDurationLabel(
                                            sequenceModePreview?.fast
                                          )})`;
                                          const sequenceMediumOptionLabel = `Medium (${getSequenceModeDurationLabel(
                                            sequenceModePreview?.medium
                                          )})`;
                                          const sequenceSlowOptionLabel = `Slow (${getSequenceModeDurationLabel(
                                            sequenceModePreview?.slow
                                          )})`;
                                          const durationLabel = sequenceStepPreview?.durationLabel
                                            ? sequenceStepPreview.durationLabel
                                            : formatDurationSecondsCompact(
                                                Number(sequenceStepPreview?.effectiveDurationSeconds || 0)
                                              );
                                          const completionLabel =
                                            sequenceStepPreview?.completionTimeLabel || "-";
                                          const waitSettings = sequenceWaits?.[sequenceStepIndex] || {
                                            amount: 1,
                                            unit: "days",
                                          };
                                          return (
                                            <Fragment
                                              key={`run-sequence-step-${sequence.sequenceId}-${sequenceStep.stepId}-${stepOrderIndex}`}
                                            >
                                              <div className="sendora-run-sequence-step-card">
                                                <div className="sendora-run-sequence-step-header">
                                                  <div className="sendora-run-step-title-wrap">
                                                    <div className="sendora-run-step-title">{`Step ${stepOrderIndex}`}</div>
                                                    {isOttSequenceStep && (
                                                      <span className="sendora-run-step-ott-badge">
                                                        {ottBadgeLabel}
                                                      </span>
                                                    )}
                                                  </div>
                                                  {Number(sequenceStep?.blockedCount || 0) > 0 && (
                                                    <div className="sendora-run-step-metric is-blocked-bad-email">
                                                      <span>Blocked</span>
                                                      <strong>{blockedCount}</strong>
                                                    </div>
                                                  )}
                                                </div>
                                                <div
                                                  className="sendora-run-step-subject"
                                                  title={String(sequenceStep?.subject || "")}
                                                >
                                                  {String(sequenceStep?.subject || "").trim() || "No subject"}
                                                </div>
                                                <div className="sendora-run-step-preview-text">
                                                  {htmlToPreviewText(sequenceStep?.body || "") ||
                                                    "No body content."}
                                                </div>
                                                <div className="sendora-progress-row">
                                                  <div className="sendora-progress-row-label">Progress</div>
                                                  <div className="sendora-progress-row-bar-wrap">
                                                    <div className="sendora-progress-label">
                                                      {formatMetricNumber(sentForDisplay)} /{" "}
                                                      {formatMetricNumber(totalForProgress)} (
                                                      {progressPercent}%)
                                                    </div>
                                                    <div className="sendora-progress-track">
                                                      <div
                                                        className="sendora-progress-fill"
                                                        style={{ width: `${progressPercent}%` }}
                                                      />
                                                    </div>
                                                  </div>
                                                </div>
                                                <div className="sendora-run-step-configs">
                                                  <div className="sequence-step-speed-panel sendora-run-step-speed-panel">
                                                  <div className="sequence-step-speed-row">
                                                    <div className="sequence-step-speed-label">
                                                      Send speed (duration)
                                                    </div>
                                                    <select
                                                      className="form-control sendora-run-step-speed-input"
                                                      value={sequenceStepSpeedSettings.speed}
                                                      disabled={!hasSequenceDraftStep}
                                                      onChange={(e) => {
                                                        if (!hasSequenceDraftStep) return;
                                                        const nextSpeed = normalizeStepSpeed(e.target.value);
                                                        handleUpdateRunStepSpeed(
                                                          sequenceDraftStepIndex,
                                                          {
                                                            speed: nextSpeed,
                                                            targetDurationSeconds:
                                                              nextSpeed === "custom"
                                                                ? Math.max(
                                                                    60,
                                                                    Number(
                                                                      sequenceStepSpeedSettings.targetDurationSeconds ||
                                                                        0
                                                                    )
                                                                  )
                                                                : 0,
                                                          },
                                                          { sequenceId: sequence.sequenceId }
                                                        );
                                                      }}
                                                    >
                                                      <option value="fast">{sequenceFastOptionLabel}</option>
                                                      <option value="medium">{sequenceMediumOptionLabel}</option>
                                                      <option value="slow">{sequenceSlowOptionLabel}</option>
                                                      <option value="custom">Custom duration</option>
                                                    </select>
                                                  </div>
                                                  {sequenceStepSpeedSettings.speed === "custom" && (
                                                    <div className="sequence-step-speed-row is-custom-duration-row">
                                                      <div className="sequence-step-speed-label">
                                                        Custom duration
                                                      </div>
                                                      <div className="sendora-custom-duration-inline">
                                                        {(() => {
                                                          const stepId = String(
                                                            sequenceDraftStep?.stepId || ""
                                                          ).trim();
                                                          const inputKey = getRunStepDurationInputKey(
                                                            stepId,
                                                            sequence.sequenceId
                                                          );
                                                          const selectedParts = hasSequenceDraftStep
                                                            ? runStepDailyLimitInputs?.[inputKey] ||
                                                              secondsToDurationParts(
                                                                Number(
                                                                  sequenceStepSpeedSettings.targetDurationSeconds || 0
                                                                )
                                                              )
                                                            : secondsToDurationParts(
                                                                Number(
                                                                  sequenceStepSpeedSettings.targetDurationSeconds || 0
                                                                )
                                                              );
                                                          return (
                                                            <>
                                                              <select
                                                                className="form-control sendora-run-step-speed-input is-small"
                                                                value={clampInt(selectedParts?.days, 0, 7)}
                                                                disabled={!hasSequenceDraftStep}
                                                                onChange={(e) =>
                                                                  hasSequenceDraftStep &&
                                                                  handleRunStepDailyLimitInputChange(
                                                                    sequenceDraftStepIndex,
                                                                    "days",
                                                                    e.target.value,
                                                                    { sequenceId: sequence.sequenceId }
                                                                  )
                                                                }
                                                              >
                                                                {CUSTOM_DURATION_DAY_OPTIONS.map((day) => (
                                                                  <option
                                                                    key={`sequence-custom-days-${day}`}
                                                                    value={day}
                                                                  >
                                                                    {`${day}d`}
                                                                  </option>
                                                                ))}
                                                              </select>
                                                              <select
                                                                className="form-control sendora-run-step-speed-input is-small"
                                                                value={clampInt(selectedParts?.hours, 0, 24)}
                                                                disabled={!hasSequenceDraftStep}
                                                                onChange={(e) =>
                                                                  hasSequenceDraftStep &&
                                                                  handleRunStepDailyLimitInputChange(
                                                                    sequenceDraftStepIndex,
                                                                    "hours",
                                                                    e.target.value,
                                                                    { sequenceId: sequence.sequenceId }
                                                                  )
                                                                }
                                                              >
                                                                {CUSTOM_DURATION_HOUR_OPTIONS.map((hour) => (
                                                                  <option
                                                                    key={`sequence-custom-hours-${hour}`}
                                                                    value={hour}
                                                                  >
                                                                    {`${hour}h`}
                                                                  </option>
                                                                ))}
                                                              </select>
                                                              <select
                                                                className="form-control sendora-run-step-speed-input is-small"
                                                                value={clampInt(selectedParts?.minutes, 0, 60)}
                                                                disabled={!hasSequenceDraftStep}
                                                                onChange={(e) =>
                                                                  hasSequenceDraftStep &&
                                                                  handleRunStepDailyLimitInputChange(
                                                                    sequenceDraftStepIndex,
                                                                    "minutes",
                                                                    e.target.value,
                                                                    { sequenceId: sequence.sequenceId }
                                                                  )
                                                                }
                                                              >
                                                                {CUSTOM_DURATION_MINUTE_OPTIONS.map((minute) => (
                                                                  <option
                                                                    key={`sequence-custom-minutes-${minute}`}
                                                                    value={minute}
                                                                  >
                                                                    {`${minute}m`}
                                                                  </option>
                                                                ))}
                                                              </select>
                                                            </>
                                                          );
                                                        })()}
                                                      </div>
                                                    </div>
                                                  )}
                                                  <div className="sequence-step-speed-gap">
                                                    {`Minimum required custom duration: ${formatDurationMinutes(
                                                      Math.max(
                                                        1,
                                                        Math.ceil(
                                                          Number(sequenceRequiredCustomTargetSeconds || 0) / 60
                                                        )
                                                      )
                                                    )}`}
                                                  </div>
                                                </div>
                                                <div className="sequence-step-speed-panel sendora-run-step-preview-panel">
                                                  <div className="sequence-step-preview-compact-grid">
                                                    <div className="sequence-step-preview-compact-item is-leads">
                                                      <div className="sequence-step-preview-compact-label">
                                                        Leads
                                                      </div>
                                                      <div className="sequence-step-preview-compact-value">
                                                        {Number(
                                                          sequenceStepPreview?.leadsRemaining || 0
                                                        ).toLocaleString()}
                                                      </div>
                                                    </div>
                                                    <div className="sequence-step-preview-compact-item is-runtime">
                                                      <div className="sequence-step-preview-compact-label">
                                                        Runtime
                                                      </div>
                                                      <div className="sequence-step-preview-compact-value">
                                                        {isSequencePreviewUpdating ||
                                                        isSequencePreviewAwaitingBackendSync ? (
                                                          <span className="sendora-inline-preview-updating">
                                                            <i
                                                              className="fa fa-spinner fa-spin"
                                                              aria-hidden="true"
                                                            />{" "}
                                                            Updating...
                                                          </span>
                                                        ) : (
                                                          durationLabel || "-"
                                                        )}
                                                      </div>
                                                    </div>
                                                    <div className="sequence-step-preview-compact-item is-estimated is-highlight">
                                                      <div className="sequence-step-preview-compact-label">
                                                        Estimated completion
                                                      </div>
                                                      <div className="sequence-step-preview-compact-value">
                                                        {isSequencePreviewUpdating ||
                                                        isSequencePreviewAwaitingBackendSync ? (
                                                          <span className="sendora-inline-preview-updating">
                                                            <i
                                                              className="fa fa-spinner fa-spin"
                                                              aria-hidden="true"
                                                            />{" "}
                                                            Updating...
                                                          </span>
                                                        ) : (
                                                          completionLabel
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                  </div>
                                                </div>
                                              </div>
                                              {sequenceStepIndex < orderedSequenceSteps.length - 1 && (
                                                <div className="sequence-chain-connector is-readonly-run">
                                                  <span className="sequence-chain-delay-readonly-text">
                                                    {`Wait ${formatSequenceWaitLabel(waitSettings)}`}
                                                  </span>
                                                  <div className="sequence-chain-connector-chevron">
                                                    <i className="fa fa-chevron-down" aria-hidden="true" />
                                                  </div>
                                                </div>
                                              )}
                                            </Fragment>
                                          );
                                        })}
                                    </div>
                                    <div className="sequence-step-speed-panel sendora-run-step-preview-panel">
                                      <div className="sequence-step-preview-compact-grid">
                                        <div className="sequence-step-preview-compact-item is-runtime">
                                          <div className="sequence-step-preview-compact-label">
                                            Sequence runtime
                                          </div>
                                          <div className="sequence-step-preview-compact-value">
                                            {sequenceRuntimePreview?.isUpdating ? (
                                              <span className="sendora-inline-preview-updating">
                                                <i className="fa fa-spinner fa-spin" aria-hidden="true" />{" "}
                                                Updating...
                                              </span>
                                            ) : (
                                              sequenceRuntimePreview?.durationLabel || "-"
                                            )}
                                          </div>
                                        </div>
                                        <div className="sequence-step-preview-compact-item is-estimated is-highlight">
                                          <div className="sequence-step-preview-compact-label">
                                            Sequence completion
                                          </div>
                                          <div className="sequence-step-preview-compact-value">
                                            {sequenceRuntimePreview?.isUpdating ? (
                                              <span className="sendora-inline-preview-updating">
                                                <i className="fa fa-spinner fa-spin" aria-hidden="true" />{" "}
                                                Updating...
                                              </span>
                                            ) : (
                                              sequenceRuntimePreview?.completionTimeLabel || "-"
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="sendora-run-step-actions">
                                      <Button
                                        variant="outlined"
                                        className="sendora-btn-outline"
                                        onClick={() =>
                                          handleClearSequence(
                                            sequence.sequenceId,
                                            sequence.sequenceName,
                                            sequence.sequenceLetter
                                          )
                                        }
                                        disabled={runningMemberAction}
                                      >
                                        <i className="fa fa-trash-o" aria-hidden="true" /> Disable sequence
                                      </Button>
                                      <div className="sendora-run-action-btn-wrap">
                                        <Button
                                          variant="outlined"
                                          className="sendora-btn sendora-run-start-action-btn"
                                          onClick={() => {
                                            if (sequence.isCompleted && !sequence.canStart) return;
                                            if (!sequence.hasStarted) {
                                              handleTriggerSequence(sequence.sequenceId);
                                              return;
                                            }
                                            handleSetSequencePause(
                                              sequence.sequenceId,
                                              sequence.status === "paused" ? "resume" : "pause"
                                            );
                                          }}
                                          disabled={
                                            runningMemberAction ||
                                            savingSequence ||
                                            isSequenceDirty ||
                                            isSequenceDefinitionsDirty ||
                                            (!sequence.hasStarted && !sequence.canStart)
                                          }
                                        >
                                          <i
                                            className={`fa ${
                                              !sequence.hasStarted || sequence.status === "paused"
                                                ? "fa-play"
                                                : "fa-pause"
                                            }`}
                                            aria-hidden="true"
                                          />{" "}
                                          {!sequence.hasStarted
                                            ? sequence.isCompleted
                                              ? "Completed"
                                              : "Start sequence"
                                            : sequence.status === "paused"
                                            ? "Resume"
                                            : "Pause"}
                                        </Button>
                                        {!sequence.hasStarted &&
                                          (runningMemberAction ||
                                            savingSequence ||
                                            isSequenceDirty ||
                                            isSequenceDefinitionsDirty ||
                                            (!sequence.hasStarted && !sequence.canStart)) && (
                                            <div className="sendora-run-action-disabled-reason">
                                              {runningMemberAction
                                                ? "Another action is currently running."
                                                : savingSequence || isSequenceDirty || isSequenceDefinitionsDirty
                                                ? "Saving speed changes. Start sequence will unlock when save completes."
                                                : sequence.isCompleted
                                                ? "Add new eligible leads to start this sequence again."
                                                : "No eligible unsent leads are ready for this sequence."}
                                            </div>
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                        {hasManualEnabledLeads && (
                          <>
                            <div className="section-title">Manual step sending controls</div>
                            <div className="section-desc">
                              Use these controls to start, pause, stop, and configure manual sending for
                              each step.
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {hasManualEnabledLeads && <div className="sendora-run-steps-grid">
                      {runStepMetrics.length ? (
                        runStepMetrics.map((step) => {
                          const draftStepIndex = (sequenceDraft?.steps || []).findIndex(
                            (draftStep) =>
                              Number(draftStep?.orderIndex || 0) === Number(step?.orderIndex || 0)
                          );
                          const draftStep =
                            draftStepIndex >= 0 ? (sequenceDraft?.steps || [])[draftStepIndex] : null;
                          const stepSpeedSettings = getStepSpeedSettings(draftStep || {});
                          const stepSpeedPreview = stepSpeedPreviewByOrderIndex.get(
                            Number(step?.orderIndex || 0)
                          );
                          const manualStepId = String(
                            step?.persistedStepId || step?.stepId || draftStep?.stepId || ""
                          ).trim();
                          const manualBackendMinimumCustomTargetSeconds =
                            getBackendMinimumCustomSecondsForStep(manualStepId);
                          const manualRequiredCustomTargetSeconds =
                            manualBackendMinimumCustomTargetSeconds > 0
                              ? manualBackendMinimumCustomTargetSeconds
                              : Math.max(0, Number(stepSpeedPreview?.minCustomTargetSeconds || 0));
                          const manualCustomTargetTooLow =
                            stepSpeedSettings.speed === "custom" &&
                            manualRequiredCustomTargetSeconds > 0 &&
                            Number(stepSpeedSettings.targetDurationSeconds || 0) <
                              manualRequiredCustomTargetSeconds;
                          const hasDraftStep = draftStepIndex >= 0;
                          const manualPreviewUpdatingKey = getRunStepDurationInputKey(
                            String(draftStep?.stepId || step?.persistedStepId || "").trim(),
                            ""
                          );
                          const isManualPreviewUpdating = !!runStepPreviewUpdatingByKey?.[
                            manualPreviewUpdatingKey
                          ];
                          const manualPreviewStepId = String(
                            step?.persistedStepId || step?.stepId || draftStep?.stepId || ""
                          ).trim();
                          const isManualPreviewAwaitingBackendSync =
                            getRunStepPreviewSyncAliasKeys(manualPreviewStepId).some(
                              (aliasKey) => !!runStepPreviewBackendSyncStateByStepId?.[aliasKey]
                            );
                          const stepBreakdownId = String(
                            step?.persistedStepId || step?.stepId || `order-${step?.orderIndex || 0}`
                          ).trim();
                          const stepIsPreparing = !!step?.isPreparing;
                          const stepIsPauseControlled = Number(step?.queuedCount || 0) > 0;
                          const showStepBreakdown = !!expandedRunStepBreakdownIds?.[stepBreakdownId];
                          const optionModePreview = stepSpeedPreview?.modePreviews || {};
                          const getModeDurationLabel = (preview = {}) => {
                            const scheduleAdjustedLabel = String(preview?.durationLabel || "").trim();
                            if (scheduleAdjustedLabel && scheduleAdjustedLabel !== "0m") {
                              return scheduleAdjustedLabel;
                            }
                            return formatDurationMinutesWithSubMinuteLabel(
                              Number(preview?.durationSeconds || 0) / 60
                            );
                          };
                          const fastOptionLabel = `Fast (${getModeDurationLabel(optionModePreview?.fast)})`;
                          const mediumOptionLabel = `Medium (${getModeDurationLabel(optionModePreview?.medium)})`;
                          const slowOptionLabel = `Slow (${getModeDurationLabel(optionModePreview?.slow)})`;
                          return (
                          <div className="sendora-run-step-card" key={`run-step-${step.stepId}`}>
                            <div className="sendora-run-step-header">
                              <div className="sendora-run-step-title">{`Step ${step.orderIndex}`}</div>
                              <div className="sendora-run-step-subject" title={step.subject}>
                                {step.subject}
                              </div>
                              {step.isPaused && (
                                <div className="sendora-live-monitor-paused-indicator">
                                  <span>Step paused</span>
                                </div>
                              )}
                              {!step.isPaused && !!step.isPreparing && (
                                <div className="sendora-live-monitor-paused-indicator is-running">
                                  <span>Step preparing</span>
                                </div>
                              )}
                            </div>
                            <div className="sendora-run-step-body">
                              <div className="sendora-run-step-metrics">
                                <div className="sendora-run-step-metric is-enabled">
                                  <span>Enabled leads</span>
                                  <strong>{step.approvedCount}</strong>
                                </div>
                                <div className="sendora-run-step-metric is-sent">
                                  <span>Emails sent</span>
                                  <strong>{step.sentCount}</strong>
                                </div>
                                <div className="sendora-run-step-metric is-queued">
                                  <span>Queued</span>
                                  <strong>{step.queuedCount}</strong>
                                </div>
                                {Number(step.blockedBadEmailCount || 0) > 0 && (
                                  <div className="sendora-run-step-metric is-blocked-bad-email">
                                    <span>Blocked (bad email)</span>
                                    <strong>{step.blockedBadEmailCount}</strong>
                                  </div>
                                )}
                                {Number(step.salesforceStatusPrunedCount || 0) > 0 && (
                                  <div className="sendora-run-step-metric is-salesforce-pruned">
                                    <span>Unqueued (SF status changed)</span>
                                    <strong>{step.salesforceStatusPrunedCount}</strong>
                                  </div>
                                )}
                                <div className="sendora-run-step-metric is-ready-to-queue">
                                  <span>Ready to queue</span>
                                  <strong>{step.pendingEligibleCount}</strong>
                                </div>
                              </div>
                              <div className="sendora-run-step-configs">
                                <div className="sequence-step-speed-panel sendora-run-step-speed-panel sendora-run-manual-step-speed-panel">
                                  <div className="sequence-step-speed-row">
                                    <div className="sequence-step-speed-label">Send speed (duration)</div>
                                    <select
                                      className="form-control sendora-run-step-speed-input"
                                      value={stepSpeedSettings.speed}
                                      disabled={!hasDraftStep}
                                      onChange={(e) => {
                                        if (!hasDraftStep) return;
                                        const nextSpeed = normalizeStepSpeed(e.target.value);
                                        handleUpdateRunStepSpeed(draftStepIndex, {
                                          speed: nextSpeed,
                                          targetDurationSeconds:
                                            nextSpeed === "custom"
                                              ? Math.max(
                                                  60,
                                                  Number(stepSpeedSettings.targetDurationSeconds || 0)
                                                )
                                              : 0,
                                        });
                                      }}
                                    >
                                      <option value="fast">{fastOptionLabel}</option>
                                      <option value="medium">{mediumOptionLabel}</option>
                                      <option value="slow">{slowOptionLabel}</option>
                                      <option value="custom">Custom duration</option>
                                    </select>
                                  </div>
                                  {stepSpeedSettings.speed === "custom" && (
                                    <div className="sequence-step-speed-row is-custom-duration-row">
                                      <div className="sequence-step-speed-label">Custom duration</div>
                                      <div className="sendora-custom-duration-inline">
                                        {(() => {
                                          const stepId = String(draftStep?.stepId || "").trim();
                                          const selectedParts = hasDraftStep
                                            ? runStepDailyLimitInputs?.[stepId] ||
                                              secondsToDurationParts(
                                                Number(stepSpeedSettings.targetDurationSeconds || 0)
                                              )
                                            : secondsToDurationParts(
                                                Number(stepSpeedSettings.targetDurationSeconds || 0)
                                              );
                                          return (
                                            <>
                                              <select
                                                className="form-control sendora-run-step-speed-input is-small"
                                                value={clampInt(selectedParts?.days, 0, 7)}
                                                disabled={!hasDraftStep}
                                                onChange={(e) =>
                                                  hasDraftStep &&
                                                  handleRunStepDailyLimitInputChange(
                                                    draftStepIndex,
                                                    "days",
                                                    e.target.value,
                                                    {}
                                                  )
                                                }
                                              >
                                                {CUSTOM_DURATION_DAY_OPTIONS.map((day) => (
                                                  <option key={`custom-days-${day}`} value={day}>
                                                    {`${day}d`}
                                                  </option>
                                                ))}
                                              </select>
                                              <select
                                                className="form-control sendora-run-step-speed-input is-small"
                                                value={clampInt(selectedParts?.hours, 0, 24)}
                                                disabled={!hasDraftStep}
                                                onChange={(e) =>
                                                  hasDraftStep &&
                                                  handleRunStepDailyLimitInputChange(
                                                    draftStepIndex,
                                                    "hours",
                                                    e.target.value,
                                                    {}
                                                  )
                                                }
                                              >
                                                {CUSTOM_DURATION_HOUR_OPTIONS.map((hour) => (
                                                  <option key={`custom-hours-${hour}`} value={hour}>
                                                    {`${hour}h`}
                                                  </option>
                                                ))}
                                              </select>
                                              <select
                                                className="form-control sendora-run-step-speed-input is-small"
                                                value={clampInt(selectedParts?.minutes, 0, 60)}
                                                disabled={!hasDraftStep}
                                                onChange={(e) =>
                                                  hasDraftStep &&
                                                  handleRunStepDailyLimitInputChange(
                                                    draftStepIndex,
                                                    "minutes",
                                                    e.target.value,
                                                    {}
                                                  )
                                                }
                                              >
                                                {CUSTOM_DURATION_MINUTE_OPTIONS.map((minute) => (
                                                  <option key={`custom-minutes-${minute}`} value={minute}>
                                                    {`${minute}m`}
                                                  </option>
                                                ))}
                                              </select>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  )}
                                  <div className="sequence-step-speed-gap">
                                    {`Minimum required custom duration: ${formatDurationMinutes(
                                      Math.max(
                                        1,
                                        Math.ceil(Number(manualRequiredCustomTargetSeconds || 0) / 60)
                                      )
                                    )}`}
                                  </div>
                                </div>
                                <div className="sequence-step-speed-panel sendora-run-step-preview-panel">
                                  <div className="sequence-step-preview-compact-grid">
                                    <div className="sequence-step-preview-compact-item">
                                      <div className="sequence-step-preview-compact-label">Leads</div>
                                      <div className="sequence-step-preview-compact-value">
                                        {Number(stepSpeedPreview?.leadsRemaining || 0).toLocaleString()}
                                      </div>
                                    </div>
                                    <div className="sequence-step-preview-compact-item">
                                      <div className="sequence-step-preview-compact-label">Runtime</div>
                                      <div className="sequence-step-preview-compact-value">
                                        {isManualPreviewUpdating ||
                                        isManualPreviewAwaitingBackendSync ||
                                        backendStepSchedulePreviewLoadingByStepId?.[
                                          String(step?.persistedStepId || step?.stepId || "").trim()
                                        ] ? (
                                          <span className="sendora-inline-preview-updating">
                                            <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Updating...
                                          </span>
                                        ) : (
                                          (() => {
                                            const summary =
                                              backendStepSchedulePreviewByStepId?.[
                                                String(step?.persistedStepId || step?.stepId || "").trim()
                                              ] || null;
                                            const firstScheduledTime = Number(summary?.firstScheduledTime || 0);
                                            const lastScheduledTime = Number(summary?.lastScheduledTime || 0);
                                            if (firstScheduledTime > 0 && lastScheduledTime >= firstScheduledTime) {
                                              const durationMinutes = Math.max(
                                                0,
                                                (lastScheduledTime - firstScheduledTime) / 60
                                              );
                                              return formatDurationMinutes(durationMinutes);
                                            }
                                            return stepSpeedPreview?.durationLabel || "-";
                                          })()
                                        )}
                                      </div>
                                    </div>
                                    <div className="sequence-step-preview-compact-item is-wide is-highlight">
                                      <div className="sequence-step-preview-compact-label">
                                        Estimated completion
                                      </div>
                                      <div className="sequence-step-preview-compact-value">
                                        {isManualPreviewUpdating ||
                                        isManualPreviewAwaitingBackendSync ||
                                        backendStepSchedulePreviewLoadingByStepId?.[
                                          String(step?.persistedStepId || step?.stepId || "").trim()
                                        ] ? (
                                          <span className="sendora-inline-preview-updating">
                                            <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Updating...
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            onClick={() =>
                                              openEstimatedCompletionTimelineModal({
                                                title: `Step ${Number(step.orderIndex || 1)} estimated completion timeline`,
                                                leadsRemaining: Number(
                                                  stepSpeedPreview?.leadsRemaining || 0
                                                ),
                                                gapMinutes: Number(stepSpeedPreview?.gapMinutes || 0),
                                                completionTimeLabel: String(
                                                  (() => {
                                                    const summary =
                                                      backendStepSchedulePreviewByStepId?.[
                                                        String(
                                                          step?.persistedStepId || step?.stepId || ""
                                                        ).trim()
                                                      ] || null;
                                                    const timezone = String(summary?.timezone || "UTC");
                                                    const lastScheduledTime = Number(
                                                      summary?.lastScheduledTime || 0
                                                    );
                                                    if (lastScheduledTime > 0) {
                                                      return formatCompletionTimeLabel(
                                                        new Date(lastScheduledTime * 1000),
                                                        timezone
                                                      );
                                                    }
                                                    return stepSpeedPreview?.completionTimeLabel || "";
                                                  })()
                                                ),
                                                stepId: String(
                                                  step?.persistedStepId || step?.stepId || ""
                                                ).trim(),
                                                stepPatch: {
                                                  speed: stepSpeedSettings.speed,
                                                  speedMode: stepSpeedSettings.speed,
                                                  targetDurationSeconds: Number(
                                                    stepSpeedSettings.targetDurationSeconds || 0
                                                  ),
                                                },
                                              })
                                            }
                                          >
                                            {(() => {
                                              const summary =
                                                backendStepSchedulePreviewByStepId?.[
                                                  String(step?.persistedStepId || step?.stepId || "").trim()
                                                ] || null;
                                              const timezone = String(summary?.timezone || "UTC");
                                              const lastScheduledTime = Number(summary?.lastScheduledTime || 0);
                                              if (lastScheduledTime > 0) {
                                                return formatCompletionTimeLabel(
                                                  new Date(lastScheduledTime * 1000),
                                                  timezone
                                                );
                                              }
                                              return stepSpeedPreview?.completionTimeLabel || "-";
                                            })()}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="sendora-run-step-actions">
                                  <Button
                                    variant="outlined"
                                    className="sendora-btn-outline"
                                    onClick={() =>
                                      handleClearStepQueue(
                                        step.persistedStepId,
                                        step.orderIndex,
                                        step.queuedCount
                                      )
                                    }
                                    disabled={
                                      runningMemberAction ||
                                      isSequenceDirty ||
                                      !step.hasPersistedStepId ||
                                      step.queuedCount <= 0
                                    }
                                  >
                                    <i className="fa fa-trash-o" aria-hidden="true" /> Clear queue
                                  </Button>
                                  <div className="sendora-run-action-btn-wrap">
                                    <Button
                                      variant="outlined"
                                      className="sendora-btn sendora-run-start-action-btn"
                                      onClick={() => {
                                        if (stepIsPreparing) {
                                          return;
                                        }
                                        if (step.isPaused && stepIsPauseControlled) {
                                          handleSetStepPause(
                                            step.persistedStepId,
                                            "resume",
                                            step.orderIndex
                                          );
                                          return;
                                        }
                                        if (!step.isPaused && stepIsPauseControlled) {
                                          handleSetStepPause(
                                            step.persistedStepId,
                                            "pause",
                                            step.orderIndex
                                          );
                                          return;
                                        }
                                        handleTriggerStep(
                                          step.persistedStepId,
                                          step.orderIndex,
                                          step.pendingEligibleCount
                                        );
                                      }}
                                      disabled={
                                        runningMemberAction ||
                                        isSequenceDirty ||
                                        !step.hasPersistedStepId ||
                                        stepIsPreparing ||
                                        (!stepIsPauseControlled && step.pendingEligibleCount <= 0)
                                      }
                                      title={
                                        runningMemberAction
                                          ? "Another action is currently running"
                                          : isSequenceDirty
                                          ? "Save sequence changes first"
                                          : !step.hasPersistedStepId
                                          ? "Save sequence so this step exists in backend"
                                          : stepIsPreparing
                                          ? "Step start is in progress"
                                          : !stepIsPauseControlled && step.pendingEligibleCount <= 0
                                          ? "No eligible unsent leads are currently ready for this step"
                                          : ""
                                      }
                                    >
                                      <i
                                        className={`fa ${
                                          stepIsPreparing
                                            ? "fa-spinner fa-spin"
                                            : step.isPaused || !stepIsPauseControlled
                                            ? "fa-play"
                                            : "fa-pause"
                                        }`}
                                        aria-hidden="true"
                                      />{" "}
                                      {stepIsPreparing
                                        ? `Starting step ${step.orderIndex}...`
                                        : step.isPaused
                                        ? "Resume"
                                        : stepIsPauseControlled
                                        ? "Pause"
                                        : `Start step ${step.orderIndex}`}
                                    </Button>
                                    {(runningMemberAction ||
                                      isSequenceDirty ||
                                      !step.hasPersistedStepId ||
                                      stepIsPreparing ||
                                      (!stepIsPauseControlled && step.pendingEligibleCount <= 0)) && (
                                      <div className="sendora-run-action-disabled-reason">
                                        {runningMemberAction
                                          ? "Another action is currently running."
                                          : isSequenceDirty
                                          ? "Save sequence changes first."
                                          : !step.hasPersistedStepId
                                          ? "Save this sequence before starting the step."
                                          : stepIsPreparing
                                          ? "Step start is in progress. Queue updates will appear shortly."
                                          : "No queued or eligible unsent leads are ready for this step."}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="sendora-run-step-breakdown-toggle-wrap">
                              <button
                                type="button"
                                className="sendora-run-step-breakdown-toggle"
                                onClick={() =>
                                  setExpandedRunStepBreakdownIds((prev) => ({
                                    ...(prev || {}),
                                    [stepBreakdownId]: !showStepBreakdown,
                                  }))
                                }
                              >
                                {showStepBreakdown ? "Hide breakdown" : "View breakdown"}
                              </button>
                            </div>
                            {showStepBreakdown && (
                              <div className="sendora-run-step-breakdown-panel">
                                {(() => {
                                  const fullBatchTimeline = Array.isArray(stepSpeedPreview?.batchTimeline)
                                    ? stepSpeedPreview.batchTimeline
                                    : [];
                                  const totalBatchRows = fullBatchTimeline.length;
                                  const showAllBatchRows = !!expandedRunStepAllBatchRows?.[stepBreakdownId];
                                  const visibleBatchTimeline =
                                    showAllBatchRows || totalBatchRows <= 6
                                      ? fullBatchTimeline
                                      : fullBatchTimeline.slice(0, 6);
                                  return (
                                    <>
                                <div className="sendora-run-step-breakdown-summary">
                                  <div>
                                    <span>
                                      Batches (unique companies)
                                      <CustomTooltip
                                        content="Sendora batches one lead per unique company in each round. This spaces outreach across companies and reduces back-to-back sends to the same company."
                                        placement="top"
                                      >
                                        <span
                                          className="sendora-breakdown-info-icon"
                                          role="button"
                                          tabIndex={0}
                                          aria-label="Why batches are grouped by unique company"
                                        >
                                          <i className="fa fa-info-circle" aria-hidden="true" />
                                        </span>
                                      </CustomTooltip>
                                    </span>
                                    <strong>{Number(stepSpeedPreview?.batchCount || 0)}</strong>
                                  </div>
                                  <div>
                                    <span>Gap between batches</span>
                                    <strong>
                                      {formatDurationSecondsCompact(
                                        Number(stepSpeedPreview?.interBatchWaitSeconds || 0)
                                      )}
                                    </strong>
                                  </div>
                                  <div>
                                    <span>Gap within batch</span>
                                    <strong>
                                      {formatDurationSecondsCompact(
                                        Number(stepSpeedPreview?.intraBatchGapSeconds || 0)
                                      )}
                                    </strong>
                                  </div>
                                  <div>
                                    <span>Active send time</span>
                                    <strong>
                                      {formatDurationSecondsCompact(
                                        Number(stepSpeedPreview?.activeDurationSeconds || 0)
                                      )}
                                    </strong>
                                  </div>
                                </div>
                                <div className="sendora-run-step-breakdown-list">
                                  {visibleBatchTimeline.map((row) => {
                                    const rowKey = `${stepBreakdownId}:batch:${row.batchIndex}`;
                                    const isBatchExpanded = !!expandedRunStepBatchRows?.[rowKey];
                                    return (
                                      <div
                                        className={`sendora-run-step-breakdown-row ${isBatchExpanded ? "is-expanded" : ""}`.trim()}
                                        key={`batch-row-${stepBreakdownId}-${row.batchIndex}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                          setExpandedRunStepBatchRows((prev) => ({
                                            ...(prev || {}),
                                            [rowKey]: !isBatchExpanded,
                                          }))
                                        }
                                        onKeyDown={(event) => {
                                          if (event.key !== "Enter" && event.key !== " ") return;
                                          event.preventDefault();
                                          setExpandedRunStepBatchRows((prev) => ({
                                            ...(prev || {}),
                                            [rowKey]: !isBatchExpanded,
                                          }));
                                        }}
                                      >
                                        <span>{`Batch ${row.batchIndex}`}</span>
                                        <span>{`${row.batchSize} leads`}</span>
                                        <span>{`Send duration ${formatDurationSecondsCompact(
                                          Number(row.inBatchDurationSeconds || 0)
                                        )}`}</span>
                                        <span>
                                          {row.gapAfterSeconds > 0
                                            ? `Then wait ${formatDurationSecondsCompact(
                                                Number(row.gapAfterSeconds || 0)
                                              )}`
                                            : "Final batch"}
                                        </span>
                                        {isBatchExpanded && (
                                          <div className="sendora-run-step-breakdown-emails">
                                            {(row?.emails || []).map((email) => (
                                              <div
                                                className="sendora-run-step-breakdown-email-row"
                                                key={`batch-email-${rowKey}-${email}`}
                                              >
                                                {email}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                                {totalBatchRows > 6 && (
                                  <div className="sendora-run-step-breakdown-view-all-wrap">
                                    <button
                                      type="button"
                                      className="sendora-run-step-breakdown-toggle"
                                      onClick={() =>
                                        setExpandedRunStepAllBatchRows((prev) => ({
                                          ...(prev || {}),
                                          [stepBreakdownId]: !showAllBatchRows,
                                        }))
                                      }
                                    >
                                      {showAllBatchRows
                                        ? "Show fewer batches"
                                        : `View all ${totalBatchRows} batches`}
                                    </button>
                                  </div>
                                )}
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        );
                        })
                      ) : (
                        <div className="sendora-live-monitor-empty">No sequence steps configured yet.</div>
                      )}
                    </div>}
                    {hasRunSafetyLogEntries && (
                      <div className="sendora-run-safety-logs-wrap">
                        <div className="section-title">Run safeguards logs</div>
                        <div className="section-desc">
                          Leads removed due to Salesforce status changes and leads blocked by email
                          verification, plus SES bounce and complaint suppressions.
                        </div>
                        <div className="sendora-run-safety-logs-grid">
                          <div className="sendora-run-safety-log-card">
                            <div className="sendora-run-safety-log-title">
                              Removed after Salesforce status change
                            </div>
                            {runSalesforcePrunedLeadLogs.length ? (
                              <>
                                <div className="sendora-run-safety-log-list">
                                  {(showAllRunSalesforcePrunedLogs
                                    ? runSalesforcePrunedLeadLogs
                                    : runSalesforcePrunedLeadLogs.slice(0, 4)
                                  ).map((entry) => (
                                    <div className="sendora-run-safety-log-row" key={entry.key}>
                                      <div className="sendora-run-safety-log-row-title">
                                        {entry.title}
                                      </div>
                                      <div className="sendora-run-safety-log-row-detail">
                                        {entry.detail}
                                      </div>
                                      {Number(entry?.eventTime || 0) > 0 && (
                                        <div className="sendora-run-safety-log-row-time">
                                          {formatMonitorEpoch(entry.eventTime)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {runSalesforcePrunedLeadLogs.length > 4 && (
                                  <button
                                    type="button"
                                    className="sendora-run-step-breakdown-toggle"
                                    onClick={() =>
                                      setShowAllRunSalesforcePrunedLogs((prev) => !prev)
                                    }
                                  >
                                    {showAllRunSalesforcePrunedLogs
                                      ? "Show fewer"
                                      : `View all ${runSalesforcePrunedLeadLogs.length}`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="sendora-live-monitor-empty">
                                No Salesforce status removals logged.
                              </div>
                            )}
                          </div>
                          <div className="sendora-run-safety-log-card">
                            <div className="sendora-run-safety-log-title">
                              Emails marked as bad by verifier
                            </div>
                            {runVerificationBlockedLeadLogs.length ? (
                              <>
                                <div className="sendora-run-safety-log-list">
                                  {(showAllRunVerificationBlockedLogs
                                    ? runVerificationBlockedLeadLogs
                                    : runVerificationBlockedLeadLogs.slice(0, 4)
                                  ).map((entry) => (
                                    <div className="sendora-run-safety-log-row" key={entry.key}>
                                      <div className="sendora-run-safety-log-row-title">
                                        {entry.title}
                                      </div>
                                      <div className="sendora-run-safety-log-row-detail">
                                        {entry.detail}
                                      </div>
                                      {Number(entry?.eventTime || 0) > 0 && (
                                        <div className="sendora-run-safety-log-row-time">
                                          {formatMonitorEpoch(entry.eventTime)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {runVerificationBlockedLeadLogs.length > 4 && (
                                  <button
                                    type="button"
                                    className="sendora-run-step-breakdown-toggle"
                                    onClick={() =>
                                      setShowAllRunVerificationBlockedLogs((prev) => !prev)
                                    }
                                  >
                                    {showAllRunVerificationBlockedLogs
                                      ? "Show fewer"
                                      : `View all ${runVerificationBlockedLeadLogs.length}`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="sendora-live-monitor-empty">
                                No bad email verification blocks logged.
                              </div>
                            )}
                          </div>
                          <div className="sendora-run-safety-log-card">
                            <div className="sendora-run-safety-log-title">
                              Suppressed due to SES bounces
                            </div>
                            {runBounceLeadLogs.length ? (
                              <>
                                <div className="sendora-run-safety-log-list">
                                  {(showAllRunBounceLogs
                                    ? runBounceLeadLogs
                                    : runBounceLeadLogs.slice(0, 4)
                                  ).map((entry) => (
                                    <div className="sendora-run-safety-log-row" key={entry.key}>
                                      <div className="sendora-run-safety-log-row-title">
                                        {entry.title}
                                      </div>
                                      <div className="sendora-run-safety-log-row-detail">
                                        {entry.detail}
                                      </div>
                                      {Number(entry?.eventTime || 0) > 0 && (
                                        <div className="sendora-run-safety-log-row-time">
                                          {formatMonitorEpoch(entry.eventTime)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {runBounceLeadLogs.length > 4 && (
                                  <button
                                    type="button"
                                    className="sendora-run-step-breakdown-toggle"
                                    onClick={() => setShowAllRunBounceLogs((prev) => !prev)}
                                  >
                                    {showAllRunBounceLogs
                                      ? "Show fewer"
                                      : `View all ${runBounceLeadLogs.length}`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="sendora-live-monitor-empty">
                                No SES bounce suppressions logged.
                              </div>
                            )}
                          </div>
                          <div className="sendora-run-safety-log-card">
                            <div className="sendora-run-safety-log-title">
                              Suppressed due to SES complaints
                            </div>
                            {runComplaintLeadLogs.length ? (
                              <>
                                <div className="sendora-run-safety-log-list">
                                  {(showAllRunComplaintLogs
                                    ? runComplaintLeadLogs
                                    : runComplaintLeadLogs.slice(0, 4)
                                  ).map((entry) => (
                                    <div className="sendora-run-safety-log-row" key={entry.key}>
                                      <div className="sendora-run-safety-log-row-title">
                                        {entry.title}
                                      </div>
                                      <div className="sendora-run-safety-log-row-detail">
                                        {entry.detail}
                                      </div>
                                      {Number(entry?.eventTime || 0) > 0 && (
                                        <div className="sendora-run-safety-log-row-time">
                                          {formatMonitorEpoch(entry.eventTime)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {runComplaintLeadLogs.length > 4 && (
                                  <button
                                    type="button"
                                    className="sendora-run-step-breakdown-toggle"
                                    onClick={() => setShowAllRunComplaintLogs((prev) => !prev)}
                                  >
                                    {showAllRunComplaintLogs
                                      ? "Show fewer"
                                      : `View all ${runComplaintLeadLogs.length}`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="sendora-live-monitor-empty">
                                No SES complaint suppressions logged.
                              </div>
                            )}
                          </div>
                          <div className="sendora-run-safety-log-card">
                            <div className="sendora-run-safety-log-title">Last SES events</div>
                            {runLastSesEventLogs.length ? (
                              <>
                                <div className="sendora-run-safety-log-list">
                                  {(showAllRunLastSesEventLogs
                                    ? runLastSesEventLogs
                                    : runLastSesEventLogs.slice(0, 4)
                                  ).map((entry) => (
                                    <div className="sendora-run-safety-log-row" key={entry.key}>
                                      <div className="sendora-run-safety-log-row-title">
                                        {entry.title}
                                      </div>
                                      <div className="sendora-run-safety-log-row-detail">
                                        {entry.detail}
                                      </div>
                                      {Number(entry?.eventTime || 0) > 0 && (
                                        <div className="sendora-run-safety-log-row-time">
                                          {formatMonitorEpoch(entry.eventTime)}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {runLastSesEventLogs.length > 4 && (
                                  <button
                                    type="button"
                                    className="sendora-run-step-breakdown-toggle"
                                    onClick={() => setShowAllRunLastSesEventLogs((prev) => !prev)}
                                  >
                                    {showAllRunLastSesEventLogs
                                      ? "Show fewer"
                                      : `View all ${runLastSesEventLogs.length}`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="sendora-live-monitor-empty">
                                No SES bounce/complaint events yet.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                  )}

                  {activeTab === "leads" && (
                  <Card className="admin-card p-3 mb-3">
                    {renderTabControls()}
                    {isLeadsTabLoading ? (
                      <div className="loading-inline py-4">
                        <img src={BlueSquaresLoading} alt="Loading leads" />
                      </div>
                    ) : (
                      <>
                    <div className="section-top-row">
                      <div>
                        <div className="section-title">Leads</div>
                        <div className="section-desc">
                          Enable leads for specific steps. Start each step from the Run tab.
                        </div>
                      </div>
                      <div className="section-actions">
                        <Button
                          variant="outlined"
                          className="sendora-btn-outline sendora-refresh-btn"
                          onClick={handleRefreshMembers}
                          disabled={runningMemberAction}
                        >
                          <i className="fa fa-refresh" aria-hidden="true" /> Refresh Leads from Salesforce
                        </Button>
                      </div>
                    </div>

                    <div className="leads-summary-row">
                      <div className="leads-status-totals">
                          <div className="leads-status-totals-row is-total">
                            <span>Total leads in Salesforce</span>
                            <strong>{formatMetricNumber(totalLeadsBeforeSalesforceFilters)}</strong>
                          </div>
                          <button
                            type="button"
                            className={`leads-status-totals-row is-total leads-sf-filter-summary-btn ${
                              allSalesforceFiltersBypassed ? "is-muted" : ""
                            }`}
                            onClick={() => setIsSalesforceFiltersModalOpen(true)}
                          >
                            <span>
                              {allSalesforceFiltersBypassed
                                ? "Salesforce filters bypassed"
                                : "Total leads (after SF filters)"}
                            </span>
                            <strong>{formatMetricNumber(totalLeadsAfterSalesforceFilters)}</strong>
                          </button>
                          {leadStatusTotalsRows.map((row) => (
                            <div
                              className={`leads-status-totals-row ${row.rowClass || "is-default"}`}
                              key={row.label}
                            >
                              <span>{row.label}</span>
                              <strong>{formatMetricNumber(row.value)}</strong>
                            </div>
                          ))}
                      </div>
                      {/* <div className="leads-queue-summary">
                          <div className="leads-queue-summary-value">
                            {headerMetricsReady ? (
                              enabledLeadCount
                            ) : (
                              <img
                                src={BlueCasualLoader}
                                alt="Loading enabled leads"
                                style={{ width: "50px", height: "auto" }}
                              />
                            )}
                          </div>
                          <div className="leads-queue-summary-label">Leads enabled for steps</div>
                      </div> */}
                      <div className="leads-status-totals leads-enabled-step-totals">
                        <div className="leads-status-totals-row is-total">
                          <span>Total enabled leads (all steps)</span>
                          <strong>
                            {formatMetricNumber(enabledLeadBreakdownByStep.totalEnabledAcrossSteps)}
                          </strong>
                        </div>
                        {enabledLeadBreakdownBySequence.rows.map((row) => (
                          <div className="leads-status-totals-row is-default" key={row.label}>
                            <span>{row.label}</span>
                            <strong>{formatMetricNumber(row.value)}</strong>
                          </div>
                        ))}
                        {enabledLeadBreakdownByStep.rows.map((row) => (
                          <div className="leads-status-totals-row is-default" key={row.label}>
                            <span>
                              {enabledLeadBreakdownBySequence.rows.length > 0
                                ? `Manual ${row.label}`
                                : row.label}
                            </span>
                            <strong>{formatMetricNumber(row.value)}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="leads-filter-and-steps-row">
                      <div className="leads-filter-left-stack">
                        <div className="leads-filters-panel-wrap">
                        <div className="leads-filters-tabs">
                          <button type="button" className="leads-filters-tab is-active">
                            Filters
                          </button>
                        </div>
                        <div className="leads-filter-card is-with-filters-tab">
                          <div className="leads-filters-toggle-row">
                            <label className="leads-filters-toggle leads-filters-toggle-switch mb-0">
                              <input
                                type="checkbox"
                                checked={leadsFiltersEnabled}
                                onChange={(e) => setLeadsFiltersEnabled(e.target.checked)}
                              />
                              <span className="leads-filters-toggle-slider" aria-hidden="true" />
                              <span className="leads-filters-toggle-text">Enable filters</span>
                            </label>
                            <button
                              type="button"
                              className="leads-clear-filters-btn"
                              onClick={handleClearAllLeadFilters}
                            >
                              Clear all filters
                            </button>
                          </div>
                          <div className="members-filter-compact">
                            <input
                              className="form-control filter-input"
                              placeholder="Filter name"
                              value={memberNameFilter}
                              onChange={(e) => setMemberNameFilter(e.target.value)}
                              disabled={!leadsFiltersEnabled}
                            />
                            <input
                              className="form-control filter-input"
                              placeholder="Filter email"
                              value={memberEmailFilter}
                              onChange={(e) => setMemberEmailFilter(e.target.value)}
                              disabled={!leadsFiltersEnabled}
                            />
                            <input
                              className="form-control filter-input"
                              placeholder="Filter company"
                              value={memberCompanyFilter}
                              onChange={(e) => setMemberCompanyFilter(e.target.value)}
                              disabled={!leadsFiltersEnabled}
                            />
                            <input
                              className="form-control filter-input"
                              placeholder="Filter title"
                              value={memberTitleFilter}
                              onChange={(e) => setMemberTitleFilter(e.target.value)}
                              disabled={!leadsFiltersEnabled}
                            />
                            <input
                              className="form-control filter-input"
                              placeholder="Filter comments"
                              value={memberCommentsFilter}
                              onChange={(e) => setMemberCommentsFilter(e.target.value)}
                              disabled={!leadsFiltersEnabled}
                            />
                            <div className="filter-option">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <div className="option-title">Comments:</div>
                                </span>
                                <select
                                  className="form-control rmono"
                                  value={memberCommentsValueFilter}
                                  onChange={(e) => setMemberCommentsValueFilter(e.target.value)}
                                  disabled={!leadsFiltersEnabled}
                                >
                                  {memberCommentsValueFilterOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="filter-option">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <div className="option-title">Queue:</div>
                                </span>
                                <select
                                  className="form-control rmono"
                                  value={filterQueueStatus}
                                  onChange={(e) => setFilterQueueStatus(e.target.value)}
                                  disabled={!leadsFiltersEnabled}
                                >
                                  <option value="all">All</option>
                                  <option value="queued">Queued</option>
                                  <option value="not_queued">Not queued</option>
                                </select>
                              </div>
                            </div>
                            <div className="filter-option">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <div className="option-title">Sending:</div>
                                </span>
                                <select
                                  className="form-control rmono"
                                  value={memberSendingFilter}
                                  onChange={(e) => setMemberSendingFilter(e.target.value)}
                                  disabled={!leadsFiltersEnabled}
                                >
                                  <option value="all">All</option>
                                  <option value="sent">Sent</option>
                                  <option value="not_sent">Not sent</option>
                                </select>
                              </div>
                            </div>
                            <div className="filter-option">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <div className="option-title">Salesforce status:</div>
                                </span>
                                <select
                                  className="form-control rmono"
                                  value={memberStatusFilter}
                                  onChange={(e) => setMemberStatusFilter(e.target.value)}
                                  disabled={!leadsFiltersEnabled}
                                >
                                  {memberStatusFilterOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="filter-option">
                              <div className="input-group">
                                <span className="input-group-text">
                                  <div className="option-title">Email verification:</div>
                                </span>
                                <select
                                  className="form-control rmono"
                                  value={memberVerificationFilter}
                                  onChange={(e) => setMemberVerificationFilter(e.target.value)}
                                  disabled={!leadsFiltersEnabled}
                                >
                                  <option value="all">All</option>
                                  <option value="good">Good</option>
                                  <option value="good_acceptable">Good + Acceptable</option>
                                  <option value="acceptable">Acceptable</option>
                                  <option value="bad">Bad</option>
                                  <option value="not_verified">Not verified</option>
                                </select>
                              </div>
                            </div>
                            {!!leadSequencePills.length && (
                              <div
                                className={`leads-enabled-steps-filter-box ${
                                  enabledSequencesFilterEnabled ? "is-active" : ""
                                }`}
                              >
                                <div className="leads-enabled-steps-filter-label">
                                  <label className="leads-filters-toggle leads-filters-toggle-switch mb-0">
                                    <input
                                      type="checkbox"
                                      checked={enabledSequencesFilterEnabled}
                                      disabled={!leadsFiltersEnabled}
                                      onChange={(e) => setEnabledSequencesFilterEnabled(e.target.checked)}
                                    />
                                    <span className="leads-filters-toggle-slider" aria-hidden="true" />
                                    <span className="leads-enabled-steps-filter-label-text">
                                      Filter by enabled sequences
                                    </span>
                                  </label>
                                </div>
                                <div
                                  className={`leads-enabled-steps-filter-options ${
                                    !leadsFiltersEnabled || !enabledSequencesFilterEnabled
                                      ? "is-disabled"
                                      : ""
                                  }`}
                                >
                                  {leadSequencePills.map((entry) => (
                                    <label
                                      key={`enabled-sequence-filter-${entry.sequenceId}`}
                                      className="checkbox-inline mb-0 leads-enabled-steps-filter-option"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={enabledSequencesFilterSequenceIds.includes(entry.sequenceId)}
                                        disabled={!leadsFiltersEnabled || !enabledSequencesFilterEnabled}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          const sequenceId = String(entry.sequenceId || "").trim();
                                          setEnabledSequencesFilterSequenceIds((prev) => {
                                            const prevSet = new Set(
                                              (Array.isArray(prev) ? prev : [])
                                                .map((value) => String(value || "").trim())
                                                .filter(Boolean)
                                            );
                                            if (checked) prevSet.add(sequenceId);
                                            else prevSet.delete(sequenceId);
                                            return Array.from(prevSet);
                                          });
                                        }}
                                      />
                                      <span>{`${entry.letter}: ${entry.title}`}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div
                              className={`leads-enabled-steps-filter-box ${
                                enabledStepsFilterEnabled ? "is-active" : ""
                              }`}
                            >
                              <div className="leads-enabled-steps-filter-label">
                                <label className="leads-filters-toggle leads-filters-toggle-switch mb-0">
                                  <input
                                    type="checkbox"
                                    checked={enabledStepsFilterEnabled}
                                    disabled={!leadsFiltersEnabled}
                                    onChange={(e) => setEnabledStepsFilterEnabled(e.target.checked)}
                                  />
                                  <span className="leads-filters-toggle-slider" aria-hidden="true" />
                                  <span className="leads-enabled-steps-filter-label-text">
                                    Filter by enabled steps
                                  </span>
                                </label>
                              </div>
                              <div
                                className={`leads-enabled-steps-filter-options ${
                                  !leadsFiltersEnabled || !enabledStepsFilterEnabled
                                    ? "is-disabled"
                                    : ""
                                }`}
                              >
                                {sequenceStepApprovalOptions.map((option) => {
                                  const stepId = String(option?.stepId || "").trim();
                                  if (!stepId) return null;
                                  return (
                                    <label
                                      key={`enabled-step-filter-${stepId}`}
                                      className="checkbox-inline mb-0 leads-enabled-steps-filter-option"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={enabledStepsFilterStepIds.includes(stepId)}
                                        disabled={!leadsFiltersEnabled || !enabledStepsFilterEnabled}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setEnabledStepsFilterStepIds((prev) => {
                                            const prevSet = new Set(
                                              (Array.isArray(prev) ? prev : [])
                                                .map((value) => String(value || "").trim())
                                                .filter(Boolean)
                                            );
                                            if (checked) prevSet.add(stepId);
                                            else prevSet.delete(stepId);
                                            return Array.from(prevSet);
                                          });
                                        }}
                                      />
                                      <span>{`Step ${Number(option?.orderIndex || 1)}`}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            <div
                              className={`leads-enabled-steps-filter-box ${
                                sentStepsFilterEnabled ? "is-active" : ""
                              }`}
                            >
                              <div className="leads-enabled-steps-filter-label">
                                <label className="leads-filters-toggle leads-filters-toggle-switch mb-0">
                                  <input
                                    type="checkbox"
                                    checked={sentStepsFilterEnabled}
                                    disabled={!leadsFiltersEnabled}
                                    onChange={(e) => setSentStepsFilterEnabled(e.target.checked)}
                                  />
                                  <span className="leads-filters-toggle-slider" aria-hidden="true" />
                                  <span className="leads-enabled-steps-filter-label-text">
                                    Filter by sent steps
                                  </span>
                                </label>
                              </div>
                              <div
                                className={`leads-enabled-steps-filter-options ${
                                  !leadsFiltersEnabled || !sentStepsFilterEnabled
                                    ? "is-disabled"
                                    : ""
                                }`}
                              >
                                <label className="checkbox-inline mb-0 leads-enabled-steps-filter-option">
                                  <input
                                    type="checkbox"
                                    checked={sentStepsFilterIncludeNothingSent}
                                    disabled={!leadsFiltersEnabled || !sentStepsFilterEnabled}
                                    onChange={(e) =>
                                      setSentStepsFilterIncludeNothingSent(e.target.checked)
                                    }
                                  />
                                  <span>Nothing sent</span>
                                </label>
                                {sequenceStepApprovalOptions.map((option) => {
                                  const stepId = String(option?.stepId || "").trim();
                                  if (!stepId) return null;
                                  return (
                                    <label
                                      key={`sent-step-filter-${stepId}`}
                                      className="checkbox-inline mb-0 leads-enabled-steps-filter-option"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={sentStepsFilterStepIds.includes(stepId)}
                                        disabled={!leadsFiltersEnabled || !sentStepsFilterEnabled}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setSentStepsFilterStepIds((prev) => {
                                            const prevSet = new Set(
                                              (Array.isArray(prev) ? prev : [])
                                                .map((value) => String(value || "").trim())
                                                .filter(Boolean)
                                            );
                                            if (checked) prevSet.add(stepId);
                                            else prevSet.delete(stepId);
                                            return Array.from(prevSet);
                                          });
                                        }}
                                      />
                                      <span>{`Step ${Number(option?.orderIndex || 1)}`}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                        </div>
                        <div className="bulk-action-row compact-actions-row">
                          <div className="bulk-action-left compact-actions-left">
                            <label className="checkbox-inline">
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                onChange={(e) =>
                                  handleToggleSelectAllVisible(e.target.checked)
                                }
                              />
                              <span>
                                {hasActiveLeadFilters ? "Select all filtered" : "Select all"} (
                                {visibleMembers.length})
                              </span>
                            </label>
                            <div className="bulk-count">{selectedCount} selected</div>
                            <Button
                              variant="text"
                              className="sendora-clear-selection-btn"
                              onClick={() => setMemberSelection({})}
                              disabled={!selectedCount}
                            >
                              Clear selection
                            </Button>
                            <Button
                              variant="text"
                              className="sendora-clear-selection-btn"
                              onClick={handleExportSelectedLeadsAsCsv}
                              disabled={!selectedCount}
                            >
                              Export selected as CSV
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="leads-actions-panels">
                        <div className="leads-tools-panel-wrap">
                          <div className="leads-tools-tabs">
                            <button type="button" className="leads-tools-tab is-active">
                              Tools
                            </button>
                          </div>
                          <div className="leads-step-approval-panel leads-tools-panel is-with-tools-tab">
                            <div className="leads-step-approval-title">
                              Upload contacts to Salesforce Campaign
                            </div>
                            <div className="leads-tools-actions">
                              <Button
                                variant="outlined"
                                className={`leads-action-btn ${
                                  selectedCampaignSalesforceId ? "sendora-btn" : "sendora-btn-outline"
                                }`}
                                onClick={handleOpenCampaignContactUploadModal}
                                disabled={!selectedCampaignSalesforceId}
                              >
                                Load contacts from CSV
                                <i className="event-owner-add fa fa-upload ml-05" />
                              </Button>
                              <input
                                type="file"
                                accept=".csv"
                                ref={campaignContactUploadFileInputRef}
                                style={{ display: "none" }}
                                onChange={handlePickCampaignContactUploadFile}
                              />
                            </div>
                            <div className="leads-step-approval-title">Modify Salesforce status</div>
                            <div className="leads-tools-actions">
                              <select
                                className="form-control leads-tools-select"
                                value={bulkSalesforceStatusTarget}
                                onChange={(e) => setBulkSalesforceStatusTarget(e.target.value)}
                                disabled={runningMemberAction}
                              >
                                <option value="">Select status</option>
                                {runSettingsSalesforceStatusOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <Button
                                variant="outlined"
                                className={`leads-action-btn ${
                                  selectedCount && bulkSalesforceStatusTarget ? "sendora-btn" : "sendora-btn-outline"
                                }`}
                                onClick={handleOpenBulkSalesforceStatusConfirm}
                                disabled={!selectedCount || !bulkSalesforceStatusTarget || runningMemberAction}
                              >
                                Update selected
                              </Button>
                            </div>
                          </div>
                        </div>
                        <div className="leads-send-mode-panel-wrap">
                          {!!leadSequencePills.length && (
                            <div className="leads-send-mode-tabs">
                              <button
                                type="button"
                                className={`leads-send-mode-tab ${
                                  leadSendMode === "sequence" ? "is-active" : ""
                                }`}
                                onClick={() => {
                                  leadSendModeTouchedRef.current = true;
                                  setLeadSendMode("sequence");
                                  if (!selectedLeadSequenceId) {
                                    setSelectedLeadSequenceId(
                                      String(leadSequencePills[0]?.sequenceId || "")
                                    );
                                  }
                                }}
                              >
                                Sequence send mode
                              </button>
                              <button
                                type="button"
                                className={`leads-send-mode-tab ${
                                  leadSendMode === "manual" ? "is-active" : ""
                                }`}
                                onClick={() => {
                                  leadSendModeTouchedRef.current = true;
                                  setLeadSendMode("manual");
                                  setSelectedLeadSequenceId("");
                                }}
                              >
                                Manual send mode
                              </button>
                            </div>
                          )}
                          <div
                            className={`leads-step-approval-panel ${
                              leadSequencePills.length ? "is-with-send-mode-tabs" : ""
                            }`}
                          >
                          {leadSendMode === "sequence" && !!leadSequencePills.length && (
                            <div className="leads-sequence-picker">
                              <div className="leads-sequence-picker-title">
                                Enable sequence for selected leads
                              </div>
                              <div className="leads-sequence-pill-row">
                                {leadSequencePills.map((entry) => {
                                  const isActive = selectedLeadSequenceId === entry.sequenceId;
                                  return (
                                    <button
                                      type="button"
                                      key={`lead-sequence-pill-${entry.sequenceId}`}
                                      className={`leads-sequence-pill ${isActive ? "is-active" : ""}`}
                                      onClick={() => setSelectedLeadSequenceId(entry.sequenceId)}
                                    >
                                      {`${entry.letter}: ${entry.title}`}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {(leadSendMode !== "sequence" || !leadSequencePills.length) && (
                            <div className="leads-step-approval-title">
                              Enable steps for selected leads
                            </div>
                          )}
                          <div className="leads-step-approval-list">
                            {sequenceStepApprovalOptions.map((option) => {
                              const isChecked = selectedApprovalStepIds.includes(option.stepId);
                              const sourceRequiredButMissing =
                                option.mode === "ott" &&
                                !selectedApprovalStepIds.includes(option.ottSourceStepId);
                              const disabled = sourceRequiredButMissing && !isChecked;
                              const waitEdge = selectedLeadSequenceWaitByFromStepId.get(
                                String(option?.stepId || "")
                              );
                              const showWaitButton = !!selectedLeadSequenceId && !!waitEdge;
                              const isSequenceModeActive =
                                leadSendMode === "sequence" && !!leadSequencePills.length;
                              const subjectLabel =
                                option.subject.length > 28
                                  ? `${option.subject.slice(0, 28).trimEnd()}...`
                                  : option.subject;
                              return (
                                <div
                                  key={`approval-step-${option.stepId}`}
                                  className="leads-step-approval-item-row"
                                >
                                  <label
                                    className="checkbox-inline mb-0 leads-step-approval-item"
                                    style={{ opacity: disabled ? 0.6 : 1 }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      disabled={disabled}
                                      onChange={(e) =>
                                        handleToggleApprovalStepSelection(option.stepId, e.target.checked)
                                      }
                                    />
                                    <span className="leads-step-approval-item-label">
                                      {`Step ${option.orderIndex}: ${subjectLabel}`}
                                    </span>
                                  </label>
                                  {isSequenceModeActive && showWaitButton && (
                                    <button
                                      type="button"
                                      className="leads-step-wait-btn"
                                      onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleOpenLeadSequenceWaitEditor(waitEdge);
                                      }}
                                    >
                                      {`wait ${formatSequenceWaitLabel(waitEdge.wait)}`}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div className="leads-step-approval-actions is-sequence-mode">
                            <Button
                              variant="outlined"
                              className={`leads-action-btn ${
                                selectedCount ? "sendora-btn" : "sendora-btn-outline"
                              }`}
                              onClick={() => handleMemberIncludeExclude("include")}
                              disabled={!selectedCount || runningMemberAction}
                            >
                              {leadSendMode === "sequence" && !!leadSequencePills.length
                                ? "Enable sequence for selected leads"
                                : `Enable ${
                                    Number(selectedApprovalStepIds?.length || 0) === 1
                                      ? "step"
                                      : "steps"
                                  } for selected leads`}
                            </Button>
                            <Button
                              variant="outlined"
                              className="sendora-btn-danger leads-action-btn"
                              onClick={() => handleMemberIncludeExclude("exclude")}
                              disabled={!selectedCount || runningMemberAction}
                            >
                              {leadSendMode === "sequence" && !!leadSequencePills.length
                                ? "Disable sequence for selected leads"
                                : `Disable ${
                                    Number(selectedApprovalStepIds?.length || 0) === 1
                                      ? "step"
                                      : "steps"
                                  } for selected leads`}
                            </Button>
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>

                    <div className="table-wrap members-table-wrap">
                        <table className="sendora-table members-table">
                          <thead>
                            <tr>
                              <th />
                              <th>Name</th>
                              <th>Email</th>
                              <th>Company</th>
                              <th>Title</th>
                              <th>Salesforce status</th>
                              <th>Comments</th>
                              <th>Enabled Sequences</th>
                              <th>Enabled Steps</th>
                              <th className="member-emails-sent-col">Emails Sent</th>
                              <th>Queued</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleMembers.length ? (
                              displayedMembersForTable.map((member) => (
                                <tr
                                  key={member._id}
                                  className={
                                    Number(member?.lastSendSuccessTime || 0) > 0
                                      ? "member-row-is-sent"
                                      : ""
                                  }
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={!!memberSelection[member._id]}
                                      onChange={(e) =>
                                        handleToggleMemberSelection(
                                          member._id,
                                          e.target.checked
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    {(() => {
                                      const memberCampaignMemberUrl =
                                        getMemberSalesforceCampaignMemberUrl(member);
                                      const memberContactOrLeadUrl =
                                        getMemberSalesforceContactOrLeadUrl(member);
                                      const memberId = String(member?._id || "").trim();
                                      const shouldUseContactOrLeadUrl =
                                        !!memberContactOrLeadUrl &&
                                        isShiftPressed &&
                                        hoveredMemberSalesforceLinkId === memberId;
                                      const memberSalesforceUrl = shouldUseContactOrLeadUrl
                                        ? memberContactOrLeadUrl
                                        : memberCampaignMemberUrl || memberContactOrLeadUrl;
                                      const memberName = String(member?.fullName || "").trim();
                                      if (!memberName) return "-";
                                      if (!memberSalesforceUrl) return memberName;
                                      return (
                                        <a
                                          href={memberSalesforceUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="member-name-link"
                                          title={
                                            memberCampaignMemberUrl && memberContactOrLeadUrl
                                              ? "Open Campaign Member (hold Shift to open Contact/Lead)"
                                              : undefined
                                          }
                                          onMouseEnter={() => {
                                            setHoveredMemberSalesforceLinkId(memberId);
                                          }}
                                          onMouseLeave={() => {
                                            setHoveredMemberSalesforceLinkId("");
                                          }}
                                        >
                                          {memberName}
                                        </a>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {(() => {
                                      const verificationIndicator =
                                        getMemberEmailVerificationIndicator(member);
                                      return (
                                        <span className="member-email-cell">
                                          <span>{member.email || "-"}</span>
                                          {!!String(member?.email || "").trim() &&
                                            verificationIndicator && (
                                              <CustomTooltip
                                                content={verificationIndicator.title}
                                                placement="top"
                                                tooltipClassName="sendora-email-verification-tooltip"
                                                usePortal
                                              >
                                                <span
                                                  className={`member-email-verification-indicator ${
                                                    verificationIndicator.className
                                                  }`}
                                                  aria-label={verificationIndicator.title}
                                                >
                                                  <i
                                                    className={verificationIndicator.iconClass}
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              </CustomTooltip>
                                            )}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td>{member.company || "-"}</td>
                                  <td>{member.title || "-"}</td>
                                  <td
                                    className={`member-salesforce-status ${getSalesforceStatusCellClassName(
                                      member.status
                                    )}`}
                                  >
                                    {member.status || "-"}
                                  </td>
                                  <td>
                                    {(() => {
                                      const commentsText = String(member?.comments || "").trim();
                                      if (!commentsText) return "-";
                                      const compactComments =
                                        commentsText.length > 70
                                          ? `${commentsText.slice(0, 70).trimEnd()}...`
                                          : commentsText;
                                      const needsTooltip = commentsText.length > 70;
                                      if (!needsTooltip) {
                                        return (
                                          <span className="member-comments-cell">
                                            {compactComments}
                                          </span>
                                        );
                                      }
                                      return (
                                        <CustomTooltip
                                          content={commentsText}
                                          placement="top"
                                          tooltipClassName="sendora-comments-tooltip"
                                          usePortal
                                        >
                                          <span className="member-comments-cell">
                                            {compactComments}
                                          </span>
                                        </CustomTooltip>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {(() => {
                                      const enabledSequenceIds = (
                                        Array.isArray(member?.enabledSequenceIds)
                                          ? member.enabledSequenceIds
                                          : []
                                      )
                                        .map((sequenceId) => String(sequenceId || "").trim())
                                        .filter(Boolean);
                                      if (!enabledSequenceIds.length) return "-";
                                      const sequenceLabelById = new Map(
                                        (leadSequencePills || []).map((entry) => [
                                          String(entry?.sequenceId || "").trim(),
                                          `Sequence ${String(entry?.letter || "").trim()}: ${String(
                                            entry?.title || ""
                                          ).trim()}`,
                                        ])
                                      );
                                      return (
                                        <div className="member-approved-sequences">
                                          {enabledSequenceIds.map((sequenceId) => (
                                            <span
                                              key={`${String(member?._id || "")}-enabled-sequence-${sequenceId}`}
                                              className="member-approved-sequence-chip"
                                            >
                                              {sequenceLabelById.get(sequenceId) || sequenceId}
                                            </span>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {(() => {
                                      const memberId = String(member?._id || "");
                                      const memberEmail = normalizeEmail(member?.email || "");
                                      const stepApprovals =
                                        member?.stepApprovals && typeof member.stepApprovals === "object"
                                          ? member.stepApprovals
                                          : {};
                                      const approvedSteps = (sequenceStepApprovalOptions || []).filter(
                                        (option) => !!stepApprovals[String(option?.stepId || "")]
                                      );
                                      if (!approvedSteps.length) return "-";
                                      return (
                                        <div className="member-approved-steps">
                                          {approvedSteps.map((option) => {
                                            const stepId = String(option?.stepId || "");
                                            const status =
                                              memberStepDeliveryStatusByKey.get(
                                                `id:${memberId}::${stepId}`
                                              ) ||
                                              memberStepDeliveryStatusByKey.get(
                                                `email:${memberEmail}::${stepId}`
                                              ) ||
                                              "approved";
                                            return (
                                              <span
                                                key={`${memberId}-approved-step-${stepId}`}
                                                className={`member-approved-step-chip is-${status} is-manual-mode`}
                                              >
                                                {`Step ${option.orderIndex}`}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td className="member-emails-sent-col">
                                    {(() => {
                                      const memberId = String(member?._id || "").trim();
                                      const memberEmail = normalizeEmail(member?.email || "");
                                      const sentEmailRows =
                                        memberSentEmailRowsByKey.get(`id:${memberId}`) ||
                                        memberSentEmailRowsByKey.get(`email:${memberEmail}`) ||
                                        [];
                                      if (!sentEmailRows.length) return "-";
                                      return (
                                        <div className="member-email-sent-badge-list">
                                          {sentEmailRows.map((sentEmailRecord) => {
                                            const senderEmail = String(sentEmailRecord?.fromEmail || "");
                                            const senderPrefix = String(senderEmail || "")
                                              .trim()
                                              .split("@")[0]
                                              .trim();
                                            const sentAtEpoch = Number(
                                              sentEmailRecord?.sentTime ||
                                                sentEmailRecord?.updatedTime ||
                                                sentEmailRecord?.scheduledTime ||
                                                0
                                            );
                                            const stepLabel = String(sentEmailRecord?.stepLabel || "Step").trim();
                                            const variantLabelRaw = String(
                                              sentEmailRecord?.variantLabel || ""
                                            ).trim();
                                            const variantLetterMatch =
                                              variantLabelRaw.match(/variant\s*([a-z])/i) ||
                                              variantLabelRaw.match(/^([a-z])$/i);
                                            const variantLetter = variantLetterMatch
                                              ? String(variantLetterMatch[1] || "").toUpperCase()
                                              : "";
                                            const stepDisplay = variantLetter
                                              ? `${stepLabel}${variantLetter}`
                                              : stepLabel;
                                            const sentDisplay = sentAtEpoch
                                              ? epochToDateTimeString(sentAtEpoch)
                                              : "-";
                                            const senderDisplay = senderPrefix || "-";
                                            return (
                                              <button
                                                key={String(
                                                  sentEmailRecord?.queueId ||
                                                    `${sentEmailRecord?.stepId || ""}-${sentAtEpoch}`
                                                )}
                                                type="button"
                                                className={`member-email-sent-badge-btn ${
                                                  (() => {
                                                    const queueMode = String(
                                                      sentEmailRecord?.queueMode || ""
                                                    )
                                                      .trim()
                                                      .toLowerCase();
                                                    const sourceLabel = String(
                                                      sentEmailRecord?.sourceLabel || ""
                                                    )
                                                      .trim()
                                                      .toLowerCase();
                                                    const sequenceId = String(
                                                      sentEmailRecord?.sequenceId || ""
                                                    )
                                                      .trim()
                                                      .toLowerCase();
                                                    return (
                                                      queueMode === "sequence" ||
                                                      sourceLabel.includes("sequence") ||
                                                      !!sequenceId
                                                    )
                                                      ? "is-sequence-mode"
                                                      : "is-manual-mode";
                                                  })()
                                                }`}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  openMonitorEmailPreview(sentEmailRecord, "recent", event);
                                                }}
                                              >
                                                <span className="member-email-sent-pill-line">
                                                  {`${stepDisplay} · ${sentDisplay} · ${senderDisplay}`}
                                                </span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                  </td>
                                  <td>
                                    {isMemberEnabledForSendora(member) ? (
                                      <span className="member-inclusion-indicator is-included">
                                        <i className="fa fa-check" aria-hidden="true" />
                                      </span>
                                    ) : (
                                      <span className="member-inclusion-indicator is-excluded">
                                        <i className="fa fa-times" aria-hidden="true" />
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={11} className="empty-cell">
                                  No members found for current filters
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                    {visibleMembers.length > LEADS_DISPLAY_LIMIT && (
                      <div className="leads-view-all-wrap">
                        {leadsDisplayExpanded ? (
                          <Button
                            variant="text"
                            className="sendora-view-all-btn"
                            onClick={() => setLeadsDisplayExpanded(false)}
                          >
                            Show less (first {LEADS_DISPLAY_LIMIT})
                          </Button>
                        ) : (
                          <Button
                            variant="text"
                            className="sendora-view-all-btn"
                            onClick={() => setLeadsDisplayExpanded(true)}
                          >
                            View all ({visibleMembers.length - LEADS_DISPLAY_LIMIT} more)
                          </Button>
                        )}
                      </div>
                    )}
                    </div>
                      </>
                    )}
                  </Card>
                  )}

                  {activeTab === "schedule" && (
                    <Card className="admin-card p-3 mb-3 schedule-admin-card">
                      {renderTabControls()}
                      <div className="section-top-row">
                        <div>
                          <div className="section-title">Schedule</div>
                          <div className="section-desc">
                            Define send windows and active days for this campaign.
                          </div>
                        </div>
                        <div className="schedule-bypass-toggle-wrap">
                          <label className="checkbox-inline schedule-bypass-toggle mb-0">
                            <input
                              type="checkbox"
                              checked={scheduleBypassEnabled}
                              onChange={(e) => handleToggleScheduleBypass(e.target.checked)}
                            />
                            <span>Bypass schedule</span>
                          </label>
                          <CustomTooltip
                            content="Bypass schedule removes day/time limits so sending is not restricted by this schedule."
                            placement="left"
                          >
                            <span
                              className="schedule-bypass-tooltip-icon"
                              role="button"
                              tabIndex={0}
                              aria-label="Bypass schedule help"
                            >
                              <i className="fa fa-question-circle-o" aria-hidden="true" />
                            </span>
                          </CustomTooltip>
                        </div>
                      </div>
                      <div className="schedule-layout-grid">
                        <div className="schedule-right-pane">
                          <div
                            className={`schedule-options-block ${
                              scheduleBypassEnabled ? "is-disabled" : ""
                            }`.trim()}
                          >
                          {scheduleBypassEnabled && (
                            <div className="schedule-bypassed-overlay">
                              <div className="schedule-bypassed-overlay-title">
                                Scheduling Bypassed
                              </div>
                              <div className="schedule-bypassed-overlay-subtitle">
                                Campaign will run 24/7 until sending is complete
                              </div>
                            </div>
                          )}
                          <div className="schedule-options-content">
                          <div className="admin-card p-3 mb-3">
                            <div className="date-summary-row">
                              <div className="date-summary-item">
                                <i className="fa fa-calendar-o" aria-hidden="true" />
                                <span className="date-link-label">Start</span>
                                {!isDefaultStartDate && (
                                  <CustomTooltip content="Reset" placement="right">
                                    <button
                                      type="button"
                                      className="schedule-reset-btn"
                                      disabled={scheduleBypassEnabled}
                                      onClick={resetStartDateToDefault}
                                    >
                                      <i className="fa fa-undo" aria-hidden="true" />
                                    </button>
                                  </CustomTooltip>
                                )}
                                <button
                                  type="button"
                                  className="date-link-value-btn"
                                  disabled={scheduleBypassEnabled}
                                  onClick={() => openDatePicker(startDateInputRef)}
                                >
                                  <span className="date-link-value">
                                    {isDefaultStartDate
                                      ? "Now"
                                      : formatDateText(campaignDateWindow.startDate)}
                                  </span>
                                </button>
                              </div>
                              <div className="date-summary-item">
                                <i className="fa fa-calendar-o" aria-hidden="true" />
                                <span className="date-link-label">End</span>
                                {!isDefaultEndDate && (
                                  <CustomTooltip content="Reset" placement="right">
                                    <button
                                      type="button"
                                      className="schedule-reset-btn"
                                      disabled={scheduleBypassEnabled}
                                      onClick={resetEndDateToDefault}
                                    >
                                      <i className="fa fa-undo" aria-hidden="true" />
                                    </button>
                                  </CustomTooltip>
                                )}
                                <button
                                  type="button"
                                  className="date-link-value-btn"
                                  disabled={scheduleBypassEnabled}
                                  onClick={() => {
                                    setCampaignDateWindow((prev) => ({
                                      ...prev,
                                      noEndDate: false,
                                    }));
                                    openDatePicker(endDateInputRef);
                                  }}
                                >
                                  <span className="date-link-value">
                                    {campaignDateWindow.noEndDate
                                      ? "No End Date"
                                      : formatDateText(campaignDateWindow.endDate)}
                                  </span>
                                </button>
                              </div>
                            </div>
                            <input
                              ref={startDateInputRef}
                              type="date"
                              className="schedule-hidden-date-input"
                              disabled={scheduleBypassEnabled}
                              min={todayDateInput}
                              value={campaignDateWindow.startDate || todayDateInput}
                              onChange={(e) =>
                                setCampaignDateWindow((prev) => ({
                                  ...prev,
                                  startDate: e.target.value,
                                }))
                              }
                            />
                            <input
                              ref={endDateInputRef}
                              type="date"
                              className="schedule-hidden-date-input"
                              disabled={scheduleBypassEnabled}
                              min={campaignDateWindow.startDate || todayDateInput}
                              value={campaignDateWindow.endDate}
                              onChange={(e) =>
                                setCampaignDateWindow((prev) => ({
                                  ...prev,
                                  noEndDate: false,
                                  endDate: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="admin-card p-3 mb-3">
                            <div className="sendora-form-grid">
                              <div className="form-group">
                                <label>{isUsMainlandEtPtBusinessHours ? "FROM (ET)" : "From"}</label>
                                <select
                                  className="form-control schedule-time-input"
                                  value={selectedSchedule?.fromTime || "09:00"}
                                  disabled={scheduleBypassEnabled}
                                  onChange={(e) =>
                                    handleUpdateSelectedSchedule("fromTime", e.target.value)
                                  }
                                >
                                  {TIME_INTERVAL_OPTIONS.map((timeOption) => (
                                    <option key={timeOption.value} value={timeOption.value}>
                                      {timeOption.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>{isUsMainlandEtPtBusinessHours ? "TO (PT)" : "To"}</label>
                                <select
                                  className="form-control schedule-time-input"
                                  value={selectedSchedule?.toTime || "17:00"}
                                  disabled={scheduleBypassEnabled}
                                  onChange={(e) =>
                                    handleUpdateSelectedSchedule("toTime", e.target.value)
                                  }
                                >
                                  {TIME_INTERVAL_OPTIONS.map((timeOption) => (
                                    <option key={timeOption.value} value={timeOption.value}>
                                      {timeOption.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="form-group">
                                <label>Timezone</label>
                                <select
                                  className="form-control schedule-timezone-select"
                                  value={selectedSchedule?.timezone || ""}
                                  disabled={scheduleBypassEnabled}
                                  onChange={(e) =>
                                    handleUpdateSelectedSchedule("timezone", e.target.value)
                                  }
                                >
                                  {timezoneOptions.map((timezone) => (
                                    <option key={timezone.value} value={timezone.value}>
                                      {timezone.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <label className="checkbox-inline schedule-friday-noon-toggle mb-0 mt-2">
                              <input
                                type="checkbox"
                                checked={endFridayAtNoon}
                                disabled={scheduleBypassEnabled}
                                onChange={(e) => setEndFridayAtNoon(e.target.checked)}
                              />
                              <span>Pause sending after 12:00pm on Friday</span>
                            </label>
                          </div>

                          <div className="admin-card p-3">
                            <div className="form-group">
                              <label>Days</label>
                              <div className="checkbox-row">
                                {DAY_OPTIONS.map((day) => (
                                  <label key={day} className="checkbox-inline">
                                    <input
                                      type="checkbox"
                                      checked={(selectedSchedule?.days || []).includes(day)}
                                      disabled={scheduleBypassEnabled}
                                      onChange={(e) =>
                                        handleToggleSelectedScheduleDay(day, e.target.checked)
                                      }
                                    />
                                    <span className="text-capitalize">{day}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                          </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}

                </>
              )}
            </>
          )}
          <div className="sendora-feedback-footer">
            Any suggestions or feedback?{" "}
            <a
              href="mailto:mjones@landmarkventures.com?subject=Sendora%20Platform%20Feedback"
              className="sendora-feedback-link"
            >
              mjones@landmarkventures.com
            </a>
          </div>
        </div>

        {spintaxVariantRemoveControls.map((control) => (
          <button
            key={`spintax-remove-${control.index}`}
            type="button"
            className="sendora-spintax-variant-remove-btn"
            style={{
              position: "fixed",
              top: control.top,
              left: control.left,
              transform: "translate(-50%, -100%)",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleRemoveSpintaxVariant(control.index);
            }}
            aria-label="Remove spintax variant"
            title="Remove spintax variant"
          >
            &times;
          </button>
        ))}

        {spintaxSuggestionPopup.visible && (
          <div
            className="sendora-spintax-suggestion-popover"
            style={{
              position: "fixed",
              top: spintaxSuggestionPopup.top,
              left: spintaxSuggestionPopup.left,
              transform: "translate(-50%, -100%)",
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="sendora-spintax-suggestion-btn"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={handleGenerateSpintaxSuggestions}
              disabled={generatingSpintaxSuggestions}
            >
              {generatingSpintaxSuggestions ? "Generating..." : "Generate spintax"}
            </button>
          </div>
        )}

        {openCampaignMenuId &&
          openCampaignMenuCampaign &&
          createPortal(
            <div
              className="campaign-row-menu-dropdown sendora-campaign-menu-portal"
              style={{
                position: "fixed",
                top: openCampaignMenuPosition.top,
                left: openCampaignMenuPosition.left,
                right: "auto",
                transform: "translateX(-100%)",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="campaign-row-menu-item"
                onClick={(event) => openRenameCampaignModal(event, openCampaignMenuCampaign)}
              >
                <i className="fa fa-pencil" aria-hidden="true" />
                <span>Rename</span>
              </button>
              <button
                type="button"
                className="campaign-row-menu-item is-danger"
                onClick={(event) => handleDeleteCampaignRow(event, openCampaignMenuCampaign)}
              >
                <i className="fa fa-trash" aria-hidden="true" />
                <span>Delete</span>
              </button>
              <button
                type="button"
                className="campaign-row-menu-item"
                onClick={(event) => handleDuplicateCampaignRow(event, openCampaignMenuCampaign)}
              >
                <i className="fa fa-clone" aria-hidden="true" />
                <span>Duplicate campaign</span>
              </button>
            </div>,
            document.body
          )}

        {selectedMonitorEmailPreview &&
          createPortal(
            <div className="sendora-wrapper">
              <div
                ref={monitorPreviewPopupRef}
                className="sendora-live-monitor-preview-popup"
                style={{
                  top: monitorPreviewPopupPosition.top,
                  left: monitorPreviewPopupPosition.left,
                }}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="sendora-live-monitor-preview-shell">
                  <div className="sendora-live-monitor-preview-meta">
                    <div className="sendora-live-monitor-preview-meta-line">
                      <span className="sendora-live-monitor-preview-meta-label">Sender:</span>{" "}
                      {monitorPopupSenderName ? (
                        <>
                          <span className="sendora-live-monitor-preview-name">
                            {monitorPopupSenderName}
                          </span>
                          <span className="sendora-live-monitor-preview-meta-email">
                            {` <${selectedMonitorEmailPreview.fromEmail || "-"}>`}
                          </span>
                        </>
                      ) : (
                        <span className="sendora-live-monitor-preview-meta-email">
                          {selectedMonitorEmailPreview.fromEmail || "-"}
                        </span>
                      )}
                    </div>
                    <div className="sendora-live-monitor-preview-meta-line">
                      <span className="sendora-live-monitor-preview-meta-label">Recipient:</span>{" "}
                      {monitorPopupRecipientName ? (
                        <>
                          <span className="sendora-live-monitor-preview-name">
                            {monitorPopupRecipientName}
                          </span>
                          <span className="sendora-live-monitor-preview-meta-email">
                            {` <${selectedMonitorEmailPreview.memberEmail || "-"}>`}
                          </span>
                        </>
                      ) : (
                        <span className="sendora-live-monitor-preview-meta-email">
                          {selectedMonitorEmailPreview.memberEmail || "-"}
                        </span>
                      )}
                    </div>
                    <div className="sendora-live-monitor-preview-meta-line">
                      <span className="sendora-live-monitor-preview-meta-label">
                        {selectedMonitorPreviewTimestampLabel}:
                      </span>{" "}
                      <span className="sendora-live-monitor-preview-timestamp">
                        {selectedMonitorPreviewTimestampEpoch
                          ? epochToDateTimeString(selectedMonitorPreviewTimestampEpoch)
                          : "-"}
                      </span>
                    </div>
                    <div className="sendora-live-monitor-preview-meta-line">
                      <span className="sendora-live-monitor-preview-meta-label">Step/Variant:</span>{" "}
                      <span className="sendora-live-monitor-preview-timestamp">
                        {(() => {
                          const stepOrder = Number(selectedMonitorEmailPreview?.stepOrderIndex || 0);
                          const stepLabel =
                            stepLabelById.get(String(selectedMonitorEmailPreview?.stepId || "").trim()) ||
                            (stepOrder > 0 ? `Step ${stepOrder}` : "Step");
                          const variantId = String(selectedMonitorEmailPreview?.variantId || "").trim();
                          if (!variantId) return stepLabel;
                          return `${stepLabel} / Variant ${variantId.slice(0, 8)}`;
                        })()}
                      </span>
                    </div>
                    {selectedMonitorPreviewReason && (
                      <div className="sendora-live-monitor-preview-meta-line">
                        <span className="sendora-live-monitor-preview-meta-label">Reason:</span>{" "}
                        <span className="sendora-live-monitor-preview-meta-email">
                          {selectedMonitorPreviewReason}
                        </span>
                      </div>
                    )}
                  </div>
                    <div className="sendora-live-monitor-preview-subject">
                      {selectedMonitorEmailPreview.subject || "(no subject)"}
                    </div>
                    <div className="sendora-live-monitor-preview-divider" />
                    <div className="sendora-live-monitor-preview-content">
                      {selectedMonitorEmailPreviewIsLoading ? (
                        <div className="sendora-live-monitor-empty">Loading preview...</div>
                      ) : selectedMonitorEmailPreviewHasHtml ? (
                        <iframe
                          title={`sendora-email-preview-${selectedMonitorEmailPreview.queueId}`}
                          className="sendora-preview-iframe sendora-live-monitor-preview-iframe-compact"
                          sandbox=""
                          srcDoc={selectedMonitorPreviewDocumentHtml}
                        />
                      ) : (
                        <pre className="sendora-live-monitor-preview-text">
                          {selectedMonitorEmailPreview.text || "No preview content."}
                        </pre>
                      )}
                    </div>
                </div>
              </div>
            </div>,
            document.body
          )}

        <Modal
          show={isCampaignContactUploadModalOpen}
          onHide={handleCloseCampaignContactUploadModal}
          centered
          size="lg"
          className="sendora-create-modal sendora-campaign-upload-modal"
          unmountOnExit={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Upload contacts to Salesforce Campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <CampaignContactUploader
              employee={employee}
              campaignId={selectedCampaignSalesforceId}
              selectedCampaign={selectedCampaignForContactUpload}
              campaigns={salesforceCampaigns}
              refreshSelectedCampaign={({ preserveActiveTab = true } = {}) =>
                refreshSelectedCampaignData(selectedCampaignId, { preserveActiveTab })
              }
              selectedFile={campaignContactUploadSelectedFile}
              hideInitialLoadButton={true}
              onProcessingChange={handleCampaignUploadProcessingChange}
              onUploadComplete={handleCampaignUploadCompleted}
            />
          </Modal.Body>
        </Modal>

        {isCampaignContactUploadModalMinimized && (
          <button
            type="button"
            className="sendora-campaign-upload-minimized"
            onClick={handleRestoreCampaignContactUploadModal}
            title="Campaign upload is running. Click to reopen."
          >
            <i className="fa-solid fa-cloud-arrow-up" />
            <span>Upload running...</span>
          </button>
        )}

        <Modal
          show={!!leadSequenceWaitEditor?.isOpen}
          onHide={handleCloseLeadSequenceWaitEditor}
          centered
          className="sendora-create-modal leads-sequence-wait-editor-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit wait time</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="sequence-chain-delay-container">
              <div className="sequence-chain-delay-row">
                <span className="sequence-chain-delay-label">Wait</span>
                <div className="sequence-chain-delay-controls">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="form-control"
                    value={String(leadSequenceWaitEditor?.amount || "")}
                    onChange={(event) => {
                      const rawValue = String(event.target.value || "");
                      if (!/^\d*$/.test(rawValue)) return;
                      setLeadSequenceWaitEditor((prev) => ({ ...prev, amount: rawValue }));
                    }}
                    onBlur={(event) => {
                      const rawValue = String(event.target.value || "").trim();
                      const parsedValue = Math.trunc(Number(rawValue));
                      setLeadSequenceWaitEditor((prev) => ({
                        ...prev,
                        amount: String(
                          rawValue && Number.isFinite(parsedValue) && parsedValue > 0
                            ? parsedValue
                            : 1
                        ),
                      }));
                    }}
                  />
                  <select
                    className="form-control"
                    value={String(leadSequenceWaitEditor?.unit || "days")}
                    onChange={(event) =>
                      setLeadSequenceWaitEditor((prev) => ({
                        ...prev,
                        unit: event.target.value,
                      }))
                    }
                  >
                    {SEQUENCE_WAIT_UNITS.map((unit) => (
                      <option key={`lead-wait-editor-unit-${unit}`} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            {selectedLeadSequenceHasEnabledLeads && (
              <div className="leads-sequence-wait-editor-warning">
                This sequence already has enabled leads. Updating wait times will affect all leads
                currently assigned to this sequence.
              </div>
            )}
            <div className="leads-sequence-wait-editor-actions">
              <Button
                variant="outlined"
                className="sendora-btn-outline leads-sequence-wait-editor-btn"
                onClick={handleCloseLeadSequenceWaitEditor}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                className="sendora-btn leads-sequence-wait-editor-btn"
                onClick={handleApplyLeadSequenceWaitEditor}
              >
                OK
              </Button>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={isSalesforceFiltersModalOpen}
          onHide={() => setIsSalesforceFiltersModalOpen(false)}
          centered
          className="sendora-create-modal sendora-sf-filters-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Salesforce lead filters</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="sendora-sf-filters-modal-summary">
              <div className="sendora-sf-filters-modal-total">
                <span className="sendora-sf-filters-modal-total-label">
                  {allSalesforceFiltersBypassed
                    ? "Salesforce filters bypassed"
                    : "Total leads (after SF filters)"}
                </span>
                <strong className="sendora-sf-filters-modal-total-value">
                  {formatMetricNumber(totalLeadsAfterSalesforceFilters)}
                </strong>
              </div>
              <label className="sendora-sf-filters-modal-master-toggle">
                <input
                  type="checkbox"
                  checked={!allSalesforceFiltersBypassed}
                  onChange={(event) =>
                    setAllSalesforceFiltersEnabled(event.target.checked)
                  }
                />
                <span>Enable all Salesforce filters (recommended)</span>
              </label>
            </div>
            <div className="sendora-sf-filters-modal-list">
              {salesforceFilterCatchRows.map((row) => (
                <label key={row.key} className="sendora-sf-filters-modal-row">
                  <span className="sendora-sf-filters-modal-row-left">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(event) =>
                        toggleSalesforceFilter(row.key, event.target.checked)
                      }
                    />
                    <span>{row.label}</span>
                  </span>
                  <strong className="sendora-sf-filters-modal-row-count">
                    {formatMetricNumber(row.caughtCount)}
                    {" caught"}
                  </strong>
                </label>
              ))}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="sendora-btn-outline"
              onClick={() => setSalesforceFilterToggles(DEFAULT_SALESFORCE_FILTER_TOGGLES)}
            >
              Reset defaults
            </Button>
            <Button className="sendora-btn" onClick={() => setIsSalesforceFiltersModalOpen(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={isBulkSalesforceStatusConfirmModalOpen}
          onHide={() => setIsBulkSalesforceStatusConfirmModalOpen(false)}
          centered
          className="sendora-create-modal sendora-salesforce-status-confirm-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Salesforce status update</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {`You are updating the status of ${selectedCount} ${selectedLeadNoun} to `}
            <strong>{selectedBulkSalesforceStatusLabel || "-"}</strong>
            {" in Salesforce."}
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="sendora-btn-outline"
              onClick={() => setIsBulkSalesforceStatusConfirmModalOpen(false)}
              disabled={runningMemberAction}
            >
              Cancel
            </Button>
            <Button
              className="sendora-btn"
              onClick={handleConfirmBulkSalesforceStatusUpdate}
              disabled={runningMemberAction || !selectedCount || !bulkSalesforceStatusTarget}
            >
              {`Update Salesforce status for ${selectedCount} ${selectedLeadNoun}`}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={isPreviewModalOpen}
          onHide={() => setIsPreviewModalOpen(false)}
          centered
          size="xl"
          className="sendora-preview-modal"
        >
          <Modal.Body>
            <div className="sendora-preview-layout">
              <div className="sendora-preview-sidebar">
                <div className="sendora-preview-side-title">
                  <i className="fa fa-paper-plane-o" aria-hidden="true" /> Test Email
                </div>
                <div className="form-group">
                  <label>Send from:</label>
                  <select
                    className="form-control"
                    value={previewForm.sendFrom}
                    onChange={(e) =>
                      setPreviewForm((prev) => ({ ...prev, sendFrom: e.target.value }))
                    }
                  >
                    {previewSenderOptions.length ? (
                      previewSenderOptions.map((emailOption) => {
                        const account = senderAccountByEmail.get(normalizeEmail(emailOption)) || {
                          emailLowercase: emailOption,
                        };
                        return (
                          <option key={emailOption} value={emailOption}>
                            {formatSenderAccountLabel(account)}
                          </option>
                        );
                      })
                    ) : (
                      <option value="">Select...</option>
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label>Send to:</label>
                  <select
                    className="form-control"
                    value={previewForm.sendTo}
                    onChange={(e) =>
                      setPreviewForm((prev) => ({ ...prev, sendTo: e.target.value }))
                    }
                  >
                    <option value={PREVIEW_RECIPIENT_CUSTOM_VALUE}>Custom Email</option>
                    {previewRecipientOptions.map((emailOption) => (
                      <option key={emailOption} value={emailOption}>
                        {emailOption}
                      </option>
                    ))}
                  </select>
                  {isCustomPreviewRecipient && (
                    <input
                      type="email"
                      className="form-control mt-2"
                      placeholder="name@example.com"
                      value={previewForm.customSendTo}
                      onChange={(e) =>
                        setPreviewForm((prev) => ({ ...prev, customSendTo: e.target.value }))
                      }
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Load data for lead:</label>
                  <select
                    className="form-control"
                    value={previewForm.leadId}
                    onChange={(e) =>
                      setPreviewForm((prev) => ({ ...prev, leadId: e.target.value }))
                    }
                  >
                    <option value="">Select a lead...</option>
                    {previewLeadOptions.map((leadOption) => (
                      <option key={leadOption._id} value={leadOption._id}>
                        {leadOption.fullName || leadOption.email || leadOption._id}
                      </option>
                    ))}
                  </select>
                  {hasPreviewSpintax && (
                    <Button
                      className="sendora-btn-outline sendora-preview-randomize-btn"
                      onClick={handleRandomizePreviewSpintax}
                    >
                      <PreviewDiceIcon
                        className={`sendora-preview-randomize-icon ${
                          previewDiceRolling ? "is-rolling" : ""
                        }`}
                      />
                      Randomize spintax
                    </Button>
                  )}
                </div>
              </div>
              <div className="sendora-preview-main">
                <div className="sendora-preview-title">
                  <i className="fa fa-eye" aria-hidden="true" /> Email Preview
                </div>
                <div className="sendora-preview-meta">
                  <div>
                    <span>Send to:</span> {resolvedPreviewSendTo || "Enter email address"}
                  </div>
                  <div>
                    <span>Subject:</span>{" "}
                    {previewRenderState.loading ? (
                      "Resolving variables (including AI variables)..."
                    ) : displaySubject ? (
                      previewSpintaxDisplayOverride ? (
                        <span dangerouslySetInnerHTML={{ __html: displaySubject }} />
                      ) : (
                        displaySubject
                      )
                    ) : (
                      "(no subject)"
                    )}
                  </div>
                </div>
                {!!previewRenderedError && (
                  <div className="alert alert-warning py-2 px-3 mb-2">{previewRenderedError}</div>
                )}
                {previewRenderState.loading && (
                  <div className="sendora-preview-loading-hint">
                    <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Resolving merge +
                    AI variables...
                  </div>
                )}
                <div className="sendora-preview-frame-wrap">
                  <iframe
                    title="Sendora email preview"
                    className="sendora-preview-iframe"
                    srcDoc={previewDocumentHtml}
                  />
                </div>
                <div className="sendora-preview-actions">
                  {!!previewSendDisabledReason && !sendingTestEmail && (
                    <div className="sendora-preview-send-disabled-reason">
                      {previewSendDisabledReason}
                    </div>
                  )}
                  <Button
                    className="sendora-btn-outline"
                    onClick={() => setIsPreviewModalOpen(false)}
                    disabled={sendingTestEmail}
                  >
                    Close
                  </Button>
                  <Button
                    className="sendora-btn"
                    onClick={handleSendTestEmailPreview}
                    disabled={!canSendPreviewTestEmail}
                  >
                    <i className="fa fa-paper-plane-o" aria-hidden="true" />{" "}
                    {sendingTestEmail ? "Sending..." : "Send test email"}
                  </Button>
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal
          show={isAiVariablesModalOpen}
          onHide={closeAiVariablesModal}
          centered
          size="xl"
          className="sendora-create-modal sendora-ai-variables-modal"
          dialogClassName="sendora-ai-variables-modal-dialog"
        >
          <Modal.Header closeButton>
            <Modal.Title>AI Variables</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="sendora-ai-variables-warning">
              Warning: AI variables are costly and only recommended to use for small sends (&lt;100 leads)
            </div>
            <div className="sendora-signature-modal-layout sendora-ai-variables-modal-layout">
              <div className="sendora-signature-modal-senders sendora-ai-variables-list-pane">
                <div className="sendora-signature-modal-senders-title">Variables</div>
                <div className="sendora-signature-modal-sender-list">
                  {normalizedCampaignAiVariables.map((entry) => {
                    const tokenKey = String(entry?.tokenKey || "").trim();
                    const isActive =
                      tokenKey.toLowerCase() ===
                      String(selectedAiVariableTokenKey || "").trim().toLowerCase();
                    return (
                      <button
                        type="button"
                        key={tokenKey}
                        className={`sendora-signature-sender-item ${isActive ? "is-active" : ""}`}
                        onClick={() => setSelectedAiVariableTokenKey(tokenKey)}
                      >
                        {String(entry?.label || tokenKey).trim() || tokenKey}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="sendora-ai-variable-add-btn"
                  onClick={handleAddCampaignAiVariable}
                  disabled={savingCampaignAiVariables}
                >
                  <i className="fa fa-plus" aria-hidden="true" /> Add variable
                </button>
              </div>
              <div className="sendora-signature-modal-thumbnails sendora-ai-variables-editor-pane">
                <div className="sendora-signature-modal-thumbnails-title">
                  {selectedAiVariableDraft
                    ? `${selectedAiVariableDraft.label} prompt`
                    : "Select a variable"}
                </div>
                {selectedAiVariableDraft ? (
                  <div className="sendora-ai-variable-editor">
                    <div className="sendora-ai-variable-token-badge">
                      Token: {`{{${selectedAiVariableDraft.tokenKey}}}`}
                    </div>
                    <p className="sendora-ai-variable-help">
                      Define what this variable should generate at preview/send time using recipient,
                      sender, and email context.
                    </p>
                    <textarea
                      className="sendora-ai-variable-prompt-input"
                      value={String(selectedAiVariableDraft?.prompt || "")}
                      onChange={(event) =>
                        handleUpdateSelectedAiVariablePrompt(event?.target?.value || "")
                      }
                      placeholder="Example: Write one short sentence referencing the recipient company and role, then add a concise call-to-action."
                      rows={10}
                      maxLength={AI_VARIABLE_PROMPT_MAX_CHARS}
                      disabled={savingCampaignAiVariables}
                    />
                    <div className="sendora-ai-variable-counter">
                      {`${String(selectedAiVariableDraft?.prompt || "").length}/${AI_VARIABLE_PROMPT_MAX_CHARS}`}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted">Add or select an AI variable.</div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="sendora-btn-outline"
              onClick={closeAiVariablesModal}
              disabled={savingCampaignAiVariables}
            >
              Close
            </Button>
            {hasAiVariablesModalChanges && (
              <Button
                className="sendora-btn"
                onClick={handleSaveAiVariablesModal}
                disabled={savingCampaignAiVariables}
              >
                {savingCampaignAiVariables ? "Saving..." : "Save"}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <Modal
          show={estimatedCompletionTimelineModal.isOpen}
          onHide={closeEstimatedCompletionTimelineModal}
          centered
          size="lg"
          className="sendora-create-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{estimatedCompletionTimelineModal.title || "Estimated completion timeline"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-muted mb-2">
              Timeline preview from now to completion in `{estimatedCompletionTimelineModal.timezone}`.
            </div>
            <div className="sendora-estimated-timeline-summary">
              <span className="sendora-estimated-timeline-pill is-primary">
                {Number(estimatedCompletionTimelineModal.leadsRemaining || 0).toLocaleString()} emails
                scheduled
              </span>
              <span className="sendora-estimated-timeline-pill is-subtle">
                {estimatedCompletionTimelineModal.timezone || "UTC"}
              </span>
              {estimatedCompletionTimelineModal.completionTimeLabel ? (
                <span className="sendora-estimated-timeline-pill is-success">
                  ETA: {estimatedCompletionTimelineModal.completionTimeLabel}
                </span>
              ) : null}
            </div>
            {estimatedCompletionTimelineModal.loading ? (
              <div className="text-muted">
                <i className="fa fa-spinner fa-spin" aria-hidden="true" /> Building timeline...
              </div>
            ) : !estimatedCompletionTimelineModal.hasScheduleWindow ? (
              <div className="text-danger">No active schedule window available for this configuration.</div>
            ) : !estimatedCompletionTimelineDays.length ? (
              <div className="text-muted">No scheduled sends to display.</div>
            ) : (
              <div className="sendora-estimated-timeline">
                {estimatedCompletionTimelineDays.map((day, dayIndex) => (
                  <div className="sendora-estimated-timeline-day" key={`${day.dateLabel}-${dayIndex}`}>
                    <div className="sendora-estimated-timeline-track">
                      {day.rows.map((row, rowIndex) => (
                        <div
                          className={`sendora-estimated-timeline-event ${
                            rowIndex === day.rows.length - 1 ? "is-last" : ""
                          }`}
                          key={`${day.dateLabel}-${row.rowIndex}-${String(row?.windowLabel || "")}`}
                        >
                          <div className="sendora-estimated-timeline-dot" />
                          <div className="sendora-estimated-timeline-card">
                            <div className="sendora-estimated-timeline-card-header">
                              <div className="sendora-estimated-timeline-card-header-left">
                                <span className="sendora-estimated-timeline-date">{day.dateLabel}</span>
                                <span className="sendora-estimated-timeline-pill is-window">
                                  {String(row?.windowLabel || "Schedule window")}
                                </span>
                              </div>
                              <span className="sendora-estimated-timeline-pill is-count">
                                {Number(row?.count || 0).toLocaleString()} emails
                              </span>
                            </div>
                            <div className="sendora-estimated-timeline-time">
                              {String(row?.firstSendLabel || "")} - {String(row?.lastSendLabel || "")}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button className="sendora-btn-outline" onClick={closeEstimatedCompletionTimelineModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={isBrandVoiceModalOpen}
          onHide={closeBrandVoiceModal}
          centered
          size="xl"
          className="sendora-create-modal sendora-brand-voice-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Select brand voice</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="sendora-signature-modal-layout sendora-brand-voice-modal-layout">
              <div className="sendora-signature-modal-senders sendora-brand-voice-list-pane">
                <div className="sendora-signature-modal-senders-title">Templates</div>
                <div className="sendora-signature-modal-sender-list">
                  {NORMALIZED_SENDORA_BRAND_VOICE_TEMPLATES.map((voice) => {
                    const voiceId = String(voice?.brandVoiceId || "").trim();
                    const isActive = voiceId === selectedModalBrandVoiceId;
                    return (
                      <button
                        type="button"
                        key={voiceId}
                        className={`sendora-signature-sender-item ${isActive ? "is-active" : ""}`}
                        onClick={() => setBrandVoiceSelectedIdDraft(voiceId)}
                      >
                        {String(voice?.name || "Template").trim()}
                      </button>
                    );
                  })}
                </div>
                <div className="sendora-signature-modal-senders-title mt-3">Custom voices</div>
                <div className="sendora-signature-modal-sender-list">
                  {(brandVoiceCustomDraft || []).length ? (
                    (brandVoiceCustomDraft || []).map((voice) => {
                      const voiceId = String(voice?.brandVoiceId || "").trim();
                      const isActive = voiceId === selectedModalBrandVoiceId;
                      return (
                        <button
                          type="button"
                          key={voiceId}
                          className={`sendora-signature-sender-item ${isActive ? "is-active" : ""}`}
                          onClick={() => setBrandVoiceSelectedIdDraft(voiceId)}
                        >
                          {String(voice?.name || "Custom voice").trim()}
                        </button>
                      );
                    })
                  ) : (
                    <div className="text-muted">No custom voices yet.</div>
                  )}
                </div>
                <button
                  type="button"
                  className="sendora-ai-variable-add-btn"
                  onClick={handleAddCustomBrandVoice}
                  disabled={savingBrandVoices}
                >
                  <i className="fa fa-plus" aria-hidden="true" /> Create new brand voice
                </button>
              </div>
              <div className="sendora-signature-modal-thumbnails sendora-brand-voice-editor-pane">
                <div className="sendora-signature-modal-thumbnails-title">
                  {selectedModalBrandVoice?.name || "Select a brand voice"}
                </div>
                {selectedModalBrandVoice ? (
                  <div className="sendora-brand-voice-editor">
                    <div className="sendora-brand-voice-meta-row">
                      <span className="sendora-signature-thumbnail-tag">
                        {selectedModalBrandVoiceIsTemplate ? "Template" : "Custom"}
                      </span>
                    </div>
                    {selectedModalBrandVoiceIsTemplate ? (
                      <>
                        <p className="sendora-ai-variable-help mb-2">
                          Templates are managed centrally. Select one to guide copy generation.
                        </p>
                        <pre className="sendora-brand-voice-prompt-preview">
                          {String(selectedModalBrandVoice?.prompt || "")}
                        </pre>
                      </>
                    ) : (
                      <>
                        <div className="form-group form-group-full">
                          <label>Name</label>
                          <input
                            type="text"
                            className="form-control"
                            value={String(selectedModalBrandVoice?.name || "")}
                            onChange={(event) =>
                              handleUpdateSelectedCustomBrandVoice({
                                name: String(event?.target?.value || ""),
                              })
                            }
                            disabled={savingBrandVoices}
                            placeholder="Custom voice name"
                          />
                        </div>
                        <div className="form-group form-group-full mb-0">
                          <label>Prompt</label>
                          <textarea
                            className="sendora-brand-voice-prompt-input"
                            value={String(selectedModalBrandVoice?.prompt || "")}
                            onChange={(event) =>
                              handleUpdateSelectedCustomBrandVoice({
                                prompt: String(event?.target?.value || ""),
                              })
                            }
                            rows={14}
                            disabled={savingBrandVoices}
                            placeholder="Describe the writing tone, style, and rules this brand voice should follow."
                          />
                        </div>
                        <button
                          type="button"
                          className="sendora-brand-voice-delete-btn"
                          onClick={() =>
                            handleDeleteCustomBrandVoice(selectedModalBrandVoice?.brandVoiceId || "")
                          }
                          disabled={savingBrandVoices}
                        >
                          Delete custom voice
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-muted">Select or create a brand voice.</div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="sendora-btn-outline"
              onClick={closeBrandVoiceModal}
              disabled={savingBrandVoices}
            >
              Close
            </Button>
            {hasBrandVoiceModalChanges && (
              <Button
                className="sendora-btn"
                onClick={handleSaveBrandVoiceModal}
                disabled={savingBrandVoices}
              >
                {savingBrandVoices ? "Saving..." : "Save"}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <Modal
          show={isSenderSignatureModalOpen}
          onHide={closeSenderSignatureModal}
          centered
          size="xl"
          className="sendora-create-modal sendora-signature-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit sender signatures</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="sendora-signature-modal-layout">
              <div className="sendora-signature-modal-senders">
                <div className="sendora-signature-modal-senders-title">Senders</div>
                <div className="sendora-signature-modal-sender-list">
                  {signatureModalSenderAccounts.map((account) => {
                    const senderEmail = normalizeEmail(
                      account?.emailLowercase || account?.email || ""
                    );
                    const isActive = senderEmail === normalizeEmail(selectedSignatureSenderEmail || "");
                    return (
                      <button
                        type="button"
                        key={senderEmail}
                        className={`sendora-signature-sender-item ${isActive ? "is-active" : ""}`}
                        onClick={() => setSelectedSignatureSenderEmail(senderEmail)}
                      >
                        {senderEmail}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sendora-signature-modal-thumbnails">
                <div className="sendora-signature-modal-thumbnails-title">
                  {selectedSignatureSenderAccount
                    ? `Signatures for ${normalizeEmail(
                        selectedSignatureSenderAccount?.emailLowercase ||
                          selectedSignatureSenderAccount?.email ||
                          ""
                      )}`
                    : "Select a sender"}
                </div>
                {!!selectedSignatureSenderAccount && (
                  <div className="sendora-signature-thumbnail-grid">
                    {(() => {
                      const defaultSignatureCodeViewEnabled = !!senderSignatureCodeViewById.default;
                      return (
                      <button
                        type="button"
                        className={`sendora-signature-thumbnail ${
                          selectedSignatureIdForSender === "default" ? "is-selected" : ""
                        }`}
                        onClick={() => handleSelectSenderSignatureThumbnail("default")}
                      >
                        <div className="sendora-signature-thumbnail-header">
                          <span>Default signature</span>
                          <span className="sendora-signature-thumbnail-header-actions">
                            <span className="sendora-signature-thumbnail-tag">Not editable</span>
                            <span
                              className={`sendora-signature-code-btn ${
                                defaultSignatureCodeViewEnabled ? "is-active" : ""
                              }`}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleSenderSignatureCodeView("default");
                              }}
                              title={defaultSignatureCodeViewEnabled ? "Show rendered view" : "Show code view"}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  toggleSenderSignatureCodeView("default");
                                }
                              }}
                              aria-label={defaultSignatureCodeViewEnabled ? "Show rendered view" : "Show code view"}
                            >
                              <i className="fa fa-code" aria-hidden="true" />
                            </span>
                          </span>
                        </div>
                        {defaultSignatureCodeViewEnabled ? (
                          <pre
                            className="sendora-signature-thumbnail-preview sendora-signature-thumbnail-code"
                            dangerouslySetInnerHTML={{
                              __html: buildSenderSignatureCodeViewHtml(DEFAULT_SENDER_SIGNATURE_TEMPLATE),
                            }}
                          />
                        ) : (
                          <div
                            className="sendora-signature-thumbnail-preview"
                            dangerouslySetInnerHTML={{
                              __html: buildSenderSignaturePreviewHtml(DEFAULT_SENDER_SIGNATURE_TEMPLATE),
                            }}
                          />
                        )}
                      </button>
                      );
                    })()}
                    {selectedSignatureCustomSignatures.map((signature) => {
                      const signatureId = String(signature?.signatureId || "").trim();
                      const isSelected = selectedSignatureIdForSender === signatureId;
                      const codeViewEnabled = !!senderSignatureCodeViewById[signatureId];
                      return (
                        <button
                          type="button"
                          key={signatureId}
                          className={`sendora-signature-thumbnail ${isSelected ? "is-selected" : ""}`}
                          onClick={() => handleSelectSenderSignatureThumbnail(signatureId)}
                        >
                          <div className="sendora-signature-thumbnail-header">
                            <span>Custom signature</span>
                            <span className="sendora-signature-thumbnail-header-actions">
                              <span
                                className={`sendora-signature-code-btn ${
                                  codeViewEnabled ? "is-active" : ""
                                }`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleSenderSignatureCodeView(signatureId);
                                }}
                                title={codeViewEnabled ? "Show rendered view" : "Show code view"}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    toggleSenderSignatureCodeView(signatureId);
                                  }
                                }}
                                aria-label={codeViewEnabled ? "Show rendered view" : "Show code view"}
                              >
                                <i className="fa fa-code" aria-hidden="true" />
                              </span>
                              <span
                                className="sendora-signature-delete-btn"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDeleteCustomSenderSignature(signatureId);
                                }}
                                title="Delete custom signature"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleDeleteCustomSenderSignature(signatureId);
                                  }
                                }}
                                aria-label="Delete custom signature"
                              >
                                <i className="fa fa-trash" aria-hidden="true" />
                              </span>
                            </span>
                          </div>
                          {codeViewEnabled ? (
                            <pre
                              className="sendora-signature-thumbnail-preview sendora-signature-thumbnail-code"
                              dangerouslySetInnerHTML={{
                                __html: buildSenderSignatureCodeViewHtml(signature?.html || ""),
                              }}
                            />
                          ) : (
                            <div
                              className="sendora-signature-thumbnail-preview is-editable"
                              contentEditable
                              suppressContentEditableWarning
                              dangerouslySetInnerHTML={{
                                __html: buildSenderSignaturePreviewHtml(signature?.html || ""),
                              }}
                              onClick={(event) => event.stopPropagation()}
                              onBlur={(event) =>
                                handleSaveCustomSenderSignatureHtml(
                                  signatureId,
                                  stripVariableHighlightMarkup(
                                    String(event?.currentTarget?.innerHTML || "")
                                  )
                                )
                              }
                            />
                          )}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className="sendora-signature-add-custom-btn"
                      onClick={handleAddCustomSenderSignature}
                      title="Duplicate default signature"
                    >
                      <i className="fa fa-plus" aria-hidden="true" />
                      <span>Add custom signature</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="sendora-btn-outline"
              onClick={closeSenderSignatureModal}
              disabled={savingSenderSignatures}
            >
              Close
            </Button>
            {hasSignatureModalChanges && (
              <Button
                className="sendora-btn"
                onClick={handleSaveSenderSignatureModal}
                disabled={savingSenderSignatures}
              >
                {savingSenderSignatures ? "Saving..." : "Save"}
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <Modal
          show={isSenderAccountModalOpen}
          onHide={() => setIsSenderAccountModalOpen(false)}
          centered
          className="sendora-create-modal"
        >
          <form onSubmit={handleSaveSenderAccount}>
            <Modal.Header closeButton>
              <Modal.Title>Sender accounts</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="form-group form-group-full">
                <label>Existing sender accounts</label>
                <div className="collaborator-controls">
                  <select
                    className="form-control"
                    value={selectedSenderAccountEmail}
                    onChange={(e) => setSelectedSenderAccountEmail(e.target.value)}
                  >
                    <option value="">Select existing sender account</option>
                    {(senderAccounts || []).map((account) => {
                      const emailKey = normalizeEmail(account?.emailLowercase || account?.email || "");
                      const isAlreadyAdded = sequenceSenderAccountEmailSet.has(emailKey);
                      return (
                        <option key={emailKey} value={emailKey} disabled={isAlreadyAdded}>
                          {formatSenderAccountLabel(account)}
                          {isAlreadyAdded ? " (Already added)" : ""}
                        </option>
                      );
                    })}
                  </select>
                  <Button
                    className="sendora-btn"
                    onClick={handleAddExistingSenderAccountFromModal}
                    disabled={!selectedSenderAccountEmail}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="sender-modal-divider" role="separator" aria-hidden="true">
                <span>Or create a new sender account</span>
              </div>
              <div className="sendora-form-grid mb-0">
                <div className="form-group form-group-full">
                  <label>Seed from Landmark employee (optional)</label>
                  <select
                    className="form-control"
                    value=""
                    onChange={(e) => {
                      const pickedEmail = normalizeEmail(e.target.value);
                      const picked = (senderDirectory || []).find(
                        (entry) =>
                          normalizeEmail(entry?.emailLowercase || entry?.email || "") === pickedEmail
                      );
                      if (!picked) return;
                      setSenderAccountForm({
                        email: picked.emailLowercase || picked.email || "",
                        firstName: picked.firstName || "",
                        lastName: picked.lastName || "",
                      });
                    }}
                  >
                    <option value="">Select employee...</option>
                    {(senderDirectory || []).map((account) => {
                      const emailKey = normalizeEmail(account?.emailLowercase || account?.email || "");
                      return (
                        <option key={emailKey} value={emailKey}>
                          {formatSenderAccountLabel(account)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="form-group form-group-full">
                  <label>Sender email</label>
                  <input
                    className="form-control"
                    value={senderAccountForm.email}
                    onChange={(e) =>
                      setSenderAccountForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="sender@landmarkventures.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>First name</label>
                  <input
                    className="form-control"
                    value={senderAccountForm.firstName}
                    onChange={(e) =>
                      setSenderAccountForm((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                    placeholder="First"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last name</label>
                  <input
                    className="form-control"
                    value={senderAccountForm.lastName}
                    onChange={(e) =>
                      setSenderAccountForm((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                    placeholder="Last"
                    required
                  />
                </div>
              </div>
              <div className="sender-create-actions">
                <Button
                  type="submit"
                  className="sendora-btn"
                  disabled={!isSenderAccountFormComplete}
                >
                  Add new sender
                </Button>
                <Button
                  type="button"
                  className="sendora-btn-outline"
                  onClick={() => setIsSenderAccountModalOpen(false)}
                >
                  Close
                </Button>
              </div>
            </Modal.Body>
          </form>
        </Modal>

        <Modal
          show={variantSenderModal.isOpen}
          onHide={closeVariantSenderModal}
          centered
          className="sendora-create-modal"
        >
            <Modal.Header closeButton>
              <Modal.Title>Variant settings</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="form-group mb-0">
                <label>Select sender account</label>
                <select
                  className="form-control"
                  value={normalizeEmail(
                    variantModalVariant?.senderAccount?.emailLowercase ||
                      variantModalVariant?.senderAccount?.email ||
                      variantModalVariant?.senderAccountEmail ||
                      ""
                  )}
                  onChange={(e) => handleSetVariantSenderOverride(e.target.value)}
                >
                  <option value="">Default (use sequence sending accounts)</option>
                  {(senderAccounts || []).map((account) => {
                    const emailKey = normalizeEmail(account?.emailLowercase || account?.email || "");
                    return (
                      <option key={emailKey} value={emailKey}>
                        {formatSenderAccountLabel(account)}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="section-desc mt-2 mb-0">
                This sender will always override the sequence sending accounts for this variant.
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button className="sendora-btn-outline" onClick={closeVariantSenderModal}>
                Close
              </Button>
            </Modal.Footer>
        </Modal>

        <Modal
          show={isRenameCampaignModalOpen}
          onHide={() => {
            setIsRenameCampaignModalOpen(false);
            setRenameCampaignId("");
            setRenameCampaignName("");
          }}
          centered
          className="sendora-rename-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Rename campaign</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group mb-0">
              <label>New name</label>
              <input
                className="form-control"
                value={renameCampaignName}
                onChange={(e) => setRenameCampaignName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleRenameCampaign();
                  }
                }}
                placeholder="My Campaign"
                autoFocus
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="sendora-btn-outline"
              onClick={() => {
                setIsRenameCampaignModalOpen(false);
                setRenameCampaignId("");
                setRenameCampaignName("");
              }}
              disabled={campaignRowActionId === renameCampaignId}
            >
              Cancel
            </Button>
            <Button
              className="sendora-btn"
              onClick={handleRenameCampaign}
              disabled={campaignRowActionId === renameCampaignId}
            >
              Rename
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={isCreateModalOpen}
          onHide={closeCreateCampaignModal}
          centered
          className="sendora-create-modal"
        >
          <form
            onSubmit={handleCreateCampaign}
            className={`create-campaign-modal-form ${savingCampaign ? "is-submitting" : ""} ${
              isCreateCampaignModalWaitingForSalesforce ? "is-waiting-for-salesforce" : ""
            }`}
          >
            {(savingCampaign || isCreateCampaignModalWaitingForSalesforce) && (
              <div
                className={`create-campaign-modal-loader ${
                  isCreateCampaignModalWaitingForSalesforce ? "is-waiting-for-salesforce" : ""
                }`}
              >
                <img src={BlueSquaresLoading} alt="Loading" />
                <div className="create-campaign-modal-loader-label">
                  {savingCampaign
                    ? "Loading leads from Salesforce campaign..."
                    : "Loading campaigns from Salesforce..."}
                </div>
              </div>
            )}
            <Modal.Header closeButton>
              <Modal.Title>Create campaign</Modal.Title>
            </Modal.Header>
            {!isCreateCampaignModalWaitingForSalesforce && (
              <>
                <Modal.Body>
                  <div className="sendora-form-grid mb-0">
                    <div className="form-group form-group-full">
                      <label>Linked Salesforce campaign</label>
                      <Select
                        className="sendora-react-select"
                        classNamePrefix="sendora-react-select"
                        value={createModalSelectedSalesforceOption}
                        onChange={(option) =>
                          handleCreateSalesforceCampaignChange(String(option?.value || ""))
                        }
                        options={createModalSalesforceSelectOptions}
                        isSearchable
                        isClearable
                        placeholder="Select Salesforce campaign..."
                        noOptionsMessage={() => "No campaigns found"}
                      />
                    </div>
                    <div className="form-group form-group-full">
                      <label>Campaign name</label>
                      <input
                        className="form-control"
                        value={createModalForm.name}
                        onChange={(e) =>
                          setCreateModalForm((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Glean London Outreach"
                        required
                      />
                    </div>
                    <div className="form-group form-group-full">
                      <label>Notes (optional)</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={createModalForm.notes}
                        onChange={(e) =>
                          setCreateModalForm((prev) => ({
                            ...prev,
                            notes: e.target.value,
                          }))
                        }
                        placeholder="Describe campaign goal and audience"
                      />
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    className="sendora-btn-outline"
                    onClick={closeCreateCampaignModal}
                    disabled={savingCampaign}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="sendora-btn" disabled={savingCampaign}>
                    {savingCampaign ? "Creating..." : "Create campaign"}
                  </Button>
                </Modal.Footer>
              </>
            )}
          </form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default SendoraDashboard;
